import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  alpha,
  CircularProgress,
  Tooltip,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Avatar,
  Backdrop,
  Alert,
  Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Snackbar,
  Select, MenuItem, FormControl,
  Popover,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MapIcon from '@mui/icons-material/Map';
import PlaceIcon from '@mui/icons-material/Place';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InboxIcon from '@mui/icons-material/Inbox';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import TimerIcon from '@mui/icons-material/Timer';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import EngineeringIcon from '@mui/icons-material/Engineering';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import CellTowerIcon from '@mui/icons-material/CellTower';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SecurityIcon from '@mui/icons-material/Security';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import axios from './api';
import { getUser } from 'src/utils/Common';
import {
  buildOpenStreetMapMarkerSrc,
  buildOpenStreetMapScheduleSrc,
} from './utils/openStreetMap';
import {
  calculateToaScheduling,
  resolveToaStatusDescription,
} from './utils/toaSchedulingCalculator';
import {
  getFirstServicePaperId,
  getServicePaperIds,
  unwrapServicePaperDetailResponse,
} from './utils/toaSchedulingDetails';
import { useQueryClient } from '@tanstack/react-query';
import Combo from './Combo';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';
import DraggableResizablePopup from './DraggableResizablePopup';
import MoveSimulation from './MoveSimulation/App';
import { getMoveImpact } from './MoveSimulation/calculator';
import { removeTechnicianSkillCache } from './utils/indexedDBPersister';
import useTechnicianLiveLocation, { TECH_LOCATION_STATUS } from './useTechnicianLiveLocation';

dayjs.extend(customParseFormat);
dayjs.locale('tr');

// ── Gün etiketleri (Grup Mahalle) ──
const GM_DAY_LABELS = { 1: 'Pazartesi', 2: 'Salı', 3: 'Çarşamba', 4: 'Perşembe', 5: 'Cuma', 6: 'Cumartesi', 7: 'Pazar' };
const GM_DAYS_INIT = { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false, 7: false };

// ── Saat aralığı seçenekleri (Teknisyen Mahalle) ──
const TM_TIME_SLOTS = [
  '00:00-00:00', '09:00-10:00', '09:00-11:00', '10:00-11:00', '10:00-12:00',
  '11:00-12:00', '13:00-14:00', '13:00-15:00', '14:00-15:00', '14:00-16:00',
  '15:00-16:00', '15:00-17:00', '16:00-17:00', '16:00-18:00', '17:00-18:00',
  '17:00-19:00', '18:00-19:00', '18:00-20:00', '19:00-20:00', '19:00-21:00',
  '20:00-21:00', '20:00-22:00', '21:00-22:00',
];

// ── İşlem seçenekleri (Teknisyen Mahalle) ──
const TM_OPERATIONS = [
  { value: 100, label: 'Ana Çalışma Bölgesi(100)', bg: '#c8e6c9' },
  { value: 50, label: 'Orta Yakın Çalışma Bölgesi(50)', bg: '#dcedc8' },
  { value: 10, label: 'Uzak Çalışma Bölgesi(10)', bg: '#ffe0b2' },
  { value: 1, label: 'Çok Uzak Çalışma Bölgesi(1)', bg: '#ffcdd2' },
  { value: 0, label: 'Kesinlikle Çalışmaz(0)', bg: '#ff0400' },
];

// ── İşlem seçenekleri (Teknisyen Uzmanlık) ──
const TU_OPERATIONS = [
  { value: 100, label: 'Çalışır', bg: '#c8e6c9' },
  { value: 68, label: 'Gerekirse Çalışır', bg: '#fff9c4' },
  { value: 2, label: 'Çalışmaz', bg: '#ffcdd2' },
  { value: 21, label: 'Kesinlikle Çalışmaz', bg: '#ff0400' },
];

// ── Renk paleti ──
const COLORS = {
  travel: '#FF9800',
  travelDark: '#F57C00',
  work: '#00C853',
  workDark: '#00A844',
  idle: '#90A4AE',
  idleDark: '#78909C',
  appointment: '#448AFF',
  nowLine: '#FF1744',
  headerBg: 'linear-gradient(180deg, #f0f4ff 0%, #e8ecf8 100%)',
  headerBgFlat: '#f0f4ff',
  headerBgDark: 'linear-gradient(180deg, #1a1a2e 0%, #16162a 100%)',
  headerBgDarkFlat: '#1a1a2e',
  rowEven: '#ffffff',
  rowEvenDark: '#18181c',
  rowOdd: '#f7f9ff',
  rowOddDark: '#1c1c26',
  rowHover: '#dbeafe',
  rowHoverDark: '#1e2d4d',
  offHours: 'rgba(0,0,0,0.03)',
  offHoursDark: 'rgba(255,255,255,0.02)',
  workHoursBorder: 'rgba(33,150,243,0.12)',
  workHoursBorderDark: 'rgba(33,150,243,0.08)',
};

// ── Durum renkleri (referans görseldeki gibi) ──
const STATUS_COLORS = {
  started: { bg: '#c8e6c9', text: '#2e7d32', label: 'Başladı', icon: '▶️' },
  completed: { bg: '#bbdefb', text: '#0d47a1', label: 'Tamamlandı', icon: '✅' },
  pending: { bg: '#e0e0e0', text: '#616161', label: 'Beklemede', icon: '⏳' },
  cancelled: { bg: '#ffcdd2', text: '#b71c1c', label: 'İptal', icon: '❌' },
  notdone: { bg: '#fff3e0', text: '#e65100', label: 'Yapılmadı', icon: '⚠️' },
  suspended: { bg: '#e1bee7', text: '#6a1b9a', label: 'Askıda', icon: '⏸️' },
  road: { bg: '#fff8e1', text: '#f57f17', label: 'Yolda', icon: '🚗' },
  launch: { bg: '#e0f7fa', text: '#00695c', label: 'Launch', icon: '🚀' },
  bucket: { bg: '#e3f2fd', text: '#0277bd', label: 'Servis Lokasyonu', icon: '🏢' },
};

const STATUS_KEY_ALIASES = {
  başladı: 'started',
  basladi: 'started',
  tamamlandı: 'completed',
  tamamlandi: 'completed',
  beklemede: 'pending',
  bekliyor: 'pending',
  iptal: 'cancelled',
  'iptal edildi': 'cancelled',
  yapılmadı: 'notdone',
  yapilmadi: 'notdone',
  'bitmiş değil': 'notdone',
  'bitmis degil': 'notdone',
  askıda: 'suspended',
  askida: 'suspended',
  'askıya alındı': 'suspended',
  'askiya alindi': 'suspended',
  yolda: 'road',
  'yola çıkıldı': 'road',
  'yola çikildi': 'road',
  'yola cikildi': 'road',
  servis: 'bucket',
  'servis lokasyonu': 'bucket',
};

const normalizeStatusKey = (status) => {
  const key = String(status || '').toLocaleLowerCase('tr').trim();
  return STATUS_KEY_ALIASES[key] || key;
};

// Günlük İş Listesi popup'ında hangi işlerin listeleneceğini belirler.
// Teknisyen satırı  -> yalnızca o teknisyenin işleri.
// Servis (bucket) satırı -> servisin TÜM işleri (teknisyenlere dağıtılmış olanlar dahil).
// Pano GetAronToaSchedulingAvailable'a oturumdaki servisle gittiği için data'nın
// tamamı zaten tek servise aittir; bu yüzden bucket satırında data'nın tümü kullanılır.
const getTechDetailJobs = (allData, techCode) => {
  const rows = Array.isArray(allData) ? allData : [];
  const ownJobs = rows.filter(d => String(d.TechCode) === String(techCode));
  const isBucket = ownJobs.some(d => normalizeStatusKey(d.StatusDescription) === 'bucket');
  return { jobs: isBucket ? rows : ownJobs, isBucket };
};

const isBucketNeighbourhood = (item) => String(
  item?.Neighbourhood
  ?? item?.Neighborhood
  ?? item?.NEIGHBOURHOOD
  ?? '',
).toLocaleLowerCase('tr').trim() === 'bucket';

const getStatusColorObj = (status) => {
  if (!status) return { bg: '#f5f5f5', text: '#757575', label: 'Bilinmiyor', icon: '❓' };
  const key = normalizeStatusKey(status);
  return STATUS_COLORS[key] || { bg: '#ede7f6', text: '#4527a0', label: status, icon: '🔹' };
};

// ── Saat parse helper ──
const parseTime = (timeStr) => {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return null;
  // 00:00-05:59 arası saatler ertesi güne ait → 24:00-29:59 olarak kaydır
  if (h < HOUR_START) return (h + 24) * 60 + m;
  return h * 60 + m;
};

const formatMinutes = (min) => {
  if (min == null) return '--:--';
  const h = Math.floor(min / 60) % 24;
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const formatDuration = (min) => {
  if (!min) return '0dk';
  if (min < 60) return `${min}dk`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}s ${m}dk` : `${h}s`;
};

// Zaman aralığı sabitleri
const HOUR_START = 6;
const HOUR_END = 30; // 30 = ertesi gün 06:00
const TOTAL_HOURS = HOUR_END - HOUR_START;
const HOUR_WIDTH_DEFAULT = 80;
const HOUR_WIDTH_DEFAULT_MOBILE = 50;
const HOUR_WIDTH_MIN = 30;
const HOUR_WIDTH_MAX = 200;
const ZOOM_STEP = 20;
const ROW_HEIGHT = 38;
const ROW_HEIGHT_MOBILE = 34;
const ROW_HEIGHTS = { compact: 28, comfortable: 38, expanded: 50 };
const ROW_HEIGHTS_MOBILE = { compact: 24, comfortable: 34, expanded: 44 };
const RESOURCE_COL_WIDTH = 220;
const RESOURCE_COL_WIDTH_MOBILE = 160;
const AUTO_REFRESH_INTERVAL = 5 * 60 * 1000; // 5dk
const APPOINTMENT_END_WARNING_MINUTES = 15;

const APPOINTMENT_ALERT_IGNORED_STATUSES = new Set([
  'started',
  'road',
  'completed',
  'cancelled',
  'notdone',
  'suspended',
  'bucket',
]);

const getAppointmentOperationalAlert = (job, nowMin, isSelectedToday) => {
  if (!isSelectedToday || !job || !hasValidAppointmentNo(job.OfscNr)) return null;

  const neighbourhood = String(job.Neighbourhood || job.Neighborhood || '').toLocaleLowerCase('tr').trim();
  if (neighbourhood === 'yemek') return null;

  const status = resolveToaStatusDescription(
    job.TOA_DURUM ?? job.toa_durum ?? job.ToaDurum ?? job.toaDurum,
    job.StatusDescription || '',
  );
  const statusKey = normalizeStatusKey(status);
  if (APPOINTMENT_ALERT_IGNORED_STATUSES.has(statusKey)) return null;

  const appointmentStart = parseTime(job.AppointmentTime);
  if (appointmentStart == null) return null;
  const parsedAppointmentEnd = parseTime(job.AppointmentTimeEnd);
  const appointmentEnd = parsedAppointmentEnd != null && parsedAppointmentEnd >= appointmentStart
    ? parsedAppointmentEnd
    : appointmentStart;

  const minutesUntilAppointment = appointmentStart - nowMin;
  if (minutesUntilAppointment > 0) return null;

  if (nowMin <= appointmentEnd) {
    const remainingMinutes = Math.max(0, appointmentEnd - nowMin);
    if (remainingMinutes > APPOINTMENT_END_WARNING_MINUTES) return null;

    return {
      type: 'departure-required',
      label: 'Yola çıkılmadı',
      detail: `Randevu aralığı ${job.AppointmentTime}-${job.AppointmentTimeEnd || job.AppointmentTime}; bitime ${formatDuration(remainingMinutes)} kaldı ve durum hâlâ “${getStatusColorObj(status).label}”.`,
      shortTime: remainingMinutes === 0 ? 'son dakika' : `${formatDuration(remainingMinutes)} kaldı`,
      color: '#c2410c',
      background: '#fff7ed',
      border: '#fb923c',
      priority: 2,
    };
  }

  const elapsedMinutes = Math.max(0, nowMin - appointmentEnd);
  return {
    type: 'appointment-overdue',
    label: 'Randevu aralığı geçti',
    detail: `${job.AppointmentTime}-${job.AppointmentTimeEnd || job.AppointmentTime} randevu aralığı ${formatDuration(elapsedMinutes)} önce bitti; durum hâlâ “${getStatusColorObj(status).label}”.`,
    shortTime: `+${formatDuration(elapsedMinutes)}`,
    color: '#b91c1c',
    background: '#fef2f2',
    border: '#fca5a5',
    priority: 2,
  };
};

const isAppointmentDelayed = (job, nowMin, isSelectedToday) => {
  if (!job || !hasValidAppointmentNo(job.OfscNr)) return false;

  const neighbourhood = String(job.Neighbourhood || job.Neighborhood || '').toLocaleLowerCase('tr').trim();
  if (neighbourhood === 'yemek') return false;

  const status = resolveToaStatusDescription(
    job.TOA_DURUM ?? job.toa_durum ?? job.ToaDurum ?? job.toaDurum,
    job.StatusDescription || '',
  );
  const statusKey = normalizeStatusKey(status);
  if (['completed', 'cancelled', 'notdone', 'suspended', 'bucket'].includes(statusKey)) return false;

  const appointmentStart = parseTime(job.AppointmentTime);
  const parsedAppointmentEnd = parseTime(job.AppointmentTimeEnd);
  // API bazı randevularda bitiş saatini göndermiyor. Kart uyarısında olduğu gibi
  // bu durumda başlangıç saatini bitiş kabul ederek kırmızı uyarı ve filtreyi eşitle.
  const appointmentEnd = parsedAppointmentEnd != null && (
    appointmentStart == null || parsedAppointmentEnd >= appointmentStart
  )
    ? parsedAppointmentEnd
    : appointmentStart;
  if (appointmentEnd == null) return false;

  const arrival = parseTime(job.ArrivalatCustomer);
  // Kırmızı randevu uyarısıyla birebir aynı eşik: randevu aralığının bitişi.
  // Böylece ekrandaki kırmızı iş sayısı ile "Geciken" filtresi aynı sonucu verir.
  const delayThreshold = appointmentEnd;
  return arrival != null
    ? arrival > delayThreshold
    : isSelectedToday && nowMin > delayThreshold;
};

// Lejanttaki "Geciken" filtresi, zaman çizelgesinde uyarı rozeti/çerçevesi
// gösterilen işlerle birebir aynı kümeden beslenir. Bu küme; randevu bitimine
// 15 dakika kalmasına rağmen yola çıkılmamış ve randevu aralığı geçmiş işleri kapsar.
const hasAppointmentWarning = (job, nowMin, isSelectedToday) => (
  isAppointmentDelayed(job, nowMin, isSelectedToday)
  || Boolean(getAppointmentOperationalAlert(job, nowMin, isSelectedToday))
);

// Avatar arka plan renkleri (pastel, sırayla tekrarla)
const AVATAR_COLORS = ['#EEEDFE', '#E1F5EE', '#FAECE7', '#FBEAF0'];
const APPEARANCE_STORAGE_KEY = 'toaSchedulingAppearance';
const DEFAULT_APPEARANCE = {
  useCustomColors: false,
  pageBg: '#f4f6fb',
  toolbarBg: '#ffffff',
  accentColor: '#1565c0',
  viewScale: 100,
};

const readAppearanceSettings = () => {
  if (typeof window === 'undefined') return DEFAULT_APPEARANCE;
  try {
    const stored = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
    return stored ? { ...DEFAULT_APPEARANCE, ...JSON.parse(stored) } : DEFAULT_APPEARANCE;
  } catch (_) {
    return DEFAULT_APPEARANCE;
  }
};

const safeHexColor = (value, fallback) => (
  /^#[0-9a-f]{6}$/i.test(String(value || '').trim()) ? String(value).trim() : fallback
);

const parseDetailDateValue = (value) => {
  if (!value) return null;
  const parsed = dayjs(value, [
    'YYYY-MM-DDTHH:mm:ss.SSS',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DD',
    'DD.MM.YYYY HH:mm:ss',
    'DD.MM.YYYY HH:mm',
    'DD.MM.YYYY',
  ], true);
  if (parsed.isValid()) return parsed.toDate();
  const loose = dayjs(value);
  return loose.isValid() ? loose.toDate() : null;
};

const findDetailAppointmentDate = (detail) => {
  const keys = [
    'AppointmentDate',
    'appointmentDate',
    'APPOINTMENTDATE',
    'AppointmentStartDate',
    'appointmentStartDate',
    'RandevuTarihi',
    'randevuTarihi',
  ];
  const seen = new Set();
  const visit = (node, depth = 0) => {
    if (!node || depth > 4 || typeof node !== 'object' || seen.has(node)) return null;
    seen.add(node);
    for (const key of keys) {
      const date = parseDetailDateValue(node[key]);
      if (date) return date;
    }
    if (Array.isArray(node)) {
      for (const item of node) {
        const date = visit(item, depth + 1);
        if (date) return date;
      }
      return null;
    }
    for (const value of Object.values(node)) {
      const date = visit(value, depth + 1);
      if (date) return date;
    }
    return null;
  };
  return visit(detail);
};

// İş saatleri (request parametrelerinden)
const WORK_START = '08:00';
const WORK_END = '18:00';
const WORK_START_MIN = parseTime(WORK_START);
const WORK_END_MIN = parseTime(WORK_END);
const DEFAULT_TRAVEL_DURATION_THRESHOLD = 40;
const DEFAULT_TECHNICIAN_MOVE_CAPACITY_RULE_ENABLED = true;
const DEFAULT_TECHNICIAN_MOVE_CAPACITY_ALLOWANCE_MINUTES = 120;
const DEFAULT_TECHNICIAN_MOVE_CAPACITY_MAX_WORK_END_TIME = '20:00';
const DEFAULT_TECHNICIAN_TO_BUCKET_MOVE_ENABLED = false;

const getTravelDurationThreshold = () => {
  const configuredValue = typeof window !== 'undefined'
    ? window.APP_CONFIG?.travelDurationThreshold
    : undefined;
  const threshold = Number(configuredValue);

  return configuredValue !== '' && Number.isFinite(threshold) && threshold >= 0
    ? threshold
    : DEFAULT_TRAVEL_DURATION_THRESHOLD;
};

const getTechnicianMoveCapacityConfig = () => {
  const runtimeConfig = typeof window !== 'undefined' ? (window.APP_CONFIG || {}) : {};
  const configuredEnabled = runtimeConfig.technicianMoveCapacityRuleEnabled;
  const configuredAllowance = runtimeConfig.technicianMoveCapacityAllowanceMinutes;
  const configuredMaxWorkEndTime = runtimeConfig.technicianMoveCapacityMaxWorkEndTime;
  let enabled = DEFAULT_TECHNICIAN_MOVE_CAPACITY_RULE_ENABLED;

  if (typeof configuredEnabled === 'boolean') {
    enabled = configuredEnabled;
  } else if (typeof configuredEnabled === 'number') {
    enabled = configuredEnabled !== 0;
  } else if (typeof configuredEnabled === 'string') {
    const normalizedEnabled = configuredEnabled.toLocaleLowerCase('tr').trim();
    if (['true', '1', 'yes', 'on'].includes(normalizedEnabled)) enabled = true;
    if (['false', '0', 'no', 'off'].includes(normalizedEnabled)) enabled = false;
  }

  const allowanceMinutes = Number(configuredAllowance);
  const defaultMaxWorkEndMinutes = parseTime(
    DEFAULT_TECHNICIAN_MOVE_CAPACITY_MAX_WORK_END_TIME,
  );
  const configuredMaxWorkEndMinutes = parseTime(String(configuredMaxWorkEndTime || ''));
  const maxWorkEndMinutes = configuredMaxWorkEndMinutes != null
    ? configuredMaxWorkEndMinutes
    : defaultMaxWorkEndMinutes;
  return {
    enabled,
    allowanceMinutes: configuredAllowance !== '' && configuredAllowance != null
      && Number.isFinite(allowanceMinutes) && allowanceMinutes >= 0
      ? Math.round(allowanceMinutes)
      : DEFAULT_TECHNICIAN_MOVE_CAPACITY_ALLOWANCE_MINUTES,
    maxWorkEndMinutes,
    maxWorkEndTime: formatMinutes(maxWorkEndMinutes),
  };
};

const getTechnicianToBucketMoveEnabled = () => {
  const configuredValue = typeof window !== 'undefined'
    ? window.APP_CONFIG?.technicianToBucketMoveEnabled
    : undefined;
  if (typeof configuredValue === 'boolean') return configuredValue;
  if (typeof configuredValue === 'number') return configuredValue !== 0;
  if (typeof configuredValue === 'string') {
    const normalizedValue = configuredValue.toLocaleLowerCase('tr').trim();
    if (['true', '1', 'yes', 'on'].includes(normalizedValue)) return true;
    if (['false', '0', 'no', 'off'].includes(normalizedValue)) return false;
  }
  return DEFAULT_TECHNICIAN_TO_BUCKET_MOVE_ENABLED;
};

// Randevu zaman dilimleri
const TIME_SLOTS = [
  '09-11', '10-12', '11-13', '12-14', '13-15', '14-16',
  '15-17', '16-18', '17-19', '18-20', '19-21', '20-22', '21-23',
  '09-13', '13-19', '09-22',
];

// Dakika değerini en uygun zaman dilimine eşle
const getTimeSlot = (minutes) => {
  const hour = Math.floor(minutes / 60);
  // Önce 2 saatlik dar dilimleri dene
  const narrow = TIME_SLOTS.find(s => {
    const parts = s.split('-');
    return parts[1] - parts[0] === 2 && Number(parts[0]) === hour;
  });
  if (narrow) return narrow;
  // Saat aralık dışındaysa en yakın 2 saatlik dilimi bul
  const twoHourSlots = TIME_SLOTS.filter(s => { const p = s.split('-'); return p[1] - p[0] === 2; });
  let best = twoHourSlots[0];
  let bestDist = Infinity;
  twoHourSlots.forEach(s => {
    const start = Number(s.split('-')[0]);
    const dist = Math.abs(start - hour);
    if (dist < bestDist) { bestDist = dist; best = s; }
  });
  return best;
};

const formatDateForApi = (date) => {
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
};

const getSchedulingPayloadContainers = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return [];
  const data = payload.Data || payload.data;
  const containers = [
    payload.Meta,
    payload.meta,
    payload.Metadata,
    payload.metadata,
    data?.Meta,
    data?.meta,
    data?.Metadata,
    data?.metadata,
    data,
    payload,
  ];
  return containers.filter(item => item && typeof item === 'object' && !Array.isArray(item));
};

const readSchedulingPayloadValue = (payload, names) => {
  const containers = getSchedulingPayloadContainers(payload);
  for (const container of containers) {
    for (const name of names) {
      if (Object.prototype.hasOwnProperty.call(container, name)) return container[name];
    }
  }
  return undefined;
};

const getSchedulingRows = (payload) => {
  if (Array.isArray(payload)) {
    const nestedRows = payload.find(item => Array.isArray(item));
    return nestedRows || payload;
  }
  const data = payload?.Data ?? payload?.data;
  const candidates = [
    data,
    data?.Rows,
    data?.rows,
    data?.Items,
    data?.items,
    payload?.Rows,
    payload?.rows,
    payload?.Items,
    payload?.items,
  ];
  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;
    const nestedRows = candidate.find(item => Array.isArray(item));
    return nestedRows || candidate;
  }
  return [];
};

const getSchedulingCalculationOptions = (payload, dayDate, requestStartedAt, serviceShop) => {
  const statusSource = readSchedulingPayloadValue(
    payload,
    ['StatusDescriptions', 'statusDescriptions'],
  );
  const statusDescriptions = Array.isArray(statusSource)
    ? statusSource.reduce((result, item) => {
      const code = item?.DURUM ?? item?.Durum ?? item?.Status ?? item?.status;
      const description = item?.ACIKLAMA ?? item?.Aciklama ?? item?.Description ?? item?.description;
      if (code != null && description != null) result[code] = description;
      return result;
    }, {})
    : statusSource;
  const serviceCenterSource = readSchedulingPayloadValue(
    payload,
    ['ServiceCenter', 'serviceCenter'],
  ) || {};
  const serviceCenter = {
    latitude: serviceCenterSource.Latitude
      ?? serviceCenterSource.latitude
      ?? readSchedulingPayloadValue(
        payload,
        ['CenterLat', 'centerLat', 'ServiceLatitude', 'serviceLatitude'],
      )
      ?? serviceShop?.latitude,
    longitude: serviceCenterSource.Longitude
      ?? serviceCenterSource.longitude
      ?? readSchedulingPayloadValue(
        payload,
        ['CenterLon', 'centerLon', 'ServiceLongitude', 'serviceLongitude'],
      )
      ?? serviceShop?.longitude,
  };

  return {
    dayDate,
    now: readSchedulingPayloadValue(
      payload,
      ['ServerCurrentDateTime', 'serverCurrentDateTime', 'CurrentDateTime', 'currentDateTime'],
    )
      || requestStartedAt,
    serviceCenter,
    statusDescriptions,
    enrichmentByOfscNr: readSchedulingPayloadValue(
      payload,
      [
        'EnrichmentByOfscNr',
        'enrichmentByOfscNr',
        'Enrichments',
        'enrichments',
        'Details',
        'details',
        'SchedulingDetails',
        'schedulingDetails',
      ],
    ),
    serviceShop,
    serviceShopId: serviceShop?.techCode,
  };
};

const formatDateDisplay = (date, short = false) => {
  const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
  const daysShort = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
  const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  const monthsShort = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  if (short) return `${date.getDate()} ${monthsShort[date.getMonth()]}, ${daysShort[date.getDay()]}`;
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}, ${days[date.getDay()]}`;
};

const isToday = (date) => {
  const today = new Date();
  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
};

// İsimleri Title Case'e çevir (ABDULKADIR AÇAN → Abdulkadir Açan)
const toTitleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());
};

// ── Toplu Ekle Schedule Tipleri ──
const SCHEDULE_TYPES = [
  { label: 'DÜZENLİ (08-18)', startTime: '08:00', endTime: '18:00' },
  { label: 'DÜZENLİ (09-18)', startTime: '09:00', endTime: '18:00' },
  { label: 'DÜZENLİ (09-19)', startTime: '09:00', endTime: '19:00' },
  { label: 'DÜZENLİ (10-18)', startTime: '10:00', endTime: '18:00' },
  { label: 'NÖBET (08-22)', startTime: '08:00', endTime: '22:00' },
  { label: 'NÖBET (09-21)', startTime: '09:00', endTime: '21:00' },
  { label: 'NÖBET (09-22)', startTime: '09:00', endTime: '22:00' },
  { label: 'NÖBET (10-22)', startTime: '10:00', endTime: '22:00' },
  { label: 'NÖBET (18-22)', startTime: '18:00', endTime: '22:00' },
  { label: 'CUMARTESİ Nöbet (08-22)', startTime: '08:00', endTime: '22:00' },
  { label: 'PAZAR Nöbet (10-17)', startTime: '10:00', endTime: '17:00' },
  { label: 'Çalışma Dışı Zaman', startTime: '', endTime: '' },
  { label: 'Özel Çalışma Zamanı', startTime: 'custom', endTime: 'custom' },
  { label: 'Varsayılan Çalışma Programı (09:30 - 18:00)', startTime: '09:30', endTime: '18:00' },
];

const REPEAT_OPTIONS = [
  { value: 'Daily', label: 'Günlük' },
  { value: 'Weekly', label: 'Haftalık' },
];

const WEEK_DAYS = [
  { value: 1, label: 'Pzt' },
  { value: 2, label: 'Sal' },
  { value: 3, label: 'Çar' },
  { value: 4, label: 'Per' },
  { value: 5, label: 'Cum' },
  { value: 6, label: 'Cmt' },
  { value: 0, label: 'Paz' },
];

// ── Segment renkleri ──
const TRAVEL_SEGMENT = { bg: '#FFF9C4', border: '#FFE082', text: '#F57F17' };

const getWorkSegmentColor = (status) => {
  const key = normalizeStatusKey(status);
  switch (key) {
    case 'completed': return { bg: '#90CAF9', border: '#64B5F6', text: '#0D47A1' };
    case 'pending': return { bg: '#CFD8DC', border: '#B0BEC5', text: '#37474F' };
    case 'bucket': return { bg: '#B3E5FC', border: '#4FC3F7', text: '#01579B' };
    case 'road': return { bg: '#FFB74D', border: '#FFA726', text: '#4E2700' };
    case 'started': return { bg: '#A5D6A7', border: '#81C784', text: '#1B5E20' };
    case 'suspended': return { bg: '#CE93D8', border: '#BA68C8', text: '#4A148C' };
    case 'cancelled': return { bg: '#EF9A9A', border: '#E57373', text: '#B71C1C' };
    case 'notdone': return { bg: '#EF9A9A', border: '#E53935', text: '#7F1D1D' };
    case 'launch': return { bg: '#80DEEA', border: '#4DD0E1', text: '#004D40' };
    default: return { bg: '#C5CAE9', border: '#9FA8DA', text: '#1A237E' };
  }
};

const hasValidAppointmentNo = (value) => {
  const normalized = String(value ?? '').trim();
  return normalized !== '' && normalized !== '-' && normalized !== '0';
};

const countServicePapers = (works = []) => works.reduce(
  (sum, work) => sum + getServicePaperIds(work?.ServicePapers).length,
  0,
);

const parseServicePaperEntries = (value) => String(value || '')
  .split(',')
  .map(item => item.trim())
  .filter(Boolean)
  .map((item) => {
    const [mainPart, ...reasonParts] = item.split('!');
    const parts = mainPart.split('|');
    return {
      fisNo: getFirstServicePaperId(parts[0]),
      customer: parts[1]?.trim() || '',
      reason: reasonParts.join('!').trim(),
    };
  })
  .filter(entry => entry.fisNo);

const cleanLegacyPartyName = (value) => String(value || '')
  .replace(/\d{7,}/g, '')
  .replace(/[-|]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getWorkApplicationReason = (work = {}) => {
  const direct = work.AppReason
    || work.appReason
    || work.ApplicationReason
    || work.applicationReason
    || work.ActivityType
    || work.activityType
    || work.WorkType
    || work.workType;
  if (direct) return String(direct).trim();
  const entries = parseServicePaperEntries(work.ServicePapers);
  return [...new Set(entries.map(entry => entry.reason).filter(Boolean))].join(', ');
};

const getWorkCustomerName = (work = {}) => {
  const direct = work.Customer
    || work.customer
    || work.CustomerName
    || work.customerName;
  if (direct) return String(direct).trim();
  const rawCustomer = parseServicePaperEntries(work.ServicePapers)
    .map(entry => entry.customer)
    .find(Boolean) || '';
  return cleanLegacyPartyName(rawCustomer.replace(/Bayi:.*$/i, ''));
};

const getWorkDealerName = (work = {}) => {
  const direct = work.Dealer
    || work.dealer
    || work.DealerName
    || work.dealerName
    || work.Bayi
    || work.bayi
    || work.BayiName
    || work.bayiName;
  if (direct && !/^(null|undefined|-)$/i.test(String(direct).trim())) {
    return String(direct).trim();
  }
  const rawCustomer = parseServicePaperEntries(work.ServicePapers)
    .map(entry => entry.customer)
    .find(Boolean) || '';
  const match = rawCustomer.match(/Bayi:\s*(.+)/i);
  return match ? cleanLegacyPartyName(match[1]) : '';
};

const normalizeWorkSearchText = (value) => String(value ?? '')
  .toLocaleLowerCase('tr')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/ı/g, 'i')
  .replace(/\s+/g, ' ')
  .trim();

const collectWorkSearchValues = (value, result = [], seen = new Set(), depth = 0) => {
  if (value == null || depth > 4) return result;
  if (typeof value !== 'object') {
    result.push(value);
    return result;
  }
  if (seen.has(value)) return result;
  seen.add(value);
  if (value instanceof Date) {
    result.push(value.toISOString());
    return result;
  }
  Object.values(value).forEach(item => collectWorkSearchValues(item, result, seen, depth + 1));
  return result;
};

const getWorkSearchText = (work = {}) => {
  const durations = getJobDisplayDurations(work);
  return normalizeWorkSearchText([
    ...collectWorkSearchValues(work),
    getStatusColorObj(work.StatusDescription).label,
    getWorkApplicationReason(work),
    getWorkCustomerName(work),
    getWorkDealerName(work),
    formatDuration(durations.travel),
    formatDuration(durations.work),
    work.AppointmentTime && work.AppointmentTimeEnd
      ? `${work.AppointmentTime}-${work.AppointmentTimeEnd}`
      : '',
  ].join(' '));
};

const isServiceNeighbourhood = (job) => (
  String(job?.Neighbourhood || job?.Neighborhood || '').toLocaleLowerCase('tr').trim() === 'servis'
);

const getAppointmentJobs = (jobs = []) => jobs.filter(j => hasValidAppointmentNo(j?.OfscNr) && !isServiceNeighbourhood(j));

const getJobRenderKey = (techCode, job, fallbackIndex) => {
  const appointmentKey = hasValidAppointmentNo(job?.OfscNr)
    ? String(job.OfscNr)
    : [
      job?.Neighbourhood || job?.Neighborhood || 'slot',
      job?.AppointmentTime || '',
      job?.RowNum ?? job?.SortOrder ?? fallbackIndex,
    ].join(':');
  return `${String(techCode || '')}:${appointmentKey}`;
};

const getTechWorkSummaryText = (jobs = []) => {
  const appointmentJobs = getAppointmentJobs(jobs);
  return `${appointmentJobs.length} rnd ${countServicePapers(appointmentJobs)} fiş`;
};

const getTimelineReferenceTimes = (job) => {
  const operationalTimes = [
    parseTime(job?.DepartureTime),
    parseTime(job?.ArrivalatCustomer),
    parseTime(job?.TaskCompletionTime),
  ].filter((time) => time != null);
  if (operationalTimes.length > 0) return operationalTimes;
  const appointmentTime = parseTime(job?.AppointmentTime);
  return appointmentTime == null ? [] : [appointmentTime];
};

const getJobDisplayDurations = (job) => {
  const plannedTravel = Math.max(0, Number(job?.TravelDuration) || 0);
  const plannedWork = Math.max(0, Number(job?.WorkDuration) || 0);
  if (normalizeStatusKey(job?.StatusDescription) !== 'completed') {
    return { travel: plannedTravel, work: plannedWork };
  }
  const departure = parseTime(job?.DepartureTime);
  const arrival = parseTime(job?.ArrivalatCustomer);
  const completion = parseTime(job?.TaskCompletionTime);
  return {
    travel: departure != null && arrival != null ? Math.max(0, arrival - departure) : 0,
    work: arrival != null && completion != null ? Math.max(0, completion - arrival) : 0,
  };
};

const CLOSED_MOVE_CAPACITY_STATUSES = new Set(['completed', 'cancelled', 'notdone']);

const getMoveCapacityWorkMinutes = (job) => {
  const durations = getJobDisplayDurations(job);
  return durations.work;
};

// Kıbrıs (KKTC) bölgesi 2 saatlik taşıma kapasitesi kuralından hariç tutulur.
const MOVE_CAPACITY_EXCLUDED_TOP_SERVICE_SHOPS = new Set(['KIBRIS BY']);

const isMoveCapacityExcludedServiceShop = (topServiceShopName) => {
  const normalized = String(topServiceShopName ?? '').toLocaleUpperCase('tr').trim();
  return MOVE_CAPACITY_EXCLUDED_TOP_SERVICE_SHOPS.has(normalized);
};

// Yemek molası kaydı OFSC'de takip edilmediği için (OfscNr=0) StatusDescription
// hep 'pending' gelir; TOA_DURUM gerçek tamamlanma durumunu yansıtmaz. Bu yüzden
// tamamlanıp tamamlanmadığı, planlanan bitiş saatinin "şu an"ı geçip geçmediğine
// bakılarak belirlenir.
const isYemekAlreadyPast = (job, currentMinutes, selectedDateIsToday) => {
  if (!selectedDateIsToday) return false;
  const endMinutes = parseTime(String(job?.AppointmentTimeEnd || ''))
    ?? parseTime(String(job?.TaskCompletionTime || ''));
  if (endMinutes == null) return false;
  return currentMinutes >= endMinutes;
};

const isOpenMoveCapacityWork = (job, { currentMinutes, selectedDateIsToday } = {}) => {
  const neighbourhood = String(
    job?.Neighbourhood ?? job?.Neighborhood ?? job?.NEIGHBOURHOOD ?? '',
  ).toLocaleLowerCase('tr').trim();
  if (neighbourhood === 'servis') return false;

  if (CLOSED_MOVE_CAPACITY_STATUSES.has(normalizeStatusKey(job?.StatusDescription))) return false;

  // Yemek molası henüz tamamlanmadıysa (saati henüz geçmediyse), teknisyenin
  // kalan mesaisinden bir yer kaplamaya devam ettiği için yüke dahil edilir.
  if (neighbourhood === 'yemek') {
    return !isYemekAlreadyPast(job, currentMinutes, selectedDateIsToday);
  }

  const ofscNr = job?.OfscNr ?? job?.OFSC_NR ?? job?.ofscNr;
  return hasValidAppointmentNo(ofscNr);
};

// Teknisyenin mesai başlangıç/bitiş metni tanımlı ve geçerli bir saat mi?
// Takvimi o gün kapalıysa backend bu alanları boş/null/'-' döndürebiliyor.
const isValidWorkTimeText = (value) => {
  if (value == null) return false;
  const text = String(value).trim();
  if (text === '' || text === '-') return false;
  return parseTime(text) != null;
};

// Bazı satırlarda (ör. teknisyen olarak tanımlanan bayi/servis noktaları) takvim
// kapalıyken backend TechWorkStart=TechWorkEnd gibi sıfır uzunluklu bir aralık da
// döndürebiliyor; bu da gerçek bir mesai penceresi olmadığı anlamına gelir.
const hasOpenShiftWindow = (row) => {
  if (!isValidWorkTimeText(row?.TechWorkStart) || !isValidWorkTimeText(row?.TechWorkEnd)) return false;
  const start = parseTime(String(row.TechWorkStart));
  const end = parseTime(String(row.TechWorkEnd));
  return start != null && end != null && end > start;
};

export const evaluateTechnicianMoveCapacity = ({
  rows = [],
  movedJobs = [],
  targetTechCode,
  targetTechName = '',
  selectedDate,
  now = new Date(),
  topServiceShopName = '',
} = {}) => {
  const config = getTechnicianMoveCapacityConfig();
  if (!config.enabled) return { allowed: true, ruleEnabled: false };
  if (isMoveCapacityExcludedServiceShop(topServiceShopName)) {
    return { allowed: true, ruleEnabled: true, excludedServiceShop: true };
  }

  const targetCode = String(targetTechCode ?? '').trim();
  const uniqueMovedJobs = new Map();
  movedJobs.forEach((job, index) => {
    const ofscNr = job?.OfscNr ?? job?.OFSC_NR ?? job?.ofscNr;
    uniqueMovedJobs.set(hasValidAppointmentNo(ofscNr) ? String(ofscNr) : `row-${index}`, job);
  });
  const jobsToMove = [...uniqueMovedJobs.values()];
  const isCrossTechnicianMove = jobsToMove.some(
    job => String(job?.TechCode ?? job?.TEKNISYEN_KODU ?? '').trim() !== targetCode,
  );
  if (!targetCode || jobsToMove.length === 0 || !isCrossTechnicianMove) {
    return { allowed: true, ruleEnabled: true };
  }

  const targetRows = rows.filter(
    row => String(row?.TechCode ?? row?.TEKNISYEN_KODU ?? '').trim() === targetCode,
  );
  const movedOfscNrs = new Set(jobsToMove.map(
    job => String(job?.OfscNr ?? job?.OFSC_NR ?? job?.ofscNr ?? '').trim(),
  ));
  const selectedDateIsToday = selectedDate instanceof Date && isToday(selectedDate);
  const selectedDateStart = selectedDate instanceof Date
    ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()).getTime()
    : null;
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const selectedDateIsPast = selectedDateStart != null && selectedDateStart < todayStart;
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const existingOpenMinutes = targetRows.reduce((total, job) => {
    const ofscNr = String(job?.OfscNr ?? job?.OFSC_NR ?? job?.ofscNr ?? '').trim();
    if (
      movedOfscNrs.has(ofscNr)
      || !isOpenMoveCapacityWork(job, { currentMinutes, selectedDateIsToday })
    ) return total;
    return total + getMoveCapacityWorkMinutes(job);
  }, 0);
  const movedMinutes = jobsToMove.reduce(
    (total, job) => total + getMoveCapacityWorkMinutes(job),
    0,
  );

  const scheduleRow = targetRows.find(hasOpenShiftWindow) || targetRows[0] || null;
  const workStartText = scheduleRow?.TechWorkStart;
  const scheduledWorkEndText = scheduleRow?.TechWorkEnd;
  if (!scheduleRow || !hasOpenShiftWindow(scheduleRow)) {
    // Teknisyenin o gün için tanımlı (ve sıfırdan uzun) bir mesai/takvim kaydı
    // yok: 08:00-18:00 varsayımı uygulanmaz, taşımaya izin verilmez.
    return {
      allowed: false,
      ruleEnabled: true,
      calendarClosed: true,
      targetTechCode,
      targetTechName: targetTechName
        || scheduleRow?.TechName
        || scheduleRow?.TEKNISYEN_ADI
        || '',
    };
  }
  const workStartMinutes = parseTime(String(workStartText));
  const scheduledWorkEndMinutes = parseTime(String(scheduledWorkEndText));
  const regularWorkEndMinutes = Math.min(scheduledWorkEndMinutes, config.maxWorkEndMinutes);
  const capacityEndMinutes = Math.min(
    scheduledWorkEndMinutes + config.allowanceMinutes,
    config.maxWorkEndMinutes,
  );
  const workEndText = formatMinutes(regularWorkEndMinutes);
  const capacityEndText = formatMinutes(capacityEndMinutes);
  const totalShiftMinutes = Math.max(0, capacityEndMinutes - workStartMinutes);
  const availableShiftMinutes = selectedDateIsPast
    ? 0
    : selectedDateIsToday
      ? Math.max(0, capacityEndMinutes - Math.max(workStartMinutes, currentMinutes))
      : totalShiftMinutes;
  const maximumAllowedMinutes = availableShiftMinutes;
  const projectedOpenMinutes = existingOpenMinutes + movedMinutes;
  const shiftHasEnded = selectedDateIsPast
    || (selectedDateIsToday && currentMinutes >= capacityEndMinutes);

  return {
    allowed: projectedOpenMinutes <= maximumAllowedMinutes,
    ruleEnabled: true,
    allowanceMinutes: config.allowanceMinutes,
    availableShiftMinutes,
    capacityEndText,
    currentMinutes,
    existingOpenMinutes,
    excessMinutes: Math.max(0, projectedOpenMinutes - maximumAllowedMinutes),
    maximumAllowedMinutes,
    movedMinutes,
    projectedOpenMinutes,
    scheduledWorkEndText,
    selectedDateIsToday,
    selectedDateIsPast,
    shiftHasEnded,
    targetTechCode,
    targetTechName: targetTechName
      || scheduleRow.TechName
      || scheduleRow.TEKNISYEN_ADI
      || '',
    totalShiftMinutes,
    workEndText,
    workStartText,
  };
};

const getMoveCapacityBlockedMessage = (result) => {
  if (result.calendarClosed) {
    return `İş taşınamadı. ${result.targetTechName || 'Hedef teknisyen'} için bu tarihte tanımlı bir mesai/takvim bulunmuyor.`;
  }
  if (result.shiftHasEnded) {
    return `İş taşınamadı. ${result.targetTechName || 'Hedef teknisyen'} için taşıma kapasitesi ${result.capacityEndText || result.workEndText} itibarıyla sona ermiş.`;
  }
  return `İş taşınamadı. Taşıma sonrası açık iş yükü ${formatDuration(result.projectedOpenMinutes)}, izin verilen azami süre ${formatDuration(result.maximumAllowedMinutes)}. Kapasite ${formatDuration(result.excessMinutes)} aşılıyor.`;
};

const parseCoordinateValue = (value) => {
  if (value === undefined || value === null || value === '') return null;
  let normalized = String(value).trim().replace(/\s+/g, '').replace(/[^\d.,+-]/g, '');
  const commaIndex = normalized.lastIndexOf(',');
  const dotIndex = normalized.lastIndexOf('.');
  if (commaIndex >= 0 && dotIndex >= 0) {
    normalized = commaIndex > dotIndex
      ? normalized.replace(/\./g, '').replace(',', '.')
      : normalized.replace(/,/g, '');
  } else if (commaIndex >= 0) {
    normalized = normalized.replace(',', '.');
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCoordinatePair = (latitudeValue, longitudeValue) => {
  let lat = parseCoordinateValue(latitudeValue);
  let lng = parseCoordinateValue(longitudeValue);
  if (lat == null || lng == null) return { lat: null, lng: null };

  // Türkiye verisinde alanların ters gelmesi durumunu güvenli biçimde düzelt.
  const latitudeLooksLikeTurkeyLongitude = lat >= 25 && lat <= 45;
  const longitudeLooksLikeTurkeyLatitude = lng >= 35 && lng <= 43;
  const latitudeOutsideTurkey = lat < 35 || lat > 43;
  if (latitudeLooksLikeTurkeyLongitude && longitudeLooksLikeTurkeyLatitude && latitudeOutsideTurkey) {
    [lat, lng] = [lng, lat];
  }

  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || lat === 0 || lng === 0) {
    return { lat: null, lng: null };
  }
  return { lat, lng };
};

const sendCoordinateCorrections = async (rows = []) => {
  try {
    const travelDurationThreshold = getTravelDurationThreshold();
    const correctionsByOfscNr = new Map();

    rows.forEach((row) => {
      const travelDuration = Number(row?.TravelDuration);
      if (!Number.isFinite(travelDuration) || travelDuration <= travelDurationThreshold) return;

      const ofscNr = row?.OFSC_NR ?? row?.OfscNr ?? row?.ofscNr;
      const latitude = parseCoordinateValue(
        row?.LATITUDE ?? row?.Latitude ?? row?.latitude,
      );
      const longitude = parseCoordinateValue(
        row?.LONGITUDE ?? row?.Longitude ?? row?.longitude,
      );

      const hasValidCoordinates = latitude != null
        && longitude != null
        && latitude !== 0
        && longitude !== 0
        && Math.abs(latitude) <= 90
        && Math.abs(longitude) <= 180;
      if (!hasValidAppointmentNo(ofscNr) || !hasValidCoordinates) return;

      correctionsByOfscNr.set(String(ofscNr), {
        OFSC_NR: ofscNr,
        LATITUDE: latitude,
        LONGITUDE: longitude,
      });
    });

    const corrections = [...correctionsByOfscNr.values()];
    if (corrections.length === 0) return;

    await axios.post('/SetAronCoordinateCorrection', corrections, {
      silentError: true,
      timeout: 10000,
    });
  } catch {
    // Bu arka plan isteği ana planlama akışını etkilememeli.
  }
};

/* eslint-disable react/prop-types */
const AutoRefreshCountdown = React.memo(function AutoRefreshCountdown({ lastUpdate, onRefresh }) {
  const [countdown, setCountdown] = useState(AUTO_REFRESH_INTERVAL / 1000);

  useEffect(() => {
    setCountdown(AUTO_REFRESH_INTERVAL / 1000);
    const interval = window.setInterval(() => {
      setCountdown(previous => (
        previous <= 1 ? AUTO_REFRESH_INTERVAL / 1000 : previous - 1
      ));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [lastUpdate]);

  return (
    <Tooltip title={`Otomatik yenileme: ${Math.floor(countdown / 60)}dk ${countdown % 60}sn`} arrow>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 0.4,
          cursor: 'pointer',
          px: 0.75,
          py: 0.3,
          borderRadius: '12px',
          bgcolor: alpha('#4caf50', 0.06),
          border: `1px solid ${alpha('#4caf50', 0.12)}`,
          '&:hover': { bgcolor: alpha('#4caf50', 0.1) },
        }}
        onClick={() => onRefresh()}
      >
        <TimerIcon
          sx={{
            fontSize: 13,
            color: '#4caf50',
            animation: countdown <= 10 ? 'timerPulse 1s ease-in-out infinite' : 'none',
            '@keyframes timerPulse': {
              '0%,100%': { opacity: 1 },
              '50%': { opacity: 0.4 },
            },
          }}
        />
        <Typography
          sx={{
            fontSize: '0.58rem',
            fontWeight: 700,
            color: countdown <= 10 ? '#f57c00' : '#4caf50',
            fontFamily: 'monospace',
          }}
        >
          {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
        </Typography>
      </Box>
    </Tooltip>
  );
});

const SchedulingPerformanceMetrics = React.memo(React.forwardRef(function SchedulingPerformanceMetrics(_, ref) {
  const [metrics, setMetrics] = useState({
    apiTime: null,
    calculationTime: null,
    renderTime: null,
  });

  useImperativeHandle(ref, () => ({
    reset: () => setMetrics({ apiTime: null, calculationTime: null, renderTime: null }),
    setApiAndCalculation: (apiTime, calculationTime) => setMetrics({
      apiTime,
      calculationTime,
      renderTime: null,
    }),
    setRenderTime: renderTime => setMetrics(previous => ({ ...previous, renderTime })),
  }), []);

  const { apiTime, calculationTime, renderTime } = metrics;
  if (apiTime == null && calculationTime == null && renderTime == null) return null;

  const formatMetric = value => (
    value >= 1000 ? `${(value / 1000).toFixed(1)}s` : `${value}ms`
  );
  const metricSx = (value, warning, danger) => ({
    height: 20,
    fontSize: '0.58rem',
    fontWeight: 700,
    bgcolor: value < warning
      ? alpha('#2e7d32', 0.1)
      : value < danger ? alpha('#ed6c02', 0.1) : alpha('#d32f2f', 0.1),
    color: value < warning ? '#2e7d32' : value < danger ? '#ed6c02' : '#d32f2f',
  });

  const overallWorst = [
    apiTime != null ? (apiTime < 1000 ? 0 : apiTime < 3000 ? 1 : 2) : 0,
    calculationTime != null ? (calculationTime < 100 ? 0 : calculationTime < 300 ? 1 : 2) : 0,
    renderTime != null ? (renderTime < 500 ? 0 : renderTime < 1500 ? 1 : 2) : 0,
  ].reduce((a, b) => Math.max(a, b), 0);
  const iconColor = overallWorst === 0 ? '#2e7d32' : overallWorst === 1 ? '#ed6c02' : '#d32f2f';

  const tooltipContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, py: 0.25 }}>
      {apiTime != null && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography sx={{ fontSize: '0.7rem', opacity: 0.7 }}>API</Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: apiTime < 1000 ? '#81c784' : apiTime < 3000 ? '#ffb74d' : '#e57373' }}>{formatMetric(apiTime)}</Typography>
        </Box>
      )}
      {calculationTime != null && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography sx={{ fontSize: '0.7rem', opacity: 0.7 }}>Hesap</Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: calculationTime < 100 ? '#81c784' : calculationTime < 300 ? '#ffb74d' : '#e57373' }}>{formatMetric(calculationTime)}</Typography>
        </Box>
      )}
      {renderTime != null && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography sx={{ fontSize: '0.7rem', opacity: 0.7 }}>Render</Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: renderTime < 500 ? '#81c784' : renderTime < 1500 ? '#ffb74d' : '#e57373' }}>{formatMetric(renderTime)}</Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="bottom">
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', cursor: 'default' }}>
        <SpeedIcon sx={{ fontSize: 16, color: iconColor, opacity: 0.75, '&:hover': { opacity: 1 } }} />
      </Box>
    </Tooltip>
  );
}));

// ── İş barı (iki segmentli: yol + iş) ──
const JobBar = React.memo(function JobBar({ job, nowMin, isSelectedToday, onDragStart: onDragStartProp, techCode, userTechCode, techName, isFirst }) {
  const dept = parseTime(job.DepartureTime);
  const arrival = parseTime(job.ArrivalatCustomer);
  const completion = parseTime(job.TaskCompletionTime);
  const appt = parseTime(job.AppointmentTime);
  const ofsc = job.OfscNr || '';
  const rawNeighbourhood = job.Neighbourhood || job.Neighborhood || job.Neighnboorhood || '';
  const isYemek = rawNeighbourhood.toLocaleLowerCase('tr').trim() === 'yemek';
  if (!isYemek && !hasValidAppointmentNo(job.OfscNr)) return null;
  const neighbourhood = rawNeighbourhood || ofsc;
  const appointmentRange = [job.AppointmentTime, job.AppointmentTimeEnd].filter(Boolean).join(' - ');
  const hoverTitleParts = [
    rawNeighbourhood ? `Mahalle: ${rawNeighbourhood}` : 'Mahalle bilgisi yok',
    appointmentRange ? `Randevu: ${appointmentRange}` : 'Randevu bilgisi yok',
  ];
  const status = resolveToaStatusDescription(
    job.TOA_DURUM ?? job.toa_durum ?? job.ToaDurum ?? job.toaDurum,
    job.StatusDescription || '',
  );
  const servicePapersStatus = job.ServicePapersStatus || '';
  const { lat, lng } = normalizeCoordinatePair(
    job.Latitude ?? job.LATITUDE ?? job.latitude ?? job.Lat ?? job.lat,
    job.Longitude ?? job.LONGITUDE ?? job.longitude ?? job.Lng ?? job.lng ?? job.Lon ?? job.lon,
  );
  const hasCoords = lat != null && lng != null;
  const servicePapers = job.ServicePapers || '';
  const statusObj = getStatusColorObj(status);
  const workColor = getWorkSegmentColor(status);

  const sKey = normalizeStatusKey(status);
  const operationalAlert = getAppointmentOperationalAlert(job, nowMin, isSelectedToday);
  if (operationalAlert) hoverTitleParts.push(`Uyarı: ${operationalAlert.detail}`);
  const hoverTitle = hoverTitleParts.join('\n');
  const plannedTravelDur = Math.max(0, Number(job.TravelDuration) || 0);
  const plannedWorkDur = Math.max(0, Number(job.WorkDuration) || 0);
  const hasTravelSeg = dept != null && arrival != null && arrival > dept;
  const hasWorkSeg = arrival != null && completion != null && completion > arrival;

  // Bar sınırları
  let start, end;
  if (hasTravelSeg && hasWorkSeg) {
    start = dept; end = completion;
  } else if (hasTravelSeg) {
    start = dept; end = arrival;
  } else if (hasWorkSeg) {
    start = arrival; end = completion;
  } else {
    const operationalTimes = [dept, arrival, completion].filter((t) => t != null);
    if (operationalTimes.length > 0) {
      start = Math.min(...operationalTimes);
      end = Math.max(...operationalTimes);
      if (end <= start && sKey !== 'completed') end = start + 10;
    } else {
      if (appt == null) return null;
      start = appt;
      end = start + 10;
    }
  }

  const totalMin = TOTAL_HOURS * 60;
  const left = ((start - HOUR_START * 60) / totalMin) * 100;
  const width = ((end - start) / totalMin) * 100;
  if (left + width < 0 || left > 100) return null;

  const barDuration = Math.max(end - start, 1);
  const showTravelSeg = hasTravelSeg;
  const showWorkSeg = hasWorkSeg;
  const travelPct = hasTravelSeg ? ((arrival - dept) / barDuration) * 100 : 0;
  const workPct = hasWorkSeg ? ((completion - arrival) / barDuration) * 100 : 0;

  // Gecikme tespiti — filtre ve teknisyen uyarısıyla aynı ortak kuralı kullanır.
  const isDelayed = isAppointmentDelayed(job, nowMin, isSelectedToday);

  // Planlanan (sunucudan gelen) ve Gerçekleşen (zaman damgalarından hesaplanan)
  const showActual = sKey === 'completed' || sKey === 'started' || sKey === 'road';
  const hasActualTravelWindow = dept != null && arrival != null;
  const hasActualWorkWindow = arrival != null && completion != null;
  const actualTravelDur = showActual && hasActualTravelWindow ? Math.max(0, arrival - dept) : 0;
  const actualWorkDur = showActual && hasActualWorkWindow ? Math.max(0, completion - arrival) : 0;

  const isMedium = width > 0.8;

  const handleDragStart = (e) => {
    const duration = end - start;
    e.dataTransfer.setData('application/json', JSON.stringify({
      ofscNr: ofsc,
      sourceTechCode: techCode,
      sourceUserTechCode: userTechCode || techCode || '',
      sourceTechName: techName || '',
      sourceIsBucket: sKey === 'bucket',
      duration,
      startMin: start,
      travelDuration: plannedTravelDur,
      workDuration: plannedWorkDur,
    }));
    e.dataTransfer.effectAllowed = 'move';
    if (onDragStartProp) onDragStartProp(ofsc);
  };

  const [tipOpen, setTipOpen] = useState(false);
  const [popupAnchor, setPopupAnchor] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const barRef = useRef(null);
  const markerMapSrc = tipOpen && hasCoords ? buildOpenStreetMapMarkerSrc({
    lat,
    lng,
    label: ofsc ? `Teknisyen İşlem Noktası - Randevu No: ${ofsc}` : `Teknisyen İşlem Noktası${rawNeighbourhood ? ` - ${rawNeighbourhood}` : ''}`,
  }) : '';

  const updatePopupAnchor = useCallback(() => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 12;
    const gap = 14;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    const maxPopupWidth = Math.min(680, viewportWidth - (margin * 2));
    const minSideWidth = Math.min(420, maxPopupWidth);
    const centerX = rect.left + (rect.width / 2);
    const centerY = rect.top + (rect.height / 2);
    const sideMaxHeight = Math.max(240, viewportHeight - (margin * 2));
    const spaces = {
      right: viewportWidth - rect.right - gap - margin,
      left: rect.left - gap - margin,
      below: viewportHeight - rect.bottom - gap - margin,
      above: rect.top - gap - margin,
    };
    const canUseRight = spaces.right >= minSideWidth;
    const canUseLeft = spaces.left >= minSideWidth;

    if (canUseRight || canUseLeft) {
      const placement = canUseRight && (!canUseLeft || spaces.right >= spaces.left) ? 'right' : 'left';
      const availableWidth = placement === 'right' ? spaces.right : spaces.left;
      const popupWidth = Math.min(maxPopupWidth, availableWidth);
      const left = placement === 'right'
        ? rect.right + gap
        : rect.left - gap - popupWidth;
      const top = margin;

      setPopupAnchor({
        left: clamp(left, margin, viewportWidth - popupWidth - margin),
        top,
        bottom: undefined,
        width: popupWidth,
        arrowLeft: undefined,
        arrowTop: clamp(centerY - top, 18, sideMaxHeight - 18),
        placement,
        maxHeight: sideMaxHeight,
      });
      return;
    }

    const placement = spaces.below >= spaces.above ? 'below' : 'above';
    const popupWidth = maxPopupWidth;
    const left = clamp(centerX - (popupWidth / 2), margin, viewportWidth - popupWidth - margin);
    const top = placement === 'below' ? rect.bottom + gap : undefined;
    const bottom = placement === 'above' ? viewportHeight - rect.top + gap : undefined;
    const availableHeight = placement === 'below'
      ? viewportHeight - top - margin
      : rect.top - margin - gap;

    setPopupAnchor({
      left,
      top,
      bottom,
      width: popupWidth,
      arrowLeft: clamp(centerX - left, 18, popupWidth - 18),
      arrowTop: undefined,
      placement,
      maxHeight: Math.max(240, availableHeight),
    });
  }, []);

  const openTip = useCallback(() => {
    updatePopupAnchor();
    setTipOpen(true);
  }, [updatePopupAnchor]);

  const toggleTip = useCallback(() => {
    if (tipOpen) {
      setTipOpen(false);
      return;
    }
    openTip();
  }, [openTip, tipOpen]);

  const doFetchDetail = useCallback(async () => {
    const spId = getFirstServicePaperId(servicePapers);
    if (!spId) return;
    setDetailLoading(true);
    setDetailData(null);
    try {
      const resp = await axios.get(`/GetServicePaperDetail/${encodeURIComponent(spId)}/1/0`);
      const detail = unwrapServicePaperDetailResponse(resp?.data);
      setDetailData(detail || { error: 'Fiş detayı bulunamadı.' });
    } catch (err) {
      setDetailData({ error: 'Detay bilgisi alınamadı.' });
    } finally {
      setDetailLoading(false);
    }
  }, [servicePapers]);

  const handleHistoryToggle = useCallback(async (ofscNr) => {
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }

    setHistorySearch('');
    setHistoryOpen(true);
    if (historyLoaded || historyLoading) return;

    const normalizedOfscNr = Number(ofscNr);
    const hasOfscNr = Number.isFinite(normalizedOfscNr) && normalizedOfscNr > 0;
    const servicePaperId = getFirstServicePaperId(servicePapers);

    if (!hasOfscNr && !servicePaperId) {
      setHistoryError('Randevu tarihçesi için OFSC veya fiş numarası bulunamadı.');
      return;
    }

    const unwrapRows = (data) => {
      let payload = data;
      for (let depth = 0; depth < 4 && !Array.isArray(payload); depth += 1) {
        if (!payload || typeof payload !== 'object') break;
        const nested = payload.Data ?? payload.data ?? payload.Result ?? payload.result;
        if (nested === undefined || nested === payload) break;
        payload = nested;
      }
      return Array.isArray(payload) ? payload : [];
    };

    setHistoryLoading(true);
    setHistoryError('');
    try {
      let rows = [];

      if (hasOfscNr) {
        const response = await axios.get('/GetAronSchedulingHistory', {
          params: { ofscNR: normalizedOfscNr },
        });
        rows = unwrapRows(response?.data);
      }

      // Fiş kapandığında OFSC numarası canlı tabloda (T_TOA_IS_EMRI_ESLEME) kalmıyor,
      // tarihçe ise T_TARIHCE_TOA_IS_EMRI_ESLEME'de duruyor. OFSC ile sonuç gelmezse
      // aynı ucu fiş numarasıyla tekrar çağır (SP basvuruNo doluysa fiş no ile arar).
      if (rows.length === 0 && servicePaperId) {
        const fallback = await axios.get('/GetAronSchedulingHistory', {
          params: { basvuruNo: servicePaperId },
        });
        rows = unwrapRows(fallback?.data);
      }

      setHistoryRows(rows);
      setHistoryLoaded(true);
    } catch (_) {
      setHistoryError('Randevu tarihçesi alınamadı. Lütfen tekrar deneyin.');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyLoaded, historyLoading, historyOpen, servicePapers]);

  const filteredHistoryRows = useMemo(() => {
    const query = historySearch.toLocaleLowerCase('tr').trim();
    if (!query) return historyRows;
    return historyRows.filter(row => [
      row.Date,
      row.AppointmentDate,
      row.TimeSlot,
      row.TechCode,
      row.TechName,
      row.TechSurname,
      row.AppointmentStatus,
      row.AppointmentUser,
      row.PreviousAppointmentDate,
      row.PreviousTimeSlot,
      row.PreviousTechCode,
      row.PreviousTechFullName,
    ].some(value => String(value ?? '').toLocaleLowerCase('tr').includes(query)));
  }, [historyRows, historySearch]);

  useEffect(() => {
    if (tipOpen && servicePapers) {
      doFetchDetail();
    }
  }, [tipOpen, servicePapers, doFetchDetail]);

  useEffect(() => {
    const node = barRef.current;
    if (!node) return undefined;
    const handleOpen = () => openTip();
    node.addEventListener('toa-open-job-popup', handleOpen);
    return () => node.removeEventListener('toa-open-job-popup', handleOpen);
  }, [openTip]);

  useEffect(() => {
    if (!tipOpen) return undefined;
    const handleReposition = () => updatePopupAnchor();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [tipOpen, updatePopupAnchor]);

  const bar = (
    <>
      {tipOpen && (
        <Box
          onClick={() => setTipOpen(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1800,
            bgcolor: 'rgba(10,18,40,0.12)',
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              background: '#fff',
              color: '#222',
              borderRadius: '6px',
              boxShadow: '0 8px 28px rgba(0,0,0,0.26)',
              px: 3,
              py: 2.5,
              width: popupAnchor?.width || 'min(680px, calc(100vw - 32px))',
              maxWidth: 680,
              maxHeight: popupAnchor?.maxHeight || '85vh',
              boxSizing: 'border-box',
              overflowY: 'auto',
              overflowX: 'hidden',
              position: 'fixed',
              left: popupAnchor?.left ?? '50%',
              top: popupAnchor?.top,
              bottom: popupAnchor?.bottom,
              transform: popupAnchor ? 'none' : 'translateX(-50%)',
              border: '1px solid rgba(0,0,0,0.16)',
              '& *': {
                boxSizing: 'border-box',
              },
            }}
          >
            {popupAnchor && (
              <Box sx={{
                position: 'absolute',
                width: 0,
                height: 0,
                zIndex: 3,
                pointerEvents: 'none',
                overflow: 'visible',
                '--arrow-border': alpha(workColor.text, 0.78),
                '&::before, &::after': {
                  content: '""',
                  position: 'absolute',
                  width: 0,
                  height: 0,
                },
                ...(
                  popupAnchor.placement === 'right'
                    ? {
                      left: 0,
                      top: popupAnchor.arrowTop,
                      transform: 'translateY(-50%)',
                      '&::before': {
                        left: 4,
                        top: -14,
                        borderTop: '14px solid transparent',
                        borderBottom: '14px solid transparent',
                        borderRight: '14px solid var(--arrow-border)',
                        filter: 'drop-shadow(-2px 0 2px rgba(0,0,0,0.22))',
                      },
                      '&::after': {
                        left: 8,
                        top: -11,
                        borderTop: '11px solid transparent',
                        borderBottom: '11px solid transparent',
                        borderRight: '11px solid #fff',
                      },
                    }
                    : popupAnchor.placement === 'left'
                      ? {
                        right: 0,
                        top: popupAnchor.arrowTop,
                        transform: 'translateY(-50%)',
                        '&::before': {
                          right: 4,
                          top: -14,
                          borderTop: '14px solid transparent',
                          borderBottom: '14px solid transparent',
                          borderLeft: '14px solid var(--arrow-border)',
                          filter: 'drop-shadow(2px 0 2px rgba(0,0,0,0.22))',
                        },
                        '&::after': {
                          right: 8,
                          top: -11,
                          borderTop: '11px solid transparent',
                          borderBottom: '11px solid transparent',
                          borderLeft: '11px solid #fff',
                        },
                      }
                      : popupAnchor.placement === 'below'
                        ? {
                          left: popupAnchor.arrowLeft,
                          top: 0,
                          transform: 'translateX(-50%)',
                          '&::before': {
                            left: -14,
                            top: 4,
                            borderLeft: '14px solid transparent',
                            borderRight: '14px solid transparent',
                            borderBottom: '14px solid var(--arrow-border)',
                            filter: 'drop-shadow(0 -2px 2px rgba(0,0,0,0.22))',
                          },
                          '&::after': {
                            left: -11,
                            top: 8,
                            borderLeft: '11px solid transparent',
                            borderRight: '11px solid transparent',
                            borderBottom: '11px solid #fff',
                          },
                        }
                        : {
                          left: popupAnchor.arrowLeft,
                          bottom: 0,
                          transform: 'translateX(-50%)',
                          '&::before': {
                            left: -14,
                            bottom: 4,
                            borderLeft: '14px solid transparent',
                            borderRight: '14px solid transparent',
                            borderTop: '14px solid var(--arrow-border)',
                            filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.22))',
                          },
                          '&::after': {
                            left: -11,
                            bottom: 8,
                            borderLeft: '11px solid transparent',
                            borderRight: '11px solid transparent',
                            borderTop: '11px solid #fff',
                          },
                        }
                ),
              }} />
            )}
            <IconButton
              onClick={() => setTipOpen(false)}
              size="small"
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                color: '#c62828',
                bgcolor: 'rgba(198,40,40,0.08)',
                '&:hover': { bgcolor: 'rgba(198,40,40,0.16)', color: '#b71c1c' },
                zIndex: 2,
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
            {(() => {
              const spEntries = servicePapers ? servicePapers.split(',').filter(s => s.trim()).map(sp => {
                const [mainPart, ...reasonParts] = sp.trim().split('!');
                const parts = mainPart.split('|');
                return { fisNo: parts[0]?.trim(), customer: parts[1]?.trim() || '', reason: reasonParts.join('!').trim() };
              }) : [];
              const firstEntry = spEntries[0] || {};
              const rawCustomer = firstEntry.customer || job.CustomerName || job.customerName || '';
              const customerPhones = rawCustomer.match(/\d{7,}/g) || [];
              const customerName = rawCustomer.replace(/Bayi:.*$/i, '').replace(/\d{7,}/g, '').replace(/[-|]/g, ' ').replace(/\s+/g, ' ').trim();
              const customerNameParts = customerName.split(' ').filter(Boolean);
              const parsedLastName = customerNameParts.length > 1 ? customerNameParts[customerNameParts.length - 1] : '';
              const parsedFirstName = parsedLastName ? customerNameParts.slice(0, -1).join(' ') : customerName;
              const extractBayiName = (value) => {
                const match = (value || '').match(/Bayi:\s*([^!,]+)/i);
                return match ? match[1].replace(/\d{7,}/g, '').replace(/[-|]/g, ' ').replace(/\s+/g, ' ').trim() : '';
              };
              const parsedBayiName = extractBayiName(rawCustomer) || extractBayiName(servicePapers);
              const reasons = [...new Set(spEntries.map(e => e.reason).filter(Boolean))];
              const app = !detailData?.error && Array.isArray(detailData?.Apps) ? detailData.Apps[0] : null;
              const firstItem = (value) => (Array.isArray(value) ? value[0] : value);
              const customer = !detailData?.error
                ? firstItem(
                  detailData?.Customer || detailData?.Customers || detailData?.customer || detailData?.customers ||
                  detailData?.CustomerInfo || detailData?.CustomerInfos || detailData?.customerInfo ||
                  app?.Customer || app?.Customers || app?.customer || app?.customers || app?.CustomerInfo || app?.customerInfo ||
                  null
                )
                : null;
              const appAddresses = !detailData?.error
                ? (detailData?.AppAdresses || detailData?.AppAddresses || app?.AppAdresses || app?.AppAddresses || [])
                : [];
              const appAddressList = Array.isArray(appAddresses) ? appAddresses : [];
              const applicationAddr = appAddressList.find(a => a.IsApplicationAddress === 1 || a.IsApplicationAddress === true || a.IsApplicationAddress === '1') || appAddressList[0] || null;
              const allAddrs = !detailData?.error && Array.isArray(detailData?.CustomerDifferentAddresses) ? detailData.CustomerDifferentAddresses : [];
              const serviceAddr = allAddrs.find(a => a.ServicePaperAddress === 1 || a.ServicePaperAddress === true || a.ServicePaperAddress === '1') || allAddrs[0] || {};
              const addressText = [
                applicationAddr?.Address || applicationAddr?.address || serviceAddr.Address || job.Address || job.address || '',
                [
                  applicationAddr?.Neighbourhood || applicationAddr?.Neighborhood || applicationAddr?.neighbourhood || rawNeighbourhood,
                  applicationAddr?.District || applicationAddr?.district || serviceAddr.District || job.District,
                  applicationAddr?.City || applicationAddr?.city || serviceAddr.City || job.City || job.city,
                ].filter(Boolean).join(' '),
              ].filter(Boolean).join(' ');
              const customerFirstName = customer?.FirstName || customer?.firstName || customer?.FIRSTNAME || customer?.Name || customer?.name || parsedFirstName || '';
              const customerLastName = customer?.LastName || customer?.lastName || customer?.LASTNAME || customer?.Surname || customer?.surname || parsedLastName || '';
              const extractPhones = (...values) => values
                .flatMap(value => String(value || '').match(/\d{7,}/g) || [])
                .filter(Boolean);
              const customerPhoneNumbers = [...new Set([
                ...customerPhones,
                ...extractPhones(
                  customer?.Gsm, customer?.gsm, customer?.GSM,
                  customer?.Phone, customer?.phone,
                  customer?.Phone1, customer?.phone1,
                  customer?.Phone2, customer?.phone2,
                  customer?.MobilePhone, customer?.mobilePhone,
                  customer?.HomePhone, customer?.homePhone,
                  customer?.WorkPhone, customer?.workPhone,
                  detailData?.Gsm, detailData?.gsm, detailData?.Phone, detailData?.phone,
                  app?.Gsm, app?.gsm, app?.Phone, app?.phone,
                  job.Gsm, job.gsm, job.Phone, job.phone
                ),
              ])];
              const customerPhonesText = customerPhoneNumbers.join(', ');
              const bayiName =
                job.Dealer || job.dealer || job.DealerName || job.dealerName ||
                job.Bayi || job.BAYI || job.BayiName || job.bayiName ||
                customer?.BayiName || customer?.bayiName || customer?.DealerName || customer?.dealerName ||
                detailData?.BayiName || detailData?.bayiName || detailData?.DealerName || detailData?.dealerName ||
                app?.BayiName || app?.bayiName || app?.DealerName || app?.dealerName ||
                parsedBayiName || '';
              const notes = app?.Notes || app?.notes || detailData?.Notes || detailData?.notes || '';
              const applicationNote = app?.ApplicationNote || app?.applicationNote || detailData?.ApplicationNote || detailData?.applicationNote || '';
              const callCenterNote = app?.CallCenterNote || app?.callCenterNote || detailData?.CallCenterNote || detailData?.callCenterNote || '';
              const mainGroup = app?.MainGroup || app?.mainGroup || '';
              const modelName = app?.ModelName || app?.modelName || '';
              const userModelCode = app?.UserModelCode || app?.userModelCode || '';
              const warrantyDateRaw = app?.WarrantyDate || app?.warrantyDate || detailData?.WarrantyDate || detailData?.warrantyDate || '';
              const warrantyDateObj = warrantyDateRaw ? dayjs(warrantyDateRaw, ['DD.MM.YYYY', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ss.SSS'], true) : null;
              const warrantyDate = warrantyDateObj?.isValid() ? warrantyDateObj.format('DD.MM.YYYY') : (warrantyDateRaw || '');
              const travelWindow = dept != null
                ? [formatMinutes(dept), arrival != null ? formatMinutes(arrival) : null].filter(Boolean).join(' - ')
                : '-';
              const workWindow = hasActualWorkWindow ? `${formatMinutes(arrival)} - ${formatMinutes(completion)}` : '-';
              const plannedTotalDur = plannedTravelDur + plannedWorkDur;
              const actualTotalDur = actualTravelDur + actualWorkDur;
              const openFis = (fisNo) => {
                if (!fisNo) return;
                try {
                  const bc = new BroadcastChannel('aron-fis-channel');
                  bc.postMessage({ type: 'openTabByFisNo', fisNo });
                  bc.close();
                  // 'aron-app' adli pencere yoksa window.open bos bir sekme aciyordu.
                  // Donen pencere about:blank ise Aron kapali demektir; fisi orada aciyoruz.
                  // Aron aciksa BroadcastChannel fisi zaten orada actirdi, sadece one getiriyoruz.
                  const appWin = window.open('', 'aron-app');
                  if (appWin) {
                    try {
                      if (!appWin.location.href || appWin.location.href === 'about:blank') {
                        appWin.location.href = '/Main/Home/' + fisNo;
                      }
                    } catch (_) { /* okunamiyorsa zaten mevcut Aron penceresidir */ }
                    appWin.focus();
                  }
                } catch (_) {
                  window.open(`/Main/Home/${fisNo}`, 'aron-app');
                }
              };
              const fisValue = spEntries.length > 0 ? (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  {spEntries.map((entry, idx) => (
                    <React.Fragment key={`${entry.fisNo || idx}`}>
                      {idx > 0 && <Box component="span" sx={{ color: '#777' }}>,</Box>}
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          openFis(entry.fisNo);
                        }}
                        sx={{
                          color: '#1565c0',
                          fontWeight: 700,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textDecorationColor: 'rgba(21,101,192,0.4)',
                          textUnderlineOffset: '2px',
                          borderRadius: '3px',
                          px: 0.3,
                          transition: 'all 0.15s',
                          '&:hover': {
                            color: '#0d47a1',
                            bgcolor: 'rgba(21,101,192,0.08)',
                            textDecorationColor: '#0d47a1',
                          },
                        }}
                      >
                        {entry.fisNo || '-'}
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              ) : (ofsc || '-');
              const statusItems = (servicePapersStatus || job.StatusDescription || '')
                .split(',')
                .map(p => p.trim())
                .filter(Boolean);
              const statusValue = statusItems.length > 0 ? statusItems.join(', ') : (status || statusObj.label || '-');
              const productItems = (job.Products || app?.Products || app?.ProductDescription || detailData?.Products || '')
                .split(',')
                .map(p => p.trim())
                .filter(Boolean);
              const productValue = productItems.length > 0 ? productItems.join('\n') : '-';
              const normalizeMultiText = (value) => String(value || '')
                .replace(/\s*[;|/]\s*/g, ', ')
                .replace(/\s{2,}/g, ', ')
                .split(',')
                .map(p => p.trim())
                .filter(Boolean);
              const modelItems = normalizeMultiText(modelName);
              const modelValue = modelItems.length > 0 ? modelItems.join(', ') : '-';
              const openedUser =
                app?.OpenerUser || app?.openerUser ||
                app?.OpenedUser || app?.openedUser || app?.OpenUser || app?.openUser ||
                app?.AcanKullanici || app?.acanKullanici || app?.CreatedByUser || app?.CreatedBy || app?.createdBy ||
                detailData?.OpenerUser || detailData?.openerUser ||
                detailData?.OpenedUser || detailData?.openedUser || detailData?.AcanKullanici || detailData?.acanKullanici ||
                detailData?.CreatedByUser || detailData?.CreatedBy || '';
              const modifiedUser =
                app?.ChangerUser || app?.changerUser ||
                app?.ModifiedUser || app?.modifiedUser || app?.DegistirenKullanici || app?.degistirenKullanici ||
                app?.ChangedByUser || app?.UpdatedBy || app?.updatedBy || app?.ModifiedBy || app?.modifiedBy ||
                detailData?.ChangerUser || detailData?.changerUser ||
                detailData?.ModifiedUser || detailData?.modifiedUser || detailData?.DegistirenKullanici || detailData?.degistirenKullanici ||
                detailData?.ModifiedBy || detailData?.UpdatedBy || '';
              const source =
                app?.ArrivalLocation || app?.arrivalLocation ||
                app?.Source || app?.source || app?.Kaynak || app?.kaynak ||
                app?.Channel || app?.channel || app?.ChannelDescription || app?.channelDescription ||
                app?.SourceDescription || app?.sourceDescription ||
                detailData?.ArrivalLocation || detailData?.arrivalLocation ||
                detailData?.Source || detailData?.source || detailData?.Kaynak || detailData?.kaynak ||
                detailData?.Channel || detailData?.channel || detailData?.ChannelDescription || detailData?.channelDescription || '';
              const detailRows = [
                ['Başvuru Nedeni', reasons[0] || app?.ApplicationReasonId || app?.applicationReasonId || '-'],
                [`Fiş (${spEntries.length || 1} Adet)`, fisValue],
                ['Randevu', appointmentRange || '-'],
                ['Fiş Durumları', statusValue],
                ['Ad', customerFirstName || customerName || '-'],
                ['Soyad', customerLastName || '-'],
                ['Telefon', customerPhonesText || '-'],
                ['Bayi', bayiName || '-'],
                ['Adres', addressText || rawNeighbourhood || '-'],
                ['Ürünler', productValue],
                ['Ana Grup', mainGroup || '-'],
                ['Model', modelValue],
                ['Model Kodu', userModelCode || '-'],
                ['Garanti Tarihi', warrantyDate || '-'],
                ['Açan Kullanıcı', openedUser || '-'],
                ['Değiştiren Kullanıcı', modifiedUser || '-'],
                ['Geliş Yeri', source || '-'],
                ['Başvuru Notu', applicationNote || '-'],
                ['Çağrı Merkezi Notu', callCenterNote || '-'],
                ['Sonuç Notu', notes || '-'],
              ];
              const timeSummaryRows = [
                ['Yol', travelWindow],
                ['Yol Planlanan', plannedTravelDur > 0 ? formatDuration(plannedTravelDur) : '-'],
                ['Yol Gerçekleşen', showActual && hasActualTravelWindow ? formatDuration(actualTravelDur) : '-'],
                ['İş', workWindow],
                ['İş Planlanan', plannedWorkDur > 0 ? formatDuration(plannedWorkDur) : '-'],
                ['İş Gerçekleşen', showActual && hasActualWorkWindow ? formatDuration(actualWorkDur) : '-'],
                ['Toplam Planlanan', plannedTotalDur > 0 ? formatDuration(plannedTotalDur) : '-'],
                ['Toplam Gerçekleşen', showActual && (hasActualTravelWindow || hasActualWorkWindow) ? formatDuration(actualTotalDur) : '-'],
              ].filter(([, value]) => value && value !== '-');
              const wideLabels = new Set(['Bayi', 'Adres', 'Ürünler', 'Fiş Durumları', 'Model', 'Başvuru Notu', 'Çağrı Merkezi Notu', 'Sonuç Notu']);
              const normalRows = detailRows.filter(([label]) => !wideLabels.has(label));
              const wideRows = detailRows.filter(([label]) => wideLabels.has(label));

              return (
                <Box sx={{ pr: 2, maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
                  <Box sx={{ mb: 2, pb: 1.2, borderBottom: `2px solid ${alpha(workColor.text, 0.28)}`, pr: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#222', lineHeight: 1.35 }}>
                        Randevu No: {ofsc || '-'}
                      </Typography>
                      <Box sx={{
                        px: 0.7,
                        py: 0.25,
                        borderRadius: '6px',
                        bgcolor: alpha(workColor.bg, 0.45),
                        border: `1px solid ${alpha(workColor.border, 0.65)}`,
                        color: workColor.text,
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        lineHeight: 1.2,
                      }}>
                        {statusObj.label || status || 'Pending'}
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={historyLoading ? <CircularProgress size={13} color="inherit" /> : <HistoryIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => handleHistoryToggle(ofsc)}
                        disabled={historyLoading}
                        aria-expanded={historyOpen}
                        sx={{
                          ml: 'auto', minHeight: 27, px: 1, py: 0.25,
                          borderRadius: '7px', fontSize: '0.7rem', fontWeight: 800,
                          textTransform: 'none', boxShadow: 'none',
                          bgcolor: '#fff', color: '#1565c0', borderColor: alpha('#1565c0', 0.38),
                          fontFamily: 'inherit',
                          '&:hover': { bgcolor: alpha('#1565c0', 0.06), borderColor: '#1565c0', boxShadow: 'none' },
                        }}
                      >
                        Tarihçe
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap', mt: 0.7 }}>
                      {rawNeighbourhood && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, color: '#555', fontSize: '0.78rem', fontWeight: 700 }}>
                          <PlaceIcon sx={{ fontSize: 14, color: '#ef5350' }} />
                          {rawNeighbourhood}
                        </Box>
                      )}
                      {appointmentRange && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, color: '#555', fontSize: '0.78rem', fontWeight: 700 }}>
                          <ScheduleIcon sx={{ fontSize: 14, color: '#1565c0' }} />
                          {appointmentRange}
                        </Box>
                      )}
                    </Box>
                    <DraggableResizablePopup
                      open={historyOpen}
                      onClose={() => setHistoryOpen(false)}
                      title="Randevu Tarihçesi"
                      subtitle={`OFSC No: ${ofsc || '-'}`}
                      icon={HistoryIcon}
                      maxWidth="lg"
                      defaultWidth={1180}
                      defaultHeight={680}
                      primaryColor="#1565c0"
                      zIndex={2100}
                      headerActions={
                        !historyLoading && !historyError && historyRows.length > 0
                          ? <Chip label={`${historyRows.length} kayıt`} size="small" sx={{ height: 24, fontSize: '0.68rem', fontWeight: 800, bgcolor: alpha('#1565c0', 0.1), color: '#1565c0' }} />
                          : null
                      }
                    >
                      <Box sx={{ height: '100%', bgcolor: '#fff', minHeight: 220, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                        {historyLoading && (
                          <Box sx={{ minHeight: 250, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.2, color: '#64748b' }}>
                            <CircularProgress size={30} thickness={4} />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Randevu tarihçesi yükleniyor...</Typography>
                          </Box>
                        )}
                        {!historyLoading && historyError && (
                          <Box sx={{ p: 2.5 }}>
                            <Alert severity="error">{historyError}</Alert>
                          </Box>
                        )}
                        {!historyLoading && !historyError && historyLoaded && historyRows.length === 0 && (
                          <Box sx={{ minHeight: 250, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: '#64748b' }}>
                            <HistoryIcon sx={{ fontSize: 42, color: '#cbd5e1' }} />
                            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#475569' }}>Tarihçe kaydı bulunamadı</Typography>
                            <Typography sx={{ fontSize: '0.72rem' }}>Bu OFSC numarasına ait bir randevu değişikliği bulunmuyor.</Typography>
                          </Box>
                        )}
                        {!historyLoading && !historyError && historyRows.length > 0 && (
                          <>
                            <Box sx={{ px: 2, py: 1.25, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                              <TextField
                                value={historySearch}
                                onChange={(event) => setHistorySearch(event.target.value)}
                                placeholder="Tarihçede ara..."
                                size="small"
                                autoComplete="off"
                                inputProps={{ 'aria-label': 'Randevu tarihçesinde ara' }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                    </InputAdornment>
                                  ),
                                  endAdornment: historySearch ? (
                                    <InputAdornment position="end">
                                      <IconButton size="small" onClick={() => setHistorySearch('')} aria-label="Aramayı temizle">
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </InputAdornment>
                                  ) : null,
                                }}
                                sx={{
                                  width: { xs: '100%', sm: 360 },
                                  '& .MuiOutlinedInput-root': { height: 36, bgcolor: '#fff', borderRadius: 2, fontSize: '0.75rem' },
                                }}
                              />
                              {historySearch && (
                                <Typography component="span" sx={{ ml: 1.2, fontSize: '0.68rem', color: '#64748b', fontWeight: 600 }}>
                                  {filteredHistoryRows.length} sonuç
                                </Typography>
                              )}
                            </Box>
                            <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                            <Table stickyHeader
                                size="small" aria-label="Randevu tarihçesi tablosu" sx={{ minWidth: 880 }}>
                              <TableHead>
                                <TableRow>
                                  {['#', 'İşlem Tarihi', 'Randevu Tarihi', 'Saat Dilimi', 'Teknisyen', 'Durum', 'İşlemi Yapan'].map((label) => (
                                    <TableCell key={label} sx={{ py: 1.25, bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.7rem', fontWeight: 800, whiteSpace: 'nowrap', borderBottom: '1px solid #cbd5e1' }}>
                                      {label}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredHistoryRows.map((row, index) => {
                                  const colorClass = String(row.AppointmentColor || '').toLowerCase();
                                  // Backend AppointmentColor şeması:
                                  // bg-info: mor, bg-warning: sarı, bg-success: yeşil,
                                  // bg-green: koyu yeşil, bg-primary: mavi, bg-danger: kırmızı.
                                  const historyColor = colorClass.includes('info') ? '#7b1fa2'
                                    : colorClass.includes('warning') ? '#f9a825'
                                      : colorClass.includes('green') ? '#1b5e20'
                                        : colorClass.includes('success') ? '#2e7d32'
                                          : colorClass.includes('primary') ? '#1565c0'
                                            : colorClass.includes('danger') || colorClass.includes('error') ? '#d32f2f'
                                              : '#64748b';
                                  const historyTextColor = colorClass.includes('warning') ? '#7a5200' : historyColor;
                                  const appointmentDate = row.AppointmentDate && dayjs(row.AppointmentDate).isValid()
                                    ? dayjs(row.AppointmentDate).format('DD.MM.YYYY')
                                    : (row.AppointmentDate || '-');
                                  // Previous* alanları SP'de LAG ile üretiliyor; ilk satırda boş gelir.
                                  const previousAppointmentDate = row.PreviousAppointmentDate && dayjs(row.PreviousAppointmentDate).isValid()
                                    ? dayjs(row.PreviousAppointmentDate).format('DD.MM.YYYY')
                                    : (row.PreviousAppointmentDate || '');
                                  const techFullName = [row.TechName, row.TechSurname].filter(Boolean).join(' ');
                                  const previousTechFullName = String(row.PreviousTechFullName || '').trim();
                                  const dateChanged = Boolean(previousAppointmentDate) && previousAppointmentDate !== appointmentDate;
                                  const slotChanged = Boolean(row.PreviousTimeSlot) && row.PreviousTimeSlot !== row.TimeSlot;
                                  const techChanged = Boolean(previousTechFullName) && previousTechFullName !== techFullName;
                                  return (
                                    <TableRow key={`${row.Id ?? row.OfscNr ?? 'history'}-${index}`} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#f8fafc' }, '&:last-child td': { borderBottom: 0 } }}>
                                      <TableCell sx={{ py: 1, color: '#94a3b8', fontSize: '0.7rem', fontWeight: 700 }}>{index + 1}</TableCell>
                                      <TableCell sx={{ py: 1, color: '#334155', fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{row.Date || '-'}</TableCell>
                                      <TableCell sx={{ py: 1, whiteSpace: 'nowrap' }}>
                                        <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: '#0f172a' }}>{appointmentDate}</Typography>
                                        {dateChanged && (
                                          <Typography sx={{ mt: 0.15, fontSize: '0.62rem', color: '#94a3b8', textDecoration: 'line-through' }}>{previousAppointmentDate}</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ py: 1, whiteSpace: 'nowrap' }}>
                                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#334155' }}>{row.TimeSlot || '-'}</Typography>
                                        {slotChanged && (
                                          <Typography sx={{ mt: 0.15, fontSize: '0.62rem', color: '#94a3b8', textDecoration: 'line-through' }}>{row.PreviousTimeSlot}</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ py: 1 }}>
                                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>{techFullName || '-'}</Typography>
                                        {techChanged && (
                                          <Typography sx={{ mt: 0.15, fontSize: '0.62rem', color: '#94a3b8', whiteSpace: 'nowrap', textDecoration: 'line-through' }}>{previousTechFullName}</Typography>
                                        )}
                                        {row.TechCode != null && <Typography sx={{ mt: 0.15, fontSize: '0.62rem', color: '#94a3b8' }}>Kod: {row.TechCode}</Typography>}
                                      </TableCell>
                                      <TableCell sx={{ py: 1 }}>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.55, px: 0.8, py: 0.35, borderRadius: '12px', bgcolor: alpha(historyColor, 0.14), color: historyTextColor, border: `1px solid ${alpha(historyColor, 0.32)}`, fontSize: '0.68rem', fontWeight: 800, whiteSpace: 'nowrap' }}>
                                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: historyColor }} />
                                          {row.AppointmentStatus || 'Durum yok'}
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ py: 1, color: '#334155', fontSize: '0.72rem', fontWeight: 600 }}>{row.AppointmentUser || '-'}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {filteredHistoryRows.length === 0 && (
                            <Box sx={{ position: 'absolute', inset: '130px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                              <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Aramanızla eşleşen kayıt bulunamadı.</Typography>
                            </Box>
                          )}
                          </>
                        )}
                      </Box>
                    </DraggableResizablePopup>
                  </Box>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: hasCoords ? '1fr 204px' : '1fr',
                    gap: 2.5,
                    alignItems: 'start',
                    minWidth: 0,
                    maxWidth: '100%',
                    '& > *': { minWidth: 0 },
                    '@media (max-width: 720px)': { gridTemplateColumns: '1fr' },
                  }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '170px minmax(0, 1fr)', rowGap: 1.05, columnGap: 2, minWidth: 0 }}>
                      {normalRows.map(([label, value]) => (
                        <React.Fragment key={label}>
                          <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.35, color: '#777', textAlign: 'left' }}>
                            {label}
                          </Typography>
                          <Typography sx={{
                            fontSize: '0.9rem',
                            lineHeight: 1.35,
                            color: label === 'Telefon' && value !== '-' ? '#0277bd' : '#222',
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                          }}>
                            {value}
                          </Typography>
                        </React.Fragment>
                      ))}
                    </Box>
                    {hasCoords && (
                      <Box sx={{
                        width: 204,
                        height: 168,
                        mt: 3.4,
                        borderRadius: 2,
                        border: `1px solid ${alpha('#1565c0', 0.14)}`,
                        bgcolor: '#eef3f8',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 4px 14px rgba(15,23,42,0.12)',
                        '@media (max-width: 720px)': { width: '100%', height: 180, mt: 0 },
                      }}>
                        <Box
                          component="iframe"
                          title={`Randevu ${ofsc || ''} konumu`}
                          srcDoc={markerMapSrc}
                          sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                        />
                        <Box sx={{
                          position: 'absolute', left: 6, bottom: 6, px: 0.75, py: 0.35,
                          borderRadius: '6px', bgcolor: 'rgba(255,255,255,0.92)',
                          boxShadow: '0 2px 8px rgba(15,23,42,0.18)', pointerEvents: 'none',
                        }}>
                          <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#475569', fontFamily: 'monospace', lineHeight: 1.2 }}>
                            {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  {wideRows.length > 0 && (
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: '170px minmax(0, 1fr)',
                      rowGap: 1.05,
                      columnGap: 2,
                      mt: 1.05,
                      minWidth: 0,
                      maxWidth: '100%',
                    }}>
                      {wideRows.map(([label, value]) => (
                        <React.Fragment key={label}>
                          <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.35, color: '#777', textAlign: 'left' }}>
                            {label}
                          </Typography>
                          <Typography sx={{
                            fontSize: '0.9rem',
                            lineHeight: 1.35,
                            color: '#222',
                            whiteSpace: 'pre-line',
                            overflowWrap: 'anywhere',
                          }}>
                            {value}
                          </Typography>
                        </React.Fragment>
                      ))}
                    </Box>
                  )}
                  {servicePapers && detailLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5, color: '#777' }}>
                      <CircularProgress size={14} />
                      <Typography sx={{ fontSize: '0.75rem' }}>Detay bilgisi yükleniyor...</Typography>
                    </Box>
                  )}
                  {timeSummaryRows.length > 0 && (
                    <Box sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.6,
                      mt: 1.4,
                      pt: 1.1,
                      borderTop: '1px solid rgba(0,0,0,0.08)',
                    }}>
                      {timeSummaryRows.map(([label, value]) => (
                        <Box key={label} sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.4,
                          px: 0.75,
                          py: 0.35,
                          borderRadius: '6px',
                          bgcolor: 'rgba(0,0,0,0.035)',
                          border: '1px solid rgba(0,0,0,0.08)',
                        }}>
                          <Typography sx={{ fontSize: '0.68rem', color: '#777', fontWeight: 700 }}>
                            {label}
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#222', fontWeight: 700, fontFamily: 'monospace' }}>
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  <Divider sx={{ my: 1.8, borderColor: 'rgba(0,0,0,0.08)' }} />
                </Box>
              );
            })()}
            <Box sx={{ position: 'relative', minWidth: 220 }}>
              {detailData && detailData.error && (
                <Box sx={{ mb: 0.5, px: 1, py: 0.4, borderRadius: '6px', bgcolor: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.2)' }}>
                  <Typography sx={{ fontSize: '0.65rem', color: '#ef5350' }}>{detailData.error}</Typography>
                </Box>
              )}

              {isDelayed && !operationalAlert && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5,
                  px: 1, py: 0.55, borderRadius: '10px',
                  background: 'linear-gradient(135deg, rgba(255,235,238,0.98) 0%, rgba(255,205,210,0.92) 100%)',
                  border: '1px solid rgba(198,40,40,0.45)',
                  boxShadow: '0 2px 10px rgba(198,40,40,0.16), inset 0 1px 0 rgba(255,255,255,0.65)',
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  {/* Animated glow */}
                  <Box sx={{
                    position: 'absolute', left: -10, top: -10,
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(244,67,54,0.2) 0%, transparent 70%)',
                    animation: 'delayGlow 2s ease-in-out infinite',
                    '@keyframes delayGlow': {
                      '0%, 100%': { opacity: 0.5, transform: 'scale(0.9)' },
                      '50%': { opacity: 1, transform: 'scale(1.3)' },
                    },
                    pointerEvents: 'none',
                  }} />
                  <Box sx={{
                    width: 30, height: 30, borderRadius: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'linear-gradient(145deg, #e53935 0%, #b71c1c 100%)',
                    border: '1px solid rgba(127,29,29,0.85)',
                    boxShadow: '0 3px 10px rgba(183,28,28,0.42), inset 0 1px 0 rgba(255,255,255,0.28)',
                    flexShrink: 0,
                    animation: 'delayPulse 1.5s ease-in-out infinite',
                    '@keyframes delayPulse': {
                      '0%, 100%': { boxShadow: '0 3px 10px rgba(183,28,28,0.42), inset 0 1px 0 rgba(255,255,255,0.28)' },
                      '50%': { boxShadow: '0 4px 16px rgba(183,28,28,0.72), inset 0 1px 0 rgba(255,255,255,0.35)' },
                    },
                  }}>
                    <WarningAmberIcon sx={{
                      fontSize: 20,
                      color: '#fff',
                      strokeWidth: 2.4,
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))',
                    }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.62rem', color: '#b71c1c', fontWeight: 800, textTransform: 'none', lineHeight: 1 }}>Dikkat</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: '#7f1d1d', mt: 0.15 }}>Gecikme Tespit Edildi</Typography>
                  </Box>
                  {appt != null && nowMin > appt && (
                    <Box sx={{
                      px: 0.75, py: 0.3, borderRadius: '10px',
                      background: '#c62828',
                      border: '1px solid #b71c1c',
                      boxShadow: '0 1px 5px rgba(198,40,40,0.32)',
                    }}>
                      <Typography sx={{ fontSize: '0.64rem', fontWeight: 900, color: '#fff', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        +{formatDuration(nowMin - appt)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {operationalAlert && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.85, mb: 0.75,
                  px: 1, py: 0.75, borderRadius: '10px',
                  bgcolor: operationalAlert.background,
                  border: `1px solid ${operationalAlert.border}`,
                  boxShadow: `0 3px 12px ${alpha(operationalAlert.color, 0.12)}`,
                }}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: operationalAlert.color,
                    boxShadow: `0 3px 10px ${alpha(operationalAlert.color, 0.3)}`,
                  }}>
                    <WarningAmberIcon sx={{ fontSize: 19, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.15, fontWeight: 900, color: operationalAlert.color }}>
                      {operationalAlert.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.3, mt: 0.25, color: alpha(operationalAlert.color, 0.9) }}>
                      {operationalAlert.detail}
                    </Typography>
                  </Box>
                  <Chip
                    label={operationalAlert.shortTime}
                    size="small"
                    sx={{
                      height: 22, flexShrink: 0, fontSize: '0.64rem', fontWeight: 900,
                      color: '#fff', bgcolor: operationalAlert.color,
                      border: `1px solid ${alpha('#fff', 0.35)}`,
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
      <Box
        ref={barRef}
        draggable
        data-toa-job-ofsc={ofsc}
        title={hoverTitle}
        aria-label={hoverTitle}
        onDragStart={handleDragStart}
        onClick={toggleTip}
        sx={{
          position: 'absolute',
          left: `${left}%`,
          width: `${Math.max(width, 0.3)}%`,
          top: 5,
          bottom: 5,
          display: 'flex',
          borderRadius: '6px',
          overflow: tipOpen ? 'visible' : 'hidden',
          cursor: 'grab',
          zIndex: tipOpen ? 40 : (isDelayed || operationalAlert ? 12 : 10),
          border: tipOpen
            ? `2px solid ${workColor.text}`
            : isYemek
              ? (sKey === 'pending' ? `1px solid ${alpha('#000', 0.1)}` : '0.5px dashed rgba(0,0,0,0.25)')
              : isDelayed
                ? '1.5px solid #E53935'
                : operationalAlert
                  ? `1.5px solid ${operationalAlert.color}`
                  : `1px solid ${alpha(workColor.border, 0.5)}`,
          boxShadow: tipOpen
            ? `0 0 0 2px ${alpha('#fff', 0.9)}, 0 0 0 5px ${alpha(workColor.text, 0.35)}, 0 8px 24px ${alpha(workColor.text, 0.35)}`
            : isDelayed
              ? `0 2px 10px ${alpha('#f44336', 0.45)}, 0 0 16px ${alpha('#f44336', 0.15)}, inset 0 1px 0 rgba(255,255,255,0.1)`
              : operationalAlert
                ? `0 2px 10px ${alpha(operationalAlert.color, 0.35)}, 0 0 14px ${alpha(operationalAlert.color, 0.12)}`
              : `0 2px 6px ${alpha(workColor.border, 0.2)}, inset 0 1px 0 rgba(255,255,255,0.15)`,
          transition: 'all 0.2s cubic-bezier(.4,0,.2,1)',
          ...(isDelayed && !tipOpen && {
            animation: 'barDelayGlow 2s ease-in-out infinite',
          }),
          ...(isDelayed && {
            willChange: 'box-shadow',
            '@keyframes barDelayGlow': {
              '0%, 100%': { boxShadow: `0 2px 8px ${alpha('#f44336', 0.4)}, 0 0 10px ${alpha('#f44336', 0.1)}` },
              '50%': { boxShadow: `0 3px 16px ${alpha('#f44336', 0.65)}, 0 0 24px ${alpha('#f44336', 0.25)}` },
            },
          }),
          '&:hover': {
            zIndex: 30,
            transform: 'scaleY(1.3) scaleX(1.02)',
            boxShadow: `0 6px 20px ${alpha(isDelayed ? '#f44336' : (operationalAlert?.color || workColor.text), 0.35)}, 0 0 8px ${alpha(workColor.border, 0.2)}`,
            borderColor: isDelayed ? '#f44336' : (operationalAlert?.color || workColor.border),
          },
          '&:active': {
            cursor: 'grabbing',
            opacity: 0.7,
          },
        }}
      >
        {/* Yol segmenti — Parlak Sarı Gradient */}
        {showTravelSeg && (
          <Box sx={{
            width: `${travelPct}%`,
            minWidth: 0,
            background: `linear-gradient(135deg, ${TRAVEL_SEGMENT.bg} 0%, ${TRAVEL_SEGMENT.border} 100%)`,
            borderRight: showWorkSeg ? `1.5px solid ${TRAVEL_SEGMENT.border}` : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {travelPct > 28 && isMedium && (
              <DirectionsCarIcon sx={{ fontSize: 12, color: TRAVEL_SEGMENT.text, opacity: 0.85 }} />
            )}
          </Box>
        )}

        {/* İş segmenti — Durum bazlı gradient */}
        {showWorkSeg && (
          <Box sx={{
            width: `${workPct}%`,
            minWidth: 0,
            background: isYemek ? 'transparent' : `linear-gradient(135deg, ${workColor.bg} 0%, ${workColor.border} 100%)`,
            display: 'flex',
            alignItems: 'center',
            px: 0.4,
            gap: 0.3,
          }}>
            {isYemek && isMedium && (
              <RestaurantIcon sx={{ fontSize: 11, color: '#999', flexShrink: 0, opacity: 0.7 }} />
            )}
            {isMedium && (
              <Typography noWrap sx={{
                fontSize: '10px',
                fontWeight: 700,
                color: isYemek ? '#999' : workColor.text,
                lineHeight: 1,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {neighbourhood}
              </Typography>
            )}

          </Box>
        )}

        {/* Segment yoksa fallback */}
        {!showTravelSeg && !showWorkSeg && (
          <Box sx={{
            flex: 1,
            background: isYemek ? 'transparent' : `linear-gradient(135deg, ${workColor.bg} 0%, ${workColor.border} 100%)`,
            display: 'flex',
            alignItems: 'center',
            px: 0.4,
            gap: 0.3,
          }}>
            {isYemek && isMedium && (
              <RestaurantIcon sx={{ fontSize: 11, color: '#999', flexShrink: 0, opacity: 0.7 }} />
            )}
            {isMedium && (
              <Typography noWrap sx={{
                fontSize: '10px',
                fontWeight: 700,
                color: isYemek ? '#999' : workColor.text,
                lineHeight: 1,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {neighbourhood}
              </Typography>
            )}
          </Box>
        )}

        {/* Gecikme köşe göstergesi */}
        {isDelayed && !operationalAlert && (
          <>
            {/* Animated corner triangle */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 0,
              height: 0,
              borderTop: '9px solid #ef5350',
              borderLeft: '9px solid transparent',
              filter: 'drop-shadow(0 0 2px rgba(244,67,54,0.6))',
              zIndex: 2,
            }} />
            {/* Pulsing dot */}
            <Box sx={{
              position: 'absolute',
              top: -1,
              right: -1,
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: '#ff5252',
              zIndex: 3,
              animation: 'dotPulse 1.5s ease-in-out infinite',
              '@keyframes dotPulse': {
                '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 3px rgba(244,67,54,0.6)' },
                '50%': { transform: 'scale(1.5)', boxShadow: '0 0 8px rgba(244,67,54,0.9)' },
              },
            }} />
            {/* Left red edge line */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 2.5,
              height: '100%',
              background: 'linear-gradient(180deg, #ef5350 0%, rgba(244,67,54,0.3) 100%)',
              borderRadius: '4px 0 0 4px',
              zIndex: 2,
            }} />
          </>
        )}
        {operationalAlert && (
          <Tooltip title={operationalAlert.detail} arrow placement="top">
            <Box sx={{
              position: 'absolute', top: -7, right: -7, width: 17, height: 17,
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: operationalAlert.color, color: '#fff', zIndex: 4,
              border: '2px solid #fff',
              boxShadow: `0 2px 8px ${alpha(operationalAlert.color, 0.48)}`,
              animation: operationalAlert.type !== 'approaching' ? 'appointmentAlertPulse 1.5s ease-in-out infinite' : 'none',
              '@keyframes appointmentAlertPulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.18)' },
              },
            }}>
              <WarningAmberIcon sx={{ fontSize: 11 }} />
            </Box>
          </Tooltip>
        )}
      </Box>
    </>
  );

  return (
    <>
      {isFirst && (
        <Box sx={{
          position: 'absolute',
          left: `calc(${left}% - 10px)`,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 15,
          pointerEvents: 'none',
          fontSize: '11px',
          fontWeight: 800,
          color: '#1565c0',
          lineHeight: 1,
        }}>
          ›
        </Box>
      )}
      {bar}
    </>
  );
});
/* eslint-enable react/prop-types */

// ── Ana bileşen ──
// eslint-disable-next-line react/prop-types
const renderMoveImpactSummary = (impact, darkMode) => {
  if (!impact) return null;
  const newEtaViolationCount = Math.max(0, impact.donor.etaViolationCountDelta)
    + Math.max(0, impact.target.etaViolationCountDelta);
  const overtimeIncrease = Math.max(0, impact.donor.overtimeMinutesDelta)
    + Math.max(0, impact.target.overtimeMinutesDelta);
  const kmIncreases = impact.combinedKmDelta > 0;
  const hasAppointmentMismatch = newEtaViolationCount > 0;
  const signed = value => `${value > 0 ? '+' : ''}${Number(value).toFixed(1)}`;
  const statusLabel = hasAppointmentMismatch
    ? 'Randevu Uyumsuzluğu'
    : overtimeIncrease > 0 ? 'Mesai Riski' : 'Uygun';
  const statusColor = hasAppointmentMismatch
    ? '#d32f2f'
    : overtimeIncrease > 0 ? '#ed6c02' : '#2e7d32';
  const metrics = [
    {
      label: 'Mevcut teknisyen rotası',
      value: `${impact.donor.kmBefore.toFixed(1)} → ${impact.donor.kmAfter.toFixed(1)} km`,
      detail: `${signed(impact.donor.kmDelta)} km`,
      danger: impact.donor.kmDelta > 0,
    },
    {
      label: 'Hedef teknisyen rotası',
      value: `${impact.target.kmBefore.toFixed(1)} → ${impact.target.kmAfter.toFixed(1)} km`,
      detail: `${signed(impact.target.kmDelta)} km`,
      danger: impact.target.kmDelta > 0,
    },
    {
      label: 'Toplam rota farkı',
      value: `${signed(impact.combinedKmDelta)} km`,
      detail: kmIncreases ? 'Rota uzuyor' : 'Rota kısalıyor',
      danger: kmIncreases,
    },
    {
      label: 'Randevu etkisi',
      value: `${newEtaViolationCount} yeni ihlal`,
      detail: hasAppointmentMismatch ? 'Uyumsuzluk oluşuyor' : 'Yeni uyumsuzluk yok',
      danger: hasAppointmentMismatch,
    },
  ];

  return (
    <>
      {kmIncreases && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ borderRadius: 2, py: 0.25 }}>
          <b>Rota uzuyor:</b> İki teknisyenin toplam rotası{' '}
          <b>{signed(impact.combinedKmDelta)} km</b> değişiyor.
        </Alert>
      )}
      {hasAppointmentMismatch && (
        <Alert severity="error" icon={<WarningAmberIcon />} sx={{ borderRadius: 2, py: 0.25 }}>
          <b>Randevu uyumsuzluğu:</b> Taşıma sonucunda{' '}
          <b>{newEtaViolationCount} yeni randevu zaman ihlali</b> oluşuyor.
        </Alert>
      )}
      <Box sx={{
        p: 1.5,
        border: `1px solid ${alpha(hasAppointmentMismatch ? '#d32f2f' : '#1565c0', 0.22)}`,
        borderRadius: 2.5,
        bgcolor: alpha(hasAppointmentMismatch ? '#d32f2f' : '#1565c0', 0.035),
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
            <AltRouteIcon sx={{ fontSize: 18, color: '#1565c0' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: 'text.primary' }}>
              Taşıma Simülasyonu
            </Typography>
          </Box>
          <Chip size="small" label={statusLabel} sx={{
            height: 22,
            fontSize: '0.68rem',
            fontWeight: 800,
            color: statusColor,
            bgcolor: alpha(statusColor, 0.1),
          }} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
          {metrics.map(metric => (
            <Box key={metric.label} sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: darkMode ? alpha('#fff', 0.035) : '#fff',
              border: `1px solid ${alpha(metric.danger ? '#d32f2f' : '#1565c0', 0.13)}`,
            }}>
              <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary', fontWeight: 700 }}>
                {metric.label}
              </Typography>
              <Typography sx={{ mt: 0.2, fontSize: '0.78rem', color: 'text.primary', fontWeight: 800 }}>
                {metric.value}
              </Typography>
              <Typography sx={{ mt: 0.1, fontSize: '0.62rem', color: metric.danger ? '#d32f2f' : '#2e7d32', fontWeight: 700 }}>
                {metric.detail}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default function ToaScheduling({ selectedServiceShop, headerControls }) {
  const queryClient = useQueryClient();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [servicePaperLookupLoading, setServicePaperLookupLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [hourWidth, setHourWidth] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768 ? HOUR_WIDTH_DEFAULT_MOBILE : HOUR_WIDTH_DEFAULT);
  const [dragOverTech, setDragOverTech] = useState(null);
  const [dropConfirm, setDropConfirm] = useState(null); // { ofscNr, sourceTechCode, targetTechCode, timeSlot, appointmentDate, delta, payload }
  const [dropLoading, setDropLoading] = useState(false);

  // ── Teknisyen Detay Popup State ──
  const [techDetailOpen, setTechDetailOpen] = useState(false);
  const [techDetailLoading, setTechDetailLoading] = useState(false);
  const [techDetailError, setTechDetailError] = useState('');
  const [techPhoto, setTechPhoto] = useState(null);
  const [techPhotoOpen, setTechPhotoOpen] = useState(false);
  const [techWorks, setTechWorks] = useState([]);
  const [techDetailIsBucket, setTechDetailIsBucket] = useState(false); // popup bir servis (bucket) satırı için mi açıldı
  const [techInfo, setTechInfo] = useState(null);
  const [techSortField, setTechSortField] = useState(null);
  const [techSortDir, setTechSortDir] = useState('asc');
  const [techWorkSearch, setTechWorkSearch] = useState('');
  const [techMapOpen, setTechMapOpen] = useState(false);
  const [techMapData, setTechMapData] = useState(null); // { techName, jobs: [{lat,lng,ofsc,neighbourhood,status,order}] }
  const [techMapShowRoute, setTechMapShowRoute] = useState(true);
  const [techMapAnimating, setTechMapAnimating] = useState(false);
  const techMapIframeRef = useRef(null);
  const showTechMapRouteControls = true;
  const [techWorkChecked, setTechWorkChecked] = useState([]); // seçili OfscNr listesi
  const [techMoveOpen, setTechMoveOpen] = useState(false); // taşıma dialog
  const [techMoveTarget, setTechMoveTarget] = useState(''); // hedef teknisyen kodu
  const [techMoveLoading, setTechMoveLoading] = useState(false);
  const [bucketExpanded, setBucketExpanded] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // ── Teknisyen Gruplama State ──
  const [techGroups, setTechGroups] = useState([]); // [{Id, GroupName, TechCodes: ['T1','T2',...]}]
  const [techGroupsEnabled, setTechGroupsEnabled] = useState(false); // ağaç görünümü aktif mi
  const [techGroupsExpanded, setTechGroupsExpanded] = useState({}); // {groupId: true/false}
  const [techGroupDialogOpen, setTechGroupDialogOpen] = useState(false);
  const [techGroupEditing, setTechGroupEditing] = useState(null); // düzenlenen grup
  const techGroupNewNameRef = useRef('');
  const [techGroupLoading, setTechGroupLoading] = useState(false);
  const [techGroupSaving, setTechGroupSaving] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentMinuteTimestamp, setCurrentMinuteTimestamp] = useState(() => Date.now());
  const renderStartRef = useRef(null);
  const performanceMetricsRef = useRef(null);
  const fetchingRef = useRef(false);
  const schedulingAbortRef = useRef(null);
  const dataRef = useRef([]);
  const rawSchedulingDataRef = useRef([]);
  const schedulingCalculationOptionsRef = useRef({});

  // ── Professional features state ──
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]); // aktif durum filtreleri
  const [rowDensity, setRowDensity] = useState('comfortable'); // compact | comfortable | expanded
  const [resourcePanelCollapsed, setResourcePanelCollapsed] = useState(false);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [appearanceAnchor, setAppearanceAnchor] = useState(null);
  const [appearanceSettings, setAppearanceSettings] = useState(readAppearanceSettings);

  // ── Takvim Popup State ──
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [moveSimulationOpen, setMoveSimulationOpen] = useState(false);
  const [moveSimulationCacheVersion, setMoveSimulationCacheVersion] = useState(0);
  const [calendarSelectedTech, setCalendarSelectedTech] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarTechList, setCalendarTechList] = useState([]);
  const [calendarTechListLoading, setCalendarTechListLoading] = useState(false);
  const [calendarTechSearch, setCalendarTechSearch] = useState('');
  const [calendarTechSort, setCalendarTechSort] = useState('name-asc');
  const [calendarSaving, setCalendarSaving] = useState(false);
  const [calendarEditRows, setCalendarEditRows] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());

  // ── Tüm Teknisyenleri Kapat State ──
  const [closeAllOpen, setCloseAllOpen] = useState(false);
  const [closeAllDate, setCloseAllDate] = useState(null);
  const [closeAllSaving, setCloseAllSaving] = useState(false);

  // ── Toplu Ekle Dialog State ──
  const [bulkAddDialogOpen, setBulkAddDialogOpen] = useState(false);
  const [bulkScheduleType, setBulkScheduleType] = useState('DÜZENLİ (08-18)');
  const [bulkDialogEndDate, setBulkDialogEndDate] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [bulkRepeat, setBulkRepeat] = useState('Daily');
  const [bulkEveryN, setBulkEveryN] = useState(1);
  const [bulkWeekDays, setBulkWeekDays] = useState([1, 2, 3, 4, 5, 6]); // Pzt-Cmt
  const [bulkCustomStartHour, setBulkCustomStartHour] = useState('09');
  const [bulkCustomStartMin, setBulkCustomStartMin] = useState('00');
  const [bulkCustomEndHour, setBulkCustomEndHour] = useState('18');
  const [bulkCustomEndMin, setBulkCustomEndMin] = useState('00');

  // ── Ayarlar Popup State ──
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsActiveTab, setSettingsActiveTab] = useState('bayiServis');
  // Bayi Servis İlişkisi
  const [settingsDealerCombo, setSettingsDealerCombo] = useState(null);
  const [settingsTimeSlotCombo, setSettingsTimeSlotCombo] = useState(null);
  const [settingsDealerList, setSettingsDealerList] = useState([]);
  const [settingsDealerLoading, setSettingsDealerLoading] = useState(false);
  const [settingsDealerSearch, setSettingsDealerSearch] = useState('');
  const [settingsDealerPage, setSettingsDealerPage] = useState(0);
  const [settingsDealerRowsPerPage, setSettingsDealerRowsPerPage] = useState(15);

  // Teknisyen Mahalle
  const [tmTechCombo, setTmTechCombo] = useState(null);
  const [tmIlCombo, setTmIlCombo] = useState(null);
  const [tmIlceCombo, setTmIlceCombo] = useState(null);
  const [tmMahalleCombo, setTmMahalleCombo] = useState(null);
  const [tmGrupMahalleCombo, setTmGrupMahalleCombo] = useState(null);
  const [tmData, setTmData] = useState([]);
  const tmOriginalRef = useRef([]);
  const [tmQueryMode, setTmQueryMode] = useState('mahalle'); // 'mahalle' | 'grup'
  const [tmLoading, setTmLoading] = useState(false);
  const [tmSaving, setTmSaving] = useState(false);
  const [tmSearch, setTmSearch] = useState('');

  // Teknisyen Uzmanlık
  const [tuTechCombo, setTuTechCombo] = useState(null);
  const [tuData, setTuData] = useState([]);
  const tuOriginalRef = useRef([]);
  const [tuLoading, setTuLoading] = useState(false);
  const [tuSaving, setTuSaving] = useState(false);
  const [tuSearch, setTuSearch] = useState('');

  // Grup Mahalle
  const [gmGrupName, setGmGrupName] = useState('');
  const [gmBagliYer, setGmBagliYer] = useState('0');
  const [gmDays, setGmDays] = useState({ ...GM_DAYS_INIT });
  const [gmGrupCombo, setGmGrupCombo] = useState(null);
  const [gmGrupComboKey, setGmGrupComboKey] = useState(0);
  const [gmIlCombo, setGmIlCombo] = useState(null);
  const [gmIlceCombo, setGmIlceCombo] = useState(null);
  const [gmMahalleCombo, setGmMahalleCombo] = useState(null);
  const [gmDetailData, setGmDetailData] = useState([]);
  const [gmDetailLoading, setGmDetailLoading] = useState(false);
  const [gmCreating, setGmCreating] = useState(false);
  const [gmAdding, setGmAdding] = useState(false);
  const [gmSaving, setGmSaving] = useState(false);
  const [gmFreeTech, setGmFreeTech] = useState(false);
  const [gmMahallePopupOpen, setGmMahallePopupOpen] = useState(false);
  const [gmMahalleList, setGmMahalleList] = useState([]);
  const [gmMahalleSelected, setGmMahalleSelected] = useState({});
  const [gmMahalleListLoading, setGmMahalleListLoading] = useState(false);
  const [gmStagedMahalles, setGmStagedMahalles] = useState([]);
  const [gmDayPopupOpen, setGmDayPopupOpen] = useState(false);
  const [gmDayPopupDays, setGmDayPopupDays] = useState({ ...GM_DAYS_INIT });

  // Aynı Gün Bağlı Yerler
  const [byBagliYer1, setByBagliYer1] = useState(null);
  const [byBagliYer2, setByBagliYer2] = useState(null);
  const [bySaving, setBySaving] = useState(false);
  const [byList, setByList] = useState([]);
  const [byLoading, setByLoading] = useState(false);
  const [byDeleting, setByDeleting] = useState(null); // silinen IDKEY1

  // Mahalle Kısıtlaması Hariç
  const [mkTechCombo, setMkTechCombo] = useState(null);
  const [mkList, setMkList] = useState([]);
  const [mkLoading, setMkLoading] = useState(false);
  const [mkSaving, setMkSaving] = useState(false);

  // ── Memoized values for settings performance ──
  const currentUser = useMemo(() => getUser() || {}, []);
  const serviceShopId = useMemo(
    () => selectedServiceShop?.id || '',
    [selectedServiceShop]
  );
  const serviceShop = useMemo(() => ({
    techCode: selectedServiceShop?.id || '',
    userTechCode: selectedServiceShop?.id || '',
    techName: selectedServiceShop?.name || '',
    topServiceShopName: selectedServiceShop?.name || '',
    latitude: selectedServiceShop?.latitude,
    longitude: selectedServiceShop?.longitude,
  }), [selectedServiceShop]);

  // Canlı ekranla aynı davranış: yeni tarih/servis bağlamı her zaman açık bucket ile başlar.
  useEffect(() => {
    setBucketExpanded(true);
  }, [selectedDate, serviceShopId]);

  const currentUserId = useMemo(() => currentUser.UserId || 0, [currentUser]);
  const dealerFilter = useMemo(() => `BIRIM_KODU = '${serviceShopId}'`, [serviceShopId]);
  const techFilter = useMemo(() => `BIRIM_KODU = '${serviceShopId}'`, [serviceShopId]);
  const tmIlParams = useMemo(() => ({ ServiceShopId: serviceShopId }), [serviceShopId]);
  const tmIlceParams = useMemo(() => ({ ServiceShopId: serviceShopId, IlKod: tmIlCombo?.id ?? '-1' }), [serviceShopId, tmIlCombo?.id]);
  const tmMahalleParams = useMemo(() => ({ ServiceShopId: serviceShopId, IlKod: tmIlCombo?.id ?? '-1', IlceKod: tmIlceCombo?.id ?? '-1' }), [serviceShopId, tmIlCombo?.id, tmIlceCombo?.id]);
  const tmGrupParams = useMemo(() => ({ ServiceShopId: serviceShopId }), [serviceShopId]);

  // ── Teknisyen Mahalle: Ayarlar > tekMahalle sekmesi açıldığında sıralı axios çağrısıyla
  //    il → ilçe → mahalle → grupMahalle otomatik seçimi ──
  useEffect(() => {
    if (!settingsOpen || settingsActiveTab !== 'tekMahalle') return;
    if (tmIlCombo) return; // kullanıcı zaten seçim yaptıysa override etme

    const city = (currentUser.ServiceShopCity || currentUser.BirimIl || '').trim();
    if (!city) return;

    let cancelled = false;
    const cityNorm = city.toLocaleLowerCase('tr-TR');

    (async () => {
      try {
        // 1. İl listesi → ServiceShopCity eşleştir
        const ilResp = await axios.post('/GetCombo', {
          functionName: 'GetTechNeighbourhoodIl',
          idField: 'IL_KOD',
          textField: 'IL',
          functionParameters: { ServiceShopId: serviceShopId },
          connectionName: 'SarisDb',
        });
        if (cancelled) return;

        const ilItems = Array.isArray(ilResp?.data) ? ilResp.data : [];
        const matchedIl = ilItems.find(item => {
          const n = String(item.IL ?? item.Text ?? item.text ?? item.Name ?? item.name ?? '').trim().toLocaleLowerCase('tr-TR');
          const k = String(item.IL_KOD ?? item.Value ?? item.value ?? item.Id ?? item.id ?? '').trim().toLocaleLowerCase('tr-TR');
          return n === cityNorm || k === cityNorm || n.includes(cityNorm) || cityNorm.includes(n);
        });
        if (!matchedIl || cancelled) return;

        const ilId = matchedIl.IL_KOD ?? matchedIl.Value ?? matchedIl.value ?? matchedIl.Id ?? matchedIl.id;
        const ilName = matchedIl.IL ?? matchedIl.Text ?? matchedIl.text ?? matchedIl.Name ?? matchedIl.name ?? '';
        setTmIlCombo({ id: ilId, name: ilName });

        // 2. İlçe listesi → ilk öğe
        const ilceResp = await axios.post('/GetCombo', {
          functionName: 'GetTechNeighbourhoodIlce',
          idField: 'ID',
          textField: 'ACIKLAMA',
          functionParameters: { ServiceShopId: serviceShopId, IlKod: ilId },
          connectionName: 'SarisDb',
        });
        if (cancelled) return;

        const sortedIlce = (Array.isArray(ilceResp?.data) ? ilceResp.data : [])
          .map(i => ({ id: i.ID ?? i.Value ?? i.value ?? i.Id ?? i.id, name: i.ACIKLAMA ?? i.Text ?? i.text ?? i.Name ?? i.name ?? '' }))
          .filter(x => x.id != null)
          .sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr', { sensitivity: 'base' }));
        if (cancelled) return;

        const firstIlce = sortedIlce[0];
        if (firstIlce) setTmIlceCombo(firstIlce);

        // 3. Mahalle listesi → ilk öğe
        if (firstIlce) {
          const mahalleResp = await axios.post('/GetCombo', {
            functionName: 'GetTechNeighbourhoodMahalle',
            idField: 'ID',
            textField: 'ACIKLAMA',
            functionParameters: { ServiceShopId: serviceShopId, IlKod: ilId, IlceKod: firstIlce.id },
            connectionName: 'SarisDb',
          });
          if (cancelled) return;

          const sortedMahalle = (Array.isArray(mahalleResp?.data) ? mahalleResp.data : [])
            .map(i => ({ id: i.ID ?? i.Value ?? i.value ?? i.Id ?? i.id, name: i.ACIKLAMA ?? i.Text ?? i.text ?? i.Name ?? i.name ?? '' }))
            .filter(x => x.id != null)
            .sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr', { sensitivity: 'base' }));
          if (sortedMahalle.length && !cancelled) setTmMahalleCombo(sortedMahalle[0]);
        }

        // 4. Grup Mahalle listesi → ilk öğe
        const grupResp = await axios.post('/GetCombo', {
          functionName: 'GetTechNeighbourhoodGrupMahalle',
          idField: 'ID',
          textField: 'ACIKLAMA',
          functionParameters: { ServiceShopId: serviceShopId },
          connectionName: 'SarisDb',
        });
        if (cancelled) return;

        const sortedGrup = (Array.isArray(grupResp?.data) ? grupResp.data : [])
          .map(i => ({ id: i.ID ?? i.Value ?? i.value ?? i.Id ?? i.id, name: i.ACIKLAMA ?? i.Text ?? i.text ?? i.Name ?? i.name ?? '' }))
          .filter(x => x.id != null)
          .sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr', { sensitivity: 'base' }));
        if (sortedGrup.length && !cancelled) setTmGrupMahalleCombo(sortedGrup[0]);
      } catch (_) { /* ignore */ }
    })();

    return () => { cancelled = true; };
  }, [settingsOpen, settingsActiveTab]);

  // İlçe değiştiğinde ilk mahalleyi otomatik seç
  useEffect(() => {
    if (!settingsOpen || settingsActiveTab !== 'tekMahalle') return;
    if (tmIlceCombo?.id == null || tmIlCombo?.id == null) return;

    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.post('/GetCombo', {
          functionName: 'GetTechNeighbourhoodMahalle',
          idField: 'ID',
          textField: 'ACIKLAMA',
          functionParameters: { ServiceShopId: serviceShopId, IlKod: tmIlCombo.id, IlceKod: tmIlceCombo.id },
          connectionName: 'SarisDb',
        });
        if (cancelled) return;
        const sorted = (Array.isArray(resp?.data) ? resp.data : [])
          .map(i => ({ id: i.ID ?? i.Value ?? i.value ?? i.Id ?? i.id, name: i.ACIKLAMA ?? i.Text ?? i.text ?? i.Name ?? i.name ?? '' }))
          .filter(x => x.id != null)
          .sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr', { sensitivity: 'base' }));
        if (sorted.length && !cancelled) setTmMahalleCombo(sorted[0]);
      } catch (_) { /* ignore */ }
    })();

    return () => { cancelled = true; };
  }, [tmIlceCombo?.id]);

  const gmIlParams = useMemo(() => ({ ServiceShopId: serviceShopId }), [serviceShopId]);
  const gmIlceParams = useMemo(() => ({ ServiceShopId: serviceShopId, IlKod: gmIlCombo?.id ?? '-1' }), [serviceShopId, gmIlCombo?.id]);
  const gmMahalleParams = useMemo(() => ({ ServiceShopId: serviceShopId, IlKod: gmIlCombo?.id ?? '-1', IlceKod: gmIlceCombo?.id ?? '-1' }), [serviceShopId, gmIlCombo?.id, gmIlceCombo?.id]);
  const gmGrupParams = useMemo(() => ({ ServiceShopId: serviceShopId }), [serviceShopId]);
  const byBagliYerParams = useMemo(() => ({ ServiceShopId: serviceShopId }), [serviceShopId]);
  const bySplFilter = useCallback((item) => item.name && item.name.toUpperCase().includes('SPL'), []);
  const tmTumuFilter = useCallback((item) => String(item.name || '').trim().toLocaleLowerCase('tr') !== 'tümü' && String(item.id ?? '') !== '-1', [])

  // ── Pre-rendered select options (avoid re-creating on every row render) ──
  const tmTimeSlotOptions = useMemo(() => TM_TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>), []);
  const tmOperationOptions = useMemo(() => TM_OPERATIONS.map(o => (
    <option key={o.value} value={o.value} style={{ backgroundColor: o.bg, color: o.value === 0 ? '#fff' : '#333' }}>{o.label}</option>
  )), []);
  const tuOperationOptions = useMemo(() => TU_OPERATIONS.map(o => (
    <option key={o.value} value={o.value} style={{ backgroundColor: o.bg, color: o.value === 21 ? '#fff' : '#333' }}>{o.label}</option>
  )), []);

  // ── Memoized dealer filtered list ──
  const filteredDealerList = useMemo(() => {
    if (!settingsDealerSearch) return settingsDealerList;
    const q = settingsDealerSearch.toLowerCase();
    return settingsDealerList.filter(r =>
      (r.DealerCode || '').toLowerCase().includes(q) ||
      (r.UserDealerCode || '').toLowerCase().includes(q) ||
      (r.DealerName || '').toLowerCase().includes(q) ||
      (r.TimeSlot || '').toLowerCase().includes(q)
    );
  }, [settingsDealerList, settingsDealerSearch]);

  const visibleCalendarTechList = useMemo(() => {
    const searchText = calendarTechSearch.trim().toLocaleLowerCase('tr-TR');
    const [sortField, sortDirection] = calendarTechSort.split('-');
    const directionMultiplier = sortDirection === 'desc' ? -1 : 1;
    const getTechnicianName = (tech) => {
      const displayName = String(tech.TechName || '').trim();
      const separatorIndex = displayName.indexOf(' - ');
      return separatorIndex >= 0 ? displayName.slice(separatorIndex + 3).trim() : displayName;
    };

    return calendarTechList
      .filter((tech) => {
        if (!searchText) return true;
        const techName = String(tech.TechName || '').toLocaleLowerCase('tr-TR');
        const techCode = String(tech.TechCode || '').toLocaleLowerCase('tr-TR');
        return techName.includes(searchText) || techCode.includes(searchText);
      })
      .sort((firstTech, secondTech) => {
        const compareOptions = { numeric: true, sensitivity: 'base' };
        const firstValue = sortField === 'code' ? String(firstTech.TechCode || '') : getTechnicianName(firstTech);
        const secondValue = sortField === 'code' ? String(secondTech.TechCode || '') : getTechnicianName(secondTech);
        const primaryComparison = firstValue.localeCompare(
          secondValue,
          'tr',
          compareOptions
        );

        if (primaryComparison !== 0) return primaryComparison * directionMultiplier;

        const firstSecondaryValue = sortField === 'code' ? getTechnicianName(firstTech) : String(firstTech.TechCode || '');
        const secondSecondaryValue = sortField === 'code' ? getTechnicianName(secondTech) : String(secondTech.TechCode || '');
        return firstSecondaryValue.localeCompare(
          secondSecondaryValue,
          'tr',
          compareOptions
        );
      });
  }, [calendarTechList, calendarTechSearch, calendarTechSort]);

  const hoverLineRef = useRef(null);
  const ganttWrapperRef = useRef(null);
  const containerRef = useRef(null);

  // ── Mobile detection ──
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useLayoutEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const rowHeight = isMobile ? (ROW_HEIGHTS_MOBILE[rowDensity] || ROW_HEIGHT_MOBILE) : (ROW_HEIGHTS[rowDensity] || ROW_HEIGHT);
  const timelineWidth = TOTAL_HOURS * hourWidth;
  const scrollRef = useRef(null);
  const headerScrollRef = useRef(null);
  const resourceScrollRef = useRef(null);
  const timelineContainerRef = useRef(null);

  const darkMode = document.body.classList.contains('dark-mode') ||
    document.documentElement.getAttribute('data-theme') === 'dark' ||
    window.matchMedia?.('(prefers-color-scheme: dark)').matches;

  // ── Fullscreen toggle ──
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => { });
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => { });
    }
  }, []);
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const fetchData = useCallback(async (silent = false, replaceActive = false) => {
    const isSilent = silent === true;
    if (fetchingRef.current && !replaceActive) return;
    if (replaceActive) schedulingAbortRef.current?.abort();

    const abortController = new AbortController();
    schedulingAbortRef.current = abortController;
    fetchingRef.current = true;
    if (!isSilent) setLoading(true);
    setError(null);
    performanceMetricsRef.current?.reset();
    const apiStart = performance.now();
    const requestStartedAt = new Date();
    try {
      const dateStr = formatDateForApi(selectedDate);
      const response = await axios.post('/GetAronToaSchedulingAvailable', {
        ServiceShopId: Number(serviceShopId),
        TechnicianId: 0,
        Date: dateStr,
        StartTime: WORK_START,
        EndTime: WORK_END,
      }, {
        signal: abortController.signal,
      });
      if (abortController.signal.aborted) return;
      const apiEnd = performance.now();
      const resultList = getSchedulingRows(response?.data);
      const calculationStart = performance.now();
      const calculationOptions = getSchedulingCalculationOptions(
        response?.data,
        selectedDate,
        requestStartedAt,
        serviceShop,
      );
      const calculatedData = calculateToaScheduling(resultList, calculationOptions);
      const calculationEnd = performance.now();
      if (abortController.signal.aborted) return;

      rawSchedulingDataRef.current = resultList;
      schedulingCalculationOptionsRef.current = calculationOptions;
      performanceMetricsRef.current?.setApiAndCalculation(
        Math.round(apiEnd - apiStart),
        Math.round(calculationEnd - calculationStart),
      );
      renderStartRef.current = performance.now();
      setData(calculatedData);
      setLastUpdate(new Date());
      window.setTimeout(() => {
        void sendCoordinateCorrections(calculatedData);
      }, 0);
    } catch (err) {
      if (!axios.isCancel?.(err) && err?.code !== 'ERR_CANCELED') {
        setError('Veri yüklenirken hata oluştu');
        console.error('ToaScheduling fetch error:', err);
      }
    } finally {
      if (schedulingAbortRef.current === abortController) {
        schedulingAbortRef.current = null;
        setLoading(false);
        fetchingRef.current = false;
      }
    }
  }, [selectedDate, serviceShop]);

  useEffect(() => {
    fetchData(false, true);
    return () => schedulingAbortRef.current?.abort();
  }, [fetchData]);

  // dataRef'i güncel tut (fetchTechDetail fallback için)
  useEffect(() => { dataRef.current = data; }, [data]);

  // Render süresini ölç — data değiştiğinde DOM çizimi tamamlandığında
  useEffect(() => {
    if (renderStartRef.current && data.length > 0 && !loading) {
      requestAnimationFrame(() => {
        const renderEnd = performance.now();
        performanceMetricsRef.current?.setRenderTime(
          Math.round(renderEnd - renderStartRef.current),
        );
        renderStartRef.current = null;
      });
    }
  }, [data, loading]);

  // 5 dakikada bir otomatik yenile (sadece tab görünürse)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!fetchingRef.current && document.visibilityState === 'visible') fetchData(true);
    }, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Veri yüklendiğinde en erken/en geç işe göre otomatik zoom & scroll
  useEffect(() => {
    if (!data.length || !scrollRef.current) return;
    let minTime = Infinity;
    let maxTime = -Infinity;
    data.forEach((item) => {
      const times = getTimelineReferenceTimes(item);
      if (times.length === 0) return;
      minTime = Math.min(minTime, ...times);
      maxTime = Math.max(maxTime, ...times);
    });
    if (minTime === Infinity || maxTime === -Infinity) return;
    // 30dk marj ekle
    minTime = Math.max(HOUR_START * 60, minTime - 30);
    maxTime = Math.min(HOUR_END * 60, maxTime + 30);
    const rangeHours = (maxTime - minTime) / 60;
    if (rangeHours <= 0) return;
    const containerWidth = scrollRef.current.clientWidth;
    const idealHourWidth = Math.max(HOUR_WIDTH_MIN, Math.min(HOUR_WIDTH_MAX, Math.floor(containerWidth / rangeHours)));
    setHourWidth(idealHourWidth);
    // Scroll pozisyonunu ayarla
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        const totalTimelineWidth = TOTAL_HOURS * idealHourWidth;
        const scrollLeft = ((minTime - HOUR_START * 60) / (TOTAL_HOURS * 60)) * totalTimelineWidth;
        scrollRef.current.scrollLeft = Math.max(0, scrollLeft);
      }
    });
  }, [data]);

  // Şu anki saat yalnızca dakika değiştiğinde ana zaman çizelgesini yeniler.
  useEffect(() => {
    let minuteInterval = null;
    const delayToNextMinute = 60000 - (Date.now() % 60000) + 25;
    const minuteTimeout = window.setTimeout(() => {
      setCurrentMinuteTimestamp(Date.now());
      minuteInterval = window.setInterval(() => {
        setCurrentMinuteTimestamp(Date.now());
      }, 60000);
    }, delayToNextMinute);
    return () => {
      window.clearTimeout(minuteTimeout);
      if (minuteInterval) window.clearInterval(minuteInterval);
    };
  }, []);

  // Şu anki saat çizgisi (keyboard shortcuts ve scrollToNow için erken tanımlanmalı)
  const now = new Date(currentMinuteTimestamp);
  const isSelectedToday = isToday(selectedDate);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const nowPercent = ((nowMinutes - HOUR_START * 60) / (TOTAL_HOURS * 60)) * 100;
  const selectedDateDayjs = useMemo(() => dayjs(selectedDate), [selectedDate]);

  // ── Takvim teknisyen listesi ──
  const fetchCalendarTechList = useCallback(async () => {
    setCalendarTechListLoading(true);
    try {
      const resp = await axios.post('/GetCombo', {
        tableName: 'T_YAPI_TEKNISYEN',
        idField: 'TEKNISYEN_KODU',
        textField: "KUL_TEK_KODU + ' - ' + ADI + ' ' + SOYADI",
        filter: `BIRIM_KODU = '${serviceShopId}'`,
        connectionName: 'SarisDb',
      });
      const result = resp?.data || [];
      setCalendarTechList(Array.isArray(result) ? result.map(item => ({
        TechCode: item.TEKNISYEN_KODU ?? item.Value ?? item.value ?? item.Id ?? item.id ?? '',
        TechName: item["KUL_TEK_KODU + ' - ' + ADI + ' ' + SOYADI"] ?? item.Text ?? item.text ?? item.Name ?? item.name ?? '',
      })) : []);
    } catch (err) {
      console.error('fetchCalendarTechList error:', err);
      setCalendarTechList([]);
    } finally {
      setCalendarTechListLoading(false);
    }
  }, [serviceShopId]);

  // ── Takvim fonksiyonları ──
  const fetchCalendar = useCallback(async (techCode) => {
    setCalendarLoading(true);
    setCalendarData([]);
    setCalendarEditRows([]);

    try {
      const resp = await axios.post('/GetAronCalenders', {
        TechnicianId: techCode,
      });

      const result =
        resp?.data?.Data ??
        resp?.data?.data ??
        resp?.data ??
        [];

      const sourceRows = Array.isArray(result) ? result : [];

      const rows = sourceRows.map((r, i) => {
        let dateStr = r?.Date ?? '';

        if (dateStr.includes('.')) {
          const [day, month, year] = dateStr.split('.');

          if (day && month && year) {
            dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
        }

        return {
          id: r?.Id ?? i,
          Date: dateStr,
          StartTime: r?.StartTime ?? '09:00',
          EndTime: r?.EndTime ?? '18:00',
        };
      });

      setCalendarData(rows);
      setCalendarEditRows(rows.map((row) => ({ ...row })));

      if (rows.length === 0) {
        setSnackbar({
          open: true,
          message: 'Teknisyene ait takvim kaydı bulunamadı',
          severity: 'info',
        });
      }
    } catch (err) {
      console.error('GetAronCalenders error:', err);

      setSnackbar({
        open: true,
        message: 'Takvim verileri alınamadı',
        severity: 'error',
      });
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  const handleCalendarTechSelect = useCallback((tech) => {
    setCalendarSelectedTech(tech);
    fetchCalendar(tech.TechCode);
  }, [fetchCalendar]);

  const handleCalendarRemoveRow = useCallback((id) => {
    setCalendarEditRows(prev => prev.filter(r => r.id !== id));
  }, []);

  const handleCalendarRowChange = useCallback((id, field, value) => {
    setCalendarEditRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  }, []);

  const handleCalendarSave = useCallback(async () => {
    if (!calendarSelectedTech) return;
    // Sadece değişiklik olan kayıtları bul
    const originalMap = {};
    calendarData.forEach(r => { originalMap[r.Date] = r; });
    const editMap = {};
    calendarEditRows.forEach(r => { editMap[r.Date] = r; });

    const changedRows = [];
    // Yeni veya değiştirilmiş kayıtlar
    calendarEditRows.forEach(r => {
      const orig = originalMap[r.Date];
      if (!orig || orig.StartTime !== r.StartTime || orig.EndTime !== r.EndTime) {
        changedRows.push(r);
      }
    });
    // Silinen kayıtlar (orijinalde var ama edit'te yok)
    const deletedRows = calendarData.filter(r => !editMap[r.Date]).map(r => ({ ...r, StartTime: '', EndTime: '' }));

    const allChanges = [...changedRows, ...deletedRows];
    if (allChanges.length === 0) {
      setSnackbar({ open: true, message: 'Değişiklik bulunamadı', severity: 'info' });
      return;
    }

    setCalendarSaving(true);





    try {
      const payload = allChanges.map(r => {
        // YYYY-MM-DD -> dd.MM.yyyy formatına çevir
        let dateStr = r.Date || '';
        if (dateStr && dateStr.includes('-')) {
          const parts = dateStr.split('-');
          if (parts.length === 3) dateStr = `${parts[2]}.${parts[1]}.${parts[0]}`;
        }
        return { TechnicianId: calendarSelectedTech.TechCode, Date: dateStr, StartTime: r.StartTime, EndTime: r.EndTime, Repeat: '0' };
      });
      const resp = await axios.post('/SetAronCalenders', payload);
      const { ErrorCode, ErrorDescription } = resp.data || {};
      if (Number(ErrorCode) !== 0) {
        setSnackbar({ open: true, message: ErrorDescription || 'Takvim kaydedilemedi', severity: 'error' });
        return;
      }
      setSnackbar({ open: true, message: `${calendarSelectedTech.TechName} takvimi kaydedildi (${allChanges.length} değişiklik)`, severity: 'success' });
      setCalendarData(calendarEditRows.map(r => ({ ...r })));
    } catch (err) {
      console.error('SetAronCalenders error:', err);
      setSnackbar({ open: true, message: err?.response?.data?.ErrorDescription || 'Takvim kaydedilemedi', severity: 'error' });
    } finally {
      setCalendarSaving(false);
    }
  }, [calendarSelectedTech, calendarEditRows, calendarData]);

  // ── Toplu Ekle Dialog handlers ──
  const handleBulkAddOpen = useCallback(() => {
    setBulkScheduleType('DÜZENLİ (08-18)');
    setBulkDialogEndDate(dayjs().format('YYYY-MM-DD'));
    setBulkRepeat('Daily');
    setBulkEveryN(1);
    setBulkWeekDays([1, 2, 3, 4, 5, 6]);
    setBulkCustomStartHour('09');
    setBulkCustomStartMin('00');
    setBulkCustomEndHour('18');
    setBulkCustomEndMin('00');
    setBulkAddDialogOpen(true);
  }, []);

  // ── Tüm Teknisyenleri Kapat handler ──
  const handleCloseAllSubmit = useCallback(async () => {
    if (!closeAllDate) {
      setSnackbar({ open: true, message: 'Lütfen bir tarih seçin', severity: 'warning' });
      return;
    }
    setCloseAllSaving(true);
    try {
      const dateStr = dayjs(closeAllDate).format('DD.MM.YYYY');
      const closeResp = await axios.post('/SetAronCalenders', [{
        TechnicianId: 1000000,
        Date: dateStr,
        StartTime: '',
        EndTime: '',
        Repeat: '0',
      }]);
      const { ErrorCode: closeEC, ErrorDescription: closeED } = closeResp.data || {};
      if (Number(closeEC) !== 0) {
        setSnackbar({ open: true, message: closeED || 'Tüm teknisyenleri kapatma başarısız', severity: 'error' });
        return;
      }
      setSnackbar({ open: true, message: `${dayjs(closeAllDate).format('DD MMMM YYYY')} tarihinde tüm teknisyenler kapatıldı`, severity: 'success' });
      setCloseAllOpen(false);
      setCloseAllDate(null);
      // Seçili teknisyenin takvimini yenile
      if (calendarSelectedTech) {
        fetchCalendar(calendarSelectedTech.TechCode);
      }
    } catch (err) {
      console.error('CloseAll error:', err);
      setSnackbar({ open: true, message: err?.response?.data?.ErrorDescription || 'Tüm teknisyenleri kapatma başarısız', severity: 'error' });
    } finally {
      setCloseAllSaving(false);
    }
  }, [closeAllDate, calendarSelectedTech, fetchCalendar]);

  const handleBulkAddSubmit = useCallback(async () => {
    if (!calendarSelectedTech) return;
    const scheduleEntry = SCHEDULE_TYPES.find(s => s.label === bulkScheduleType);
    if (!scheduleEntry) return;

    let startTime, endTime;
    if (scheduleEntry.startTime === 'custom') {
      startTime = `${bulkCustomStartHour}:${bulkCustomStartMin}`;
      endTime = `${bulkCustomEndHour}:${bulkCustomEndMin}`;
    } else if (scheduleEntry.label === 'Çalışma Dışı Zaman') {
      startTime = '';
      endTime = '';
    } else {
      startTime = scheduleEntry.startTime;
      endTime = scheduleEntry.endTime;
    }

    const today = dayjs().format('YYYY-MM-DD');
    const endDate = bulkDialogEndDate || today;

    // Toplu eklenecek takvim kayıtlarını oluştur
    const bulkRows = [];
    let d = dayjs(today);
    const end = dayjs(endDate);
    if (bulkRepeat === 'Daily') {
      while (d.isBefore(end) || d.isSame(end, 'day')) {
        bulkRows.push({ Date: d.format('YYYY-MM-DD'), StartTime: startTime, EndTime: endTime });
        d = d.add(bulkEveryN, 'day');
      }
    } else if (bulkRepeat === 'Weekly') {
      while (d.isBefore(end) || d.isSame(end, 'day')) {
        if (bulkWeekDays.includes(d.day())) {
          bulkRows.push({ Date: d.format('YYYY-MM-DD'), StartTime: startTime, EndTime: endTime });
        }
        d = d.add(1, 'day');
      }
    }

    if (bulkRows.length === 0) {
      setSnackbar({ open: true, message: 'Eklenecek gün bulunamadı', severity: 'warning' });
      return;
    }

    setCalendarSaving(true);
    try {
      const calendars = bulkRows.map(r => {
        let dateStr = r.Date;
        if (dateStr && dateStr.includes('-')) {
          const parts = dateStr.split('-');
          if (parts.length === 3) dateStr = `${parts[2]}.${parts[1]}.${parts[0]}`;
        }
        return { TechnicianId: calendarSelectedTech.TechCode, Date: dateStr, StartTime: r.StartTime, EndTime: r.EndTime, Repeat: bulkRepeat };
      });
      const bulkResp = await axios.post('/SetAronCalenders', calendars);
      const { ErrorCode: bulkEC, ErrorDescription: bulkED } = bulkResp.data || {};
      if (Number(bulkEC) !== 0) {
        setSnackbar({ open: true, message: bulkED || 'Toplu ekleme kaydedilemedi', severity: 'error' });
        return;
      }

      // Takvim verilerini API'den tekrar çek
      await fetchCalendar(calendarSelectedTech.TechCode);

      setBulkAddDialogOpen(false);
      setSnackbar({ open: true, message: `Toplu ekleme kaydedildi: ${bulkScheduleType} (${bulkRows.length} gün)`, severity: 'success' });
    } catch (err) {
      console.error('SetAronCalenders bulk error:', err);
      setSnackbar({ open: true, message: err?.response?.data?.ErrorDescription || 'Toplu ekleme kaydedilemedi', severity: 'error' });
    } finally {
      setCalendarSaving(false);
    }
  }, [calendarSelectedTech, bulkScheduleType, bulkDialogEndDate, bulkRepeat, bulkEveryN, bulkWeekDays, bulkCustomStartHour, bulkCustomStartMin, bulkCustomEndHour, bulkCustomEndMin, fetchCalendar]);

  const calendarMonthDays = useMemo(() => {
    const start = dayjs(calendarMonth).startOf('month');
    const end = dayjs(calendarMonth).endOf('month');
    const days = [];
    let d = start;
    while (d.isBefore(end) || d.isSame(end, 'day')) {
      days.push(d.format('YYYY-MM-DD'));
      d = d.add(1, 'day');
    }
    return days;
  }, [calendarMonth]);

  const calendarDayMap = useMemo(() => {
    const map = {};
    calendarEditRows.forEach(r => { map[r.Date] = r; });
    return map;
  }, [calendarEditRows]);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
      switch (e.key) {
        case 'ArrowLeft': e.preventDefault(); goToPrevDay(); break;
        case 'ArrowRight': e.preventDefault(); goToNextDay(); break;
        case 't': case 'T': if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); goToToday(); } break;
        case '+': case '=': e.preventDefault(); setHourWidth((w) => Math.min(HOUR_WIDTH_MAX, w + ZOOM_STEP)); break;
        case '-': e.preventDefault(); setHourWidth((w) => Math.max(HOUR_WIDTH_MIN, w - ZOOM_STEP)); break;
        case '0': e.preventDefault(); setHourWidth(HOUR_WIDTH_DEFAULT); break;
        case 'r': case 'R': if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); fetchData(); } break;
        case 'f': case 'F': if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); toggleFullscreen(); } break;
        case 'Escape': setShortcutHelpOpen(false); break;
        case '?': e.preventDefault(); setShortcutHelpOpen(p => !p); break;
        case 'n': case 'N': {
          e.preventDefault();
          if (isSelectedToday && scrollRef.current) {
            const scrollLeft = ((nowMinutes - HOUR_START * 60) / (TOTAL_HOURS * 60)) * timelineWidth - scrollRef.current.clientWidth / 2;
            scrollRef.current.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
          }
          break;
        }
        default: break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fetchData, toggleFullscreen, isSelectedToday, nowMinutes, timelineWidth]);

  // ── Scroll to now ──
  const scrollToNow = useCallback(() => {
    if (!isSelectedToday || !scrollRef.current) return;
    const scrollLeft = ((nowMinutes - HOUR_START * 60) / (TOTAL_HOURS * 60)) * timelineWidth - scrollRef.current.clientWidth / 2;
    scrollRef.current.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  }, [isSelectedToday, nowMinutes, timelineWidth]);

  const goToPrevDay = () => {
    setSelectedDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 1);
      return d;
    });
  };

  const goToNextDay = () => {
    setSelectedDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 1);
      return d;
    });
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const validateTechnicianMoveCapacity = useCallback((
    targetTechCode,
    movedJobs,
    targetTechName = '',
  ) => {
    const result = evaluateTechnicianMoveCapacity({
      rows: data,
      movedJobs,
      targetTechCode,
      targetTechName,
      selectedDate,
      now: new Date(currentMinuteTimestamp),
      topServiceShopName: serviceShop.topServiceShopName,
    });
    if (result.allowed) return true;

    setSnackbar({
      open: true,
      message: getMoveCapacityBlockedMessage(result),
      severity: 'error',
      duration: 7000,
    });
    return false;
  }, [currentMinuteTimestamp, data, selectedDate, serviceShop.topServiceShopName]);

  const showTechnicianToBucketMoveBlocked = useCallback(() => {
    setSnackbar({
      open: true,
      message: 'Teknisyenden servis seviyesine iş taşınamaz.',
      severity: 'error',
      duration: 5000,
    });
  }, []);

  // ── Drag & Drop handler ──
  const handleDrop = useCallback((e, targetTechCode, targetUserTechCode, targetTechName, isBucketTarget = false) => {
    e.preventDefault();
    setDragOverTech(null);
    try {
      const payload = JSON.parse(e.dataTransfer.getData('application/json'));
      if (!payload.ofscNr) return;

      const movedJob = data.find(
        item => String(item?.OfscNr ?? item?.OFSC_NR) === String(payload.ofscNr),
      ) || {
        OfscNr: payload.ofscNr,
        TechCode: payload.sourceTechCode,
        TravelDuration: payload.travelDuration,
        WorkDuration: payload.workDuration,
      };
      const sourceIsBucket = payload.sourceIsBucket === true
        || normalizeStatusKey(movedJob?.StatusDescription) === 'bucket';
      if (
        isBucketTarget
        && !sourceIsBucket
        && !getTechnicianToBucketMoveEnabled()
      ) {
        showTechnicianToBucketMoveBlocked();
        return;
      }
      if (
        !isBucketTarget
        && !validateTechnicianMoveCapacity(targetTechCode, [movedJob], targetTechName)
      ) return;

      // Fare pozisyonundan yeni başlangıç saatini hesapla
      const container = timelineContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const scrollLeft = container.closest('[style*="overflow"]')?.scrollLeft || scrollRef.current?.scrollLeft || 0;
      const x = e.clientX - rect.left + scrollLeft;
      const totalPx = timelineWidth;
      const ratio = x / totalPx;
      const newStartMin = Math.round(HOUR_START * 60 + ratio * TOTAL_HOURS * 60);
      // 15 dk'lık dilime yuvarla
      const snappedStart = Math.round(newStartMin / 15) * 15;
      const delta = snappedStart - payload.startMin;

      // Yeni zaman dilimini hesapla
      const isNextDaySlot = snappedStart >= 24 * 60;
      const displayMin = isNextDaySlot ? snappedStart - 24 * 60 : snappedStart;
      const timeSlot = getTimeSlot(displayMin);
      // Randevu tarihini oluştur (selectedDate + timeSlot, ertesi güne taşıyorsa +1 gün)
      const apptDate = dayjs(selectedDate)
        .add(isNextDaySlot ? 1 : 0, 'day')
        .hour(Math.floor(displayMin / 60))
        .minute(displayMin % 60)
        .second(0)
        .format('YYYY-MM-DDTHH:mm:ss');

      let moveImpact = null;
      if (!isBucketTarget && !sourceIsBucket && String(payload.sourceTechCode) !== String(targetTechCode)) {
        try {
          moveImpact = getMoveImpact(
            payload.ofscNr,
            targetTechCode,
            rawSchedulingDataRef.current,
            data,
            schedulingCalculationOptionsRef.current,
          );
        } catch (simulationError) {
          console.warn('Taşıma etkisi hesaplanamadı:', simulationError);
        }
      }

      // Onay dialog'unu aç
      setDropConfirm({
        ofscNr: payload.ofscNr,
        sourceTechCode: payload.sourceTechCode,
        sourceUserTechCode: payload.sourceUserTechCode || payload.sourceTechCode || '',
        sourceTechName: payload.sourceTechName || '',
        targetTechCode,
        targetUserTechCode: targetUserTechCode || targetTechCode || '',
        targetTechName: targetTechName || '',
        timeSlot,
        appointmentDate: apptDate,
        delta,
        payload,
        isBucket: isBucketTarget,
        sourceIsBucket,
        moveImpact,
      });
    } catch (err) {
      // geçersiz drop, yok say
    }
  }, [
    data,
    selectedDate,
    showTechnicianToBucketMoveBlocked,
    timelineWidth,
    validateTechnicianMoveCapacity,
  ]);

  // Onaylanan drop işlemini gerçekleştir
  const handleDropConfirm = useCallback(async () => {
    if (!dropConfirm) return;
    const {
      ofscNr,
      targetTechCode,
      targetTechName,
      timeSlot,
      appointmentDate,
      delta,
      isBucket,
      payload,
      sourceIsBucket,
    } = dropConfirm;
    const movedJob = data.find(
      item => String(item?.OfscNr ?? item?.OFSC_NR) === String(ofscNr),
    ) || {
      OfscNr: ofscNr,
      TechCode: payload?.sourceTechCode,
      TravelDuration: payload?.travelDuration,
      WorkDuration: payload?.workDuration,
    };
    const confirmedSourceIsBucket = sourceIsBucket === true
      || payload?.sourceIsBucket === true
      || normalizeStatusKey(movedJob?.StatusDescription) === 'bucket';
    if (
      isBucket
      && !confirmedSourceIsBucket
      && !getTechnicianToBucketMoveEnabled()
    ) {
      setDropConfirm(null);
      showTechnicianToBucketMoveBlocked();
      return;
    }
    if (
      !isBucket
      && !validateTechnicianMoveCapacity(targetTechCode, [movedJob], targetTechName)
    ) {
      setDropConfirm(null);
      return;
    }

    setDropLoading(true);
    try {
      const resp = await axios.post('/SetAronUpdateOfscTechnician', {
        OfscNr: ofscNr,
        TechnicianCode: targetTechCode,
        TimeSlot: timeSlot,
        AppointmentDate: appointmentDate,
        Type: isBucket ? 1 : 0,
      });

      const result = resp?.data;
      if (result?.ErrorCode && result.ErrorCode !== 0) {
        setSnackbar({ open: true, message: result.Description || 'İşlem sırasında hata oluştu', severity: 'error' });
        setDropLoading(false);
        return;
      }

      // Başarılıysa UI'ı güncelle
      setData((prev) => {
        const updated = prev.map((item) => {
          if (String(item.OfscNr) !== String(ofscNr)) return item;
          const shiftTime = (timeStr) => {
            const m = parseTime(timeStr);
            if (m == null) return timeStr;
            return formatMinutes(m + delta);
          };
          return {
            ...item,
            TechCode: targetTechCode,
            DepartureTime: shiftTime(item.DepartureTime),
            ArrivalatCustomer: shiftTime(item.ArrivalatCustomer),
            TaskCompletionTime: shiftTime(item.TaskCompletionTime),
            AppointmentTime: shiftTime(item.AppointmentTime),
            IdleTimeStart: shiftTime(item.IdleTimeStart),
            IdleTimeEnd: shiftTime(item.IdleTimeEnd),
          };
        });
        return updated;
      });
      setDropConfirm(null);
      setSnackbar({ open: true, message: 'Atama başarıyla güncellendi', severity: 'success' });
      // Veriyi yenile
      await fetchData(false, true);
    } catch (err) {
      console.error('SetAronUpdateOfscTechnician error:', err);
      setSnackbar({ open: true, message: 'Atama güncellenirken hata oluştu: ' + (err?.response?.data?.Message || err.message || 'Bilinmeyen hata'), severity: 'error' });
    } finally {
      setDropLoading(false);
    }
  }, [
    data,
    dropConfirm,
    fetchData,
    showTechnicianToBucketMoveBlocked,
    validateTechnicianMoveCapacity,
  ]);

  // Arama & durum filtresi
  const filtered = useMemo(() => {
    let items = data;
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (d) =>
          String(d.TechCode || '').toLowerCase().includes(q) ||
          (d.TechName || '').toLowerCase().includes(q) ||
          String(d.OfscNr || '').toLowerCase().includes(q) ||
          (d.Neighbourhood || '').toLowerCase().includes(q) ||
          (d.StatusDescription || '').toLowerCase().includes(q) ||
          (d.Products || '').toLowerCase().includes(q) ||
          (d.ServicePapers || '').toLowerCase().includes(q) ||
          (d.DepartureTime || '').toLowerCase().includes(q) ||
          (d.ArrivalatCustomer || '').toLowerCase().includes(q) ||
          (d.TaskCompletionTime || '').toLowerCase().includes(q) ||
          (d.AppointmentTime || '').toLowerCase().includes(q) ||
          (d.CustomerName || d.customerName || '').toLowerCase().includes(q) ||
          (d.Address || d.address || '').toLowerCase().includes(q) ||
          (d.ActivityType || '').toLowerCase().includes(q) ||
          (d.WorkType || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter.length > 0) {
      const statusKeys = statusFilter.filter(key => key !== 'delayed');
      const delayedFilterActive = statusFilter.includes('delayed');
      items = items.filter((job) => (
        statusKeys.includes(normalizeStatusKey(job.StatusDescription))
        || (delayedFilterActive && hasAppointmentWarning(job, nowMinutes, isSelectedToday))
      ));
    }
    return items;
  }, [data, isSelectedToday, nowMinutes, search, statusFilter]);

  const searchServicePaperNo = useMemo(() => {
    const value = search.trim();
    if (!value) return '';
    const normalized = value.replace(/^#/, '').trim();
    return /^\d{3,}$/.test(normalized) ? normalized : '';
  }, [search]);

  const showServicePaperLookup = Boolean(searchServicePaperNo && search.trim() && filtered.length === 0 && !loading);

  const handleServicePaperLookup = useCallback(async () => {
    if (!searchServicePaperNo) return;
    setServicePaperLookupLoading(true);
    try {
      const resp = await axios.get(`/GetServicePaperDetail/${encodeURIComponent(searchServicePaperNo)}/1/0`);
      const detail = unwrapServicePaperDetailResponse(resp?.data);
      if (!detail) {
        setSnackbar({ open: true, message: 'Fiş detayı bulunamadı', severity: 'warning' });
      } else {
        const appointmentDate = findDetailAppointmentDate(detail);
        if (!appointmentDate) {
          setSnackbar({ open: true, message: 'Fiş detayında randevu tarihi bulunamadı', severity: 'warning' });
          return;
        }
        const isSameSelectedDate = dayjs(appointmentDate).isSame(dayjs(selectedDate), 'day');
        setSelectedDate(appointmentDate);
        setSnackbar({
          open: true,
          message: `Fiş ${searchServicePaperNo} için ${dayjs(appointmentDate).format('DD.MM.YYYY')} tarihine gidiliyor`,
          severity: 'info',
        });
        if (isSameSelectedDate) fetchData(false, true);
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Fiş detayı alınamadı', severity: 'error' });
    } finally {
      setServicePaperLookupLoading(false);
    }
  }, [fetchData, searchServicePaperNo, selectedDate]);

  // Teknisyenleri TechCode'a göre grupla
  const groupedTechs = useMemo(() => {
    const map = new Map();
    filtered.forEach((item) => {
      const key = item.TechCode || item.TechName;
      if (!map.has(key)) {
        map.set(key, {
          TechCode: item.TechCode,
          TechName: item.TechName,
          UserTechCode: item.UserTechCode,
          StatusDescription: item.StatusDescription,
          TechWorkStart: item.TechWorkStart || WORK_START,
          TechWorkEnd: item.TechWorkEnd || WORK_END,
          jobs: [],
          totalIdle: 0,
          totalWork: 0,
          totalTravel: 0,
        });
      }
      const group = map.get(key);
      const displayDurations = getJobDisplayDurations(item);
      group.jobs.push(item);
      group.totalIdle += item.IdleDuration || 0;
      group.totalWork += displayDurations.work;
      group.totalTravel += displayDurations.travel;
      if (item.StatusDescription) {
        group.StatusDescription = item.StatusDescription;
      }
    });
    return Array.from(map.values());
  }, [filtered]);

  // İşleri sırala: mesai aşımı olanlar sona
  const sortedGrouped = useMemo(() => {
    return groupedTechs.map((tech) => {
      const sorted = [...tech.jobs].sort((a, b) => {
        const aT = getTimelineReferenceTimes(a);
        const bT = getTimelineReferenceTimes(b);
        const aStart = aT.length ? Math.min(...aT) : 9999;
        const bStart = bT.length ? Math.min(...bT) : 9999;
        return aStart - bStart;
      });
      const techWorkEndMin = parseTime(tech.TechWorkEnd || WORK_END) || WORK_END_MIN;
      const hasOvertime = sorted.some((j) => {
        const times = getTimelineReferenceTimes(j);
        return times.length > 0 && Math.max(...times) > techWorkEndMin;
      });
      const hasDelayRisk = sorted.some(j => hasAppointmentWarning(j, nowMinutes, isSelectedToday));
      const techWsMin = parseTime(tech.TechWorkStart || WORK_START) || WORK_START_MIN;
      const techWeMin = parseTime(tech.TechWorkEnd || WORK_END) || WORK_END_MIN;
      const totalMin = TOTAL_HOURS * 60;
      return { ...tech, jobs: sorted, hasOvertime, hasDelayRisk, techWorkStartMin: techWsMin, techWorkEndMin: techWeMin, techWorkStartPct: ((techWsMin - HOUR_START * 60) / totalMin) * 100, techWorkEndPct: ((techWeMin - HOUR_START * 60) / totalMin) * 100 };
    });
  }, [groupedTechs, isSelectedToday, nowMinutes]);

  // Bucket teknisyenleri en üste, diğerleri alta
  const bucketTechs = useMemo(() => sortedGrouped.filter(t => normalizeStatusKey(t.StatusDescription) === 'bucket'), [sortedGrouped]);
  const nonBucketTechs = useMemo(() => sortedGrouped.filter(t => normalizeStatusKey(t.StatusDescription) !== 'bucket').sort((a, b) => (a.TechName || '').localeCompare(b.TechName || '', 'tr')), [sortedGrouped]);
  const finalSorted = useMemo(() => [...bucketTechs, ...(bucketExpanded ? nonBucketTechs : [])], [bucketTechs, nonBucketTechs, bucketExpanded]);

  // ── Gruplanmış teknisyen sıralaması (ağaç yapısı) ──
  const groupedDisplayRows = useMemo(() => {
    if (!techGroupsEnabled || !bucketExpanded) return null;
    const rows = []; // [{type: 'group-header', group, techCount, jobCount} | {type: 'tech', tech, idx}]
    const assignedCodes = new Set();
    techGroups.forEach(g => {
      const groupTechs = nonBucketTechs.filter(t => g.TechCodes.includes(String(t.TechCode)));
      if (groupTechs.length === 0) return;
      const isExpanded = techGroupsExpanded[g.Id] !== false;
      const jobCount = groupTechs.reduce((s, t) => s + t.jobs.filter(j => j.OfscNr).length, 0);
      rows.push({ type: 'group-header', group: g, techCount: groupTechs.length, jobCount, isExpanded });
      if (isExpanded) {
        groupTechs.forEach(t => {
          if (!assignedCodes.has(String(t.TechCode))) {
            rows.push({ type: 'tech', tech: t });
          }
          assignedCodes.add(String(t.TechCode));
        });
      } else {
        groupTechs.forEach(t => assignedCodes.add(String(t.TechCode)));
      }
    });
    const ungrouped = nonBucketTechs.filter(t => !assignedCodes.has(String(t.TechCode)));
    if (ungrouped.length > 0) {
      const jobCount = ungrouped.reduce((s, t) => s + t.jobs.filter(j => j.OfscNr).length, 0);
      const isExpanded = techGroupsExpanded['ungrouped'] !== false;
      rows.push({ type: 'group-header', group: { Id: 'ungrouped', GroupName: 'Grupsuz' }, techCount: ungrouped.length, jobCount, isExpanded });
      if (isExpanded) {
        ungrouped.forEach(t => { rows.push({ type: 'tech', tech: t }); });
      }
    }
    return rows;
  }, [techGroupsEnabled, techGroups, nonBucketTechs, techGroupsExpanded, bucketExpanded]);

  // Teknisyen isimlerine göre dinamik sütun genişliği hesapla
  const dynamicResColWidth = useMemo(() => {
    if (!groupedTechs.length) return RESOURCE_COL_WIDTH;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = '600 11.5px Arcelik';
    let maxNameWidth = 0;
    groupedTechs.forEach((tech) => {
      const name = toTitleCase(tech.TechName || '');
      const jobsText = ` · ${getTechWorkSummaryText(tech.jobs)}`;
      const w = ctx.measureText(name + jobsText).width;
      if (w > maxNameWidth) maxNameWidth = w;
    });
    // padding (pl+pr ~28px) + map icon (26px) + gaps (12px) + risk icon (22px) + utilization badge (32px) + extra safety (20px)
    const totalExtra = 140;
    return Math.max(RESOURCE_COL_WIDTH_MOBILE, Math.ceil(maxNameWidth + totalExtra));
  }, [groupedTechs]);

  const resColWidth = resourcePanelCollapsed ? 40 : (isMobile ? RESOURCE_COL_WIDTH_MOBILE : dynamicResColWidth);

  // Özet istatistikler
  const summary = useMemo(() => {
    const total = groupedTechs.length;
    const totalJobs = groupedTechs.reduce((s, d) => s + d.jobs.filter(j => j.OfscNr).length, 0);
    const totalIdle = groupedTechs.reduce((s, d) => s + d.totalIdle, 0);
    const totalWork = groupedTechs.reduce((s, d) => s + d.totalWork, 0);
    const totalTravel = groupedTechs.reduce((s, d) => s + d.totalTravel, 0);
    return { total, totalJobs, totalIdle, totalWork, totalTravel };
  }, [groupedTechs]);

  // Scroll sync
  const handleBodyScroll = useCallback((e) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.style.transform = `translateX(-${e.target.scrollLeft}px)`;
    }
    if (resourceScrollRef.current) {
      resourceScrollRef.current.scrollTop = e.target.scrollTop;
    }
  }, []);

  // ── Status badge ──
  const getStatusColor = (status) => {
    const s = normalizeStatusKey(status);
    if (s === 'started') return '#4caf50';
    if (s === 'completed') return '#1976d2';
    if (s === 'pending') return '#9e9e9e';
    if (s === 'cancelled' || s === 'notdone') return '#f44336';
    if (s === 'road') return '#ff9800';
    if (s === 'suspended') return '#9c27b0';
    if (s === 'launch') return '#00897b';
    return '#9e9e9e';
  };

  // ── Teknisyen Detay helpers ──
  const maskName = (name) => {
    if (!name || name === '-') return name;
    return name.split(' ').map(w => w.length <= 2 ? w : w.slice(0, 2) + '*'.repeat(w.length - 2)).join(' ');
  };

  const sortedTechWorks = useMemo(() => {
    if (!techSortField) return techWorks;
    return [...techWorks].sort((a, b) => {
      let aVal, bVal;
      if (techSortField === 'spType') {
        aVal = getWorkApplicationReason(a).toLocaleLowerCase('tr');
        bVal = getWorkApplicationReason(b).toLocaleLowerCase('tr');
      } else if (techSortField === 'CustomerName') {
        aVal = getWorkCustomerName(a).toLocaleLowerCase('tr');
        bVal = getWorkCustomerName(b).toLocaleLowerCase('tr');
      } else if (techSortField === 'BayiName') {
        aVal = getWorkDealerName(a).toLocaleLowerCase('tr');
        bVal = getWorkDealerName(b).toLocaleLowerCase('tr');
      } else {
        aVal = a[techSortField] ?? '';
        bVal = b[techSortField] ?? '';
        if (techSortField === 'AppointmentTime' || techSortField === 'DepartureTime' || techSortField === 'ArrivalatCustomer' || techSortField === 'TaskCompletionTime') {
          aVal = parseTime(String(aVal)) ?? 9999;
          bVal = parseTime(String(bVal)) ?? 9999;
        } else if (techSortField === 'WorkDuration' || techSortField === 'TravelDuration') {
          const aDurations = getJobDisplayDurations(a);
          const bDurations = getJobDisplayDurations(b);
          aVal = techSortField === 'WorkDuration' ? aDurations.work : aDurations.travel;
          bVal = techSortField === 'WorkDuration' ? bDurations.work : bDurations.travel;
        } else if (techSortField === 'IdleDuration') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        } else {
          aVal = String(aVal).toLocaleLowerCase('tr');
          bVal = String(bVal).toLocaleLowerCase('tr');
        }
      }
      if (aVal < bVal) return techSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return techSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [techWorks, techSortField, techSortDir]);

  const filteredTechWorks = useMemo(() => {
    // Bucket ve Mesai Sonu Boşluk kayıtlarını gizle (servis popup'ında bucket işlerin kendisi listelendiği için bu filtre atlanır)
    let list = sortedTechWorks.filter(w => {
      const status = normalizeStatusKey(w.StatusDescription);
      const neighbourhood = String(w.Neighbourhood || w.Neighborhood || '').toLocaleLowerCase('tr').trim();
      if (!techDetailIsBucket) {
        if (status === 'bucket') return false;
        if (neighbourhood === 'servis') return false;
      } else if (!w.OfscNr || w.OfscNr === 0 || w.OfscNr === '0') {
        // Servis popup'ında gerçek bir fişi olmayan yer tutucu (anchor) satırı gizle
        return false;
      }
      // OfscNr 0 ve sadece idle süresi olan kayıtlar (Mesai Sonu Boşluk)
      if ((!w.OfscNr || w.OfscNr === 0) && w.IdleDuration > 0 && !w.WorkDuration && !w.TravelDuration) return false;
      return true;
    });
    if (!techWorkSearch.trim()) return list;
    const q = normalizeWorkSearchText(techWorkSearch);
    return list.filter(work => getWorkSearchText(work).includes(q));
  }, [sortedTechWorks, techWorkSearch, techDetailIsBucket]);

  const visibleTechWorkServicePaperCount = useMemo(
    () => countServicePapers(filteredTechWorks.filter(w => w.OfscNr)),
    [filteredTechWorks]
  );

  const selectedMoveJobs = useMemo(() => techWorkChecked.map((ofscNr) => (
    data.find(item => String(item?.OfscNr ?? item?.OFSC_NR) === String(ofscNr))
    || filteredTechWorks.find(item => String(item?.OfscNr ?? item?.OFSC_NR) === String(ofscNr))
    || {
      OfscNr: ofscNr,
      TechCode: techInfo?.code,
      TravelDuration: 0,
      WorkDuration: 0,
    }
  )), [data, filteredTechWorks, techInfo?.code, techWorkChecked]);

  const selectedMoveSource = selectedMoveJobs[0] || {};
  const selectedMoveSourceCode = selectedMoveSource.TechCode
    ?? selectedMoveSource.TEKNISYEN_KODU
    ?? techInfo?.code
    ?? '';
  const selectedMoveSourceName = selectedMoveSource.TechName
    ?? selectedMoveSource.TEKNISYEN_ADI
    ?? [techInfo?.name, techInfo?.surname].filter(Boolean).join(' ');

  const handleTechMoveTargetChange = useCallback((targetTechCode) => {
    if (!targetTechCode) {
      setTechMoveTarget('');
      return;
    }

    const targetRow = data.find(
      item => String(item?.TechCode ?? item?.TEKNISYEN_KODU) === String(targetTechCode),
    );
    if (!validateTechnicianMoveCapacity(
      targetTechCode,
      selectedMoveJobs,
      targetRow?.TechName || targetRow?.TEKNISYEN_ADI || '',
    )) {
      setTechMoveTarget('');
      return;
    }

    setTechMoveTarget(targetTechCode);
  }, [data, selectedMoveJobs, validateTechnicianMoveCapacity]);

  const fetchTechDetail = useCallback(async (techCode) => {
    if (!techCode) return;
    const tc = String(techCode).trim();
    if (!tc) return;
    setTechDetailLoading(true);
    setTechDetailError('');
    setTechPhoto(null);
    setTechWorks([]);
    setTechInfo({ code: tc });
    setTechWorkSearch('');

    try {
      const imgResp = await axios.get(`/GetTechnicianImage/${encodeURIComponent(tc)}`);
      const d = imgResp.data;
      if (d) {
        setTechInfo(prev => ({
          ...(prev || {}),
          code: tc,
          userCode: prev?.userCode || String(d.TechnicianCode || tc).trim(),
          name: d.TechnicianName || '',
          surname: d.TechnicianSurname || '',
          expert: d.TechnicianExpert || '',
        }));
        if (d.TechnicianImage) setTechPhoto(`data:image/jpeg;base64,${d.TechnicianImage}`);
      }
    } catch { /* bilgi yoksa varsayılan kalır */ }

    try {
      const dateStr = formatDateForApi(selectedDate);
      const requestStartedAt = new Date();
      const resp = await axios.post('/GetAronToaSchedulingAvailable', {
        ServiceShopId: Number(serviceShopId),
        TechnicianId: tc,
        Date: dateStr,
        StartTime: WORK_START,
        EndTime: WORK_END,
      });
      const list = calculateToaScheduling(
        getSchedulingRows(resp?.data),
        getSchedulingCalculationOptions(
          resp?.data,
          selectedDate,
          requestStartedAt,
          serviceShop,
        ),
      );
      if (list.length > 0) {
        const filtered = list.filter(d => String(d.TechCode) === tc);
        setTechWorks(filtered.length > 0 ? filtered : list);
      } else {
        // API tek teknisyen için boş dönerse ana veriden fallback
        const fallback = dataRef.current.filter(d => String(d.TechCode) === tc);
        if (fallback.length > 0) setTechWorks(fallback);
      }
    } catch { /* hata */ }

    setTechDetailLoading(false);
  }, [selectedDate, serviceShop]);

  // ── Toplu İş Taşıma ──
  const handleBulkMove = useCallback(async () => {
    if (!techMoveTarget || techWorkChecked.length === 0) return;

    const targetRow = data.find(
      item => String(item?.TechCode ?? item?.TEKNISYEN_KODU) === String(techMoveTarget),
    );
    if (!validateTechnicianMoveCapacity(
      techMoveTarget,
      selectedMoveJobs,
      targetRow?.TechName || targetRow?.TEKNISYEN_ADI || '',
    )) return;

    setTechMoveLoading(true);
    try {
      const errors = [];
      let successCount = 0;
      for (const ofscNr of techWorkChecked) {
        // İşin mevcut randevu saatini bul
        const work = selectedMoveJobs.find(
          item => String(item?.OfscNr ?? item?.OFSC_NR) === String(ofscNr),
        );
        const apptTime = work?.AppointmentTime || '09:00';
        const [hh, mm] = apptTime.split(':');
        const apptDate = dayjs(selectedDate)
          .hour(parseInt(hh) || 9)
          .minute(parseInt(mm) || 0)
          .second(0)
          .format('YYYY-MM-DDTHH:mm:ss');
        const resp = await axios.post('/SetAronUpdateOfscTechnician', {
          OfscNr: ofscNr,
          TechnicianCode: techMoveTarget,
          TimeSlot: apptTime,
          AppointmentDate: apptDate,
          Type: 0,
        });
        const result = resp?.data;
        if (result?.ErrorCode && result.ErrorCode !== 0) {
          errors.push(`#${ofscNr}: ${result.Description || 'Hata oluştu'}`);
        } else {
          successCount++;
        }
      }
      if (errors.length > 0) {
        setSnackbar({ open: true, message: errors.join(' | '), severity: 'error' });
      }
      if (successCount > 0) {
        setMoveSimulationCacheVersion(current => current + 1);
        setSnackbar({ open: true, message: `${successCount} iş başarıyla taşındı${errors.length > 0 ? `, ${errors.length} hata` : ''}`, severity: errors.length > 0 ? 'warning' : 'success' });
      }
      setTechWorkChecked([]);
      setTechMoveOpen(false);
      setTechMoveTarget('');
      await fetchData(false, true);
    } catch (err) {
      setSnackbar({ open: true, message: 'Taşıma hatası: ' + (err?.response?.data?.Message || err.message || 'Bilinmeyen hata'), severity: 'error' });
    } finally {
      setTechMoveLoading(false);
    }
  }, [
    data,
    selectedMoveJobs,
    techMoveTarget,
    techWorkChecked,
    fetchData,
    selectedDate,
    validateTechnicianMoveCapacity,
  ]);

  const handleOpenTimelineJobPopup = useCallback((ofscNr) => {
    if (!ofscNr) return;
    const jobBar = Array.from(document.querySelectorAll('[data-toa-job-ofsc]'))
      .find(el => el.dataset.toaJobOfsc === String(ofscNr));
    if (jobBar) {
      jobBar.dispatchEvent(new CustomEvent('toa-open-job-popup'));
    } else {
      setSnackbar({ open: true, message: 'Randevu detayı ana ekranda bulunamadı', severity: 'warning' });
    }
  }, []);

  const handleTechRowClick = useCallback((techCode) => {
    if (!techCode) return;
    setTechDetailOpen(true);
    setTechDetailError('');
    setTechPhoto(null);
    setTechWorkSearch('');

    // Mevcut veriden işleri al (servis satırında servisin tüm işleri gelir)
    const { jobs: techJobs, isBucket } = getTechDetailJobs(data, techCode);
    setTechWorks(techJobs);

    // Teknisyen bilgisini mevcut veriden çek
    const first = data.find(d => String(d.TechCode) === String(techCode));
    const tc = String(techCode || '').trim();
    if (first) {
      setTechInfo({ code: tc, userCode: first.UserTechCode || tc, name: first.TechName || '', surname: '', expert: '' });
    } else {
      setTechInfo({ code: tc, userCode: tc });
    }

    // Servis (bucket) satırının fotoğrafı/kişi bilgisi olmadığından bu satırlar için isteği atla (404 önlenir)
    setTechDetailIsBucket(isBucket);
    if (isBucket) return;

    // Fotoğrafı arka planda yükle
    axios.get(`/GetTechnicianImage/${encodeURIComponent(tc)}`).then(imgResp => {
      const d = imgResp.data;
      if (d) {
        setTechInfo(prev => ({ ...prev, name: d.TechnicianName || prev?.name || '', surname: d.TechnicianSurname || '', expert: d.TechnicianExpert || '' }));
        if (d.TechnicianImage) setTechPhoto(`data:image/jpeg;base64,${d.TechnicianImage}`);
      }
    }).catch(() => { });
  }, [data]);

  const handleSimulationMoveClick = useCallback((ofscNr, targetTechCode) => {
    if (!ofscNr || !targetTechCode) return;
    setTechWorkChecked([ofscNr]);
    setTechMoveTarget(targetTechCode);
    setTechMoveOpen(true);
  }, []);

  useEffect(() => {
    if (!techDetailOpen || !techInfo?.code) return;
    // handleTechRowClick ile aynı kural: servis satırında servisin tüm işleri listelenir.
    const { jobs: techJobs, isBucket } = getTechDetailJobs(data, techInfo.code);
    setTechWorks(techJobs);
    setTechWorkChecked([]);
    setTechDetailIsBucket(isBucket);
    const own = data.find(d => String(d.TechCode) === String(techInfo.code));
    if (own) {
      setTechInfo(prev => ({
        ...prev,
        code: prev?.code || String(own.TechCode || '').trim(),
        userCode: prev?.userCode || own.UserTechCode || String(own.TechCode || '').trim(),
        name: prev?.name || own.TechName || '',
      }));
    }
  }, [data, techDetailOpen, techInfo?.code]);

  const buildRouteMapJobs = useCallback((jobs = []) => {
    const readCoordinate = (item, keys) => {
      for (const key of keys) {
        const value = item?.[key];
        if (value === undefined || value === null || value === '') continue;
        const parsed = parseCoordinateValue(value);
        if (parsed != null && parsed !== 0) return parsed;
      }
      return null;
    };
    let visibleOrder = 0;
    return jobs
      .filter((j) => {
        const statusKey = normalizeStatusKey(j.StatusDescription);
        const isServiceLocation = statusKey === 'bucket' || isBucketNeighbourhood(j);
        return (isServiceLocation || hasValidAppointmentNo(j.OfscNr)) && (j.Neighbourhood || '').toLowerCase() !== 'yemek';
      })
      .map((j) => {
        const statusKey = normalizeStatusKey(j.StatusDescription);
        const isServiceLocation = statusKey === 'bucket' || isBucketNeighbourhood(j);
        const rawLat = readCoordinate(j, ['Latitude', 'LATITUDE', 'latitude', 'Lat', 'lat', 'ServiceLatitude', 'serviceLatitude', 'ServiceLat', 'ShopLatitude', 'DealerLatitude', 'TechnicianLatitude', 'TechLatitude']);
        const rawLng = readCoordinate(j, ['Longitude', 'LONGITUDE', 'longitude', 'Lng', 'lng', 'Lon', 'lon', 'Long', 'long', 'ServiceLongitude', 'serviceLongitude', 'ServiceLng', 'ShopLongitude', 'DealerLongitude', 'TechnicianLongitude', 'TechLongitude']);
        const { lat, lng } = normalizeCoordinatePair(rawLat, rawLng);
        if (lat == null || lng == null) return null;
        const sortTime = isServiceLocation ? -1 : (parseTime(j.DepartureTime) ?? 9999);
        const servicePapers = j.ServicePapers || '';
        const rawCustomers = servicePapers.split(',')
          .filter(s => s.trim())
          .map(sp => {
            const [mainPart] = sp.trim().split('!');
            const parts = mainPart.split('|');
            return parts[1]?.trim() || '';
          })
          .filter(Boolean);
        const customerNames = [...new Set(rawCustomers.map(raw => raw
          .replace(/Bayi:.*$/i, '')
          .replace(/\d{7,}/g, '')
          .replace(/[-|]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()).filter(Boolean))];
        const dealerNames = [...new Set(rawCustomers.map(raw => {
          const match = raw.match(/Bayi:\s*([^!,]+)/i);
          return match ? match[1]
            .replace(/\d{7,}/g, '')
            .replace(/[-|]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim() : '';
        }).filter(Boolean))];
        const displayDurations = getJobDisplayDurations(j);
        return {
          lat, lng,
          ofsc: j.OfscNr || '',
          neighbourhood: isServiceLocation ? (j.Neighbourhood || 'Servis Lokasyonu') : (j.Neighbourhood || ''),
          status: isServiceLocation ? 'bucket' : (j.StatusDescription || ''),
          appt: j.AppointmentTime || '',
          apptEnd: j.AppointmentTimeEnd || '',
          departureTime: j.DepartureTime || '',
          arrivalTime: j.ArrivalatCustomer || '',
          completionTime: j.TaskCompletionTime || '',
          workDuration: displayDurations.work,
          travelDuration: displayDurations.travel,
          products: j.Products || '',
          servicePapers,
          customerName: customerNames.join(', '),
          dealerName: dealerNames.join(', '),
          serviceLocation: isServiceLocation,
          simulationMoved: j.simulationMoved === true,
          sortTime,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.sortTime - b.sortTime)
      .map((j) => {
        if (!j.serviceLocation) visibleOrder += 1;
        return { ...j, order: j.serviceLocation ? 0 : visibleOrder };
      });
  }, []);

  // Teknisyen harita popupı
  const sendTechMapCommand = useCallback((message) => {
    const target = techMapIframeRef.current?.contentWindow;
    if (target) target.postMessage(message, '*');
  }, []);

  const handleTechLiveLocation = useCallback((location, { focus = false } = {}) => {
    sendTechMapCommand({
      type: 'updateTechnicianLocation',
      location,
      focus,
    });
  }, [sendTechMapCommand]);

  const clearTechLiveLocation = useCallback(() => {
    sendTechMapCommand({ type: 'clearTechnicianLocation' });
  }, [sendTechMapCommand]);

  const techLiveTrackingTarget = useMemo(() => ({
    techCode: techMapData?.techCode || '',
    userTechCode: techMapData?.userTechCode || '',
    trackingKey: techMapData?.trackingKey || techMapData?.techCode || techMapData?.userTechCode || '',
  }), [techMapData?.techCode, techMapData?.trackingKey, techMapData?.userTechCode]);

  const {
    tracking: techLiveTracking,
    status: techLiveStatus,
    lastLocation: techLiveLocation,
    error: techLiveError,
    start: startTechLiveTracking,
    stop: stopTechLiveTracking,
    getCurrentLocation: getCurrentTechLiveLocation,
  } = useTechnicianLiveLocation({
    enabled: techMapOpen && !techMapData?.comparisonRoutes,
    technician: techLiveTrackingTarget,
    onLocation: handleTechLiveLocation,
    onClear: clearTechLiveLocation,
  });

  const techLiveTooltip = useMemo(() => {
    if (!techMapData?.trackingKey) return 'Bu teknisyen için takip kimliği bulunamadı.';
    if (techLiveError) return techLiveError;
    if (techLiveStatus === TECH_LOCATION_STATUS.CONNECTING) return 'Canlı konum sunucusuna bağlanıyor...';
    if (techLiveStatus === TECH_LOCATION_STATUS.RECONNECTING) return 'Bağlantı yenileniyor; harita kullanılmaya devam edebilir.';
    if (techLiveStatus === TECH_LOCATION_STATUS.CONNECTED) return 'Bağlandı; teknisyenden konum verisi bekleniyor.';
    if (techLiveLocation) {
      const updateTime = dayjs(techLiveLocation.timestamp).isValid()
        ? dayjs(techLiveLocation.timestamp).format('HH:mm:ss')
        : '-';
      const accuracy = techLiveLocation.accuracy != null ? ` · ±${Math.round(techLiveLocation.accuracy)} m` : '';
      return `${techLiveLocation.stale ? 'Son bilinen konum' : 'Canlı konum'} · ${updateTime}${accuracy}`;
    }
    return 'Teknisyenin anlık konumunu SignalR ile göster';
  }, [techLiveError, techLiveLocation, techLiveStatus, techMapData?.trackingKey]);

  const techLivePresentation = useMemo(() => {
    const updateTime = techLiveLocation && dayjs(techLiveLocation.timestamp).isValid()
      ? dayjs(techLiveLocation.timestamp).format('HH:mm:ss')
      : '';
    if (!techMapData?.trackingKey) {
      return {
        label: 'Takip kullanılamıyor',
        detail: 'Bu teknisyen kaydında canlı takip kimliği bulunmuyor.',
        color: '#d32f2f',
        pulse: false,
      };
    }
    if (!techLiveTracking) {
      return {
        label: 'Takip kapalı',
        detail: 'Saha teknisyenini haritada izlemek için takibi başlatın.',
        color: '#64748b',
        pulse: false,
      };
    }
    if (techLiveStatus === TECH_LOCATION_STATUS.CONNECTING) {
      return { label: 'Takip başlatılıyor', detail: 'Canlı konum sunucusuna bağlanılıyor…', color: '#0288d1', pulse: true };
    }
    if (techLiveStatus === TECH_LOCATION_STATUS.RECONNECTING) {
      return { label: 'Bağlantı yenileniyor', detail: 'Takip açık; bağlantı yeniden kuruluyor.', color: '#ed6c02', pulse: true };
    }
    if (techLiveError || techLiveStatus === TECH_LOCATION_STATUS.ERROR) {
      return { label: 'Takip bağlantı sorunu', detail: techLiveError || 'Canlı konum bağlantısı kurulamadı.', color: '#d32f2f', pulse: false };
    }
    if (techLiveLocation?.stale) {
      return { label: 'Takip açık · son konum', detail: `Yeni veri bekleniyor${updateTime ? ` · Son güncelleme ${updateTime}` : ''}`, color: '#607d8b', pulse: false };
    }
    if (techLiveLocation) {
      const accuracy = techLiveLocation.accuracy != null ? ` · ±${Math.round(techLiveLocation.accuracy)} m` : '';
      return { label: 'Takip açık · canlı', detail: `Son güncelleme ${updateTime || '-'}${accuracy}`, color: '#00838f', pulse: true };
    }
    return { label: 'Takip açık · konum bekleniyor', detail: 'Bağlantı kuruldu; teknisyenden ilk konum bekleniyor.', color: '#0288d1', pulse: true };
  }, [techLiveError, techLiveLocation, techLiveStatus, techLiveTracking, techMapData?.trackingKey]);

  const handleTechMapClick = useCallback((tech, e) => {
    e.stopPropagation();
    const serviceLocationJobs = rawSchedulingDataRef.current.filter(isBucketNeighbourhood);
    const jobsWithCoords = buildRouteMapJobs([...serviceLocationJobs, ...(tech.jobs || [])]);
    if (jobsWithCoords.length === 0) return;
    setTechMapData({
      techCode: tech.TechCode || '',
      userTechCode: tech.UserTechCode || '',
      trackingKey: tech.TechCode || tech.UserTechCode || '',
      techName: `${tech.UserTechCode || tech.TechCode || ''} ${tech.TechName || ''}`.trim(),
      jobs: jobsWithCoords,
    });
    setTechMapShowRoute(true);
    setTechMapAnimating(false);
    setTechMapOpen(true);
  }, [buildRouteMapJobs]);

  const handleSimulationMapClick = useCallback(({
    ofscNr,
    targetTechCode,
    targetTechName,
    routeAfter = [],
    routeLabel = 'Simülasyon Rotası',
    comparison = false,
    sourceTechCode,
    sourceTechName,
    sourceRoute = [],
    impact = null,
  }) => {
    const movedJobKey = String(ofscNr || '').trim();
    const targetKey = String(targetTechCode || '').trim();
    const targetTech = data.find(item => (
      String(item.TechCode || '').trim() === targetKey
      || String(item.UserTechCode || '').trim() === targetKey
    ));
    const toScenarioRows = route => route.map(job => ({
      OfscNr: job.ofscNr,
      SortOrder: job.sortOrder,
      Latitude: job.latitude,
      Longitude: job.longitude,
      Neighbourhood: job.neighbourhood,
      StatusDescription: job.statusDescription,
      AppointmentTime: job.appointmentTime,
      AppointmentTimeEnd: job.appointmentTimeEnd,
      DepartureTime: job.departureTime,
      ArrivalatCustomer: job.arrivalatCustomer,
      TaskCompletionTime: job.taskCompletionTime,
      WorkDuration: job.displayWorkDuration ?? job.workDuration,
      TravelDuration: job.travelDuration,
      Products: job.products,
      ServicePapers: job.servicePapers,
      CustomerName: job.customer,
      DealerName: job.dealer,
      simulationMoved: String(job.ofscNr || '').trim() === movedJobKey,
    }));
    const scenarioJobs = toScenarioRows(routeAfter);
    const serviceLocationJobs = rawSchedulingDataRef.current.filter(isBucketNeighbourhood);
    const jobsWithCoords = buildRouteMapJobs([...serviceLocationJobs, ...scenarioJobs]);
    if (jobsWithCoords.length === 0) {
      setSnackbar({ open: true, message: 'Simülasyon rotasında haritada gösterilecek koordinat bulunamadı.', severity: 'warning' });
      return;
    }
    const displayCode = targetTech?.UserTechCode || targetTechCode || '';
    const displayName = targetTechName || targetTech?.TechName || '';
    const sourceJobsWithCoords = comparison
      ? buildRouteMapJobs([...serviceLocationJobs, ...toScenarioRows(sourceRoute)])
      : [];
    setTechMapData({
      techCode: targetTech?.TechCode || targetTechCode || '',
      userTechCode: targetTech?.UserTechCode || '',
      trackingKey: targetTech?.TechCode || targetTech?.UserTechCode || targetTechCode || '',
      techName: `${displayCode} ${displayName} · ${routeLabel}`.trim(),
      jobs: jobsWithCoords,
      simulationMode: true,
      movedOfscNr: ofscNr,
      comparisonRoutes: comparison ? {
        source: {
          title: `${sourceTechCode || ''} ${sourceTechName || ''}`.trim(),
          subtitle: 'Nereden · Mevcut Teknisyen Rotası',
          jobs: sourceJobsWithCoords,
        },
        target: {
          title: `${displayCode} ${displayName}`.trim(),
          subtitle: 'Nereye · Yeni Hedef Rota',
          jobs: jobsWithCoords,
        },
      } : null,
      comparisonImpact: comparison ? impact : null,
    });
    setTechMapShowRoute(true);
    setTechMapAnimating(false);
    setTechMapOpen(true);
  }, [buildRouteMapJobs, data]);

  useEffect(() => {
    if (techMapData?.simulationMode) return;
    if (!techMapOpen || (!techMapData?.techCode && !techMapData?.userTechCode)) return;
    const trackedCodes = new Set([techMapData.techCode, techMapData.userTechCode].map(code => String(code || '').trim()).filter(Boolean));
    const techJobs = data.filter(d => trackedCodes.has(String(d.TechCode || '').trim()) || trackedCodes.has(String(d.UserTechCode || '').trim()));
    const serviceLocationJobs = rawSchedulingDataRef.current.filter(isBucketNeighbourhood);
    const jobsWithCoords = buildRouteMapJobs([...serviceLocationJobs, ...techJobs]);
    setTechMapData(prev => prev ? {
      ...prev,
      techName: techJobs[0]
        ? `${techJobs[0].UserTechCode || techJobs[0].TechCode || prev.userTechCode || prev.techCode || ''} ${techJobs[0].TechName || ''}`.trim()
        : prev.techName,
      jobs: jobsWithCoords,
    } : prev);
  }, [data, techMapOpen, techMapData?.techCode, techMapData?.userTechCode, techMapData?.simulationMode, buildRouteMapJobs]);

  const handleExportTechWorks = useCallback(() => {
    const maskPhone = (phone) => {
      const p = String(phone || '');
      if (p.length < 5) return p;
      return p.slice(0, 3) + '****' + p.slice(-2);
    };

    // Servis görünümünde teknisyen kolonu da eklenir (ekrandaki tabloyla aynı mantık).
    const headers = [
      '#',
      ...(techDetailIsBucket ? ['Teknisyen'] : []),
      'Fiş No(ları)',
      'Randevu Dur.', 'Fiş Dur.', 'Tip', 'Randevu', 'Çıkış', 'Varış', 'Bitiş',
      'Mahalle', 'İl / İlçe', 'Mahalle Grubu', 'Ürün', 'Müşteri', 'Bayi', 'Telefon',
      'Randevu Kanalı', 'Yol', 'İş',
    ];

    let rowNum = 0;
    const rows = filteredTechWorks.map(w => {
      const ofscNr = w.OfscNr || 0;
      if (ofscNr > 0) rowNum++;

      const servicePapers = w.ServicePapers || '';
      const spEntries = parseServicePaperEntries(servicePapers);
      const spType = getWorkApplicationReason(w);
      const rawCustomer = spEntries.length > 0 ? (spEntries[0].customer || '') : '';
      const bayiName = getWorkDealerName(w);
      const customerName = getWorkCustomerName(w);
      const phoneNums = [...new Set(rawCustomer.match(/\d{7,}/g) || [])];
      const maskedPhones = phoneNums.map(maskPhone).join(', ');

      const fisNos = spEntries.map(e => e.fisNo).filter(Boolean).join(', ');
      const apptTime = w.AppointmentTime || '';
      const apptTimeEnd = w.AppointmentTimeEnd || '';
      const apptRange = apptTime ? (apptTimeEnd ? `${apptTime} - ${apptTimeEnd}` : apptTime) : '-';
      const displayDurations = getJobDisplayDurations(w);
      const isCompleted = normalizeStatusKey(w.StatusDescription) === 'completed';

      return [
        ofscNr > 0 ? rowNum : '',
        ...(techDetailIsBucket ? [w.TechName || 'Atanmamış'] : []),
        fisNos,
        w.StatusDescription ? getStatusColorObj(w.StatusDescription).label : '',
        w.ServicePapersStatus ? getStatusColorObj(w.ServicePapersStatus).label : '',
        spType || '',
        apptRange,
        w.DepartureTime || '',
        w.ArrivalatCustomer || '',
        w.TaskCompletionTime || '',
        w.Neighbourhood || '-',
        [w.City, w.District].filter(Boolean).join(' / '),
        w.NeighbourhoodGroup || '',
        (w.Products || '').split(',')[0]?.trim() || '',
        maskName(customerName) || '-',
        bayiName || '',
        maskedPhones,
        w.AppointmentChannel || '',
        displayDurations.travel > 0 || isCompleted ? formatDuration(displayDurations.travel) : '',
        displayDurations.work > 0 || isCompleted ? formatDuration(displayDurations.work) : '',
      ];
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [
      { wch: 5 },
      ...(techDetailIsBucket ? [{ wch: 22 }] : []),
      { wch: 20 },
      { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 14 },
      { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 16 }, { wch: 20 }, { wch: 24 },
      { wch: 18 }, { wch: 22 }, { wch: 20 }, { wch: 18 }, { wch: 16 },
      { wch: 8 }, { wch: 8 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Günlük İş Listesi');
    XLSX.writeFile(wb, `Teknisyen_Gunluk_Is_${techInfo?.code || 'tum'}_${dayjs().format('YYYY-MM-DD')}.xlsx`);
  }, [filteredTechWorks, techInfo, techDetailIsBucket]);

  // ── Teknisyen Gruplama API ──
  const fetchTechGroups = useCallback(async () => {
    setTechGroupLoading(true);
    try {
      const resp = await axios.get('/GetTechnicianGroups');
      const result = resp?.data?.Data || resp?.data?.data || resp?.data || [];
      const groups = Array.isArray(result) ? result.map(g => ({
        Id: g.Id || g.id,
        GroupName: g.GroupName || g.groupName || '',
        TechCodes: (g.TechCodes || g.techCodes || '').split(',').filter(Boolean),
      })) : [];
      setTechGroups(groups);
      // İlk yüklemede tüm grupları açık başlat
      const expanded = {};
      groups.forEach(g => { expanded[g.Id] = true; });
      setTechGroupsExpanded(prev => {
        const merged = { ...expanded };
        Object.keys(prev).forEach(k => { if (k in merged) merged[k] = prev[k]; });
        return merged;
      });
    } catch (err) {
      console.error('fetchTechGroups error', err);
    } finally {
      setTechGroupLoading(false);
    }
  }, []);

  const saveTechGroup = useCallback(async (groupName, techCodes, groupId) => {
    setTechGroupSaving(true);
    try {
      const resp = await axios.post('/SetTechnicianGroup', {
        Id: groupId || 0,
        GroupName: groupName,
        TechCodes: techCodes.join(','),
      });
      const success = resp?.data?.Success !== false && resp?.data?.success !== false;
      if (success) {
        setSnackbar({ open: true, message: groupId ? 'Grup güncellendi' : 'Grup oluşturuldu', severity: 'success' });
        await fetchTechGroups();
      } else {
        setSnackbar({ open: true, message: resp?.data?.Message || 'Kayıt hatası', severity: 'error' });
      }
      return success;
    } catch (err) {
      setSnackbar({ open: true, message: 'Grup kayıt hatası: ' + (err?.message || ''), severity: 'error' });
      return false;
    } finally {
      setTechGroupSaving(false);
    }
  }, [currentUserId, serviceShopId, fetchTechGroups]);

  const deleteTechGroup = useCallback(async (groupId) => {
    setTechGroupSaving(true);
    try {
      const resp = await axios.post('/DeleteTechnicianGroup', { UserId: currentUserId, Id: groupId });
      const success = resp?.data?.Success !== false && resp?.data?.success !== false;
      if (success) {
        setSnackbar({ open: true, message: 'Grup silindi', severity: 'success' });
        await fetchTechGroups();
      } else {
        setSnackbar({ open: true, message: resp?.data?.Message || 'Silme hatası', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Grup silme hatası: ' + (err?.message || ''), severity: 'error' });
    } finally {
      setTechGroupSaving(false);
    }
  }, [currentUserId, fetchTechGroups]);

  // İlk yüklemede grupları çek
  useEffect(() => {
    if (currentUserId) fetchTechGroups();
  }, [currentUserId, fetchTechGroups]);

  // ── Ayarlar: Bayi Servis İlişkisi listesini getir ──
  const fetchDealerServiceList = useCallback(async () => {
    setSettingsDealerLoading(true);
    try {
      const resp = await axios.get('/GetDealerServiceShopScheduling');
      const result = resp?.data?.Data || resp?.data?.data || resp?.data || [];
      setSettingsDealerList(Array.isArray(result) ? result : []);
    } catch {
      setSettingsDealerList([]);
    } finally {
      setSettingsDealerLoading(false);
    }
  }, []);

  const handleDeleteDealerServiceRow = useCallback(async (row) => {
    try {
      const id = row.ID || row.Id || row.id;
      const resp = await axios.get(`/DeleteDealerServiceShopScheduling/${id}`);
      const result = resp?.data?.Data ?? resp?.data?.data ?? resp?.data;
      if (result === true || result === 'true') {
        setSettingsDealerList(prev => prev.filter(r => (r.ID || r.Id || r.id) !== id));
        setSnackbar({ open: true, message: 'Kayıt silindi', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Silme işlemi başarısız oldu', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Silme hatası: ' + (err?.message || ''), severity: 'error' });
    }
  }, []);

  const handleAddDealerServiceRow = useCallback(async () => {
    if (!settingsDealerCombo?.id || !settingsTimeSlotCombo?.id) {
      setSnackbar({ open: true, message: 'Lütfen bayi ve zaman dilimini seçin', severity: 'warning' });
      return;
    }
    try {
      await axios.post('/SetDealerServiceShopScheduling', {
        DealerId: settingsDealerCombo.id,
        TimeSlot: settingsTimeSlotCombo.id,
      });
      setSnackbar({ open: true, message: 'Kayıt eklendi', severity: 'success' });
      setSettingsDealerCombo(null);
      setSettingsTimeSlotCombo(null);
      fetchDealerServiceList();
    } catch (err) {
      setSnackbar({ open: true, message: 'Ekleme hatası: ' + (err?.message || ''), severity: 'error' });
    }
  }, [settingsDealerCombo, settingsTimeSlotCombo, fetchDealerServiceList]);

  // ── Teknisyen Mahalle: Sorgula (Mahalle bazlı) ──
  const handleTmSorgula = useCallback(async () => {
    if (!tmTechCombo?.id) {
      setSnackbar({ open: true, message: 'Lütfen teknisyen seçin', severity: 'warning' });
      return;
    }
    setTmLoading(true);
    try {
      const resp = await axios.post('/GetTechnicianNeighbourhood', {
        TechnicianCode: tmTechCombo.id,
        IlKod: tmIlCombo?.id ?? '',
        IlceKod: tmIlceCombo?.id ?? '',
        MahalleKod: tmMahalleCombo?.id ?? '',
      });
      const result = resp?.data?.Data || resp?.data?.data || resp?.data || [];
      const arr = Array.isArray(result) ? result : [];
      tmOriginalRef.current = JSON.parse(JSON.stringify(arr));
      setTmQueryMode('mahalle');
      setTmData(arr);
    } catch (err) {
      setSnackbar({ open: true, message: 'Sorgu hatası: ' + (err?.message || ''), severity: 'error' });
      setTmData([]);
      tmOriginalRef.current = [];
    } finally {
      setTmLoading(false);
    }
  }, [tmTechCombo, tmIlCombo, tmIlceCombo, tmMahalleCombo]);

  // ── Teknisyen Mahalle: Grup Mahalle Sorgula ──
  const handleTmGrupSorgula = useCallback(async () => {
    if (!tmTechCombo?.id) {
      setSnackbar({ open: true, message: 'Lütfen teknisyen seçin', severity: 'warning' });
      return;
    }
    if (!tmGrupMahalleCombo?.id && tmGrupMahalleCombo?.id !== 0) {
      setSnackbar({ open: true, message: 'Lütfen grup mahalle seçin', severity: 'warning' });
      return;
    }
    setTmLoading(true);
    try {
      const resp = await axios.post('/GetTechnicianNeighbourhoodByGroup', {
        TechnicianId: tmTechCombo.id,
        GroupId: tmGrupMahalleCombo.id,
      });
      const result = resp?.data?.Data || resp?.data?.data || resp?.data || [];
      const arr = Array.isArray(result) ? result : [];
      tmOriginalRef.current = JSON.parse(JSON.stringify(arr));
      setTmQueryMode('grup');
      setTmData(arr);
    } catch (err) {
      setSnackbar({ open: true, message: 'Sorgu hatası: ' + (err?.message || ''), severity: 'error' });
      setTmData([]);
      tmOriginalRef.current = [];
    } finally {
      setTmLoading(false);
    }
  }, [tmTechCombo, tmGrupMahalleCombo]);

  // ── Teknisyen Mahalle: Kaydet (sadece değişen satırları gönder) ──
  const handleTmSave = useCallback(async () => {
    if (!tmTechCombo?.id || tmData.length === 0) {
      setSnackbar({ open: true, message: 'Kaydedilecek veri yok', severity: 'warning' });
      return;
    }
    const orig = tmOriginalRef.current;
    const changed = tmData.filter((row, i) => {
      const o = orig[i];
      if (!o) return true;
      return row.OperationID !== o.OperationID || row.StartTime !== o.StartTime || row.EndTime !== o.EndTime;
    });
    if (changed.length === 0) {
      setSnackbar({ open: true, message: 'Değişiklik yapılmadı', severity: 'info' });
      return;
    }
    setTmSaving(true);
    try {
      const items = changed.map(row => ({
        ID: row.ID,
        OperationID: row.OperationID,
        StartTime: row.StartTime,
        EndTime: row.EndTime,
        TechnicianId: row.TechnicianCode,
      }));
      await axios.post('/SetTechnicianNeighbourhood', items);
      tmOriginalRef.current = JSON.parse(JSON.stringify(tmData));
      setSnackbar({ open: true, message: `${changed.length} kayıt güncellendi`, severity: 'success' });
    } catch (err) {
      const responseDetail = err?.response?.data;
      let errorMsg = 'Kayıt hatası: ';
      if (responseDetail && typeof responseDetail === 'object') {
        errorMsg += responseDetail.message || responseDetail.title || JSON.stringify(responseDetail);
      } else if (typeof responseDetail === 'string' && responseDetail) {
        errorMsg += responseDetail;
      } else {
        errorMsg += err?.message || '';
      }
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setTmSaving(false);
    }
  }, [tmTechCombo, tmData]);

  // ── Teknisyen Mahalle: Satır güncelle ──
  const handleTmRowChange = useCallback((index, field, value) => {
    setTmData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  // ── Teknisyen Uzmanlık: Sorgula ──
  const handleTuSorgula = useCallback(async () => {
    if (!tuTechCombo?.id) {
      setSnackbar({ open: true, message: 'Lütfen teknisyen seçin', severity: 'warning' });
      return;
    }
    setTuLoading(true);
    try {
      const resp = await axios.post('/GetTechnicianExpertise', {
        TechnicianId: tuTechCombo.id,
      });
      const result = resp?.data?.Data || resp?.data?.data || resp?.data || [];
      const arr = Array.isArray(result) ? result : [];
      tuOriginalRef.current = JSON.parse(JSON.stringify(arr));
      setTuData(arr);
    } catch (err) {
      setSnackbar({ open: true, message: 'Sorgu hatası: ' + (err?.message || ''), severity: 'error' });
      setTuData([]);
      tuOriginalRef.current = [];
    } finally {
      setTuLoading(false);
    }
  }, [tuTechCombo]);

  // ── Teknisyen Uzmanlık: Kaydet (sadece değişenler) ──
  const handleTuSave = useCallback(async () => {
    if (!tuTechCombo?.id || tuData.length === 0) {
      setSnackbar({ open: true, message: 'Kaydedilecek veri yok', severity: 'warning' });
      return;
    }
    const orig = tuOriginalRef.current;
    const changed = tuData.filter((row, i) => {
      const o = orig[i];
      if (!o) return true;
      return row.OperationId !== o.OperationId;
    });
    if (changed.length === 0) {
      setSnackbar({ open: true, message: 'Değişiklik yapılmadı', severity: 'info' });
      return;
    }
    setTuSaving(true);
    try {
      const items = changed.map(row => ({
        ID: row.ID,
        ExpertId: row.ExpertId,
        OperationId: row.OperationId,
        TechnicianId: tuTechCombo.id,
      }));
      await axios.post('/SetTechnicianExpertise', items);
      await removeTechnicianSkillCache(serviceShopId);
      tuOriginalRef.current = JSON.parse(JSON.stringify(tuData));
      setSnackbar({ open: true, message: `${changed.length} kayıt güncellendi`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Kayıt hatası: ' + (err?.message || ''), severity: 'error' });
    } finally {
      setTuSaving(false);
    }
  }, [tuTechCombo, tuData, serviceShopId]);

  // ── Teknisyen Uzmanlık: Satır güncelle ──
  const handleTuRowChange = useCallback((index, field, value) => {
    setTuData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  // ── Grup Mahalle: Gün toggle ──
  const handleGmDayToggle = useCallback((day) => {
    setGmDays(prev => ({ ...prev, [day]: !prev[day] }));
  }, []);

  // ── Grup Mahalle: Grup Adı Oluştur ──
  const handleGmCreateGroup = useCallback(async () => {
    if (!gmGrupName.trim()) {
      setSnackbar({ open: true, message: 'Grup adı girin', severity: 'warning' });
      return;
    }
    const selectedDays = Object.entries(gmDays).filter(([, v]) => v).map(([k]) => k);
    if (gmBagliYer === '1' && selectedDays.length === 0) {
      setSnackbar({ open: true, message: 'En az bir gün seçin', severity: 'warning' });
      return;
    }
    setGmCreating(true);
    try {
      const resp = await axios.post('/CreateNeighbourhoodGroup', {
        GroupName: gmGrupName.trim(),
        Days: gmBagliYer === '1' ? selectedDays.join(',') : '',
        BagliYer: gmBagliYer,
      });
      const result = typeof resp?.data === 'string' ? resp.data : (resp?.data?.Data || resp?.data?.data || '');
      if (result === 'Ok') {
        setSnackbar({ open: true, message: 'Grup oluşturuldu', severity: 'success' });
        setGmGrupName('');
        setGmDays({ ...GM_DAYS_INIT });
        queryClient.invalidateQueries({ queryKey: ['lookup', 'function', 'GetTechNeighbourhoodGrupMahalle'] });
        setGmGrupComboKey(prev => prev + 1);
      } else {
        setSnackbar({ open: true, message: result || 'Grup oluşturma hatası', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Grup oluşturma hatası: ' + (err?.message || ''), severity: 'error' });
    } finally {
      setGmCreating(false);
    }
  }, [gmGrupName, gmDays, gmBagliYer, serviceShopId]);

  // ── Grup Mahalle: Grup detayını getir ──
  const handleGmFetchDetail = useCallback(async (groupId) => {
    if (!groupId) {
      setGmDetailData([]);
      setGmFreeTech(false);
      return;
    }
    setGmDetailLoading(true);
    try {
      const resp = await axios.post('/GetNeighbourhoodGroupDetail', { GroupId: groupId });
      const result = resp?.data?.Data || resp?.data?.data || resp?.data || [];
      const detailRows = Array.isArray(result) ? result : [];
      setGmDetailData(detailRows);
      const manipulation = detailRows[0]?.Manipulation ?? detailRows[0]?.manipulation;
      setGmFreeTech(Number(manipulation) === 0);
    } catch (err) {
      setSnackbar({ open: true, message: 'Detay hatası: ' + (err?.message || ''), severity: 'error' });
      setGmDetailData([]);
      setGmFreeTech(false);
    } finally {
      setGmDetailLoading(false);
    }
  }, []);

  // ── Grup Mahalle: Gruba mahalle ekle ──
  const handleGmAddNeighbourhood = useCallback(async () => {
    if (!gmGrupCombo?.id) {
      setSnackbar({ open: true, message: 'Grup seçin', severity: 'warning' });
      return;
    }
    // Eğer comboda seçili mahalle varsa staged listeyi onunla değiştir, yoksa staged listeyi kullan
    let allMahalles;
    if (gmMahalleCombo?.id) {
      allMahalles = [{ id: gmMahalleCombo.id, name: gmMahalleCombo.name }];
    } else {
      allMahalles = [...gmStagedMahalles];
    }
    if (allMahalles.length === 0) {
      setSnackbar({ open: true, message: 'Mahalle seçin', severity: 'warning' });
      return;
    }
    setGmAdding(true);
    try {
      const mahalleKods = allMahalles.map(m => m.id);
      const resp = await axios.post('/AddNeighbourhoodToGroup', {
        GroupId: gmGrupCombo.id,
        IlKod: gmIlCombo?.id ?? '',
        IlceKod: gmIlceCombo?.id ?? '',
        MahalleKod: mahalleKods.join(',')
      });
      if (resp?.data !== 'Ok') {
        setSnackbar({ open: true, message: 'Ekleme hatası: ' + (resp?.data || 'Bilinmeyen hata'), severity: 'error' });
        return;
      }
      setSnackbar({ open: true, message: `${allMahalles.length} mahalle eklendi`, severity: 'success' });
      setGmMahalleCombo(null);
      setGmStagedMahalles([]);
      handleGmFetchDetail(gmGrupCombo.id);
    } catch (err) {
      setSnackbar({ open: true, message: 'Ekleme hatası: ' + (err?.message || ''), severity: 'error' });
    } finally {
      setGmAdding(false);
    }
  }, [gmGrupCombo, gmIlCombo, gmIlceCombo, gmMahalleCombo, gmStagedMahalles, serviceShopId, handleGmFetchDetail]);

  // ── Grup Mahalle: Mahalle çoklu seçim popup aç ──
  const handleGmOpenMahallePopup = useCallback(async () => {
    if (gmIlCombo?.id == null || gmIlceCombo?.id == null) {
      setSnackbar({ open: true, message: 'Önce İl ve İlçe seçin', severity: 'warning' });
      return;
    }
    setGmMahallePopupOpen(true);
    setGmMahalleListLoading(true);
    setGmMahalleSelected({});
    try {
      const resp = await axios.post('/GetCombo', {
        functionName: 'GetTechNeighbourhoodMahalle',
        idField: 'ID',
        textField: 'ACIKLAMA',
        functionParameters: { ServiceShopId: serviceShopId, IlKod: gmIlCombo.id, IlceKod: gmIlceCombo.id },
        connectionName: 'SarisDb',
      });
      const raw = Array.isArray(resp?.data) ? resp.data : [];
      const list = raw.map(item => ({
        id: item.ID ?? item.Value ?? item.value ?? item.Id ?? item.id,
        name: item.ACIKLAMA ?? item.Text ?? item.text ?? item.Name ?? item.name ?? '',
      })).filter(item => item.name && item.name !== 'Tümü');
      setGmMahalleList(list);
    } catch (err) {
      setSnackbar({ open: true, message: 'Mahalle listesi alınamadı: ' + (err?.message || ''), severity: 'error' });
      setGmMahalleList([]);
    } finally {
      setGmMahalleListLoading(false);
    }
  }, [gmIlCombo, gmIlceCombo, serviceShopId]);

  // ── Grup Mahalle: Seçili mahalleleri listeye ekle (staging) ──
  const handleGmBatchAddMahalle = useCallback(() => {
    const selectedIds = Object.entries(gmMahalleSelected).filter(([, v]) => v).map(([k]) => k);
    if (selectedIds.length === 0) {
      setSnackbar({ open: true, message: 'En az bir mahalle seçin', severity: 'warning' });
      return;
    }
    const newItems = selectedIds
      .map(id => gmMahalleList.find(m => String(m.id) === String(id)))
      .filter(Boolean)
      .filter(m => !gmStagedMahalles.some(s => String(s.id) === String(m.id)));
    setGmStagedMahalles(prev => [...prev, ...newItems]);
    setGmMahallePopupOpen(false);
    if (newItems.length > 0) {
      setSnackbar({ open: true, message: `${newItems.length} mahalle listeye eklendi`, severity: 'info' });
    }
  }, [gmMahalleSelected, gmMahalleList, gmStagedMahalles]);

  // ── Grup Mahalle: Tümünü seç/kaldır ──
  const handleGmToggleAllMahalle = useCallback((checked) => {
    const newSelected = {};
    gmMahalleList.forEach(m => { newSelected[m.id] = checked; });
    setGmMahalleSelected(newSelected);
  }, [gmMahalleList]);

  // ── Grup Mahalle: Satır sil ──
  const handleGmDeleteRow = useCallback(async (row) => {
    try {
      await axios.post('/DeleteNeighbourhoodFromGroup', { ID: row.Id });
      setGmDetailData(prev => prev.filter(r => r.Id !== row.Id));
      setSnackbar({ open: true, message: 'Kayıt silindi', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Silme hatası: ' + (err?.message || ''), severity: 'error' });
    }
  }, []);

  // ── Grup Mahalle: Grup sil ──
  const handleGmDeleteGroup = useCallback(async () => {
    if (!gmGrupCombo?.id) return;
    if (!window.confirm(`"${gmGrupCombo.name}" grubunu silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.post('/DeleteNeighbourhoodGroup', { GroupId: gmGrupCombo.id });
      setSnackbar({ open: true, message: 'Grup silindi', severity: 'success' });
      setGmGrupCombo(null);
      setGmDetailData([]);
      queryClient.invalidateQueries({ queryKey: ['lookup', 'function', 'GetTechNeighbourhoodGrupMahalle'] });
      setGmGrupComboKey(prev => prev + 1);
    } catch (err) {
      setSnackbar({ open: true, message: 'Grup silme hatası: ' + (err?.message || ''), severity: 'error' });
    }
  }, [gmGrupCombo]);

  // ── Grup Mahalle: Gün değiştir popup aç ──
  const handleGmOpenDayPopup = useCallback(() => {
    if (!gmGrupCombo?.id) return;
    // Mevcut günleri detay verisinden al
    const currentDayNumbers = gmDetailData.length > 0 && gmDetailData[0].DayNumber
      ? gmDetailData[0].DayNumber.split(',').map(d => d.trim())
      : [];
    const preset = { ...GM_DAYS_INIT };
    currentDayNumbers.forEach(d => { if (Object.prototype.hasOwnProperty.call(preset, d)) preset[d] = true; });
    setGmDayPopupDays(preset);
    setGmDayPopupOpen(true);
  }, [gmGrupCombo, gmDetailData]);

  // ── Grup Mahalle: Gün değiştir (API) ──
  const handleGmChangeDay = useCallback(async () => {
    if (!gmGrupCombo?.id) return;
    const selectedDays = Object.entries(gmDayPopupDays).filter(([, v]) => v).map(([k]) => k);
    if (selectedDays.length === 0) {
      setSnackbar({ open: true, message: 'En az bir gün seçin', severity: 'warning' });
      return;
    }
    setGmSaving(true);
    try {
      await axios.post('/ChangeNeighbourhoodGroupDay', {
        GroupId: gmGrupCombo.id,
        Days: selectedDays.join(','),
      });
      setSnackbar({ open: true, message: 'Günler güncellendi', severity: 'success' });
      setGmDayPopupOpen(false);
      handleGmFetchDetail(gmGrupCombo.id);
    } catch (err) {
      setSnackbar({ open: true, message: 'Gün değiştirme hatası: ' + (err?.message || ''), severity: 'error' });
    } finally {
      setGmSaving(false);
    }
  }, [gmGrupCombo, gmDayPopupDays, handleGmFetchDetail]);

  // ── Grup Mahalle: Teknisyenleri serbest bırak ──
  const handleGmFreeTech = useCallback(async (checked) => {
    if (!gmGrupCombo?.id) return;
    const actionText = checked ? 'kilitlemek' : 'serbest bırakmak';
    if (!window.confirm(
      `"${gmGrupCombo.name || 'Seçili bağlı yer'}" için teknisyenleri ${actionText} istediğinize emin misiniz?`,
    )) return;
    setGmFreeTech(checked);
    try {
      await axios.post('/SetNeighbourhoodGroupTechFree', {
        GroupId: gmGrupCombo.id,
        IsFree: checked,
      });
      setSnackbar({ open: true, message: checked ? 'Bu Bağlı Yerde Teknisyenleri Kilitlendi' : 'Bu Bağlı Yerde Teknisyenler Serbest Bırakıldı', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'İşlem hatası: ' + (err?.message || ''), severity: 'error' });
      setGmFreeTech(!checked);
    }
  }, [gmGrupCombo]);

  // ── Aynı Gün Bağlı Yerler: Kaydet ──
  const handleBySave = useCallback(async () => {
    if (!byBagliYer1?.id || !byBagliYer2?.id) {
      setSnackbar({ open: true, message: 'Lütfen her iki bağlı yeri de seçin', severity: 'warning' });
      return;
    }
    if (byBagliYer1.id === byBagliYer2.id) {
      setSnackbar({ open: true, message: 'Bağlı Yer1 ve Bağlı Yer2 aynı olamaz', severity: 'warning' });
      return;
    }
    setBySaving(true);
    try {
      const resp = await axios.post('/SaveSameDayLinkedGroups', {
        GroupId1: byBagliYer1.id,
        GroupId2: byBagliYer2.id,
      });
      const result = typeof resp?.data === 'string' ? resp.data : (resp?.data?.Data || resp?.data?.data || '');
      if (result === 'Ok') {
        setSnackbar({ open: true, message: 'Bağlı yerler kaydedildi', severity: 'success' });
        // Listeyi yenile
        fetchByList(byBagliYer1.id, byBagliYer2?.id || 0);
      } else {
        setSnackbar({ open: true, message: 'Kaydetme hatası: ' + (result || 'Bilinmeyen hata'), severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Kaydetme hatası: ' + (err?.message || ''), severity: 'error' });
    } finally {
      setBySaving(false);
    }
  }, [byBagliYer1, byBagliYer2]);

  // ── Aynı Gün Bağlı Yerler: Listele ──
  const fetchByList = useCallback(async (id1, id2 = 0) => {
    if (!id1) { setByList([]); return; }
    setByLoading(true);
    try {
      const resp = await axios.post('/GetAronManipulationNeighborhood', {
        Id1: id1,
        Id2: id2,
        ServiceShopId: serviceShopId,
        OrganizationUserId: currentUserId,
      });
      const result = resp?.data?.Data || resp?.data?.data || resp?.data || [];
      setByList(Array.isArray(result) ? result : []);
    } catch {
      setByList([]);
    } finally {
      setByLoading(false);
    }
  }, [serviceShopId, currentUserId]);

  // ── Aynı Gün Bağlı Yerler: Sil (IDKEY1 ile) ──
  const handleByDelete = useCallback(async (idKey1) => {
    setByDeleting(idKey1);
    try {
      await axios.post('/DeleteAronManipulationNeighborhood', { IdKey: idKey1 });
      setByList(prev => prev.filter(r => (r.IDKEY1 ?? r.IdKey1 ?? r.idKey1) !== idKey1));
      setSnackbar({ open: true, message: 'Kayıt silindi', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Silme hatası: ' + (err?.message || ''), severity: 'error' });
    } finally {
      setByDeleting(null);
    }
  }, []);

  // ── Mahalle Kısıtlaması Hariç: Listeyi getir ──
  const fetchMkList = useCallback(async () => {
    setMkLoading(true);
    try {
      const resp = await axios.get('/ExceptforNeighborhood');
      const result = resp?.data?.Data || resp?.data?.data || resp?.data || [];
      setMkList(Array.isArray(result) ? result : []);
    } catch {
      setMkList([]);
    } finally {
      setMkLoading(false);
    }
  }, []);

  // ── Mahalle Kısıtlaması Hariç: Kaydet ──
  const handleMkSave = useCallback(async () => {
    if (!mkTechCombo?.id) {
      setSnackbar({ open: true, message: 'Lütfen teknisyen seçin', severity: 'warning' });
      return;
    }
    setMkSaving(true);
    try {
      const resp = await axios.post('/SetExceptforNeighborhood', {
        TechnicianId: mkTechCombo.id,
        Operation: 1,
      });
      const result = typeof resp?.data === 'string' ? resp.data : (resp?.data?.Data || resp?.data?.data || '');
      if (result === 'Ok') {
        setSnackbar({ open: true, message: 'Teknisyen eklendi', severity: 'success' });
        setMkTechCombo(null);
        fetchMkList();
      } else {
        setSnackbar({ open: true, message: 'Kaydetme hatası: ' + (result || 'Bilinmeyen hata'), severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Kaydetme hatası: ' + (err?.message || ''), severity: 'error' });
    } finally {
      setMkSaving(false);
    }
  }, [mkTechCombo, fetchMkList]);

  // ── Mahalle Kısıtlaması Hariç: Sil ──
  const handleMkDelete = useCallback(async (row) => {
    try {
      const id = row.TechnicianId || row.technicianId || row.Id || row.id;
      const resp = await axios.post('/SetExceptforNeighborhood', {
        TechnicianId: id,
        Operation: 0,
      });
      const result = resp?.data?.Data ?? resp?.data?.data ?? resp?.data;
      if (result === true || result === 'true' || result === 'Ok') {
        setMkList(prev => prev.filter(r => (r.TechnicianId || r.technicianId || r.Id || r.id) !== id));
        setSnackbar({ open: true, message: 'Kayıt silindi', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Silme işlemi başarısız oldu', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Silme hatası: ' + (err?.message || ''), severity: 'error' });
    }
  }, []);

  const handleSettingsOpen = useCallback(() => {
    setSettingsOpen(true);
    setSettingsActiveTab('tekMahalle');
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(appearanceSettings));
    } catch (_) {
      // Görünüm ayarı kaydedilemezse ekran çalışmaya devam eder.
    }
  }, [appearanceSettings]);

  const updateAppearance = useCallback((patch) => {
    setAppearanceSettings(prev => ({ ...prev, ...patch }));
  }, []);

  const resetAppearance = useCallback(() => {
    setAppearanceSettings(DEFAULT_APPEARANCE);
  }, []);

  const appearancePageBg = safeHexColor(appearanceSettings.pageBg, DEFAULT_APPEARANCE.pageBg);
  const appearanceToolbarBg = safeHexColor(appearanceSettings.toolbarBg, DEFAULT_APPEARANCE.toolbarBg);
  const appearanceAccent = safeHexColor(appearanceSettings.accentColor, darkMode ? '#90caf9' : DEFAULT_APPEARANCE.accentColor);
  const customColorsEnabled = Boolean(appearanceSettings.useCustomColors);
  const viewScale = Math.min(120, Math.max(85, Number(appearanceSettings.viewScale) || 100));

  return (
    <Box ref={containerRef} sx={{
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 400, overflow: 'hidden',
      bgcolor: customColorsEnabled ? appearancePageBg : (darkMode ? '#0f0f1a' : '#f4f6fb'),
      background: customColorsEnabled
        ? appearancePageBg
        : (darkMode
          ? 'linear-gradient(145deg, #0f0f1a 0%, #141428 50%, #0d0d1e 100%)'
          : 'linear-gradient(145deg, #f4f6fb 0%, #eef1f9 50%, #f8faff 100%)'),
      zoom: `${viewScale}%`,
      '--toa-accent': appearanceAccent,
      '@keyframes headerShimmer': {
        '0%': { backgroundPosition: '-200% 0' },
        '100%': { backgroundPosition: '200% 0' },
      },
    }}>

      {/* ── Üst toolbar ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.75, sm: 1.5 },
          px: { xs: 1, sm: 2.5 },
          py: { xs: 1, sm: 1.5 },
          borderBottom: `1px solid ${customColorsEnabled ? alpha(appearanceAccent, 0.18) : (darkMode ? alpha('#7c4dff', 0.12) : alpha('#1565c0', 0.08))}`,
          background: customColorsEnabled
            ? alpha(appearanceToolbarBg, 0.96)
            : (darkMode
              ? `linear-gradient(135deg, ${alpha('#1565c0', 0.08)} 0%, ${alpha('#7c4dff', 0.04)} 50%, ${alpha('#1565c0', 0.06)} 100%)`
              : `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(237,242,255,0.9) 50%, rgba(255,255,255,0.95) 100%)`),
          backdropFilter: 'blur(16px)',
          boxShadow: darkMode
            ? '0 4px 24px rgba(0,0,0,0.3), inset 0 -1px 0 rgba(124,77,255,0.08)'
            : '0 4px 24px rgba(21,101,192,0.06), inset 0 -1px 0 rgba(21,101,192,0.04)',
          position: 'relative',
          overflow: 'hidden',
          flexWrap: 'wrap',
          zIndex: 10,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: customColorsEnabled
              ? `linear-gradient(90deg, transparent, ${appearanceAccent}, ${alpha(appearanceAccent, 0.55)}, ${appearanceAccent}, transparent)`
              : (darkMode
                ? 'linear-gradient(90deg, transparent, #7c4dff, #448AFF, #7c4dff, transparent)'
                : 'linear-gradient(90deg, transparent, #1565c0, #42a5f5, #1565c0, transparent)'),
            backgroundSize: '200% 100%',
            animation: 'headerShimmer 6s linear infinite',
            opacity: 0.6,
          },
        }}
      >
        {/* Sayfa başlığı / branding */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: { xs: 0, sm: 1.5 }, order: { xs: -1, sm: 0 }, width: { xs: '100%', sm: 'auto' }, mb: { xs: 0.5, sm: 0 } }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
            boxShadow: '0 2px 8px rgba(255,152,0,0.3)',
            flexShrink: 0,
          }}>
            <ScheduleIcon sx={{ fontSize: 16, color: '#fff' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: darkMode ? '#fff' : '#1a1a2e', lineHeight: 1.1, letterSpacing: '-0.01em' }}>
              Aron Rotam
            </Typography>
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 500, color: darkMode ? '#777' : '#999', lineHeight: 1, letterSpacing: '0.04em' }}>
              Randevu Planlama
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06), display: { xs: 'none', sm: 'block' } }} />
        </Box>
        {headerControls}
        <TextField
          size="small"
          placeholder="Ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, opacity: 0.4 }} />
              </InputAdornment>
            ),
            endAdornment: showServicePaperLookup ? (
              <InputAdornment position="end">
                <Tooltip title="Fişin randevu tarihine git" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleServicePaperLookup}
                      disabled={servicePaperLookupLoading}
                      sx={{
                        p: 0.35,
                        color: darkMode ? '#90caf9' : '#1565c0',
                        bgcolor: alpha(darkMode ? '#90caf9' : '#1565c0', 0.08),
                        '&:hover': { bgcolor: alpha(darkMode ? '#90caf9' : '#1565c0', 0.16) },
                      }}
                    >
                      <AssignmentIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            minWidth: { xs: 120, sm: 180 },
            flex: { xs: 1, sm: 'none' },
            order: { xs: 10, sm: 0 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '0.82rem',
              bgcolor: darkMode ? alpha('#fff', 0.04) : '#fff',
              height: { xs: 32, sm: 'auto' },
              transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${alpha('#1565c0', 0.12)}`,
              },
            },
          }}
        />

        {/* Tarih navigasyonu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#1565c0', 0.04), borderRadius: 2, px: 0.5, py: 0.25, border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#1565c0', 0.1)}` }}>
          <Tooltip title="Önceki gün" arrow>
            <IconButton onClick={goToPrevDay} disabled={loading} size="small" sx={{ p: 0.5 }}>
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Tarih seç" arrow>
            <Box
              onClick={(e) => setDatePickerAnchor(e.currentTarget)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 1.5,
                cursor: 'pointer',
                bgcolor: isToday(selectedDate) ? alpha('#1565c0', 0.08) : 'transparent',
                '&:hover': { bgcolor: alpha('#1565c0', 0.1) },
                transition: 'all 0.15s',
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 16, color: isToday(selectedDate) ? (darkMode ? '#90caf9' : '#1565c0') : (darkMode ? '#888' : '#999') }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: darkMode ? '#e0e0e0' : '#333', whiteSpace: 'nowrap', userSelect: 'none' }}>
                {formatDateDisplay(selectedDate, isMobile)}
              </Typography>
              {isToday(selectedDate) && (
                <Chip label="Bugün" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: darkMode ? alpha('#90caf9', 0.15) : alpha('#1565c0', 0.12), color: darkMode ? '#90caf9' : '#1565c0' }} />
              )}
            </Box>
          </Tooltip>
          <Tooltip title="Sonraki gün" arrow>
            <IconButton onClick={goToNextDay} disabled={loading} size="small" sx={{ p: 0.5 }}>
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          {!isToday(selectedDate) && (
            <Tooltip title="Bugüne dön" arrow>
              <IconButton onClick={goToToday} size="small" sx={{ p: 0.5, color: darkMode ? '#90caf9' : '#1565c0' }}>
                <TodayIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Popover
          open={Boolean(datePickerAnchor)}
          anchorEl={datePickerAnchor}
          onClose={() => setDatePickerAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                border: `1px solid ${darkMode ? alpha('#fff', 0.1) : alpha('#1565c0', 0.12)}`,
                overflow: 'hidden',
              },
            },
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
            <DateCalendar
              value={selectedDateDayjs}
              onChange={(newValue) => {
                if (newValue) {
                  setSelectedDate(newValue.toDate());
                  setDatePickerAnchor(null);
                }
              }}
              sx={{
                '& .MuiPickersDay-root': {
                  fontWeight: 600,
                  fontSize: '0.82rem',
                },
                '& .MuiPickersDay-today': {
                  border: `2px solid ${darkMode ? '#90caf9' : '#1565c0'} !important`,
                },
                '& .Mui-selected': {
                  bgcolor: `${darkMode ? '#1565c0' : '#1565c0'} !important`,
                  color: '#fff !important',
                  fontWeight: 700,
                },
              }}
            />
          </LocalizationProvider>
        </Popover>

        <Tooltip title="Yenile" arrow>
          <IconButton onClick={fetchData} disabled={loading} size="small" sx={{ bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#1565c0', 0.06), '&:hover': { bgcolor: alpha('#1565c0', 0.12) } }}>
            <RefreshIcon sx={{ fontSize: 18, animation: loading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
          </IconButton>
        </Tooltip>
        {lastUpdate && (
          <Typography sx={{ fontSize: '0.6rem', color: darkMode ? '#888' : '#999', fontWeight: 500, whiteSpace: 'nowrap', ml: -0.5 }}>
            {lastUpdate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </Typography>
        )}


        {/* Zoom kontrolleri */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#1565c0', 0.04), borderRadius: 2, px: 0.5, py: 0.25, border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#1565c0', 0.1)}` }}>
          <Tooltip title="Küçült" arrow>
            <span>
              <IconButton onClick={() => setHourWidth((w) => Math.max(HOUR_WIDTH_MIN, w - ZOOM_STEP))} disabled={hourWidth <= HOUR_WIDTH_MIN} size="small" sx={{ p: 0.5 }}>
                <ZoomOutIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Varsayılan" arrow>
            <IconButton onClick={() => setHourWidth(HOUR_WIDTH_DEFAULT)} size="small" sx={{ p: 0.5 }}>
              <RestartAltIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Büyüt" arrow>
            <span>
              <IconButton onClick={() => setHourWidth((w) => Math.min(HOUR_WIDTH_MAX, w + ZOOM_STEP))} disabled={hourWidth >= HOUR_WIDTH_MAX} size="small" sx={{ p: 0.5 }}>
                <ZoomInIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Profesyonel araçlar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#7c4dff', 0.04), borderRadius: 2, px: 0.5, py: 0.25, border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#7c4dff', 0.1)}` }}>
          {/* Scroll to now */}
          {isSelectedToday && (
            <Tooltip title="Şu ana git (N)" arrow>
              <IconButton onClick={scrollToNow} size="small" sx={{ p: 0.5 }}>
                <MyLocationIcon sx={{ fontSize: 16, color: COLORS.nowLine }} />
              </IconButton>
            </Tooltip>
          )}
          {/* Row density */}
          <Tooltip title={`Satır yoğunluğu: ${rowDensity === 'compact' ? 'Kompakt' : rowDensity === 'comfortable' ? 'Normal' : 'Geniş'}`} arrow>
            <IconButton
              onClick={() => setRowDensity(d => d === 'compact' ? 'comfortable' : d === 'comfortable' ? 'expanded' : 'compact')}
              size="small" sx={{ p: 0.5 }}
            >
              {rowDensity === 'compact' ? <ViewCompactIcon sx={{ fontSize: 16 }} /> : rowDensity === 'expanded' ? <ViewComfyIcon sx={{ fontSize: 16 }} /> : <ViewCompactIcon sx={{ fontSize: 16, opacity: 0.6 }} />}
            </IconButton>
          </Tooltip>
          {/* Resource panel toggle */}
          <Tooltip title={resourcePanelCollapsed ? 'Paneli Aç' : 'Paneli Gizle'} arrow>
            <IconButton onClick={() => setResourcePanelCollapsed(p => !p)} size="small" sx={{ p: 0.5 }}>
              {resourcePanelCollapsed ? <ChevronRightOutlinedIcon sx={{ fontSize: 16 }} /> : <MenuOpenIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          {/* Fullscreen */}
          <Tooltip title={isFullscreen ? 'Tam ekrandan çık (F)' : 'Tam ekran (F)'} arrow>
            <IconButton onClick={toggleFullscreen} size="small" sx={{ p: 0.5 }}>
              {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: 18 }} /> : <FullscreenIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
          {/* Keyboard help */}
          <Tooltip title="Kısayol tuşları (?)" arrow>
            <IconButton onClick={() => setShortcutHelpOpen(p => !p)} size="small" sx={{ p: 0.5 }}>
              <KeyboardIcon sx={{ fontSize: 16, opacity: 0.6 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Auto-refresh countdown */}
        <AutoRefreshCountdown lastUpdate={lastUpdate} onRefresh={fetchData} />
        <SchedulingPerformanceMetrics ref={performanceMetricsRef} />
        {/* Özet */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.75, ml: 'auto', flexWrap: 'wrap', alignItems: 'center' }}>
          <Tooltip title="Çalışma Takvimi" arrow>
            <Chip
              icon={<CalendarMonthIcon sx={{ fontSize: '16px !important', color: darkMode ? '#b388ff' : '#1565c0' }} />}
              label="Takvim"
              size="small"
              onClick={() => { setCalendarOpen(true); fetchCalendarTechList(); }}
              sx={{
                bgcolor: darkMode ? alpha('#7c4dff', 0.15) : alpha('#1565c0', 0.1), fontWeight: 700, fontSize: '0.72rem', height: 28,
                borderRadius: '14px', border: `1px solid ${darkMode ? alpha('#7c4dff', 0.25) : alpha('#1565c0', 0.2)}`,
                boxShadow: `0 2px 8px ${darkMode ? alpha('#7c4dff', 0.12) : alpha('#1565c0', 0.12)}`, cursor: 'pointer',
                '&:hover': { bgcolor: darkMode ? alpha('#7c4dff', 0.25) : alpha('#1565c0', 0.2), boxShadow: `0 4px 16px ${darkMode ? alpha('#7c4dff', 0.2) : alpha('#1565c0', 0.2)}` },
                transition: 'all 0.2s ease',
              }}
            />
          </Tooltip>
          <Tooltip title="İş Taşıma Simülasyonu" arrow>
            <Chip
              icon={<AltRouteIcon sx={{ fontSize: '16px !important', color: darkMode ? '#80cbc4' : '#00796b' }} />}
              label="Taşıma"
              size="small"
              onClick={() => setMoveSimulationOpen(true)}
              sx={{
                bgcolor: darkMode ? alpha('#26a69a', 0.15) : alpha('#00796b', 0.1), fontWeight: 700, fontSize: '0.72rem', height: 28,
                borderRadius: '14px', border: `1px solid ${darkMode ? alpha('#26a69a', 0.25) : alpha('#00796b', 0.2)}`,
                boxShadow: `0 2px 8px ${darkMode ? alpha('#26a69a', 0.12) : alpha('#00796b', 0.12)}`, cursor: 'pointer',
                '&:hover': { bgcolor: darkMode ? alpha('#26a69a', 0.25) : alpha('#00796b', 0.2), boxShadow: `0 4px 16px ${darkMode ? alpha('#26a69a', 0.2) : alpha('#00796b', 0.2)}` },
                transition: 'all 0.2s ease',
              }}
            />
          </Tooltip>
          <Tooltip title="Görünüm ayarları" arrow>
            <Chip
              icon={<SettingsIcon sx={{ fontSize: '16px !important', color: appearanceAccent }} />}
              label="Görünüm"
              size="small"
              onClick={(e) => setAppearanceAnchor(e.currentTarget)}
              sx={{
                bgcolor: alpha(appearanceAccent, customColorsEnabled ? 0.16 : 0.1),
                color: appearanceAccent,
                fontWeight: 800,
                fontSize: '0.72rem',
                height: 28,
                borderRadius: '14px',
                border: `1px solid ${alpha(appearanceAccent, 0.25)}`,
                boxShadow: `0 2px 8px ${alpha(appearanceAccent, 0.12)}`,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: alpha(appearanceAccent, 0.2),
                  boxShadow: `0 4px 16px ${alpha(appearanceAccent, 0.22)}`,
                },
                transition: 'all 0.2s ease',
              }}
            />
          </Tooltip>
          <Popover
            open={Boolean(appearanceAnchor)}
            anchorEl={appearanceAnchor}
            onClose={() => setAppearanceAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  width: 300,
                  p: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 12px 36px rgba(0,0,0,0.22)',
                  border: `1px solid ${alpha(appearanceAccent, 0.18)}`,
                  bgcolor: darkMode ? '#1f2430' : '#fff',
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.2 }}>
              <Box>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 900, color: darkMode ? '#fff' : '#1f2937' }}>
                  Görünüm Ayarları
                </Typography>
                <Typography sx={{ fontSize: '0.66rem', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                  Bu cihaz için saklanır
                </Typography>
              </Box>
              <Button size="small" onClick={resetAppearance} sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', color: appearanceAccent }}>
                Sıfırla
              </Button>
            </Box>

            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 1,
                py: 0.8,
                mb: 1,
                borderRadius: 1.5,
                bgcolor: alpha(appearanceAccent, 0.06),
                border: `1px solid ${alpha(appearanceAccent, 0.12)}`,
                cursor: 'pointer',
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: darkMode ? '#e5e7eb' : '#374151' }}>
                Özel renkleri kullan
              </Typography>
              <Box
                component="input"
                type="checkbox"
                checked={customColorsEnabled}
                onChange={(e) => updateAppearance({ useCustomColors: e.target.checked })}
                sx={{ width: 16, height: 16, accentColor: appearanceAccent, cursor: 'pointer' }}
              />
            </Box>

            {[
              ['Sayfa Arka Plan', 'pageBg'],
              ['Üst Bar Rengi', 'toolbarBg'],
              ['Vurgu Rengi', 'accentColor'],
            ].map(([label, key]) => {
              const colorValue = safeHexColor(appearanceSettings[key], DEFAULT_APPEARANCE[key]);
              return (
                <Box key={key} sx={{ display: 'grid', gridTemplateColumns: '1fr 44px 82px', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: darkMode ? '#cbd5e1' : '#4b5563' }}>
                    {label}
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={colorValue}
                    onChange={(e) => updateAppearance({ [key]: e.target.value })}
                    sx={{
                      width: 38,
                      height: 28,
                      p: 0,
                      border: `1px solid ${darkMode ? alpha('#fff', 0.14) : alpha('#000', 0.12)}`,
                      borderRadius: 1,
                      bgcolor: 'transparent',
                      cursor: 'pointer',
                    }}
                  />
                  <TextField
                    size="small"
                    value={appearanceSettings[key]}
                    onChange={(e) => updateAppearance({ [key]: e.target.value })}
                    inputProps={{ maxLength: 7 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 28,
                        fontSize: '0.68rem',
                        bgcolor: darkMode ? alpha('#fff', 0.04) : '#fff',
                      },
                    }}
                  />
                </Box>
              );
            })}

            <Box sx={{ mt: 1.2, px: 1, py: 1, borderRadius: 1.5, bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#000', 0.025) }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: darkMode ? '#cbd5e1' : '#4b5563' }}>
                  Yazı / Ekran Ölçeği
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: appearanceAccent }}>
                  %{viewScale}
                </Typography>
              </Box>
              <Box
                component="input"
                type="range"
                min={85}
                max={120}
                step={5}
                value={viewScale}
                onChange={(e) => updateAppearance({ viewScale: Number(e.target.value) })}
                sx={{ width: '100%', accentColor: appearanceAccent, cursor: 'pointer' }}
              />
            </Box>
          </Popover>
          <Tooltip title="Ayarlar" arrow>
            <Chip
              icon={<SettingsIcon sx={{ fontSize: '16px !important', color: darkMode ? '#ffb74d' : '#e65100' }} />}
              label="Ayarlar"
              size="small"
              onClick={handleSettingsOpen}
              sx={{
                bgcolor: darkMode ? alpha('#ff9800', 0.15) : alpha('#e65100', 0.08), fontWeight: 700, fontSize: '0.72rem', height: 28,
                borderRadius: '14px', border: `1px solid ${darkMode ? alpha('#ff9800', 0.25) : alpha('#e65100', 0.2)}`,
                boxShadow: `0 2px 8px ${darkMode ? alpha('#ff9800', 0.12) : alpha('#e65100', 0.1)}`, cursor: 'pointer',
                '&:hover': { bgcolor: darkMode ? alpha('#ff9800', 0.25) : alpha('#e65100', 0.15), boxShadow: `0 4px 16px ${darkMode ? alpha('#ff9800', 0.2) : alpha('#e65100', 0.2)}` },
                transition: 'all 0.2s ease',
              }}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* ── Lejant + Durum Filtresi ── */}
      <Box sx={{
        display: { xs: 'none', sm: 'flex' }, gap: 1.5, px: 2.5, py: 0.75,
        borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.04) : alpha('#1565c0', 0.06)}`,
        alignItems: 'center', flexWrap: 'wrap',
        background: darkMode
          ? `linear-gradient(90deg, ${alpha('#1a1a2e', 0.95)} 0%, ${alpha('#1e1e32', 0.9)} 100%)`
          : `linear-gradient(90deg, rgba(255,255,255,0.98) 0%, rgba(248,250,255,0.95) 100%)`,
      }}>
        {[
          { bg: '#B5D4F4', border: '#90CAF9', label: 'Tamamlandı', textColor: '#0C447C', glow: '#90CAF9', filterKey: 'completed' },
          { bg: '#FAC775', border: '#E6B85C', label: 'Yolda', textColor: '#633806', glow: '#E6B85C', filterKey: 'road' },
          { bg: '#C0DD97', border: '#A5D6A7', label: 'Başladı', textColor: '#27500A', glow: '#A5D6A7', filterKey: 'started' },
          { bg: '#D3D1C7', border: '#BDBDBD', label: 'Beklemede', textColor: '#444441', glow: '#BDBDBD', filterKey: 'pending' },
          { bg: '#F7C1C1', border: '#E24B4A', label: 'Geciken', textColor: '#791F1F', glow: '#E24B4A', filterKey: 'delayed' },
        ].map((item) => {
          const isFilterable = item.filterKey != null;
          const isActive = isFilterable && statusFilter.includes(item.filterKey);
          return (
            <Box
              key={item.label}
              onClick={isFilterable ? () => setStatusFilter(prev => prev.includes(item.filterKey) ? prev.filter(x => x !== item.filterKey) : [...prev, item.filterKey]) : undefined}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.35, borderRadius: '12px',
                bgcolor: isActive ? alpha(item.bg, darkMode ? 0.45 : 0.7) : alpha(item.bg, darkMode ? 0.15 : 0.4),
                border: isActive ? `2px solid ${alpha(item.border, 0.8)}` : `1px solid ${alpha(item.border, darkMode ? 0.25 : 0.35)}`,
                cursor: isFilterable ? 'pointer' : 'default',
                userSelect: 'none',
                transition: 'all 0.2s ease',
                boxShadow: isActive ? `0 2px 10px ${alpha(item.glow, 0.45)}` : 'none',
                opacity: statusFilter.length > 0 && isFilterable && !isActive ? 0.5 : 1,
                '&:hover': {
                  boxShadow: `0 2px 12px ${alpha(item.glow, 0.3)}`,
                  transform: isFilterable ? 'translateY(-1px)' : 'none',
                },
              }}
            >
              <Box sx={{
                width: 16, height: 10, borderRadius: '3px',
                background: `linear-gradient(135deg, ${item.bg} 0%, ${item.border} 100%)`,
                border: `1px solid ${alpha(item.border, 0.5)}`,
                boxShadow: `0 1px 4px ${alpha(item.glow, 0.25)}`,
              }} />
              <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: darkMode ? '#ccc' : '#555', letterSpacing: '0.02em' }}>{item.icon ? `${item.icon} ` : ''}{item.label}</Typography>
            </Box>
          );
        })}
        {statusFilter.length > 0 && (
          <Tooltip title="Aktif durum filtresini kaldır ve tüm işleri göster" arrow>
            <Chip
              label="Filtreyi Kaldır"
              size="small"
              onClick={() => setStatusFilter([])}
              sx={{
                height: 20, fontSize: '0.55rem', fontWeight: 700,
                bgcolor: alpha('#f44336', 0.08), color: '#f44336',
                border: `1px solid ${alpha('#f44336', 0.2)}`,
                cursor: 'pointer',
                '&:hover': { bgcolor: alpha('#f44336', 0.15) },
              }}
            />
          </Tooltip>
        )}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75, alignItems: 'center' }}>
          <Chip icon={<PersonIcon sx={{ fontSize: '14px !important' }} />} label={`${summary.total} Teknisyen`} size="small"
            sx={{ bgcolor: alpha('#1565c0', 0.1), fontWeight: 700, fontSize: '0.72rem', height: 24, borderRadius: '14px', border: `1px solid ${alpha('#1565c0', 0.15)}`, boxShadow: `0 2px 8px ${alpha('#1565c0', 0.1)}` }} />
          <Chip icon={<AssignmentIcon sx={{ fontSize: '14px !important', color: '#7c4dff' }} />} label={`${summary.totalJobs} İş Emri`} size="small"
            sx={{ bgcolor: alpha('#7c4dff', 0.1), fontWeight: 700, fontSize: '0.72rem', height: 24, borderRadius: '14px', border: `1px solid ${alpha('#7c4dff', 0.15)}`, boxShadow: `0 2px 8px ${alpha('#7c4dff', 0.1)}` }} />
        </Box>
      </Box>

      {/* ── Loading / Error ── */}
      {loading && data.length === 0 && (
        <Box sx={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10, flexDirection: 'column', gap: 2,
          position: 'relative',
        }}>
          <Box sx={{
            position: 'relative',
            width: 64, height: 64,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Box sx={{
              position: 'absolute', inset: -8,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha('#448AFF', 0.15)} 0%, transparent 70%)`,
              animation: 'loadPulse 2s ease-in-out infinite',
              '@keyframes loadPulse': {
                '0%, 100%': { transform: 'scale(0.8)', opacity: 0.5 },
                '50%': { transform: 'scale(1.3)', opacity: 1 },
              },
            }} />
            <CircularProgress size={40} thickness={3} sx={{ color: '#448AFF' }} />
          </Box>
          <Typography sx={{
            fontSize: '0.9rem', fontWeight: 600, letterSpacing: '0.02em',
            background: darkMode
              ? 'linear-gradient(90deg, #90caf9, #b388ff, #90caf9)'
              : 'linear-gradient(90deg, #1565c0, #448AFF, #1565c0)',
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'headerShimmer 3s linear infinite',
          }}>Teknisyen verileri yükleniyor...</Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography sx={{ color: '#f44336', fontSize: '0.85rem', fontWeight: 500 }}>{error}</Typography>
        </Box>
      )}

      {/* ── Gantt Chart ── */}
      {(!loading || data.length > 0) && (
        <Box ref={ganttWrapperRef} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
          {/* Loading overlay — tarih değiştiğinde eski veri üstüne */}
          {loading && data.length > 0 && (
            <Box sx={{
              position: 'absolute', inset: 0, zIndex: 100,
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              bgcolor: darkMode ? 'rgba(24,28,36,0.75)' : 'rgba(255,255,255,0.75)',
              backdropFilter: 'blur(2px)',
              borderRadius: 1,
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={36} thickness={3} sx={{ color: '#448AFF' }} />
                <Typography sx={{
                  fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.02em',
                  color: darkMode ? '#90caf9' : '#1565c0',
                }}>Veriler yükleniyor...</Typography>
              </Box>
            </Box>
          )}
          {/* Full-height mouse hover saat çizgisi */}
          <Box ref={hoverLineRef} sx={{ position: 'absolute', top: 0, bottom: 0, zIndex: 50, pointerEvents: 'none', display: 'none' }}>
            <Box sx={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: '2px',
              bgcolor: darkMode ? '#42a5f5' : '#1565c0',
              boxShadow: `0 0 8px ${darkMode ? 'rgba(66,165,245,0.5)' : 'rgba(21,101,192,0.4)'}`,
            }} />
            <Box data-hover-label sx={{
              position: 'absolute', left: -24, top: 4,
              px: 1, py: 0.4, borderRadius: '6px',
              bgcolor: darkMode ? alpha('#1565c0', 0.95) : '#1565c0',
              color: '#fff', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap',
              boxShadow: `0 2px 8px ${alpha('#1565c0', 0.4)}`,
              letterSpacing: '0.04em',
            }} />
          </Box>

          {/* Sabit saat başlığı */}
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            {/* Sol köşe */}
            <Box
              sx={{
                minWidth: resColWidth,
                maxWidth: resColWidth,
                background: darkMode
                  ? 'linear-gradient(180deg, #1a1a2e 0%, #16162a 100%)'
                  : 'linear-gradient(180deg, #e8edf8 0%, #dce4f5 100%)',
                borderBottom: `2px solid ${darkMode ? alpha('#7c4dff', 0.3) : alpha('#1565c0', 0.2)}`,
                borderRight: `1px solid ${darkMode ? alpha('#7c4dff', 0.12) : alpha('#1565c0', 0.08)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: resourcePanelCollapsed ? 'center' : 'flex-start',
                px: resourcePanelCollapsed ? 0 : 1.5,
                py: 0.5,
                transition: 'all 0.2s ease',
              }}
            >
              <PersonIcon sx={{ fontSize: 15, mr: resourcePanelCollapsed ? 0 : 0.5, color: darkMode ? '#b388ff' : '#1565c0', filter: darkMode ? 'drop-shadow(0 0 4px rgba(179,136,255,0.4))' : 'none' }} />
              {!resourcePanelCollapsed && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: darkMode ? '#b388ff' : '#1565c0', letterSpacing: '0.05em', textTransform: 'uppercase' }}>

                  </Typography>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25 }}>
                    <Tooltip title={techGroupsEnabled ? 'Gruplamayı Kapat' : 'Grupla'} arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setTechGroupsEnabled(p => !p); }}
                        sx={{
                          p: 0.3,
                          color: techGroupsEnabled ? (darkMode ? '#b388ff' : '#1565c0') : (darkMode ? '#666' : '#aaa'),
                          bgcolor: techGroupsEnabled ? alpha(darkMode ? '#b388ff' : '#1565c0', 0.12) : 'transparent',
                          '&:hover': { bgcolor: alpha(darkMode ? '#b388ff' : '#1565c0', 0.18) },
                        }}
                      >
                        <AccountTreeIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Grup Yönetimi" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setTechGroupDialogOpen(true); setTechGroupEditing(null); techGroupNewNameRef.current = ''; }}
                        sx={{ p: 0.3, color: darkMode ? '#666' : '#aaa', '&:hover': { bgcolor: alpha(darkMode ? '#b388ff' : '#1565c0', 0.18), color: darkMode ? '#b388ff' : '#1565c0' } }}
                      >
                        <CreateNewFolderIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Saat başlıkları */}
            <Box
              onMouseMove={(e) => {
                const el = hoverLineRef.current;
                const wrapper = ganttWrapperRef.current;
                if (!el || !scrollRef.current || !wrapper) return;
                const scrollContainer = scrollRef.current;
                const scrollLeft = scrollContainer.scrollLeft;
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left + scrollLeft;
                const pct = (x / timelineWidth) * 100;
                const mins = Math.round(HOUR_START * 60 + (x / timelineWidth) * TOTAL_HOURS * 60);
                if (pct >= 0 && pct <= 100) {
                  const wrapperLeft = wrapper.getBoundingClientRect().left;
                  el.style.display = 'block';
                  el.style.left = (e.clientX - wrapperLeft) + 'px';
                  const label = el.querySelector('[data-hover-label]');
                  if (label) label.textContent = formatMinutes(mins);
                } else {
                  el.style.display = 'none';
                }
              }}
              onMouseLeave={() => {
                const el = hoverLineRef.current;
                if (el) el.style.display = 'none';
              }}
              sx={{
                flex: '1 1 0', overflow: 'hidden', cursor: 'crosshair',
                background: darkMode
                  ? 'linear-gradient(180deg, #1a1a2e 0%, #16162a 100%)'
                  : 'linear-gradient(180deg, #e8edf8 0%, #dce4f5 100%)',
                borderBottom: `2px solid ${darkMode ? alpha('#7c4dff', 0.3) : alpha('#1565c0', 0.2)}`,
              }}>
              <Box ref={headerScrollRef} sx={{ width: timelineWidth, display: 'flex', position: 'relative', willChange: 'transform' }}>
                {Array.from({ length: TOTAL_HOURS }, (_, i) => {
                  const hour = i + HOUR_START;
                  const displayHour = hour % 24;
                  const isNextDay = hour >= 24;
                  const isCurrentHour = isSelectedToday && nowMinutes >= hour * 60 && nowMinutes < (hour + 1) * 60;
                  const isWorkHour = !isNextDay && hour * 60 >= WORK_START_MIN && hour * 60 < WORK_END_MIN;
                  const isMidnight = hour === 24;
                  const isFirst = i === 0;
                  const isLast = i === TOTAL_HOURS - 1;
                  return (
                    <Box
                      key={hour}
                      sx={{
                        width: hourWidth,
                        minWidth: hourWidth,
                        py: 0.6,
                        position: 'relative',
                        borderLeft: (isFirst || isMidnight) ? `1.5px solid ${isMidnight ? (darkMode ? '#ff9800' : '#e65100') : (darkMode ? alpha('#90caf9', 0.25) : alpha('#1565c0', 0.2))}` : 'none',
                        borderRight: `1.5px solid ${darkMode ? alpha('#90caf9', 0.25) : alpha('#1565c0', 0.2)}`,
                        bgcolor: isCurrentHour
                          ? alpha(COLORS.nowLine, 0.08)
                          : isNextDay
                            ? (darkMode ? 'rgba(0,0,0,0.18)' : 'rgba(240,240,248,0.9)')
                            : !isWorkHour
                              ? (darkMode ? 'rgba(0,0,0,0.12)' : 'rgba(245,245,245,0.8)')
                              : 'transparent',
                        backgroundImage: !isCurrentHour && (!isWorkHour || isNextDay)
                          ? (darkMode
                            ? `repeating-linear-gradient(0deg, transparent, transparent 9px, rgba(255,255,255,0.06) 9px, rgba(255,255,255,0.06) 10px), repeating-linear-gradient(90deg, transparent, transparent 9px, rgba(255,255,255,0.06) 9px, rgba(255,255,255,0.06) 10px)`
                            : `repeating-linear-gradient(0deg, transparent, transparent 9px, rgba(0,0,0,0.06) 9px, rgba(0,0,0,0.06) 10px), repeating-linear-gradient(90deg, transparent, transparent 9px, rgba(0,0,0,0.06) 9px, rgba(0,0,0,0.06) 10px)`)
                          : 'none',
                        '&::after': !isLast ? {
                          content: '""',
                          position: 'absolute',
                          right: -1,
                          top: '20%',
                          bottom: '20%',
                          width: '1.5px',
                          bgcolor: darkMode ? alpha('#90caf9', 0.35) : alpha('#1565c0', 0.3),
                          borderRadius: '1px',
                          zIndex: 1,
                        } : {},
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.72rem',
                          fontWeight: isCurrentHour ? 800 : isWorkHour ? 700 : 400,
                          color: isCurrentHour
                            ? COLORS.nowLine
                            : isWorkHour
                              ? (darkMode ? '#e0e0e0' : '#333')
                              : (darkMode ? '#555' : '#aaa'),
                          letterSpacing: '0.04em',
                          position: 'relative',
                          left: '-50%',
                          textAlign: 'center',
                          pointerEvents: 'none',
                        }}
                      >
                        {String(displayHour).padStart(2, '0')}:00{isNextDay ? ' ⁺¹' : ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Gövde */}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Sol kaynak sütunu */}
            <Box
              ref={resourceScrollRef}
              sx={{
                minWidth: resColWidth,
                maxWidth: resColWidth,
                overflowY: 'hidden',
                overflowX: 'hidden',
                borderRight: `1px solid ${darkMode ? alpha('#fff', 0.06) : '#e0e0e0'}`,
                transition: 'min-width 0.2s ease, max-width 0.2s ease',
              }}
            >
              {/* Bucket satırları */}
              {bucketTechs.map((tech, idx) => (
                <Box
                  key={tech.TechCode || `bucket-${idx}`}
                  onClick={() => { if (resourcePanelCollapsed) setBucketExpanded(prev => !prev); else handleTechRowClick(tech.TechCode); }}
                  sx={{
                    height: rowHeight,
                    display: 'flex',
                    alignItems: 'center',
                    px: resourcePanelCollapsed ? 0.5 : 1,
                    justifyContent: resourcePanelCollapsed ? 'center' : 'flex-start',
                    gap: resourcePanelCollapsed ? 0 : 0.75,
                    bgcolor: idx % 2 === 0 ? (darkMode ? COLORS.rowEvenDark : COLORS.rowEven) : (darkMode ? COLORS.rowOddDark : COLORS.rowOdd),
                    borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : '#f0f0f0'}`,
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: darkMode ? COLORS.rowHoverDark : COLORS.rowHover },
                  }}
                >
                  {resourcePanelCollapsed ? (
                    <Tooltip title={`${tech.UserTechCode || tech.TechCode} ${tech.TechName || 'Bucket'}`} arrow placement="right">
                      <InboxIcon sx={{ fontSize: 14, color: darkMode ? '#ffb74d' : '#f57c00' }} />
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip title={bucketExpanded ? 'Teknisyen listesini gizle' : 'Teknisyen listesini göster'} arrow>
                        <Box
                          onClick={(e) => { e.stopPropagation(); setBucketExpanded(prev => !prev); }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 0.25,
                            borderRadius: '4px',
                            flexShrink: 0,
                            '&:hover': { bgcolor: darkMode ? alpha('#ffb74d', 0.18) : alpha('#f57c00', 0.12) },
                          }}
                        >
                          {bucketExpanded ? <ExpandLessIcon sx={{ fontSize: 14, color: darkMode ? '#ffb74d' : '#f57c00' }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: darkMode ? '#ffb74d' : '#f57c00' }} />}
                        </Box>
                      </Tooltip>
                      <InboxIcon sx={{ fontSize: 14, color: darkMode ? '#ffb74d' : '#f57c00', flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                        <Tooltip title="İş taşıma için tıklayın" arrow>
                          <Typography noWrap sx={{ fontSize: '0.62rem', fontWeight: 800, color: darkMode ? '#ffb74d' : '#e65100', lineHeight: 1.1 }}>
                            {tech.UserTechCode || tech.TechCode} {tech.TechName || 'Bucket'}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </>
                  )}
                </Box>
              ))}
              {/* Teknisyen satırları */}
              {bucketExpanded && (groupedDisplayRows || nonBucketTechs.map(t => ({ type: 'tech', tech: t }))).map((row, rIdx) => {
                // ── Grup Başlığı ──
                if (row.type === 'group-header') {
                  const g = row.group;
                  const isUngrouped = g.Id === 'ungrouped';
                  const groupColor = isUngrouped ? (darkMode ? '#78909c' : '#607d8b') : (darkMode ? '#b388ff' : '#5c6bc0');
                  if (resourcePanelCollapsed) {
                    return (
                      <Tooltip key={`gh-${g.Id}`} title={`${g.GroupName} (${row.techCount})`} arrow placement="right">
                        <Box
                          onClick={() => setTechGroupsExpanded(p => ({ ...p, [g.Id]: !row.isExpanded }))}
                          sx={{
                            height: rowHeight, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: darkMode ? alpha(groupColor, 0.08) : alpha(groupColor, 0.04),
                            borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha(groupColor, 0.15)}`,
                            borderLeft: `3px solid ${groupColor}`,
                            cursor: 'pointer',
                          }}
                        >
                          {row.isExpanded ? <FolderOpenIcon sx={{ fontSize: 14, color: groupColor }} /> : <FolderIcon sx={{ fontSize: 14, color: groupColor }} />}
                        </Box>
                      </Tooltip>
                    );
                  }
                  return (
                    <Box
                      key={`gh-${g.Id}`}
                      onClick={() => setTechGroupsExpanded(p => ({ ...p, [g.Id]: !row.isExpanded }))}
                      sx={{
                        height: rowHeight, display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75,
                        bgcolor: darkMode ? alpha(groupColor, 0.08) : alpha(groupColor, 0.04),
                        borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha(groupColor, 0.15)}`,
                        borderLeft: `3px solid ${groupColor}`,
                        cursor: 'pointer', userSelect: 'none',
                        '&:hover': { bgcolor: darkMode ? alpha(groupColor, 0.14) : alpha(groupColor, 0.08) },
                        transition: 'background 0.15s',
                      }}
                    >
                      {row.isExpanded ? <ExpandLessIcon sx={{ fontSize: 14, color: groupColor, flexShrink: 0 }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: groupColor, flexShrink: 0 }} />}
                      {row.isExpanded ? <FolderOpenIcon sx={{ fontSize: 14, color: groupColor, flexShrink: 0 }} /> : <FolderIcon sx={{ fontSize: 14, color: groupColor, flexShrink: 0 }} />}
                      <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                        <Typography noWrap sx={{ fontSize: '0.68rem', fontWeight: 800, color: groupColor, lineHeight: 1.2 }}>
                          {g.GroupName}
                        </Typography>
                      </Box>
                      <Chip label={`${row.techCount}`} size="small" sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha(groupColor, 0.15), color: groupColor, minWidth: 0 }} />
                      <Typography sx={{ fontSize: '0.56rem', color: darkMode ? '#777' : '#999', fontWeight: 600 }}>{row.jobCount}iş</Typography>
                    </Box>
                  );
                }
                // ── Teknisyen Satırı ──
                const tech = row.tech;
                const idx = rIdx;
                const statusColor = getStatusColor(tech.StatusDescription);
                const hasBucketParent = bucketTechs.length > 0;
                // Utilization calculation — use API data if available, otherwise compute from job times
                const techWorkingMin = (parseTime(tech.TechWorkEnd || WORK_END) || WORK_END_MIN) - (parseTime(tech.TechWorkStart || WORK_START) || WORK_START_MIN);
                let busyMin = tech.totalWork + tech.totalTravel;
                if (busyMin === 0) {
                  tech.jobs.forEach(j => {
                    const dep = parseTime(j.DepartureTime);
                    const comp = parseTime(j.TaskCompletionTime);
                    const arr = parseTime(j.ArrivalatCustomer);
                    if (dep != null && comp != null && comp > dep) busyMin += (comp - dep);
                    else if (arr != null && comp != null && comp > arr) busyMin += (comp - arr);
                  });
                }
                const utilization = techWorkingMin > 0 ? Math.min(100, Math.round((busyMin / techWorkingMin) * 100)) : 0;
                const utilizationColor = darkMode ? '#b0b0b0' : '#90a4ae';
                const techSummaryText = getTechWorkSummaryText(tech.jobs);

                // Collapsed mode: just show avatar
                if (resourcePanelCollapsed) {
                  return (
                    <Tooltip key={tech.TechCode || idx} arrow placement="right" title={`${tech.TechName || ''} · ${techSummaryText} · %${utilization}`}>
                      <Box
                        onClick={() => handleTechRowClick(tech.TechCode)}
                        sx={{
                          height: rowHeight, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: dragOverTech === tech.TechCode ? alpha('#1565c0', 0.08) : idx % 2 === 0 ? (darkMode ? COLORS.rowEvenDark : COLORS.rowEven) : (darkMode ? COLORS.rowOddDark : COLORS.rowOdd),
                          borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : '#f0f0f0'}`,
                          cursor: 'pointer', transition: 'all 0.15s', position: 'relative',
                          '&:hover': { bgcolor: darkMode ? COLORS.rowHoverDark : COLORS.rowHover },
                        }}
                      >
                        <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: AVATAR_COLORS[idx % AVATAR_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 800, color: '#444', border: `2px solid ${alpha(utilizationColor, 0.5)}` }}>
                          {(tech.TechName || '').substring(0, 1).toUpperCase()}
                        </Box>
                        {tech.hasDelayRisk && <Box sx={{ position: 'absolute', top: 2, right: 2, width: 6, height: 6, borderRadius: '50%', bgcolor: '#E24B4A', animation: 'riskBlink 1.2s ease-in-out infinite' }} />}
                      </Box>
                    </Tooltip>
                  );
                }

                return (
                  <Tooltip
                    key={tech.TechCode || idx}
                    arrow
                    placement="right"
                    enterDelay={400}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: darkMode ? 'rgba(30,30,40,0.96)' : '#fff',
                          color: darkMode ? '#fff' : '#333',
                          borderRadius: '8px',
                          boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.12)',
                          border: `1px solid ${darkMode ? alpha('#fff', 0.08) : '#e0e0e0'}`,
                          px: 1.5,
                          py: 1,
                          maxWidth: 260,
                          '& .MuiTooltip-arrow': { color: darkMode ? 'rgba(30,30,40,0.96)' : '#fff' },
                        },
                      },
                    }}
                    title={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: AVATAR_COLORS[idx % AVATAR_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#444' }}>
                            {(tech.TechName || '').substring(0, 2).toUpperCase()}
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: darkMode ? '#e0e0e0' : '#212121', lineHeight: 1.2 }}>
                              {toTitleCase(tech.TechName)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.62rem', color: darkMode ? '#aaa' : '#888', lineHeight: 1.2 }}>
                              {tech.UserTechCode || tech.TechCode}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ height: '1px', bgcolor: darkMode ? alpha('#fff', 0.08) : '#eee', mb: 0.75 }} />
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <AssignmentIcon sx={{ fontSize: 13, color: darkMode ? '#90caf9' : '#1565c0' }} />
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: darkMode ? '#ccc' : '#555' }}>{techSummaryText}</Typography>
                          </Box>
                          {tech.totalWork > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <BuildIcon sx={{ fontSize: 13, color: COLORS.work }} />
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: COLORS.work }}>{formatDuration(tech.totalWork)}</Typography>
                            </Box>
                          )}
                          {tech.totalTravel > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <DirectionsCarIcon sx={{ fontSize: 13, color: COLORS.travel }} />
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: COLORS.travel }}>{formatDuration(tech.totalTravel)}</Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5 }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: statusColor }} />
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 500, color: darkMode ? '#aaa' : '#888' }}>{tech.StatusDescription || 'Bilinmiyor'}</Typography>
                        </Box>
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        height: rowHeight,
                        display: 'flex',
                        alignItems: 'center',
                        pl: hasBucketParent ? 2.5 : 1,
                        pr: 1,
                        gap: 0.75,
                        bgcolor: dragOverTech === tech.TechCode
                          ? (darkMode ? alpha('#1565c0', 0.15) : alpha('#1565c0', 0.08))
                          : idx % 2 === 0 ? (darkMode ? COLORS.rowEvenDark : COLORS.rowEven) : (darkMode ? COLORS.rowOddDark : COLORS.rowOdd),
                        borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : '#f0f0f0'}`,
                        borderLeft: hasBucketParent ? `2px solid ${darkMode ? alpha('#1565c0', 0.15) : alpha('#1565c0', 0.1)}` : undefined,
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        '&:hover': { bgcolor: darkMode ? COLORS.rowHoverDark : COLORS.rowHover },
                      }}
                      onClick={() => handleTechRowClick(tech.TechCode)}
                    >

                      {!isMobile && (
                        <Tooltip title="Haritada Göster" arrow placement="right">
                          <Box
                            onClick={(e) => handleTechMapClick(tech, e)}
                            sx={{
                              width: 18, height: 18, borderRadius: '4px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: darkMode ? alpha('#4caf50', 0.12) : alpha('#4caf50', 0.08),
                              border: `1px solid ${alpha('#4caf50', 0.2)}`,
                              cursor: 'pointer',
                              flexShrink: 0,
                              transition: 'all 0.15s',
                              '&:hover': {
                                bgcolor: alpha('#4caf50', 0.2),
                                transform: 'scale(1.15)',
                              },
                            }}
                          >
                            <MapIcon sx={{ fontSize: 11, color: '#4caf50' }} />
                          </Box>
                        </Tooltip>
                      )}
                      <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography noWrap sx={{ fontSize: '0.72rem', fontWeight: 600, color: darkMode ? '#e0e0e0' : '#212121', lineHeight: 1.2 }}>
                          {toTitleCase(tech.TechName)}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: '0.72rem', fontWeight: 500, color: darkMode ? '#777' : '#9CA3AF', lineHeight: 1.2, flexShrink: 0 }}>
                          · {techSummaryText}
                        </Typography>
                      </Box>
                      {/* Gecikme riski */}
                      {tech.hasDelayRisk && (
                        <Tooltip title="Gecikme riski var" arrow placement="right">
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: alpha('#E24B4A', 0.1),
                            border: `1px solid ${alpha('#E24B4A', 0.35)}`,
                            flexShrink: 0,
                            animation: 'riskBlink 1.2s ease-in-out infinite',
                            '@keyframes riskBlink': {
                              '0%, 100%': { opacity: 1, boxShadow: `0 0 3px ${alpha('#E24B4A', 0.25)}` },
                              '50%': { opacity: 0.5, boxShadow: `0 0 6px ${alpha('#E24B4A', 0.5)}` },
                            },
                          }}>
                            <WarningAmberIcon sx={{ fontSize: 10, color: '#E24B4A' }} />
                          </Box>
                        </Tooltip>
                      )}
                      {/* Utilization badge */}
                      {!isMobile && (
                        <Tooltip title={`Doluluk: %${utilization}`} arrow placement="left">
                          <Box sx={{
                            position: 'relative', width: 26, height: 26, flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {/* Background circle */}
                            <Box sx={{
                              position: 'absolute', inset: 0, borderRadius: '50%',
                              background: `conic-gradient(${utilizationColor} ${utilization * 3.6}deg, ${darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08)} ${utilization * 3.6}deg)`,
                            }} />
                            {/* Inner circle */}
                            <Box sx={{
                              position: 'relative', width: 22, height: 22, borderRadius: '50%',
                              bgcolor: idx % 2 === 0 ? (darkMode ? COLORS.rowEvenDark : COLORS.rowEven) : (darkMode ? COLORS.rowOddDark : COLORS.rowOdd),
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Typography sx={{ fontSize: '0.80rem !important', fontWeight: 800, color: utilizationColor, lineHeight: 1, letterSpacing: '-0.03em', minWidth: 0, p: 0 }}>
                                {utilization}
                              </Typography>
                            </Box>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>

            {/* Sağ zaman çizelgesi */}
            <Box ref={scrollRef} onScroll={handleBodyScroll} sx={{ flex: '1 1 0', overflow: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}>
              <Box
                ref={timelineContainerRef}
                sx={{ width: timelineWidth, position: 'relative' }}
              >

                {/* Dikey saat çizgileri */}
                {Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => i).map((i) => {
                  const hour = i + HOUR_START;
                  const isMidnight = hour === 24;
                  return (
                    <Box key={i} sx={{
                      position: 'absolute', left: i * hourWidth, top: 0, bottom: 0,
                      width: isMidnight ? 2 : 1,
                      bgcolor: isMidnight
                        ? (darkMode ? '#ff9800' : '#e65100')
                        : (darkMode ? alpha('#fff', 0.03) : alpha('#000', 0.04)),
                      zIndex: isMidnight ? 10 : 0,
                      opacity: isMidnight ? 0.6 : 1,
                    }} />
                  );
                })}

                {/* Yarım saat çizgileri */}
                {Array.from({ length: TOTAL_HOURS }, (_, i) => i).map((i) => (
                  <Box key={`half-${i}`} sx={{ position: 'absolute', left: i * hourWidth + hourWidth / 2, top: 0, bottom: 0, width: 1, bgcolor: darkMode ? alpha('#fff', 0.015) : alpha('#000', 0.02), zIndex: 0, borderLeft: '1px dotted', borderColor: darkMode ? alpha('#fff', 0.03) : alpha('#000', 0.04) }} />
                ))}

                {/* Şu anki saat çizgisi */}
                {isSelectedToday && nowPercent >= 0 && nowPercent <= 100 && (
                  <Box sx={{ position: 'absolute', left: `${nowPercent}%`, top: 0, bottom: 0, zIndex: 15, pointerEvents: 'none' }}>
                    <Box sx={{
                      position: 'absolute', left: -1.5, top: 0, bottom: 0, width: 3,
                      background: `linear-gradient(180deg, ${COLORS.nowLine} 0%, ${alpha(COLORS.nowLine, 0.6)} 50%, ${COLORS.nowLine} 100%)`,
                      boxShadow: `0 0 12px ${alpha(COLORS.nowLine, 0.5)}, 0 0 4px ${alpha(COLORS.nowLine, 0.3)}`,
                      borderRadius: '2px',
                    }} />
                    <Box sx={{
                      position: 'absolute', left: -7, top: -2, width: 14, height: 14, borderRadius: '50%',
                      background: `radial-gradient(circle, ${COLORS.nowLine} 40%, ${alpha(COLORS.nowLine, 0.6)} 100%)`,
                      boxShadow: `0 0 16px ${alpha(COLORS.nowLine, 0.6)}, 0 0 6px ${alpha(COLORS.nowLine, 0.4)}`,
                      animation: 'nowDotPulse 2s ease-in-out infinite',
                      '@keyframes nowDotPulse': {
                        '0%, 100%': { transform: 'scale(1)', boxShadow: `0 0 12px ${alpha(COLORS.nowLine, 0.5)}` },
                        '50%': { transform: 'scale(1.3)', boxShadow: `0 0 24px ${alpha(COLORS.nowLine, 0.8)}` },
                      },
                    }} />
                    <Box sx={{
                      position: 'absolute', left: -20, top: -18,
                      px: 0.75, py: 0.25, borderRadius: '6px',
                      background: `linear-gradient(135deg, ${COLORS.nowLine} 0%, #d50000 100%)`,
                      color: '#fff', fontSize: '0.58rem', fontWeight: 800, whiteSpace: 'nowrap',
                      boxShadow: `0 3px 12px ${alpha(COLORS.nowLine, 0.5)}`,
                      letterSpacing: '0.04em',
                    }}>
                      {formatMinutes(nowMinutes)}
                    </Box>
                    {/* Glow trail */}
                    <Box sx={{
                      position: 'absolute', left: -8, top: 0, bottom: 0, width: 16,
                      background: `linear-gradient(90deg, transparent, ${alpha(COLORS.nowLine, 0.06)}, transparent)`,
                      pointerEvents: 'none',
                    }} />
                  </Box>
                )}



                {/* Teknisyen satırları */}
                {/* Bucket satırları (sağ taraf - drop aktif) */}
                {bucketTechs.map((tech, idx) => (
                  <Box
                    key={tech.TechCode || `bucket-${idx}`}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverTech(tech.TechCode); }}
                    onDragLeave={() => setDragOverTech(null)}
                    onDrop={(e) => handleDrop(e, tech.TechCode, tech.UserTechCode, tech.TechName, true)}
                    sx={{
                      height: rowHeight,
                      position: 'relative',
                      bgcolor: dragOverTech === tech.TechCode
                        ? (darkMode ? alpha('#ff9800', 0.2) : alpha('#ff9800', 0.12))
                        : idx % 2 === 0 ? (darkMode ? COLORS.rowEvenDark : COLORS.rowEven) : (darkMode ? COLORS.rowOddDark : COLORS.rowOdd),
                      borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : '#f0f0f0'}`,
                      border: dragOverTech === tech.TechCode ? `1px dashed ${alpha('#ff9800', 0.5)}` : undefined,
                      transition: 'background 0.15s, border 0.15s',
                    }}
                  >
                    {/* Mesai dışı sol */}
                    {tech.techWorkStartPct > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          width: `${tech.techWorkStartPct}%`,
                          top: 0,
                          bottom: 0,
                          bgcolor: darkMode ? alpha('#000', 0.35) : alpha('#b0bec5', 0.25),
                          backgroundImage: darkMode
                            ? 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(255,255,255,0.08) 3px, rgba(255,255,255,0.08) 4px)'
                            : 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 4px)',
                          zIndex: 1,
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                    {/* Mesai dışı sağ */}
                    {tech.techWorkEndPct < 100 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${tech.techWorkEndPct}%`,
                          right: 0,
                          top: 0,
                          bottom: 0,
                          bgcolor: darkMode ? alpha('#000', 0.35) : alpha('#b0bec5', 0.25),
                          backgroundImage: darkMode
                            ? 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(255,255,255,0.08) 3px, rgba(255,255,255,0.08) 4px)'
                            : 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 4px)',
                          zIndex: 1,
                          pointerEvents: 'none'
                        }}
                      />
                    )}
                    {/* Mesai başlangıç çizgi */}
                    <Box sx={{ position: 'absolute', left: `${tech.techWorkStartPct}%`, top: 0, bottom: 0, width: '2px', bgcolor: darkMode ? alpha('#42a5f5', 0.6) : alpha('#1565c0', 0.4), zIndex: 2, pointerEvents: 'none' }} />
                    {/* Mesai bitiş çizgi */}
                    <Box sx={{ position: 'absolute', left: `${tech.techWorkEndPct}%`, top: 0, bottom: 0, width: '2px', bgcolor: darkMode ? alpha('#ef5350', 0.6) : alpha('#e53935', 0.4), zIndex: 2, pointerEvents: 'none' }} />
                    {tech.jobs.map((job, ji) => (
                      <JobBar key={getJobRenderKey(tech.TechCode, job, ji)} job={job} nowMin={nowMinutes} isSelectedToday={isSelectedToday} techCode={tech.TechCode}
                        userTechCode={tech.UserTechCode} techName={tech.TechName} isFirst={ji === 0} />
                    ))}
                  </Box>
                ))}
                {/* Normal teknisyen satırları */}
                {bucketExpanded && (groupedDisplayRows || nonBucketTechs.map(t => ({ type: 'tech', tech: t }))).map((row, idx) => {
                  if (row.type === 'group-header') {
                    const g = row.group;
                    const isUngrouped = g.Id === 'ungrouped';
                    const groupColor = isUngrouped ? (darkMode ? '#78909c' : '#607d8b') : (darkMode ? '#b388ff' : '#5c6bc0');
                    return (
                      <Box key={`tgh-${g.Id}`} sx={{
                        height: rowHeight,
                        bgcolor: darkMode ? alpha(groupColor, 0.04) : alpha(groupColor, 0.02),
                        borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha(groupColor, 0.15)}`,
                        borderLeft: `3px solid ${groupColor}`,
                        display: 'flex', alignItems: 'center', pl: 1.5,
                      }}>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: alpha(groupColor, 0.5), letterSpacing: '0.04em' }}>
                          {g.GroupName} — {row.techCount} teknisyen · {row.jobCount} iş
                        </Typography>
                      </Box>
                    );
                  }
                  const tech = row.tech;
                  return (
                    <Box
                      key={tech.TechCode || idx}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverTech(tech.TechCode); }}
                      onDragLeave={() => setDragOverTech(null)}
                      onDrop={(e) => handleDrop(e, tech.TechCode, tech.UserTechCode, tech.TechName)}
                      sx={{
                        height: rowHeight,
                        position: 'relative',
                        bgcolor: dragOverTech === tech.TechCode
                          ? (darkMode ? alpha('#1565c0', 0.15) : alpha('#1565c0', 0.08))
                          : idx % 2 === 0 ? (darkMode ? COLORS.rowEvenDark : COLORS.rowEven) : (darkMode ? COLORS.rowOddDark : COLORS.rowOdd),
                        borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : '#f0f0f0'}`,
                        border: dragOverTech === tech.TechCode ? `1px dashed ${alpha('#1565c0', 0.5)}` : undefined,
                        '&:hover': { bgcolor: darkMode ? COLORS.rowHoverDark : COLORS.rowHover },
                        transition: 'background 0.15s, border 0.15s',
                      }}
                    >
                      {/* Mesai dışı sol */}
                      {tech.techWorkStartPct > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            width: `${tech.techWorkStartPct}%`,
                            top: 0,
                            bottom: 0,
                            bgcolor: darkMode ? alpha('#000', 0.35) : alpha('#b0bec5', 0.25),
                            backgroundImage: darkMode
                              ? 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(255,255,255,0.08) 3px, rgba(255,255,255,0.08) 4px)'
                              : 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 4px)',
                            zIndex: 1,
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      {/* Mesai dışı sağ */}
                      {tech.techWorkEndPct < 100 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${tech.techWorkEndPct}%`,
                            right: 0,
                            top: 0,
                            bottom: 0,
                            bgcolor: darkMode ? alpha('#000', 0.35) : alpha('#b0bec5', 0.25),
                            backgroundImage: darkMode
                              ? 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(255,255,255,0.08) 3px, rgba(255,255,255,0.08) 4px)'
                              : 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 4px)',
                            zIndex: 1,
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      {/* Mesai başlangıç çizgi */}
                      <Box sx={{ position: 'absolute', left: `${tech.techWorkStartPct}%`, top: 0, bottom: 0, width: '2px', bgcolor: darkMode ? alpha('#42a5f5', 0.6) : alpha('#1565c0', 0.4), zIndex: 2, pointerEvents: 'none' }} />
                      {/* Mesai bitiş çizgi */}
                      <Box sx={{ position: 'absolute', left: `${tech.techWorkEndPct}%`, top: 0, bottom: 0, width: '2px', bgcolor: darkMode ? alpha('#ef5350', 0.6) : alpha('#e53935', 0.4), zIndex: 2, pointerEvents: 'none' }} />
                      {tech.jobs.map((job, ji) => (
                        <JobBar key={getJobRenderKey(tech.TechCode, job, ji)} job={job} nowMin={nowMinutes} isSelectedToday={isSelectedToday} techCode={tech.TechCode} userTechCode={tech.UserTechCode} techName={tech.TechName} isFirst={ji === 0} />
                      ))}
                    </Box>
                  );
                })}

                {/* Boş durum */}
                {finalSorted.length === 0 && !loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10, flexDirection: 'column', gap: 2 }}>
                    <Box sx={{
                      width: 72, height: 72, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: darkMode
                        ? `linear-gradient(145deg, ${alpha('#448AFF', 0.1)} 0%, ${alpha('#7c4dff', 0.05)} 100%)`
                        : `linear-gradient(145deg, ${alpha('#1565c0', 0.06)} 0%, ${alpha('#42a5f5', 0.03)} 100%)`,
                      border: `2px dashed ${darkMode ? alpha('#448AFF', 0.2) : alpha('#1565c0', 0.15)}`,
                    }}>
                      <InboxIcon sx={{ fontSize: 32, color: darkMode ? alpha('#448AFF', 0.4) : alpha('#1565c0', 0.3) }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: darkMode ? '#666' : '#aaa' }}>
                      {search ? 'Arama sonucu bulunamadı' : 'Veri bulunamadı'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: darkMode ? '#555' : '#bbb' }}>
                      {search ? 'Farklı bir arama terimi deneyin' : 'Seçili tarih için kayıt yok'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Alt bilgi çubuğu ── */}
      {data.length > 0 && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          px: { xs: 1, sm: 2.5 },
          py: 0.5,
          borderTop: `1px solid ${darkMode ? alpha('#fff', 0.04) : alpha('#1565c0', 0.06)}`,
          background: darkMode
            ? alpha('#0f0f1a', 0.95)
            : alpha('#f8faff', 0.95),
          backdropFilter: 'blur(8px)',
          flexWrap: 'wrap',
          flexShrink: 0,
          minHeight: 28,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#00C853', boxShadow: '0 0 6px rgba(0,200,83,0.5)', animation: 'statusPulse 2s ease-in-out infinite', '@keyframes statusPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
            <Typography sx={{ fontSize: '0.6rem', color: darkMode ? '#777' : '#999', fontWeight: 500 }}>
              Aktif
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.6rem', color: darkMode ? '#555' : '#bbb' }}>|</Typography>
          <Typography sx={{ fontSize: '0.6rem', color: darkMode ? '#777' : '#999', fontWeight: 500 }}>
            {summary.total} teknisyen · {summary.totalJobs} iş emri
          </Typography>
          {lastUpdate && (
            <>
              <Typography sx={{ fontSize: '0.6rem', color: darkMode ? '#555' : '#bbb' }}>|</Typography>
              <Typography sx={{ fontSize: '0.6rem', color: darkMode ? '#666' : '#aaa', fontWeight: 500 }}>
                Son güncelleme: {lastUpdate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </>
          )}
          <Typography sx={{ fontSize: '0.6rem', color: darkMode ? '#555' : '#bbb' }}>|</Typography>
          <Typography sx={{ fontSize: '0.55rem', color: darkMode ? '#555' : '#bbb', fontWeight: 500, fontFamily: 'monospace' }}>
            ? kısayollar
          </Typography>
          <Box sx={{ ml: 'auto', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5 }}>
            {[
              { icon: <BuildIcon sx={{ fontSize: 11 }} />, label: formatDuration(summary.totalWork), color: COLORS.work },
              { icon: <DirectionsCarIcon sx={{ fontSize: 11 }} />, label: formatDuration(summary.totalTravel), color: COLORS.travel },
              { icon: <FreeBreakfastIcon sx={{ fontSize: 11 }} />, label: formatDuration(summary.totalIdle), color: COLORS.idle },
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <Box sx={{ color: item.color, display: 'flex' }}>{item.icon}</Box>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: item.color }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Teknisyen Detay Popup ── */}
      {techDetailOpen && (
        <DraggableResizablePopup
          open={techDetailOpen}
          onClose={() => { setTechDetailOpen(false); setTechWorkChecked([]); setTechDetailIsBucket(false); }}
          title={techInfo?.name && techInfo?.surname ? `${techInfo.name} ${techInfo.surname}` : techInfo?.userCode || techInfo?.code || 'Teknisyen Detayı'}
          subtitle={techInfo?.userCode || techInfo?.code || ''}
          icon={PersonSearchIcon}
          maxWidth="xl"
          primaryColor="#FF9800"
          headerActions={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={techPhoto ? 'Fotoğrafı büyüt' : ''} arrow>
                <Avatar
                  src={techPhoto}
                  onClick={(e) => { if (techPhoto) { e.stopPropagation(); setTechPhotoOpen(true); } }}
                  sx={{
                    width: 36, height: 36,
                    bgcolor: alpha('#FF9800', 0.10), color: '#E65100',
                    fontSize: '0.85rem', fontWeight: 700,
                    border: '2px solid', borderColor: alpha('#FF9800', 0.25),
                    cursor: techPhoto ? 'pointer' : 'default',
                    transition: 'transform 0.2s',
                    '&:hover': techPhoto ? { transform: 'scale(1.1)' } : {},
                  }}
                >
                  {techInfo?.name ? (techInfo.name[0] + (techInfo.surname?.[0] || '')).toUpperCase() : 'T'}
                </Avatar>
              </Tooltip>
              {techInfo?.expert && (
                <Chip label={techInfo.expert} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, maxWidth: 150, bgcolor: alpha('#FF9800', 0.1), color: '#E65100' }} />
              )}
              <Tooltip title="Sunucudan Yenile" arrow>
                <IconButton size="small" onClick={() => fetchTechDetail(techInfo?.code)} disabled={techDetailLoading}
                  sx={{ width: 28, height: 28, bgcolor: alpha('#FF9800', 0.08), color: '#E65100', '&:hover': { bgcolor: alpha('#FF9800', 0.16) } }}>
                  <RefreshIcon sx={{ fontSize: 16, animation: techDetailLoading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Excel'e Aktar" arrow>
                <IconButton size="small" disabled={techWorks.length === 0} onClick={handleExportTechWorks}
                  sx={{ width: 28, height: 28, bgcolor: alpha('#4CAF50', 0.08), color: '#2E7D32', '&:hover': { bgcolor: alpha('#4CAF50', 0.16) } }}>
                  <FileDownloadIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={`${visibleTechWorkServicePaperCount} fiş`} arrow>
                <Chip label={visibleTechWorkServicePaperCount} size="small" sx={{ height: 20, minWidth: 24, fontWeight: 800, fontSize: '0.7rem', bgcolor: alpha('#FF9800', 0.1), color: '#E65100' }} />
              </Tooltip>
            </Box>
          }
        >
          {techDetailLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
              <CircularProgress size={36} thickness={4} sx={{ color: '#FF9800' }} />
              <Typography variant="body2" color="text.secondary">Yükleniyor...</Typography>
            </Box>
          ) : (
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
              {techDetailError && <Alert severity="warning" sx={{ mx: 2, mt: 1, borderRadius: 2 }}>{techDetailError}</Alert>}
              <Box sx={{ px: 2.5, pt: 1.5, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon sx={{ fontSize: 18, color: '#FF9800', flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>Günlük İş Listesi</Typography>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.25,
                        ml: 0.5,
                        px: 0.4,
                        py: 0.15,
                        borderRadius: 1.5,
                        bgcolor: alpha('#1565c0', 0.04),
                        border: `1px solid ${alpha('#1565c0', 0.10)}`,
                      }}>
                        <Tooltip title="Önceki gün" arrow>
                          <IconButton onClick={goToPrevDay} disabled={loading} size="small" sx={{ p: 0.25, color: '#1565c0' }}>
                            <ChevronLeftIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Tarih seç" arrow>
                          <Box
                            onClick={(e) => setDatePickerAnchor(e.currentTarget)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.45,
                              px: 0.7,
                              py: 0.25,
                              borderRadius: 1.2,
                              cursor: 'pointer',
                              bgcolor: isToday(selectedDate) ? alpha('#1565c0', 0.08) : 'transparent',
                              '&:hover': { bgcolor: alpha('#1565c0', 0.10) },
                            }}
                          >
                            <CalendarMonthIcon sx={{ fontSize: 14, color: isToday(selectedDate) ? '#1565c0' : 'text.disabled' }} />
                            <Typography variant="caption" sx={{ lineHeight: 1.3, fontWeight: 800, color: '#374151', whiteSpace: 'nowrap' }}>
                              {dayjs(selectedDate).format('DD MMMM YYYY, dddd')}
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Tooltip title="Sonraki gün" arrow>
                          <IconButton onClick={goToNextDay} disabled={loading} size="small" sx={{ p: 0.25, color: '#1565c0' }}>
                            <ChevronRightIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        {!isToday(selectedDate) && (
                          <Tooltip title="Bugüne dön" arrow>
                            <IconButton onClick={goToToday} disabled={loading} size="small" sx={{ p: 0.25, color: '#1565c0' }}>
                              <TodayIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    {techWorks.length > 0 && (() => {
                      const realWorks = filteredTechWorks.filter(w => w.OfscNr);
                      const totalJobs = realWorks.length;
                      const totalServicePapers = visibleTechWorkServicePaperCount;
                      const totalWorkMin = filteredTechWorks.reduce((sum, work) => sum + getJobDisplayDurations(work).work, 0);
                      const totalTravelMin = filteredTechWorks.reduce((sum, work) => sum + getJobDisplayDurations(work).travel, 0);
                      const completedCount = realWorks.filter(w => normalizeStatusKey(w.StatusDescription) === 'completed').length;
                      const techWorkStart = filteredTechWorks[0]?.TechWorkStart || WORK_START;
                      const techWorkEnd = filteredTechWorks[0]?.TechWorkEnd || WORK_END;
                      const items = [
                        { label: `Toplam Fiş: ${totalServicePapers}`, short: `${totalServicePapers} fiş`, color: '#1565c0' },
                        { label: `İş Süresi: ${formatDuration(totalWorkMin)}`, short: formatDuration(totalWorkMin), color: '#2e7d32' },
                        { label: `Yol Süresi: ${formatDuration(totalTravelMin)}`, short: formatDuration(totalTravelMin), color: '#e65100' },
                        { label: `Tamamlanan: ${completedCount}/${totalJobs}`, short: `${completedCount}✓`, color: '#00695c' },
                        { label: `Mesai: ${techWorkStart}-${techWorkEnd}`, short: `${techWorkStart}-${techWorkEnd}`, color: '#6a1b9a' },
                      ];
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexWrap: 'wrap', mt: 0.25 }}>
                          {items.map((item, idx) => (
                            <React.Fragment key={item.label}>
                              {idx > 0 && <Box component="span" sx={{ fontSize: '0.55rem', color: 'text.disabled', lineHeight: 1 }}>·</Box>}
                              <Tooltip title={item.label} arrow placement="bottom">
                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: item.color, cursor: 'default', lineHeight: 1 }}>
                                  {item.short}
                                </Typography>
                              </Tooltip>
                            </React.Fragment>
                          ))}
                        </Box>
                      );
                    })()}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <TextField size="small" placeholder="Ara..." value={techWorkSearch} onChange={(e) => setTechWorkSearch(e.target.value)}
                      slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>, sx: { borderRadius: 2, fontSize: '0.75rem', height: 30 } } }}
                      sx={{ width: 180 }} />
                  </Box>
                </Box>
              </Box>
              <Divider />
              {techWorks.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
                  <ScheduleIcon sx={{ fontSize: 36, mb: 1 }} />
                  <Typography variant="body2">Seçili tarihe ait iş kaydı bulunamadı</Typography>
                </Box>
              ) : (
                <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 0, flex: 1, minHeight: 0 }}>
                  {/* İş tablosu */}
                  <Box sx={{ 
                    // Yukseklik sinirlanmazsa kapsayici icerikle buyuyor ve yatay kaydirma
                    // cubugu sayfanin en altinda kaliyor; sinirlayinca tablo kendi icinde
                    // kayiyor ve cubuk her zaman gorunur alanin altinda duruyor.
                    // Kolonlar artinca tablo kapsayiciya sigmiyor; tableLayout:fixed
                    // oldugu icin minWidth verilmezse kolonlar ezilir, kaydirma cubugu cikmaz.
                    overflowX: 'auto', overflowY: 'auto', flex: 1, mt: 0.5,
                    '&::-webkit-scrollbar': { height: 10, width: 10 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#1565c0', 0.35), borderRadius: 5 },
                    '&::-webkit-scrollbar-thumb:hover': { bgcolor: alpha('#1565c0', 0.55) },
                    '&::-webkit-scrollbar-track': { bgcolor: alpha('#000', 0.04) },
                  }}>
                    <Box component="table" sx={{
                      width: '100%',
                      minWidth: 1460,
                      borderCollapse: 'collapse',
                      tableLayout: 'fixed',
                      fontSize: '0.86rem',
                      '& th': {
                        px: '9px', py: '7px',
                        fontWeight: 800,
                        fontSize: '0.86rem',
                        color: '#444',
                        textAlign: 'left',
                        bgcolor: alpha('#1565c0', 0.05),
                        borderBottom: `2px solid ${alpha('#1565c0', 0.15)}`,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                      },
                      '& th *': {
                        fontSize: '0.86rem !important',
                      },
                      '& td': {
                        px: '9px', py: '6px',
                        fontSize: '0.86rem',
                        borderBottom: `1px solid ${alpha('#000', 0.06)}`,
                        overflow: 'hidden',
                        verticalAlign: 'middle',
                        whiteSpace: 'nowrap',
                      },
                      '& td *:not(svg)': {
                        fontSize: '0.86rem !important',
                      },
                      '& .MuiChip-root': {
                        height: 24,
                      },
                      '& .MuiChip-label': {
                        fontSize: '0.82rem !important',
                        fontWeight: '700 !important',
                      },
                      '& tbody tr:hover td': { bgcolor: alpha('#1565c0', 0.03) },
                    }}>
                      <colgroup>
                        <col style={{ width: 28 }} />
                        {/* Teknisyen kolonu yalnızca servis (bucket) görünümünde */}
                        {techDetailIsBucket && <col style={{ width: 130 }} />}
                        {/* Fiş — Teknisyen'den hemen sonra */}
                        <col style={{ width: 170 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 100 }} />
                        <col style={{ width: 95 }} />
                        <col style={{ width: 60 }} />
                        <col style={{ width: 60 }} />
                        <col style={{ width: 60 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 90 }} />
                        <col style={{ width: 100 }} />
                        <col style={{ width: 90 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 55 }} />
                        <col style={{ width: 55 }} />
                      </colgroup>
                      <Box component="thead">
                        <Box component="tr">
                          {(() => {
                            const handleSort = (field) => {
                              if (techSortField === field) {
                                setTechSortDir(d => d === 'asc' ? 'desc' : 'asc');
                              } else {
                                setTechSortField(field);
                                setTechSortDir('asc');
                              }
                            };
                            // eslint-disable-next-line react/prop-types
                            const SortTh = ({ field, align = 'left', children }) => {
                              const isActive = techSortField === field;
                              return (
                                <Box
                                  component="th"
                                  onClick={field ? () => handleSort(field) : undefined}
                                  sx={{
                                    textAlign: align,
                                    cursor: field ? 'pointer' : 'default',
                                    userSelect: 'none',
                                    '&:hover': field ? { bgcolor: `${alpha('#1565c0', 0.1)} !important` } : {},
                                  }}
                                >
                                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                                    {children}
                                    {field && (
                                      <Box component="span" sx={{ fontSize: '0.6rem', color: isActive ? '#1565c0' : '#ccc', fontWeight: 700 }}>
                                        {isActive ? (techSortDir === 'asc' ? '▲' : '▼') : '⇅'}
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              );
                            };
                            return (
                              <>
                                <Box component="th" sx={{ textAlign: 'center' }}>#</Box>
                                {techDetailIsBucket && <SortTh field="TechName">Teknisyen</SortTh>}
                                <SortTh field={null}>Fiş No</SortTh>
                                <SortTh field="StatusDescription">Rndv Dur.</SortTh>
                                <SortTh field="ServicePapersStatus">Fiş Dur.</SortTh>
                                <SortTh field="spType">Tip</SortTh>
                                <SortTh field="AppointmentTime">Randevu</SortTh>
                                <SortTh field="DepartureTime">Çıkış</SortTh>
                                <SortTh field="ArrivalatCustomer">Varış</SortTh>
                                <SortTh field="TaskCompletionTime">Bitiş</SortTh>
                                <SortTh field="Neighbourhood">Mahalle</SortTh>
                                <SortTh field="Products">Ürün</SortTh>
                                <SortTh field="CustomerName">Müşteri</SortTh>
                                <SortTh field="BayiName">Bayi</SortTh>
                                <SortTh field="AppointmentChannel">Randevu Kanalı</SortTh>
                                <SortTh field="TravelDuration" align="center">Yol</SortTh>
                                <SortTh field="WorkDuration" align="center">İş</SortTh>
                              </>
                            );
                          })()}
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {(() => {
                          let rowNum = 0;
                          return filteredTechWorks.map((work, i) => {
                            const ofscNr = work.OfscNr || 0;
                            if (ofscNr > 0) rowNum++;
                            const randevuStatusDesc = work.StatusDescription || 'pending';
                            const fisStatusDesc = work.ServicePapersStatus || '';
                            const neighbourhood = work.Neighbourhood || '-';
                            const products = work.Products || '';
                            const servicePapers = work.ServicePapers || '';
                            const apptTime = work.AppointmentTime || '-';
                            const apptTimeEnd = work.AppointmentTimeEnd || '';
                            const deptTime = work.DepartureTime || '-';
                            const arrivalTime = work.ArrivalatCustomer || '-';
                            const completionTime = work.TaskCompletionTime || '-';
                            const displayDurations = getJobDisplayDurations(work);
                            const workDur = displayDurations.work;
                            const travelDur = displayDurations.travel;
                            const scRandevu = getStatusColorObj(randevuStatusDesc);
                            const scFis = fisStatusDesc ? getStatusColorObj(fisStatusDesc) : null;

                            const spEntries = parseServicePaperEntries(servicePapers);
                            const spType = getWorkApplicationReason(work);
                            const bayiName = getWorkDealerName(work);
                            const customerName = getWorkCustomerName(work);
                            // Aynı adlı mahalle farklı il/ilçelerde bulunabildiği için il/ilçe de gösteriliyor.
                            // MERKEZ İLÇE'nin kodu 0 olabildiğinden adlar üzerinden çalışıyoruz.
                            const cityDistrict = [work.City, work.District].filter(Boolean).join(' / ');
                            const neighbourhoodGroup = String(work.NeighbourhoodGroup || '').trim();
                            // Randevu kanalı: randevunun hangi uygulamadan verildiği (Geliş Yeri'nden farklı).
                            const appointmentChannel = String(work.AppointmentChannel || '').trim();

                            return (
                              <Box component="tr" key={`${ofscNr}-${i}`}>
                                {/* # */}
                                <Box component="td" sx={{ textAlign: 'center', fontWeight: 700, color: '#aaa', fontSize: '0.65rem' }}>
                                  {ofscNr > 0 ? rowNum : ''}
                                </Box>
                                {/* Teknisyen — yalnızca servis (bucket) görünümünde */}
                                {techDetailIsBucket && (
                                  <Box component="td">
                                    <Tooltip title={work.TechName || 'Teknisyene atanmamış'} arrow>
                                      <Box component="span" sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: work.TechName ? '#1e293b' : '#94a3b8',
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}>
                                        {work.TechName || 'Atanmamış'}
                                      </Box>
                                    </Tooltip>
                                  </Box>
                                )}
                                {/* Fiş numaraları */}
                                <Box component="td" sx={{ minWidth: 180, whiteSpace: 'normal' }}>
                                  <Box sx={{ display: 'flex', gap: 0.35, flexWrap: 'wrap', overflow: 'visible', alignItems: 'center' }}>
                                    {spEntries.length > 0 ? spEntries.map((entry, idx) => (
                                      <Chip
                                        key={`${entry.fisNo || idx}`}
                                        label={entry.fisNo || '-'}
                                        size="small"
                                        clickable
                                        onClick={() => {
                                          if (!entry.fisNo) return;
                                          try {
                                            const bc = new BroadcastChannel('aron-fis-channel');
                                            bc.postMessage({ type: 'openTabByFisNo', fisNo: entry.fisNo });
                                            bc.close();
                                            // 'aron-app' adli pencere yoksa window.open bos bir sekme aciyordu.
                                            // Donen pencere about:blank ise Aron kapali demektir; fisi orada aciyoruz.
                                            // Aron aciksa BroadcastChannel fisi zaten orada actirdi, sadece one getiriyoruz.
                                            const appWin = window.open('', 'aron-app');
                                            if (appWin) {
                                              try {
                                                if (!appWin.location.href || appWin.location.href === 'about:blank') {
                                                  appWin.location.href = '/Main/Home/' + entry.fisNo;
                                                }
                                              } catch (_) { /* okunamiyorsa zaten mevcut Aron penceresidir */ }
                                              appWin.focus();
                                            }
                                          } catch (_) {
                                            window.open(`/Main/Home/${entry.fisNo}`, 'aron-app');
                                          }
                                        }}
                                        sx={{
                                          height: 20,
                                          maxWidth: 'none',
                                          fontSize: '0.65rem',
                                          fontWeight: 700,
                                          flexShrink: 0,
                                          bgcolor: alpha('#1565c0', 0.08),
                                          color: '#1565c0',
                                          border: `1px solid ${alpha('#1565c0', 0.2)}`,
                                          '& .MuiChip-label': {
                                            overflow: 'visible',
                                            textOverflow: 'clip',
                                            whiteSpace: 'nowrap',
                                          },
                                          '&:hover': { bgcolor: alpha('#1565c0', 0.18) },
                                        }}
                                      />
                                    )) : (
                                      <span style={{ color: '#bbb' }}>-</span>
                                    )}
                                  </Box>
                                </Box>
                                {/* Randevu Dur. */}
                                <Box component="td">
                                  <Tooltip title={scRandevu.label} arrow>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.5, py: 0.15, borderRadius: 1, bgcolor: scRandevu.bg, border: `1px solid ${alpha(scRandevu.text, 0.2)}`, maxWidth: '100%', overflow: 'hidden' }}>
                                      <span style={{ fontSize: '0.72rem', lineHeight: 1, flexShrink: 0 }}>{scRandevu.icon}</span>
                                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: scRandevu.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {scRandevu.label.length > 9 ? scRandevu.label.substring(0, 9) + '…' : scRandevu.label}
                                      </span>
                                    </Box>
                                  </Tooltip>
                                </Box>
                                {/* Fiş Dur. */}
                                <Box component="td">
                                  {scFis ? (
                                    <Tooltip title={scFis.label} arrow>
                                      <span style={{ fontSize: '0.78rem', fontWeight: 500, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>
                                        {scFis.label.length > 15 ? scFis.label.substring(0, 15) + '…' : scFis.label}
                                      </span>
                                    </Tooltip>
                                  ) : <span style={{ color: '#bbb' }}>-</span>}
                                </Box>
                                {/* Tip */}
                                <Box component="td">
                                  {spType ? (
                                    <Tooltip title={spType} arrow>
                                      <span style={{ fontSize: '0.78rem', color: '#444', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>
                                        {spType.length > 12 ? spType.substring(0, 12) + '…' : spType}
                                      </span>
                                    </Tooltip>
                                  ) : <span style={{ color: '#bbb' }}>-</span>}
                                </Box>
                                {/* Randevu saati */}
                                <Box component="td" sx={{ fontWeight: 500, color: '#333', fontSize: '0.78rem' }}>
                                  {apptTime !== '-' ? `${apptTime}${apptTimeEnd ? '-' + apptTimeEnd : ''}` : '-'}
                                </Box>
                                {/* Çıkış */}
                                <Box component="td" sx={{ fontWeight: 500, color: '#444', fontSize: '0.78rem' }}>
                                  {deptTime}
                                </Box>
                                {/* Varış */}
                                <Box component="td" sx={{ fontWeight: 500, color: '#444', fontSize: '0.78rem' }}>
                                  {arrivalTime}
                                </Box>
                                {/* Bitiş */}
                                <Box component="td" sx={{ fontWeight: 500, color: '#444', fontSize: '0.78rem' }}>
                                  {completionTime}
                                </Box>
                                {/* Mahalle (+ il/ilçe ve varsa mahalle grubu) */}
                                <Box component="td">
                                  <Tooltip title={[neighbourhood, cityDistrict, neighbourhoodGroup && `Grup: ${neighbourhoodGroup}`].filter(Boolean).join(' · ')} arrow>
                                    <Box>
                                      <span
                                        onClick={(e) => { e.stopPropagation(); handleOpenTimelineJobPopup(ofscNr); }}
                                        style={{ fontSize: '0.78rem', fontWeight: 500, color: ofscNr > 0 ? '#1565c0' : '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%', cursor: ofscNr > 0 ? 'pointer' : 'default', textDecoration: ofscNr > 0 ? 'underline' : 'none', textDecorationColor: ofscNr > 0 ? 'rgba(21,101,192,0.4)' : 'transparent', textUnderlineOffset: '2px' }}
                                      >
                                        {neighbourhood}
                                      </span>
                                      {cityDistrict && (
                                        <Box component="span" sx={{ display: 'block', fontSize: '0.64rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {cityDistrict}
                                        </Box>
                                      )}
                                      {neighbourhoodGroup && (
                                        <Box component="span" sx={{ display: 'inline-block', mt: 0.2, px: 0.5, borderRadius: '6px', fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#1565c0', 0.1), color: '#1565c0', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {neighbourhoodGroup}
                                        </Box>
                                      )}
                                    </Box>
                                  </Tooltip>
                                </Box>
                                {/* Ürün */}
                                <Box component="td">
                                  {products ? (
                                    <Tooltip title={products} arrow>
                                      <span style={{ fontSize: '0.78rem', color: '#444', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>
                                        {products.split(',')[0]?.trim()}
                                      </span>
                                    </Tooltip>
                                  ) : <span style={{ color: '#bbb' }}>-</span>}
                                </Box>
                                {/* Müşteri */}
                                <Box component="td">
                                  {customerName ? (
                                    <Tooltip title={customerName} arrow>
                                      <span
                                        onClick={(e) => { e.stopPropagation(); handleOpenTimelineJobPopup(ofscNr); }}
                                        style={{ fontSize: '0.78rem', color: ofscNr > 0 ? '#1565c0' : '#333', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%', cursor: ofscNr > 0 ? 'pointer' : 'default', textDecoration: ofscNr > 0 ? 'underline' : 'none', textDecorationColor: ofscNr > 0 ? 'rgba(21,101,192,0.4)' : 'transparent', textUnderlineOffset: '2px' }}
                                      >
                                        {customerName}
                                      </span>
                                    </Tooltip>
                                  ) : <span style={{ color: '#bbb' }}>-</span>}
                                </Box>
                                {/* Bayi */}
                                <Box component="td">
                                  {bayiName ? (
                                    <Tooltip title={bayiName} arrow>
                                      <span style={{ fontSize: '0.78rem', color: '#444', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>
                                        {bayiName}
                                      </span>
                                    </Tooltip>
                                  ) : <span style={{ color: '#bbb' }}>-</span>}
                                </Box>
                                {/* Randevu Kanalı — randevunun verildiği uygulama (AronRotam, AronYönetim, ÇağrıMerkezi…) */}
                                <Box component="td">
                                  {appointmentChannel ? (
                                    <Tooltip title={appointmentChannel} arrow>
                                      <span style={{ fontSize: '0.75rem', color: '#444', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>
                                        {appointmentChannel}
                                      </span>
                                    </Tooltip>
                                  ) : <span style={{ color: '#bbb' }}>-</span>}
                                </Box>
                                {/* Yol */}
                                <Box component="td" sx={{ textAlign: 'center', fontWeight: 600, color: '#555', fontSize: '0.75rem' }}>
                                  {travelDur > 0 || randevuStatusDesc.toLowerCase().trim() === 'completed' ? formatDuration(travelDur) : '-'}
                                </Box>
                                {/* İş */}
                                <Box component="td" sx={{ textAlign: 'center', fontWeight: 600, color: '#555', fontSize: '0.75rem' }}>
                                  {workDur > 0 || randevuStatusDesc.toLowerCase().trim() === 'completed' ? formatDuration(workDur) : '-'}
                                </Box>
                              </Box>
                            );
                          });
                        })()}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DraggableResizablePopup>
      )}

      {/* ── Toplu İş Taşıma Dialog ── */}
      {techMoveOpen && (
        <Dialog
          open={techMoveOpen}
          sx={{ zIndex: 1400 }}
          onClose={() => {
            if (!techMoveLoading) {
              setTechMoveOpen(false);
              setTechMoveTarget('');
            }
          }}
          PaperProps={{
            sx: {
              borderRadius: 3, minWidth: 400,
              background: darkMode
                ? 'linear-gradient(160deg, #1e1e2e 0%, #252538 100%)'
                : 'linear-gradient(160deg, #ffffff 0%, #f8f9ff 100%)',
              border: `1px solid ${alpha('#1565c0', 0.15)}`,
              boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
            },
          }}
        >
          <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1565c0 0%, #42a5f5 50%, #1565c0 100%)' }} />
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1, pt: 2, px: 3 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(145deg, rgba(21,101,192,0.15) 0%, rgba(66,165,245,0.08) 100%)',
              border: '1px solid rgba(21,101,192,0.2)',
            }}>
              <SwapHorizIcon sx={{ fontSize: 22, color: '#1565c0' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem', color: darkMode ? '#fff' : '#1a1a2e' }}>
                İş Taşıma
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {techWorkChecked.length} iş seçili — hedef teknisyen seçin
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {/* Seçili işler */}
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha('#1565c0', 0.04), border: `1px solid ${alpha('#1565c0', 0.1)}` }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#1565c0', mb: 0.5 }}>Seçili İşler</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {techWorkChecked.map(nr => (
                    <Chip key={nr} label={`#${nr}`} size="small" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#1565c0', 0.1), color: '#1565c0' }} />
                  ))}
                </Box>
              </Box>

              {/* Kaynak teknisyen */}
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha('#FF9800', 0.04), border: `1px solid ${alpha('#FF9800', 0.1)}` }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#E65100', mb: 0.3 }}>Kaynak Teknisyen</Typography>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary' }}>
                  {selectedMoveSourceName || selectedMoveSourceCode} ({selectedMoveSourceCode})
                </Typography>
              </Box>

              {/* Hedef teknisyen seçimi */}
              <FormControl fullWidth size="small">
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>Hedef Teknisyen</Typography>
                <Select
                  value={techMoveTarget}
                  onChange={(e) => handleTechMoveTargetChange(e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: 2, fontSize: '0.78rem' }}
                >
                  <MenuItem value="" disabled><em>Teknisyen seçin...</em></MenuItem>
                  {nonBucketTechs
                    .filter(t => String(t.TechCode) !== String(selectedMoveSourceCode))
                    .map(t => (
                      <MenuItem key={t.TechCode} value={t.TechCode} sx={{ fontSize: '0.78rem' }}>
                        {t.TechName || t.TechCode} ({t.TechCode})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
            <Button variant="outlined" onClick={() => { setTechMoveOpen(false); setTechMoveTarget(''); }} disabled={techMoveLoading}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: alpha('#000', 0.15), color: 'text.secondary' }}>
              İptal
            </Button>
            <Button variant="contained" onClick={handleBulkMove} disabled={techMoveLoading || !techMoveTarget}
              startIcon={techMoveLoading ? <CircularProgress size={14} color="inherit" /> : <SwapHorizIcon />}
              sx={{
                borderRadius: 2, textTransform: 'none', fontWeight: 700, color: '#fff',
                bgcolor: '#1565c0', boxShadow: '0 3px 12px rgba(21,101,192,0.3)',
                '&:hover': { bgcolor: '#0d47a1' },
              }}>
              {techMoveLoading ? 'Taşınıyor...' : 'Taşı'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Taşıma Onay Dialog ── */}
      {!!dropConfirm && (
        <Dialog
          open={!!dropConfirm}
          fullWidth
          maxWidth="sm"
          onClose={() => !dropLoading && setDropConfirm(null)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              minWidth: { sm: 580 },
              background: darkMode
                ? 'linear-gradient(160deg, #1e1e2e 0%, #252538 100%)'
                : 'linear-gradient(160deg, #ffffff 0%, #f8f9ff 100%)',
              border: `1px solid ${alpha('#1565c0', 0.15)}`,
              boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
              overflow: 'hidden',
            },
          }}
        >
          {/* Decorative top bar */}
          <Box sx={{
            height: 4,
            background: 'linear-gradient(90deg, #1565c0 0%, #42a5f5 50%, #1565c0 100%)',
          }} />
          <DialogTitle sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            pb: 1, pt: 2.5, px: 3,
          }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(145deg, rgba(21,101,192,0.15) 0%, rgba(66,165,245,0.08) 100%)',
              border: '1px solid rgba(21,101,192,0.2)',
              boxShadow: '0 3px 8px rgba(21,101,192,0.1)',
            }}>
              <SwapHorizIcon sx={{ fontSize: 24, color: '#1565c0' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: darkMode ? '#fff' : '#1a1a2e' }}>
                Teknisyen Randevu Değişikliği Onayı
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Bu işlemi onaylıyor musunuz?
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pb: 1 }}>
            {dropConfirm && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* OfscNr */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.5, py: 1, borderRadius: '10px',
                  bgcolor: alpha('#1565c0', 0.06),
                  border: '1px solid ' + alpha('#1565c0', 0.12),
                }}>
                  <AssignmentIcon sx={{ fontSize: 18, color: '#1565c0' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 600, minWidth: 80 }}>Randevu Nr</Typography>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: darkMode ? '#90caf9' : '#1565c0', fontFamily: 'monospace' }}>
                    #{dropConfirm.ofscNr}
                  </Typography>
                </Box>
                {/* Teknisyen Değişimi */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.5, py: 1, borderRadius: '10px',
                  bgcolor: alpha('#FF9800', 0.06),
                  border: '1px solid ' + alpha('#FF9800', 0.12),
                }}>
                  <PersonIcon sx={{ fontSize: 18, color: '#E65100' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 600, minWidth: 80 }}>Teknisyen</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Chip label={[dropConfirm.sourceUserTechCode, dropConfirm.sourceTechName].filter(Boolean).join(' ') || dropConfirm.sourceTechCode} size="small" sx={{
                      height: 22, fontSize: '0.72rem', fontWeight: 700,
                      bgcolor: alpha('#ef5350', 0.1), color: '#c62828',
                      textDecoration: dropConfirm.sourceTechCode !== dropConfirm.targetTechCode ? 'line-through' : 'none',
                    }} />
                    {dropConfirm.sourceTechCode !== dropConfirm.targetTechCode && (
                      <>
                        <SwapHorizIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        <Chip label={[dropConfirm.targetUserTechCode, dropConfirm.targetTechName].filter(Boolean).join(' ') || dropConfirm.targetTechCode} size="small" sx={{
                          height: 22, fontSize: '0.72rem', fontWeight: 700,
                          bgcolor: alpha('#4CAF50', 0.1), color: '#2E7D32',
                        }} />
                      </>
                    )}
                  </Box>
                </Box>
                {renderMoveImpactSummary(dropConfirm.moveImpact, darkMode)}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setDropConfirm(null)}
              disabled={dropLoading}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: alpha('#000', 0.15),
                color: 'text.secondary',
                '&:hover': { borderColor: alpha('#000', 0.3), bgcolor: alpha('#000', 0.04) },
              }}
            >
              İptal
            </Button>
            <Button
              variant="contained"
              onClick={handleDropConfirm}
              disabled={dropLoading}
              startIcon={dropLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                color: '#fff',
                fontWeight: 700,
                bgcolor: '#1565c0',
                boxShadow: '0 3px 12px rgba(21,101,192,0.3)',
                '&:hover': { bgcolor: '#0d47a1', boxShadow: '0 4px 16px rgba(21,101,192,0.4)' },
              }}
            >
              {dropLoading ? 'Güncelleniyor...' : 'Onayla'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Teknisyen Harita Popup ── */}
      {techMapOpen && (
        <DraggableResizablePopup
          open={techMapOpen}
          onClose={() => {
            sendTechMapCommand({ type: 'toggleAnimation', enabled: false });
            sendTechMapCommand({ type: 'toggleTraffic', enabled: false });
            stopTechLiveTracking();
            setTechMapAnimating(false);
            setTechMapOpen(false);
            setTechMapData(null);
          }}
          title={techMapData?.comparisonRoutes ? 'Taşıma Rota Karşılaştırması' : (techMapData ? `${techMapData.techName} - Rota Haritası` : 'Rota Haritası')}
          icon={MapIcon}
          maxWidth="xl"
          defaultWidth={1800}
          defaultHeight={980}
          zIndex={techMapData?.simulationMode ? 1450 : undefined}
          primaryColor="#4caf50"
          headerActions={
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}>
              {showTechMapRouteControls && !techMapData?.comparisonRoutes && (
                <>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.65,
                    pl: 0.75,
                    pr: 0.35,
                    py: 0.3,
                    borderRadius: 1.5,
                    bgcolor: alpha(techLivePresentation.color, darkMode ? 0.18 : 0.08),
                    border: `1px solid ${alpha(techLivePresentation.color, 0.3)}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.45, minWidth: 0 }}>
                      <RadioButtonCheckedIcon sx={{
                        fontSize: 12,
                        color: techLivePresentation.color,
                        animation: techLivePresentation.pulse ? 'tech-live-pulse 1.4s ease-in-out infinite' : 'none',
                        '@keyframes tech-live-pulse': {
                          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                          '50%': { opacity: 0.42, transform: 'scale(0.78)' },
                        },
                      }} />
                      <Typography sx={{
                        fontSize: '0.67rem',
                        fontWeight: 900,
                        color: techLivePresentation.color,
                        whiteSpace: 'nowrap',
                      }}>
                        {techLivePresentation.label}
                      </Typography>
                    </Box>
                    <Tooltip title={techLiveTooltip} arrow>
                      <span style={{ display: 'inline-flex' }}>
                      <Button
                        size="small"
                        variant={techLiveTracking ? 'contained' : 'outlined'}
                        startIcon={techLiveStatus === TECH_LOCATION_STATUS.CONNECTING || techLiveStatus === TECH_LOCATION_STATUS.RECONNECTING
                          ? <CircularProgress size={13} thickness={5} color="inherit" />
                          : techLiveTracking
                            ? <StopCircleIcon sx={{ fontSize: '16px !important' }} />
                            : <CellTowerIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={techLiveTracking ? stopTechLiveTracking : startTechLiveTracking}
                        disabled={!techMapData?.trackingKey
                          || techLiveStatus === TECH_LOCATION_STATUS.CONNECTING
                          || techLiveStatus === TECH_LOCATION_STATUS.RECONNECTING}
                        sx={{
                          height: 30,
                          px: 1.1,
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontSize: '0.72rem',
                          fontWeight: 850,
                          color: techLiveTracking ? '#fff' : (darkMode ? '#80deea' : '#007c91'),
                          bgcolor: techLiveTracking
                            ? (techLiveStatus === TECH_LOCATION_STATUS.RECONNECTING ? '#ed6c02' : (techLiveLocation?.stale ? '#607d8b' : '#00838f'))
                            : 'transparent',
                          borderColor: darkMode ? alpha('#80deea', 0.42) : alpha('#00838f', 0.42),
                          boxShadow: techLiveStatus === TECH_LOCATION_STATUS.LIVE && !techLiveLocation?.stale
                            ? `0 0 0 3px ${alpha('#00acc1', 0.16)}`
                            : 'none',
                          '&:hover': {
                            bgcolor: techLiveTracking ? '#006064' : alpha('#00acc1', 0.1),
                            borderColor: '#00838f',
                          },
                          '&.Mui-disabled': { opacity: 0.48 },
                        }}
                      >
                        {techLiveTracking ? 'Takibi Durdur' : 'Takibi Başlat'}
                      </Button>
                      </span>
                    </Tooltip>
                  </Box>
                  <Tooltip title={techMapShowRoute ? 'Yol çizimini kapat' : 'Sıralı yolu çiz'} arrow>
                    <Button
                      size="small"
                      variant={techMapShowRoute ? 'contained' : 'outlined'}
                      startIcon={<AltRouteIcon sx={{ fontSize: '16px !important' }} />}
                      onClick={() => {
                        const enabled = !techMapShowRoute;
                        setTechMapShowRoute(enabled);
                        if (!enabled) {
                          setTechMapAnimating(false);
                          sendTechMapCommand({ type: 'toggleAnimation', enabled: false });
                        }
                        sendTechMapCommand({ type: 'toggleRoute', enabled });
                      }}
                      disabled={!techMapData || techMapData.jobs.length < 2}
                      sx={{
                        height: 30,
                        px: 1.1,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontSize: '0.72rem',
                        fontWeight: 850,
                        color: techMapShowRoute ? '#fff' : (darkMode ? '#a5d6a7' : '#2e7d32'),
                        bgcolor: techMapShowRoute ? '#2e7d32' : 'transparent',
                        borderColor: darkMode ? alpha('#a5d6a7', 0.35) : alpha('#2e7d32', 0.35),
                        '&:hover': {
                          bgcolor: techMapShowRoute ? '#1b5e20' : alpha('#4caf50', 0.12),
                          borderColor: '#2e7d32',
                        },
                      }}
                    >
                      Rota çiz
                    </Button>
                  </Tooltip>
                </>
              )}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.45,
                py: 0.2,
                borderRadius: 1.5,
                bgcolor: darkMode ? alpha('#fff', 0.05) : alpha('#4caf50', 0.08),
                border: `1px solid ${darkMode ? alpha('#fff', 0.10) : alpha('#4caf50', 0.18)}`,
              }}>
                <Tooltip title="Önceki gün" arrow>
                  <IconButton onClick={goToPrevDay} disabled={loading} size="small" sx={{ p: 0.3, color: darkMode ? '#a5d6a7' : '#2e7d32' }}>
                    <ChevronLeftIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Tarih seç" arrow>
                  <Box
                    onClick={(e) => setDatePickerAnchor(e.currentTarget)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 0.8,
                      py: 0.3,
                      borderRadius: 1.2,
                      cursor: 'pointer',
                      bgcolor: isToday(selectedDate) ? alpha('#4caf50', 0.12) : 'transparent',
                      '&:hover': { bgcolor: alpha('#4caf50', 0.14) },
                    }}
                  >
                    <CalendarMonthIcon sx={{ fontSize: 15, color: isToday(selectedDate) ? '#2e7d32' : (darkMode ? '#9ca3af' : '#64748b') }} />
                    <Typography sx={{ fontSize: '0.74rem', lineHeight: 1.3, fontWeight: 850, color: darkMode ? '#f8fafc' : '#1f2937', whiteSpace: 'nowrap' }}>
                      {dayjs(selectedDate).format('DD MMMM YYYY, dddd')}
                    </Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Sonraki gün" arrow>
                  <IconButton onClick={goToNextDay} disabled={loading} size="small" sx={{ p: 0.3, color: darkMode ? '#a5d6a7' : '#2e7d32' }}>
                    <ChevronRightIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
                {!isToday(selectedDate) && (
                  <Tooltip title="Bugüne dön" arrow>
                    <IconButton onClick={goToToday} disabled={loading} size="small" sx={{ p: 0.3, color: darkMode ? '#a5d6a7' : '#2e7d32' }}>
                      <TodayIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          }
        >
          {techMapData?.comparisonRoutes ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 0,
              position: 'relative',
              height: '100%',
              minHeight: { xs: 900, md: 720 },
              p: 1.5,
              bgcolor: darkMode ? alpha('#0f172a', 0.2) : '#f8fafc',
            }}>
              {[
                { key: 'source', accent: '#ef6c00', ...techMapData.comparisonRoutes.source },
                { key: 'target', accent: '#2e7d32', ...techMapData.comparisonRoutes.target },
              ].map(route => (
                <Box key={route.key} sx={{
                  display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0,
                  overflow: 'hidden',
                  borderRadius: route.key === 'source'
                    ? { xs: '12px 12px 0 0', md: '12px 0 0 12px' }
                    : { xs: '0 0 12px 12px', md: '0 12px 12px 0' },
                  border: `1px solid ${alpha(route.accent, 0.38)}`,
                  bgcolor: darkMode ? '#111827' : '#fff',
                  boxShadow: `0 12px 30px ${alpha(route.accent, darkMode ? 0.2 : 0.12)}`,
                }}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 1.6, py: 1.2,
                    borderBottom: `1px solid ${alpha(route.accent, 0.24)}`,
                    background: `linear-gradient(135deg, ${alpha(route.accent, darkMode ? 0.22 : 0.1)}, ${darkMode ? alpha('#111827', 0.96) : '#fff'})`,
                  }}>
                    <Box sx={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 34, height: 34, borderRadius: '10px', color: '#fff', bgcolor: route.accent,
                    }}>
                      <MapIcon sx={{ fontSize: 19 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: route.accent, fontSize: '0.66rem', fontWeight: 950, letterSpacing: 0.45 }}>
                        {route.subtitle}
                      </Typography>
                      <Typography sx={{ color: darkMode ? '#f8fafc' : '#1f2937', fontSize: '0.9rem', fontWeight: 900 }} noWrap>
                        {route.title || '-'}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${route.jobs.filter(job => !job.serviceLocation).length} iş`}
                      size="small"
                      sx={{ ml: 'auto', height: 23, color: route.accent, bgcolor: alpha(route.accent, 0.1), fontSize: '0.66rem', fontWeight: 900 }}
                    />
                  </Box>
                  {route.jobs.length > 0 ? (
                    <Box
                      component="iframe"
                      srcDoc={buildOpenStreetMapScheduleSrc({ jobs: route.jobs })}
                      onLoad={(event) => event.currentTarget.contentWindow?.postMessage({ type: 'toggleRoute', enabled: true }, '*')}
                      title={`${route.title} rota haritası`}
                      sx={{ flex: 1, width: '100%', minHeight: { xs: 410, md: 650 }, border: 0, display: 'block' }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', p: 4, color: 'text.secondary', fontWeight: 800 }}>
                      Haritada gösterilecek koordinat bulunamadı
                    </Box>
                  )}
                </Box>
              ))}
              {(() => {
                const kmDelta = Number(techMapData.comparisonImpact?.kmDelta) || 0;
                const effectColor = kmDelta > 0 ? '#d32f2f' : kmDelta < 0 ? '#2e7d32' : '#64748b';
                const kmText = kmDelta === 0
                  ? 'Kilometre değişmedi'
                  : `${Math.abs(kmDelta).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km ${kmDelta < 0 ? 'kısaldı' : 'uzadı'}`;
                return (
                  <Box sx={{
                    position: 'absolute', zIndex: 8, left: '50%', top: '50%',
                    width: 194, height: 70, transform: 'translate(-50%, -50%)',
                    filter: 'drop-shadow(0 8px 14px rgba(15,23,42,0.28))',
                    pointerEvents: 'none',
                  }}>
                    <Box sx={{
                      position: 'absolute', inset: 0, bgcolor: effectColor,
                      clipPath: 'polygon(0 9%, 72% 9%, 72% 0, 100% 50%, 72% 100%, 72% 91%, 0 91%)',
                    }} />
                    <Box sx={{
                      position: 'absolute', inset: '3px 4px 3px 3px',
                      display: 'flex', alignItems: 'center',
                      bgcolor: darkMode ? '#111827' : '#fff',
                      clipPath: 'polygon(0 9%, 71% 9%, 71% 0, 100% 50%, 71% 100%, 71% 91%, 0 91%)',
                    }}>
                      <Box sx={{ width: '73%', pl: 1.4, pr: 0.4, textAlign: 'left' }}>
                        <Typography sx={{ color: darkMode ? '#94a3b8' : '#64748b', fontSize: '0.54rem', fontWeight: 900, letterSpacing: 0.55 }}>
                          Km Etkisi
                        </Typography>
                        <Typography sx={{ mt: 0.35, color: effectColor, fontSize: '0.93rem', fontWeight: 950, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                          {kmText}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          ) : techMapData && (techMapData.jobs.length > 0 ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '380px minmax(620px, 1fr)' },
              gap: 1.5,
              height: '100%',
              minHeight: { xs: 640, md: 700 },
              p: 1.5,
              bgcolor: darkMode ? alpha('#0f172a', 0.2) : '#f8fafc',
            }}>
              {/* Harita */}
              <Box sx={{
                gridColumn: { xs: '1', md: '2' },
                gridRow: { xs: '2', md: '1' },
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${alpha('#4caf50', 0.24)}`,
                minHeight: { xs: 420, md: 700 },
                boxShadow: darkMode ? '0 14px 34px rgba(0,0,0,0.36)' : '0 14px 34px rgba(15,23,42,0.14)',
                bgcolor: '#e8f5e9',
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: 12,
                  left: 52,
                  zIndex: 5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  maxWidth: { xs: 'calc(100% - 118px)', sm: 390 },
                  px: 1.2,
                  py: 0.85,
                  borderRadius: 1.5,
                  bgcolor: darkMode ? alpha('#0f172a', 0.92) : alpha('#fff', 0.94),
                  border: `1px solid ${alpha(techLivePresentation.color, 0.3)}`,
                  boxShadow: '0 5px 16px rgba(15,23,42,0.18)',
                  backdropFilter: 'blur(7px)',
                  pointerEvents: 'none',
                }}>
                  <Box sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: alpha(techLivePresentation.color, 0.14),
                    color: techLivePresentation.color,
                  }}>
                    <CellTowerIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{
                      color: techLivePresentation.color,
                      fontSize: '0.73rem',
                      lineHeight: 1.2,
                      fontWeight: 950,
                    }}>
                      Teknisyen takibi: {techLivePresentation.label}
                    </Typography>
                    <Typography sx={{
                      mt: 0.2,
                      color: darkMode ? '#cbd5e1' : '#475569',
                      fontSize: '0.66rem',
                      lineHeight: 1.25,
                      fontWeight: 700,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {techLivePresentation.detail}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  component="iframe"
                  ref={techMapIframeRef}
                  onLoad={() => {
                    sendTechMapCommand({ type: 'toggleRoute', enabled: techMapShowRoute });
                    sendTechMapCommand({ type: 'toggleAnimation', enabled: techMapAnimating });
                    const currentLiveLocation = getCurrentTechLiveLocation();
                    if (techLiveTracking && currentLiveLocation) {
                      sendTechMapCommand({ type: 'updateTechnicianLocation', location: currentLiveLocation, focus: false });
                    }
                  }}
                  srcDoc={buildOpenStreetMapScheduleSrc({ jobs: techMapData.jobs }) || (() => {
                    const jobs = techMapData.jobs;

                    const dataJs = JSON.stringify(jobs.map(j => ({
                      lat: j.lat, lng: j.lng, order: j.order, ofsc: j.ofsc,
                      nb: j.neighbourhood, st: j.status, appt: j.appt, apptEnd: j.apptEnd,
                      dep: j.departureTime, arr: j.arrivalTime, comp: j.completionTime,
                      wd: j.workDuration, td: j.travelDuration, prod: j.products, sp: j.servicePapers,
                      customer: j.customerName, dealer: j.dealerName, svc: j.serviceLocation,
                    })));
                    const runtimeConfig = typeof window !== 'undefined' ? (window.APP_CONFIG || {}) : {};
                    const locationBoxConfig = {
                      routeProxy: runtimeConfig.locationBoxRouteProxy || process.env.REACT_APP_LOCATIONBOX_ROUTE_PROXY || '',
                      key: runtimeConfig.locationBoxKey || process.env.REACT_APP_LOCATIONBOX_KEY || '',
                      endpoint: runtimeConfig.locationBoxEndpoint || process.env.REACT_APP_LOCATIONBOX_ENDPOINT || 'https://www.locationbox.com.tr/locationbox/services',
                    };

                    // LocationBox IMapper canlı ve etkileşimli harita
                    return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
                    <style>
                      html,body,#mapWrap{margin:0;padding:0;width:100%;height:100%;overflow:hidden;font-family:'Arcelik';background:#eef3f8;cursor:default;}
                      #map{position:absolute!important;inset:0!important;width:100%!important;height:100%!important;z-index:1!important;pointer-events:auto;visibility:visible!important;opacity:1!important;}
                      #routeSvg,#pinLayer{display:none;}
                      #nativePinLayer{position:absolute;inset:0;z-index:4;pointer-events:none;}
                      .pin{position:absolute;width:28px;height:28px;border-radius:50%;border:2.5px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);cursor:pointer;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:900;font-size:12px;transform:translate(-50%,-50%);transition:transform .15s;user-select:none;}
                      .pin:hover,.pin.hl{transform:translate(-50%,-50%) scale(1.28);z-index:100;}
                      #pinLayer.native-route-pins .pin{display:none;}
                      .native-route-pin-button{position:absolute;width:32px;height:32px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);transform:translate(-50%,-50%);display:flex;align-items:center;justify-content:center;color:#fff;font:900 12px Arial,sans-serif;cursor:pointer;pointer-events:auto;padding:0;transition:transform .15s;}
                      .native-route-pin-button:hover,.native-route-pin-button:focus{transform:translate(-50%,-50%) scale(1.18);outline:none;z-index:10;}
                      .live-technician-button{position:absolute;width:68px;height:68px;border:0;background:transparent center/contain no-repeat;filter:drop-shadow(0 5px 8px rgba(0,54,64,.48));transform:translate(-50%,-50%);display:flex;align-items:flex-end;justify-content:center;cursor:pointer;pointer-events:auto;padding:0;z-index:80;transition:transform .15s,filter .15s;}
                      .live-technician-button:hover,.live-technician-button:focus{transform:translate(-50%,-50%) scale(1.14);filter:drop-shadow(0 7px 11px rgba(0,54,64,.62));outline:none;}
                      .live-technician-badge{margin-bottom:-4px;padding:2px 6px;border-radius:8px;background:#d50000;color:#fff;border:2px solid #fff;box-shadow:0 2px 5px rgba(0,0,0,.28);font:900 8px Arial,sans-serif;letter-spacing:.35px;white-space:nowrap;}
                      .vehicle{position:absolute;width:30px;height:30px;border-radius:50%;border:2px solid #fff;background:#7b1fa2;box-shadow:0 3px 10px rgba(30,0,45,.42);transform:translate(-50%,-50%);display:none;align-items:center;justify-content:center;z-index:150;pointer-events:none;}
                      .vehicle svg{width:19px;height:19px;display:block;}
                      .map-tools{position:absolute;right:12px;top:12px;z-index:200;display:flex;flex-direction:column;gap:6px;}
                      .map-tools button{width:34px;height:34px;border-radius:8px;border:1px solid rgba(15,23,42,.15);background:#fff;box-shadow:0 4px 12px rgba(15,23,42,.12);font-size:18px;font-weight:900;cursor:pointer;color:#0f172a;}
                      .map-tools button:hover{background:#f0f4ff;}
                      .map-notice{position:absolute;left:12px;bottom:12px;z-index:210;max-width:360px;padding:7px 10px;border-radius:8px;background:rgba(15,23,42,.86);color:#fff;font-size:11px;font-weight:750;box-shadow:0 4px 14px rgba(15,23,42,.24);display:none;}
                      .popup{position:fixed;min-width:220px;max-width:290px;border-radius:10px;background:#fff;box-shadow:0 12px 30px rgba(15,23,42,.28);overflow:hidden;z-index:9999;display:none;}
                      .jp-close{position:absolute;top:4px;right:4px;border:none;background:rgba(198,40,40,.1);color:#c62828;width:22px;height:22px;border-radius:5px;cursor:pointer;font-size:16px;line-height:22px;text-align:center;padding:0;z-index:1;}
                      .jp-close:hover{background:rgba(198,40,40,.25);}
                      .jp-head{display:flex;align-items:center;gap:6px;padding:7px 9px;color:#fff;}.jp-order{width:22px;height:22px;border-radius:50%;background:rgba(255,255,255,.22);display:flex;align-items:center;justify-content:center;font-weight:900;font-size:11px;border:1.5px solid rgba(255,255,255,.45)}.jp-slot{font-weight:900;font-size:12px;white-space:nowrap}.jp-status{margin-left:auto;font-size:9px;font-weight:900;background:rgba(255,255,255,.18);padding:2px 6px;border-radius:10px}.jp-body{padding:8px;display:flex;flex-direction:column;gap:5px}.jp-row{display:flex;gap:6px;font-size:10.5px;color:#475569;line-height:1.3}.jp-label{min-width:44px;color:#64748b;font-weight:900}.jp-value{font-weight:750;color:#1f2937;overflow-wrap:anywhere}.jp-fis{display:flex;gap:3px;flex-wrap:wrap}.jp-fis span{font-size:9.5px;font-weight:900;color:#1565c0;background:#f1f5f9;border:1px solid #dbeafe;border-radius:8px;padding:1px 5px}
                      @keyframes spin{to{transform:rotate(360deg)}}
                    </style></head><body>
                    <div id="mapWrap">
                      <div id="map"></div>
                      <svg id="routeSvg"></svg>
                      <div id="pinLayer"><div id="vehicle" class="vehicle"><svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#fff" d="M3 6.5A1.5 1.5 0 0 1 4.5 5h10A1.5 1.5 0 0 1 16 6.5V8h2.2c.7 0 1.34.4 1.65 1.03L21 11.35V17h-1.1a2.9 2.9 0 0 1-5.8 0H9.9a2.9 2.9 0 0 1-5.8 0H3V6.5Zm2 1V12h9V7.5H5Zm11 2.5v2h3l-.85-1.7a.55.55 0 0 0-.5-.3H16ZM7 15.5A1.5 1.5 0 1 0 7 18.5a1.5 1.5 0 0 0 0-3Zm10 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"/></svg></div></div>
                      <div id="nativePinLayer"></div>
                      <div class="map-tools">
                        <button id="btnFit" title="Tümünü Göster">⊞</button>
                      </div>
                      <div id="mapNotice" class="map-notice"></div>
                    </div>
                    <div id="popup" class="popup"><button class="jp-close" onclick="document.getElementById('popup').style.display='none'">×</button><div id="popupBody"></div></div>
                    <script>
                      var d=${dataJs};
                      var locationBox=${JSON.stringify(locationBoxConfig)};
                      var stColors={started:"#2e7d32",completed:"#0d47a1",pending:"#616161",cancelled:"#b71c1c",notdone:"#e65100",suspended:"#6a1b9a",road:"#f57f17",launch:"#00695c",bucket:"#0277bd"};
                      var stLabels={started:"Başladı",completed:"Tamamlandı",pending:"Beklemede",cancelled:"İptal",notdone:"Yapılmadı",suspended:"Askıda",road:"Yolda",launch:"Launch",bucket:"Servis Lokasyonu"};
                      var mapper=null,trafficEnabled=true,trafficLayerAdded=false,trafficInitTimer=0,trafficRenderTimers=[],routeEnabled=false,animationEnabled=false,animationFrame=0,routeRequestSeq=0,routeLoading=false,routeAnimationPoints=[],vehicleIconUrl='',noticeTimer=0,nativeMarkerRecords=[],nativePinSyncFrame=0,nativePinSyncTimer=0;
                      var routeFeatureId='tech_route',vehicleFeatureId='tech_vehicle',liveLocationFeatureId='live_technician';
                      var liveLocationPayload=null,liveLocationIconUrl='',liveLocationMarkerRecord=null,liveLocationBindTimer=0;
                      function showNotice(message){var el=document.getElementById('mapNotice');if(!el)return;window.clearTimeout(noticeTimer);el.textContent=message;el.style.display='block';noticeTimer=window.setTimeout(function(){el.style.display='none';},5000);}
                      function esc(v){return String(v||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&#39;");}
                      function km(v){return(v/1000).toLocaleString("tr-TR",{minimumFractionDigits:1,maximumFractionDigits:1})+" km";}
                      function fmtDur(m){if(!m)return"-";if(m<60)return m+"dk";var h=Math.floor(m/60),r=m%60;return r>0?h+"s "+r+"dk":h+"s";}
                      function hav(a,b){var R=6371000,rad=Math.PI/180,dLat=(b.lat-a.lat)*rad,dLng=(b.lng-a.lng)*rad,la1=a.lat*rad,la2=b.lat*rad;var x=Math.sin(dLat/2)**2+Math.cos(la1)*Math.cos(la2)*Math.sin(dLng/2)**2;return 2*R*Math.atan2(Math.sqrt(x),Math.sqrt(1-x));}
                      d.forEach(function(p,i){p.prevSeg=i>0?{from:d[i-1].svc?"S":d[i-1].order,to:p.svc?"S":p.order,dist:hav(d[i-1],p)}:null;p.nextSeg=i<d.length-1?{from:p.svc?"S":p.order,to:d[i+1].svc?"S":d[i+1].order,dist:hav(p,d[i+1])}:null;});
                      function popupHtml(p){var sk=(p.st||"").toLowerCase().trim(),bg=stColors[sk]||"#4527a0",st=stLabels[sk]||p.st,rows="";if(p.nb)rows+='<div class="jp-row"><span class="jp-label">Mahalle</span><span class="jp-value">'+esc(p.nb)+'</span></div>';if(p.customer)rows+='<div class="jp-row"><span class="jp-label">Müşteri</span><span class="jp-value">'+esc(p.customer)+'</span></div>';if(p.dealer)rows+='<div class="jp-row"><span class="jp-label">Bayi</span><span class="jp-value">'+esc(p.dealer)+'</span></div>';if(p.dep||p.arr||p.comp)rows+='<div class="jp-row"><span class="jp-label">Zaman</span><span class="jp-value">'+[p.dep?"Çıkış "+p.dep:null,p.arr?"Varış "+p.arr:null,p.comp?"Bitiş "+p.comp:null].filter(Boolean).join(" · ")+'</span></div>';if(p.td>0||p.wd>0)rows+='<div class="jp-row"><span class="jp-label">Süre</span><span class="jp-value">'+[p.td>0?"Yol "+fmtDur(p.td):null,p.wd>0?"İş "+fmtDur(p.wd):null].filter(Boolean).join(" · ")+'</span></div>';if(p.prevSeg)rows+='<div class="jp-row"><span class="jp-label">Önceki</span><span class="jp-value">'+p.prevSeg.from+" → "+p.prevSeg.to+" · "+km(p.prevSeg.dist)+'</span></div>';if(p.nextSeg)rows+='<div class="jp-row"><span class="jp-label">Sonraki</span><span class="jp-value">'+p.nextSeg.from+" → "+p.nextSeg.to+" · "+km(p.nextSeg.dist)+'</span></div>';if(p.sp)rows+='<div class="jp-row"><span class="jp-label">Fiş</span><span class="jp-fis">'+p.sp.split(",").map(function(x){return "<span>"+esc(x.split("|")[0].trim())+"</span>";}).join("")+'</span></div>';return '<div class="jp-head" style="background:'+bg+'"><div class="jp-order">'+(p.svc?"S":p.order)+'</div><span class="jp-slot">'+(p.svc?"Servis Lokasyonu":(p.appt?p.appt+(p.apptEnd?" - "+p.apptEnd:""):"#"+p.ofsc))+'</span><span class="jp-status">'+st+'</span></div><div class="jp-body">'+rows+'</div>';}
                      function compassDirection(value){var names=['Kuzey','Kuzeydoğu','Doğu','Güneydoğu','Güney','Güneybatı','Batı','Kuzeybatı'];return names[Math.round(value/45)%8];}
                      function nearestRoutePoint(location){var live={lat:Number(location.latitude),lng:Number(location.longitude)},nearest=null;d.forEach(function(point){if(!Number.isFinite(point.lat)||!Number.isFinite(point.lng))return;var distance=hav(live,point);if(!nearest||distance<nearest.distance)nearest={point:point,distance:distance};});return nearest;}
                      function liveLocationPopupHtml(p){
                        var stale=!!p.stale,status=stale?'Son bilinen konum':'Canlı konum',rows='',lat=Number(p.latitude),lng=Number(p.longitude);
                        var speedMps=p.speed==null?null:Number(p.speed),speedKmh=Number.isFinite(speedMps)&&speedMps>=0?speedMps*3.6:null;
                        var heading=p.heading==null?null:Number(p.heading);if(Number.isFinite(heading))heading=((heading%360)+360)%360;else heading=null;
                        var accuracy=p.accuracy==null?null:Number(p.accuracy),recordedAt=new Date(p.timestamp),time=Number.isNaN(recordedAt.getTime())?'-':recordedAt.toLocaleString('tr-TR',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',second:'2-digit'});
                        var nearest=nearestRoutePoint(p);
                        rows+='<div class="jp-row"><span class="jp-label">Durum</span><span class="jp-value">'+status+'</span></div>';
                        rows+='<div class="jp-row"><span class="jp-label">Zaman</span><span class="jp-value">'+esc(time)+'</span></div>';
                        rows+='<div class="jp-row"><span class="jp-label">Hız</span><span class="jp-value">'+(speedKmh==null?'Bilinmiyor':speedKmh.toLocaleString('tr-TR',{maximumFractionDigits:1})+' km/sa')+'</span></div>';
                        rows+='<div class="jp-row"><span class="jp-label">Yön</span><span class="jp-value">'+(heading==null?'Bilinmiyor':Math.round(heading)+'° · '+compassDirection(heading))+'</span></div>';
                        rows+='<div class="jp-row"><span class="jp-label">Doğruluk</span><span class="jp-value">'+(Number.isFinite(accuracy)?'±'+Math.round(accuracy)+' m':'Bilinmiyor')+'</span></div>';
                        rows+='<div class="jp-row"><span class="jp-label">Koordinat</span><span class="jp-value">'+lat.toFixed(6)+', '+lng.toFixed(6)+'</span></div>';
                        if(nearest){var point=nearest.point,pointLabel=point.svc?'Servis lokasyonu':'Rota '+point.order;rows+='<div class="jp-row"><span class="jp-label">Rota</span><span class="jp-value">En yakın: '+esc(pointLabel)+(point.appt?' · '+esc(point.appt):'')+'</span></div>';if(point.nb)rows+='<div class="jp-row"><span class="jp-label">Çevre</span><span class="jp-value">'+esc(point.nb)+'</span></div>';rows+='<div class="jp-row"><span class="jp-label">Mesafe</span><span class="jp-value">'+km(nearest.distance)+' · kuş uçuşu</span></div>';if(speedMps!=null&&speedMps>=0.8){var eta=Math.max(1,Math.round(nearest.distance/speedMps/60));rows+='<div class="jp-row"><span class="jp-label">Tahmin</span><span class="jp-value">≈'+eta+' dk · mevcut hızla kuş uçuşu</span></div>';}}
                        rows+='<div class="jp-row"><span class="jp-label">Katman</span><span class="jp-value">Rota '+(routeEnabled?'açık':'kapalı')+' · Trafik '+(trafficEnabled?'açık':'kapalı')+'</span></div>';
                        return '<div class="jp-head" style="background:'+(stale?'#546e7a':'#00796b')+'"><div class="jp-order">T</div><span class="jp-slot">'+esc(p.technicianCode||'Teknisyen')+'</span><span class="jp-status">'+status+'</span></div><div class="jp-body">'+rows+'</div>';
                      }
                      var lastMX=100,lastMY=100;
                      document.addEventListener('mousemove',function(e){lastMX=e.clientX;lastMY=e.clientY;},{passive:true});
                      function showPopupContent(html,anchorX,anchorY){
                        document.getElementById('popupBody').innerHTML=html;
                        var pop=document.getElementById('popup'),vw=innerWidth,vh=innerHeight,x=Number.isFinite(anchorX)?anchorX:lastMX,y=Number.isFinite(anchorY)?anchorY:lastMY;
                        pop.style.display='block';var rect=pop.getBoundingClientRect(),pw=rect.width||290,ph=rect.height||250,left=Math.max(8,Math.min(x-pw/2,vw-pw-8)),top=y-ph-12;
                        if(top<8)top=Math.min(vh-ph-8,y+18);pop.style.left=left+'px';pop.style.top=Math.max(8,top)+'px';
                      }
                      function showPopup(p,anchorX,anchorY){showPopupContent(popupHtml(p),anchorX,anchorY);}
                      function showLiveLocationPopup(p,anchorX,anchorY){showPopupContent(liveLocationPopupHtml(p),anchorX,anchorY);}
                      function closePopup(){document.getElementById('popup').style.display='none';}
                      function findNativeMarkerImage(record){
                        if(record.image&&document.contains(record.image))return record.image;
                        record.image=Array.prototype.slice.call(document.images).find(function(candidate){return candidate.src===record.url;})||null;return record.image;
                      }
                      function syncNativePinOverlays(){
                        nativePinSyncFrame=0;var wrap=document.getElementById('mapWrap'),records=nativeMarkerRecords.slice();if(liveLocationMarkerRecord)records.push(liveLocationMarkerRecord);if(!wrap||!records.length)return;var wrapRect=wrap.getBoundingClientRect();
                        records.forEach(function(record){var image=findNativeMarkerImage(record),button=record.button;if(!image||!button)return;var rect=image.getBoundingClientRect(),x=rect.left-wrapRect.left+rect.width/2,y=rect.top-wrapRect.top+rect.height/2;button.style.left=x+'px';button.style.top=y+'px';button.style.display=(x>=-40&&x<=wrapRect.width+40&&y>=-40&&y<=wrapRect.height+40)?'flex':'none';});
                      }
                      function scheduleNativePinSync(){if(nativePinSyncFrame)return;nativePinSyncFrame=window.requestAnimationFrame(syncNativePinOverlays);}
                      function startNativePinTracking(){
                        if(nativePinSyncTimer)return;
                        nativePinSyncTimer=window.setInterval(scheduleNativePinSync,80);
                      }
                      var pts=d.filter(function(p){return p.lat&&p.lng;});
                      var mapCL=0,mapCN=0,mapZoom=11,imgW=1,imgH=1,viewScale=1,viewX=0,viewY=0,dragging=false,dragStart=null;
                      if(pts.length){mapCL=pts.reduce(function(s,p){return s+p.lat;},0)/pts.length;mapCN=pts.reduce(function(s,p){return s+p.lng;},0)/pts.length;}
                      function mercProj(lat,lng){var T=256,sc=T*Math.pow(2,mapZoom);function wx(la,lo){var s=Math.sin(la*Math.PI/180);return{x:(lo+180)/360*sc,y:(0.5-Math.log((1+s)/(1-s))/(4*Math.PI))*sc};}var c=wx(mapCL,mapCN),p=wx(lat,lng);return{x:imgW/2+(p.x-c.x),y:imgH/2+(p.y-c.y)};}
                      function clampView(){
                        var vw=innerWidth||imgW,vh=innerHeight||imgH,scaledW=imgW*viewScale,scaledH=imgH*viewScale;
                        viewX=scaledW<=vw?(vw-scaledW)/2:Math.min(0,Math.max(vw-scaledW,viewX));
                        viewY=scaledH<=vh?(vh-scaledH)/2:Math.min(0,Math.max(vh-scaledH,viewY));
                      }
                      function applyView(){clampView();var t='translate('+viewX+'px,'+viewY+'px) scale('+viewScale+')';['map','bgImg','routeSvg','pinLayer'].forEach(function(id){var el=document.getElementById(id);if(el)el.style.transform=t;});scheduleNativePinSync();}
                      function zoomAt(ns,cx,cy){var old=viewScale;viewScale=Math.max(1,Math.min(6,ns));viewX=cx-(cx-viewX)*(viewScale/old);viewY=cy-(cy-viewY)*(viewScale/old);applyView();}
                      function getNativeMarkerIds(){var ids=nativeMarkerRecords.map(function(record){return record.id;});if(liveLocationPayload)ids.push(liveLocationFeatureId);return ids.join(',');}
                      function fitAll(){
                        var markerIds=getNativeMarkerIds();
                        if(!mapper||!markerIds||typeof mapper.zoomToMarkers!=='function')return;
                        try{mapper.zoomToMarkers(markerIds);window.setTimeout(scheduleNativePinSync,250);}catch(ignore){}
                      }
                      function focusNativePin(point){
                        var record=nativeMarkerRecords.find(function(item){return item.point===point;});
                        if(!record)return;
                        if(mapper&&typeof mapper.zoomToMarkers==='function'){try{mapper.zoomToMarkers(record.id);}catch(ignore){}}
                        window.setTimeout(function(){scheduleNativePinSync();var button=record.button;if(button){var rect=button.getBoundingClientRect();showPopup(point,rect.left+rect.width/2,rect.top);}else{showPopup(point);}},300);
                      }
                      function ensureLiveLocationIcon(){
                        if(liveLocationIconUrl)return liveLocationIconUrl;
                        var svg='<svg xmlns="http://www.w3.org/2000/svg" width="68" height="68" viewBox="0 0 52 52"><circle cx="26" cy="26" r="24" fill="#00e5ff" opacity=".42"><animate attributeName="r" values="20;25;20" dur="1.5s" repeatCount="indefinite"/><animate attributeName="opacity" values=".55;.18;.55" dur="1.5s" repeatCount="indefinite"/></circle><circle cx="26" cy="26" r="19" fill="#004d5b" stroke="#fff" stroke-width="4"/><circle cx="26" cy="26" r="15.5" fill="#00a8bd" stroke="#002f35" stroke-width="1.5"/><path d="M14 30v-9c0-1.7 1.3-3 3-3h16c2 0 3.7 1 4.9 2.7l3 4.2V30h-2.4a4.5 4.5 0 0 1-8.6 0h-6.8a4.5 4.5 0 0 1-8.6 0H14Zm4-9v4h11v-4H18Zm14 0v4h5.5L35 21h-3Z" fill="#fff"/><circle cx="19" cy="30" r="2.7" fill="#17252a" stroke="#fff"/><circle cx="34" cy="30" r="2.7" fill="#17252a" stroke="#fff"/><path d="M20 22.5l4-1.4-1.1 3.6-3.7.9.8-3.1Z" fill="#e30613"/></svg>';
                        liveLocationIconUrl=URL.createObjectURL(new Blob([svg],{type:'image/svg+xml'}));return liveLocationIconUrl;
                      }
                      function bindLiveLocationMarker(attempt){
                        if(!liveLocationPayload||!liveLocationMarkerRecord)return;
                        var record=liveLocationMarkerRecord,image=findNativeMarkerImage(record);
                        if(!image){if(attempt<20){window.clearTimeout(liveLocationBindTimer);liveLocationBindTimer=window.setTimeout(function(){bindLiveLocationMarker(attempt+1);},100);}return;}
                        image.style.opacity='0';image.style.pointerEvents='none';
                        if(!record.button){var button=document.createElement('button');button.type='button';button.className='live-technician-button';button.innerHTML='<span class="live-technician-badge"></span>';button.addEventListener('mousedown',function(event){event.stopPropagation();});button.addEventListener('click',function(event){event.preventDefault();event.stopPropagation();var rect=button.getBoundingClientRect();showLiveLocationPopup(liveLocationPayload,rect.left+rect.width/2,rect.top);});document.getElementById('nativePinLayer').appendChild(button);record.button=button;}
                        record.button.style.backgroundImage='url("'+liveLocationIconUrl+'")';record.button.title=liveLocationPayload.stale?'Teknisyen son bilinen konumu':'Teknisyen canlı konumu';record.button.setAttribute('aria-label',record.button.title);var badge=record.button.querySelector('.live-technician-badge');if(badge){badge.textContent=liveLocationPayload.stale?'SON KONUM':'CANLI';badge.style.background=liveLocationPayload.stale?'#546e7a':'#d50000';}
                        startNativePinTracking();scheduleNativePinSync();
                      }
                      function clearLiveTechnicianLocation(){
                        liveLocationPayload=null;window.clearTimeout(liveLocationBindTimer);liveLocationBindTimer=0;if(liveLocationMarkerRecord&&liveLocationMarkerRecord.button)liveLocationMarkerRecord.button.remove();liveLocationMarkerRecord=null;
                        if(mapper&&typeof mapper.removeFeature==='function'){try{mapper.removeFeature(liveLocationFeatureId);}catch(ignore){}}
                      }
                      function renderLiveTechnicianLocation(){
                        if(!liveLocationPayload||!mapper||typeof mapper.addCustomMarker!=='function')return;
                        var p=liveLocationPayload,lat=Number(p.latitude),lng=Number(p.longitude);if(!Number.isFinite(lat)||!Number.isFinite(lng)||Math.abs(lat)>90||Math.abs(lng)>180)return;
                        if(typeof mapper.removeFeature==='function'){try{mapper.removeFeature(liveLocationFeatureId);}catch(ignore){}}
                        var updateTime='';try{updateTime=new Date(p.timestamp).toLocaleTimeString('tr-TR',{hour:'2-digit',minute:'2-digit',second:'2-digit'});}catch(ignore){}
                        var title=(p.stale?'Teknisyen Son Bilinen Konumu':'Teknisyen Anlık Konumu')+(p.technicianCode?' - '+p.technicianCode:'')+(updateTime?' - '+updateTime:'')+(p.accuracy!=null?' - ±'+Math.round(Number(p.accuracy))+' m':'');
                        var iconUrl=ensureLiveLocationIcon();try{mapper.addCustomMarker(liveLocationFeatureId,lat,lng,title,iconUrl,68,68,null);}catch(ignore){return;}
                        if(!liveLocationMarkerRecord)liveLocationMarkerRecord={id:liveLocationFeatureId,url:iconUrl,image:null,button:null};else{liveLocationMarkerRecord.url=iconUrl;liveLocationMarkerRecord.image=null;}
                        bindLiveLocationMarker(0);
                      }
                      function updateLiveTechnicianLocation(message){
                        var p=message&&message.location?message.location:message;if(!p)return;
                        var lat=Number(p.latitude),lng=Number(p.longitude);if(!Number.isFinite(lat)||!Number.isFinite(lng)||lat===0||lng===0||Math.abs(lat)>90||Math.abs(lng)>180)return;
                        liveLocationPayload=p;renderLiveTechnicianLocation();
                      }
                      function getRoutePoints(){
                        return d.filter(function(p){return p.lat&&p.lng;}).slice();
                      }
                      function renderOverlay(){
                        var pl=document.getElementById('pinLayer'),sv=document.getElementById('routeSvg');
                        pl.innerHTML='';sv.innerHTML='';
                        var routePoints=getRoutePoints();
                        if(routeEnabled&&routePoints.length>1){var pts2=routePoints.map(function(p){var q=mercProj(p.lat,p.lng);return q.x+','+q.y;}).join(' ');sv.insertAdjacentHTML('beforeend','<polyline points="'+pts2+'" fill="none" stroke="#fff" stroke-width="6" stroke-linejoin="round" opacity=".9"/><polyline points="'+pts2+'" fill="none" stroke="#2e7d32" stroke-width="3.5" stroke-dasharray="8,4" stroke-linejoin="round" opacity=".85"/>');}
                        d.forEach(function(p){var q=mercProj(p.lat,p.lng),sk=(p.st||'').toLowerCase().trim(),bg=stColors[sk]||'#4527a0',txt=p.svc?'S':String(p.order);var el=document.createElement('div');el.className='pin';el.style.cssText='left:'+q.x+'px;top:'+q.y+'px;background:'+bg+';';el.textContent=txt;el.onclick=function(e){e.stopPropagation();showPopup(p,e.clientX,e.clientY);};pl.appendChild(el);});
                        var vehicle=document.createElement('div');vehicle.id='vehicle';vehicle.className='vehicle';vehicle.innerHTML='<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#fff" d="M3 6.5A1.5 1.5 0 0 1 4.5 5h10A1.5 1.5 0 0 1 16 6.5V8h2.2c.7 0 1.34.4 1.65 1.03L21 11.35V17h-1.1a2.9 2.9 0 0 1-5.8 0H9.9a2.9 2.9 0 0 1-5.8 0H3V6.5Zm2 1V12h9V7.5H5Zm11 2.5v2h3l-.85-1.7a.55.55 0 0 0-.5-.3H16ZM7 15.5A1.5 1.5 0 1 0 7 18.5a1.5 1.5 0 0 0 0-3Zm10 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"/></svg>';pl.appendChild(vehicle);
                        if(animationEnabled&&routeAnimationPoints.length>1)startVehicleAnimation();
                      }
                      function removeApiRoute(){if(mapper&&typeof mapper.removeRoute==='function'){try{mapper.removeRoute(routeFeatureId);}catch(ignore){}}}
                      function extractPathId(raw){
                        try{
                          var json=JSON.parse(raw);
                          function findPathId(value,depth){
                            if(!value||typeof value!=='object'||depth>5)return null;
                            var direct=value.pathid||value.pathId||value.PathId||value.PATHID;if(direct)return direct;
                            var keys=Object.keys(value);for(var i=0;i<keys.length;i++){var found=findPathId(value[keys[i]],depth+1);if(found)return found;}return null;
                          }
                          var jsonPathId=findPathId(json,0);if(jsonPathId)return jsonPathId;
                        }catch(ignore){}
                        var match=String(raw||'').match(new RegExp('<(?:pathid|PathId|PATHID)[^>]*>([^<]+)</(?:pathid|PathId|PATHID)>','i'));
                        return match?match[1].trim():null;
                      }
                      function orientRouteGeometryPairs(pairs){
                        var clean=pairs.filter(function(pair){return Array.isArray(pair)&&pair.length>=2&&Number.isFinite(Number(pair[0]))&&Number.isFinite(Number(pair[1]));});
                        if(clean.length<2)return[];
                        var waypoints=getRoutePoints(),start=waypoints[0],end=waypoints[waypoints.length-1],first=clean[0],last=clean[clean.length-1];
                        var lngLatScore=Math.abs(Number(first[1])-start.lat)+Math.abs(Number(first[0])-start.lng)+Math.abs(Number(last[1])-end.lat)+Math.abs(Number(last[0])-end.lng);
                        var latLngScore=Math.abs(Number(first[0])-start.lat)+Math.abs(Number(first[1])-start.lng)+Math.abs(Number(last[0])-end.lat)+Math.abs(Number(last[1])-end.lng);
                        var latFirst=latLngScore<lngLatScore,result=[];
                        clean.forEach(function(pair){
                          var point=latFirst?{lat:Number(pair[0]),lng:Number(pair[1])}:{lat:Number(pair[1]),lng:Number(pair[0])};
                          if(Math.abs(point.lat)>90||Math.abs(point.lng)>180)return;
                          var previous=result[result.length-1];if(!previous||previous.lat!==point.lat||previous.lng!==point.lng)result.push(point);
                        });
                        return result;
                      }
                      function parseRouteGeometryValue(value){
                        var pairs=[];
                        function collect(item){
                          if(item==null)return;
                          if(typeof item==='string'){
                            var pairPattern=/(-?\\d+(?:\\.\\d+)?(?:[eE][+-]?\\d+)?)\\s*[, ]\\s*(-?\\d+(?:\\.\\d+)?(?:[eE][+-]?\\d+)?)/g,match;
                            while((match=pairPattern.exec(item))!==null)pairs.push([Number(match[1]),Number(match[2])]);
                            return;
                          }
                          if(Array.isArray(item)){
                            if(item.length>=2&&item.every(function(entry){return typeof entry==='number';})){for(var pairIndex=0;pairIndex+1<item.length;pairIndex+=2)pairs.push([item[pairIndex],item[pairIndex+1]]);return;}
                            item.forEach(collect);return;
                          }
                          if(typeof item==='object'){
                            var lat=item.lat??item.latitude??item.Latitude??item.LATITUDE,lng=item.lng??item.lon??item.longitude??item.Longitude??item.LONGITUDE;
                            if(Number.isFinite(Number(lat))&&Number.isFinite(Number(lng))){pairs.push([Number(lng),Number(lat)]);return;}
                            Object.keys(item).forEach(function(key){collect(item[key]);});
                          }
                        }
                        collect(value);return orientRouteGeometryPairs(pairs);
                      }
                      function extractRouteGeometry(raw){
                        var candidates=[];
                        try{
                          var json=JSON.parse(raw);
                          function walk(value,key,depth){
                            if(value==null||depth>8)return;
                            var normalized=String(key||'').toLowerCase();
                            if(normalized&&/(geometry|coordinates|coords|ordinates|coors|routepath|route_path|wkt|shape|linestring)/.test(normalized)){
                              var parsed=parseRouteGeometryValue(value);if(parsed.length>1)candidates.push(parsed);
                            }
                            if(value&&typeof value==='object')Object.keys(value).forEach(function(childKey){walk(value[childKey],childKey,depth+1);});
                          }
                          walk(json,'',0);
                        }catch(ignore){}
                        var wktMatch=String(raw||'').match(/(?:MULTI)?LINESTRING\\s*\\([\\s\\S]*?\\)/i);
                        if(wktMatch){var wktPoints=parseRouteGeometryValue(wktMatch[0]);if(wktPoints.length>1)candidates.push(wktPoints);}
                        candidates.sort(function(a,b){return b.length-a.length;});return candidates[0]||[];
                      }
                      function requestRealRoute(){
                        var routePoints=getRoutePoints(),requestId=++routeRequestSeq;
                        removeApiRoute();
                        if(!routeEnabled||routePoints.length<2||!mapper||typeof mapper.addRoute!=='function'||!locationBox.key)return;
                        routeLoading=true;routeAnimationPoints=[];if(animationEnabled)clearVehicleMarker();
                        var base=locationBox.routeProxy||locationBox.endpoint||'https://www.locationbox.com.tr/locationbox/services';
                        var separator=base.indexOf('?')>=0?'&':'?';
                        var points=routePoints.map(function(p){return p.lat+'/'+p.lng;}).join(',');
                        var url=base+separator+'Key='+encodeURIComponent(locationBox.key)+'&Cmd=Rota&Typ=JSON&Ordered=1&Geometry=1&Encode=0&Points='+encodeURIComponent(points);
                        fetch(url,{credentials:'omit'}).then(function(response){if(!response.ok)throw new Error('HTTP '+response.status);return response.text();}).then(function(raw){
                          if(requestId!==routeRequestSeq||!routeEnabled)return;
                          routeLoading=false;routeAnimationPoints=extractRouteGeometry(raw);
                          var pathId=extractPathId(raw);if(!pathId)throw new Error('PathId bulunamadı');
                          mapper.addRoute(routeFeatureId,Number(pathId)||pathId,'ROUTE_PATH',null);
                          document.getElementById('routeSvg').style.opacity='0';
                          if(animationEnabled){if(routeAnimationPoints.length>1)startVehicleAnimation();else showNotice('Gerçek rota geometrisi alınamadığı için animasyon başlatılamadı.');}
                        }).catch(function(){
                          if(requestId!==routeRequestSeq||!routeEnabled)return;
                          routeLoading=false;routeAnimationPoints=[];if(animationEnabled)clearVehicleMarker();
                          document.getElementById('routeSvg').style.opacity='1';
                          showNotice('Yol ağı rotası alınamadı.');
                        });
                      }
                      function refreshRoute(){
                        routeRequestSeq+=1;routeLoading=false;routeAnimationPoints=[];removeApiRoute();if(animationEnabled)clearVehicleMarker();
                        var routeSvg=document.getElementById('routeSvg');if(routeSvg)routeSvg.style.opacity='1';
                        renderOverlay();
                        if(routeEnabled)requestRealRoute();
                        if(!routeEnabled)setVehicleAnimation(false);
                      }
                      function clearTrafficTimers(){
                        window.clearTimeout(trafficInitTimer);trafficInitTimer=0;
                        trafficRenderTimers.forEach(function(timer){window.clearTimeout(timer);});trafficRenderTimers=[];
                      }
                      function forceTrafficLayerVisible(){
                        var selector='#map [id*="traffic" i],#map [class*="traffic" i],#map [id*="tmc" i],#map [class*="tmc" i]';
                        try{document.querySelectorAll(selector).forEach(function(layer){layer.style.visibility='visible';layer.style.opacity='1';layer.style.display='block';var z=parseInt(layer.style.zIndex||'0',10);if(!Number.isFinite(z)||z<50)layer.style.zIndex='50';});}catch(ignore){}
                      }
                      function scheduleTrafficRendering(){
                        trafficRenderTimers.forEach(function(timer){window.clearTimeout(timer);});trafficRenderTimers=[];
                        [80,350,900].forEach(function(delay,index){
                          trafficRenderTimers.push(window.setTimeout(function(){
                            if(!trafficEnabled||!trafficLayerAdded)return;
                            forceTrafficLayerVisible();
                          },delay));
                        });
                      }
                      function removeTrafficLayer(){
                        clearTrafficTimers();
                        if(mapper&&trafficLayerAdded&&typeof mapper.removeTrafficTmc==='function'){
                          try{mapper.removeTrafficTmc();}catch(ignore){}
                        }
                        trafficLayerAdded=false;
                      }
                      function setTraffic(enabled){
                        trafficEnabled=!!enabled;
                        if(!mapper)return;
                        try{
                          if(trafficEnabled&&!trafficLayerAdded&&typeof mapper.addTrafficTmc==='function'){
                            mapper.addTrafficTmc();trafficLayerAdded=true;scheduleTrafficRendering();
                          }
                          if(trafficEnabled&&trafficLayerAdded)scheduleTrafficRendering();
                          if(!trafficEnabled)removeTrafficLayer();
                        }catch(ignore){if(trafficEnabled)showNotice('Trafik katmanı şu anda yüklenemedi.');}
                      }
                      function clearVehicleMarker(){
                        window.cancelAnimationFrame(animationFrame);animationFrame=0;
                        var vehicle=document.getElementById('vehicle');if(vehicle)vehicle.style.display='none';
                        if(mapper&&typeof mapper.removeFeature==='function'){try{mapper.removeFeature(vehicleFeatureId);}catch(ignore){}}
                        if(vehicleIconUrl){var oldVehicleIconUrl=vehicleIconUrl;vehicleIconUrl='';window.setTimeout(function(){URL.revokeObjectURL(oldVehicleIconUrl);},1000);}
                      }
                      function stopVehicleAnimation(){animationEnabled=false;clearVehicleMarker();}
                      function sampleRouteGeometry(points,maxPoints){
                        if(points.length<=maxPoints)return points.slice();
                        var sampled=[],step=(points.length-1)/(maxPoints-1);for(var i=0;i<maxPoints;i++)sampled.push(points[Math.min(points.length-1,Math.round(i*step))]);return sampled;
                      }
                      function startVehicleAnimation(){
                        if(!animationEnabled)return;
                        if(routeAnimationPoints.length<2){showNotice(routeLoading?'Gerçek rota hazırlanıyor...':'Gerçek rota geometrisi bulunamadı.');return;}
                        if(!mapper||typeof mapper.addCustomMarker!=='function'||typeof mapper.animateMarker!=='function'){showNotice('Araç animasyonu harita servisi tarafından desteklenmiyor.');return;}
                        try{
                          clearVehicleMarker();
                          var routePoints=sampleRouteGeometry(routeAnimationPoints,240);
                          var vehicleSvg='<svg xmlns="http://www.w3.org/2000/svg" width="80" height="42" viewBox="0 0 80 42"><ellipse cx="40" cy="37.5" rx="35" ry="2.5" fill="#0f172a" opacity=".2"/><path d="M5 31V8.5C5 6 7 4 9.5 4H50c5.5 0 9.1 2.2 12.2 6.8l7 9.7 5.8 2.1c1.5.6 2.5 2 2.5 3.7V31H5Z" fill="#fff" stroke="#64748b" stroke-width="1.15"/><path d="M51.5 7.2c3.2.4 5.5 2 7.7 5.2l5.7 7.9H51.5V7.2Z" fill="#263238"/><path d="M65.8 21.7h6.1l3.1 1.2c1.2.5 2 1.5 2.2 2.8H65.8v-4Z" fill="#f8fafc"/><path d="M7 29.2h67.5v3.6H7z" fill="#1f2937"/><path d="M5.2 9h2.3v9H5.2z" fill="#e53935"/><path d="M74.5 26h3v5.8h-3z" fill="#111827"/><path d="M39.8 5v23M50.2 5.2v23" stroke="#94a3b8" stroke-width=".65"/><path d="M43.8 13.1h3.3" stroke="#334155" stroke-width="1.2" stroke-linecap="round"/><path d="M18.2 14.1l5.2-1.8-1.4 4.6-4.8 1.1 1-3.9Z" fill="#e30613"/><text x="24" y="17.7" font-family="Arcelik" font-size="7.2" font-style="italic" font-weight="700" fill="#171717">arçelik</text><text x="28" y="22.1" font-family="Arcelik" font-size="3.2" font-weight="700" fill="#475569">YETKİLİ SERVİS</text><circle cx="19" cy="31.5" r="6.2" fill="#263238" stroke="#fff" stroke-width="1.3"/><circle cx="19" cy="31.5" r="3.3" fill="#aeb7c2"/><circle cx="62" cy="31.5" r="6.2" fill="#263238" stroke="#fff" stroke-width="1.3"/><circle cx="62" cy="31.5" r="3.3" fill="#aeb7c2"/><circle cx="19" cy="31.5" r="1" fill="#f8fafc"/><circle cx="62" cy="31.5" r="1" fill="#f8fafc"/></svg>';
                          vehicleIconUrl=URL.createObjectURL(new Blob([vehicleSvg],{type:'image/svg+xml'}));
                          mapper.addCustomMarker(vehicleFeatureId,routePoints[0].lat,routePoints[0].lng,'Arçelik Yetkili Servis Teknisyen Aracı',vehicleIconUrl,68,36,null);
                          var ordinates=[];routePoints.forEach(function(p){ordinates.push(p.lng,p.lat);});
                          var stepDuration=Math.max(60,Math.min(240,Math.round(18000/Math.max(1,routePoints.length-1))));
                          mapper.animateMarker(vehicleFeatureId,ordinates,stepDuration);
                        }catch(ignore){clearVehicleMarker();showNotice('Araç animasyonu başlatılamadı.');}
                      }
                      function setVehicleAnimation(enabled){
                        if(!enabled){stopVehicleAnimation();return;}
                        animationEnabled=true;
                        if(!routeEnabled){routeEnabled=true;refreshRoute();return;}
                        if(routeAnimationPoints.length>1){startVehicleAnimation();return;}
                        showNotice('Gerçek rota hazırlanıyor...');if(!routeLoading)requestRealRoute();
                      }
                      function addNativeRoutePins(){
                        if(!mapper||typeof mapper.addCustomMarker!=='function')return;
                        var markerRecords=[];
                        try{
                          d.forEach(function(p,index){
                            var markerId='route_job_'+index,sk=(p.st||'').toLowerCase().trim(),color=stColors[sk]||'#4527a0',text=p.svc?'S':String(p.order),title=(p.svc?'Servis Lokasyonu':('Sıra '+p.order))+(p.nb?' - '+p.nb:'')+(p.ofsc?' - '+p.ofsc:'');
                            var svg='<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="'+color+'" stroke="white" stroke-width="3"/><text x="16" y="20" text-anchor="middle" font-family="Arcelik" font-size="12" font-weight="900" fill="white">'+text+'</text></svg>';
                            var iconUrl=URL.createObjectURL(new Blob([svg],{type:'image/svg+xml'}));
                            mapper.addCustomMarker(markerId,p.lat,p.lng,title,iconUrl,32,32,null);
                            markerRecords.push({id:markerId,url:iconUrl,point:p,title:title,color:color,text:text,image:null,button:null});
                          });
                          nativeMarkerRecords=markerRecords;
                          window.setTimeout(fitAll,250);
                          function bindMarkerEvents(attempt){
                            var images=Array.prototype.slice.call(document.images),bound=0;
                            markerRecords.forEach(function(record){
                              var image=images.find(function(candidate){return candidate.src===record.url;});
                              if(!image)return;
                              bound+=1;record.image=image;image.style.opacity='0';image.style.pointerEvents='none';
                              if(!record.button){
                                var button=document.createElement('button');button.type='button';button.className='native-route-pin-button';button.textContent=record.text;button.title=record.title;button.setAttribute('aria-label',record.title);button.style.background=record.color;
                                button.addEventListener('mousedown',function(event){event.stopPropagation();});
                                button.addEventListener('click',function(event){event.preventDefault();event.stopPropagation();var rect=button.getBoundingClientRect();showPopup(record.point,rect.left+rect.width/2,rect.top);});
                                document.getElementById('nativePinLayer').appendChild(button);record.button=button;
                              }
                            });
                            if(bound===markerRecords.length){document.getElementById('pinLayer').classList.add('native-route-pins');startNativePinTracking();scheduleNativePinSync();window.setTimeout(fitAll,150);return;}
                            if(attempt<20){window.setTimeout(function(){bindMarkerEvents(attempt+1);},100);return;}
                            markerRecords.forEach(function(record){if(typeof mapper.removeFeature==='function'){try{mapper.removeFeature(record.id);}catch(removeError){}}URL.revokeObjectURL(record.url);});
                            nativeMarkerRecords=[];document.getElementById('nativePinLayer').innerHTML='';
                            document.getElementById('pinLayer').classList.remove('native-route-pins');
                          }
                          bindMarkerEvents(0);
                          window.addEventListener('beforeunload',function(){window.clearInterval(nativePinSyncTimer);markerRecords.forEach(function(record){URL.revokeObjectURL(record.url);});},{once:true});
                        }catch(ignore){
                          markerRecords.forEach(function(record){if(typeof mapper.removeFeature==='function'){try{mapper.removeFeature(record.id);}catch(removeError){}}URL.revokeObjectURL(record.url);});
                          nativeMarkerRecords=[];document.getElementById('nativePinLayer').innerHTML='';
                          document.getElementById('pinLayer').classList.remove('native-route-pins');
                        }
                      }
                      function initLocationBoxApi(){
                        if(!pts.length||typeof window.IMapper!=='function')return;
                        try{
                          mapper=new window.IMapper();trafficLayerAdded=false;mapper.initMap(mapCL,mapCN,mapZoom);
                          if(typeof mapper.addNavigationPanel==='function'){try{mapper.addNavigationPanel();}catch(ignore){}}
                          window.setTimeout(addNativeRoutePins,350);
                          renderLiveTechnicianLocation();
                          var liveMap=document.getElementById('map');liveMap.style.position='absolute';liveMap.style.inset='0';liveMap.style.width='100%';liveMap.style.height='100%';liveMap.style.zIndex='1';liveMap.style.visibility='visible';liveMap.style.opacity='1';
                          var loading=document.getElementById('_ld');if(loading)loading.remove();
                          if(typeof mapper.setMapListener==='function'){
                            mapper.setMapListener('before_zoom_level_change',function(){
                              removeTrafficLayer();
                            });
                            mapper.setMapListener('zoom_level_change',function(){
                              window.clearTimeout(trafficInitTimer);trafficInitTimer=window.setTimeout(function(){setTraffic(trafficEnabled);},250);
                            });
                          }
                          trafficInitTimer=window.setTimeout(function(){setTraffic(trafficEnabled);},600);
                          if(routeEnabled)requestRealRoute();else if(animationEnabled&&routeAnimationPoints.length>1)startVehicleAnimation();
                        }catch(ignore){mapper=null;var loading=document.getElementById('_ld');if(loading)loading.remove();showNotice('Canlı harita başlatılamadı.');}
                      }
                      function loadLocationBoxApi(){
                        if(!locationBox||!locationBox.key||!pts.length)return;
                        var script=document.createElement('script'),endpoint=locationBox.endpoint||'https://www.locationbox.com.tr/locationbox/services';
                        script.src=endpoint+(endpoint.indexOf('?')>=0?'&':'?')+'Key='+encodeURIComponent(locationBox.key)+'&Cmd=API&Typ=JS';
                        script.onload=initLocationBoxApi;script.onerror=function(){var loading=document.getElementById('_ld');if(loading)loading.remove();showNotice('Canlı harita servisine ulaşılamadı.');};document.head.appendChild(script);
                      }
                      if(locationBox&&locationBox.key&&pts.length){
                        document.getElementById('mapWrap').insertAdjacentHTML('beforeend','<div id="_ld" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;gap:10px;color:#1565c0;font-weight:700;font-size:13px;z-index:10;pointer-events:none;background:#eef3f8;"><div style="width:24px;height:24px;border:3px solid #1565c0;border-top-color:transparent;border-radius:50%;animation:spin .8s linear infinite"></div>Canlı harita yükleniyor...</div>');
                      } else {document.getElementById('map').innerHTML='<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#888;font-size:13px;font-weight:700;">'+(pts.length===0?'Konum verisi bulunamadı':'LocationBox API anahtarı eksik')+'</div>';}
                      loadLocationBoxApi();
                      document.getElementById('mapWrap').addEventListener('click',closePopup);
                      document.getElementById('btnFit').onclick=function(e){e.stopPropagation();fitAll();};
                      document.getElementById('mapWrap').addEventListener('mousemove',scheduleNativePinSync);
                      document.getElementById('mapWrap').addEventListener('touchmove',scheduleNativePinSync,{passive:true});
                      document.getElementById('mapWrap').addEventListener('wheel',scheduleNativePinSync,{passive:true});
                      window.addEventListener('resize',scheduleNativePinSync);
                      window.addEventListener('beforeunload',function(){if(liveLocationIconUrl)URL.revokeObjectURL(liveLocationIconUrl);},{once:true});
                      window.addEventListener('message',function(e){
                        if(e.data==='fitBounds')fitAll();
                        if(e.data&&e.data.type==='focusPin'){var p=d.find(function(x){return x.order===e.data.order;});if(p)focusNativePin(p);}
                        if(e.data&&e.data.type==='toggleTraffic')setTraffic(e.data.enabled);
                        if(e.data&&e.data.type==='toggleRoute'){routeEnabled=!!e.data.enabled;refreshRoute();}
                        if(e.data&&e.data.type==='toggleAnimation')setVehicleAnimation(!!e.data.enabled);
                        if(e.data&&e.data.type==='updateTechnicianLocation')updateLiveTechnicianLocation(e.data);
                        if(e.data&&e.data.type==='clearTechnicianLocation')clearLiveTechnicianLocation();
                      });
                    </script></body></html>`;
                  })()}
                  sx={{ width: '100%', height: '100%', border: 'none', minHeight: { xs: 420, md: 700 }, display: 'block' }}
                  title="Harita"
                />
              </Box>
              {/* Pin listesi */}
              <Box sx={{
                gridColumn: { xs: '1', md: '1' },
                gridRow: { xs: '1', md: '1' },
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#0f172a', 0.1)}`,
                bgcolor: darkMode ? alpha('#111827', 0.86) : '#fff',
                boxShadow: darkMode ? '0 10px 28px rgba(0,0,0,0.26)' : '0 10px 28px rgba(15,23,42,0.08)',
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 1.15,
                  borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
                  background: darkMode
                    ? `linear-gradient(135deg, ${alpha('#4caf50', 0.18)} 0%, ${alpha('#1565c0', 0.08)} 100%)`
                    : `linear-gradient(135deg, ${alpha('#4caf50', 0.1)} 0%, ${alpha('#1565c0', 0.05)} 100%)`,
                }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '9px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha('#4caf50', 0.14),
                    border: `1px solid ${alpha('#4caf50', 0.24)}`,
                    flexShrink: 0,
                  }}>
                    <PlaceIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: darkMode ? '#f8fafc' : '#1f2937', lineHeight: 1.2 }}>
                      İşler
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: darkMode ? '#9ca3af' : '#64748b', lineHeight: 1.2 }}>
                      {techMapData.jobs.length} konum haritada gösteriliyor
                    </Typography>
                  </Box>
                  <Tooltip title="Tüm pin'leri haritaya sığdır" arrow>
                    <IconButton size="small"
                      onClick={() => sendTechMapCommand('fitBounds')}
                      sx={{ ml: 'auto', width: 26, height: 26, bgcolor: alpha('#4caf50', 0.1), '&:hover': { bgcolor: alpha('#4caf50', 0.2) } }}>
                      <MyLocationIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9, p: 1.2, overflowY: 'auto', minHeight: 0 }}>
                  {techMapData.jobs.map((job, i) => {
                    const sc = getStatusColorObj(job.status);
                    const wColor = getWorkSegmentColor(job.status);
                    const isSimulationMoved = job.simulationMoved === true;
                    const pinBg = isSimulationMoved ? '#d32f2f' : sc.text;
                    const timeSlotText = job.serviceLocation ? 'Servis Lokasyonu' : (job.appt ? `${job.appt}${job.apptEnd ? ` - ${job.apptEnd}` : ''}` : '-');
                    const routeTooltip = (job.customerName || job.dealerName) ? (
                      <Box sx={{ color: darkMode ? '#f8fafc' : '#1f2937' }}>
                        {job.customerName && (
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'inherit' }}>
                            Müşteri: {job.customerName}
                          </Typography>
                        )}
                        {job.dealerName && (
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'inherit' }}>
                            Bayi: {job.dealerName}
                          </Typography>
                        )}
                      </Box>
                    ) : '';
                    return (
                      <Tooltip
                        key={i}
                        title={routeTooltip}
                        arrow
                        placement="right"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: darkMode ? '#111827' : '#fff',
                              color: darkMode ? '#f8fafc' : '#1f2937',
                              border: `1px solid ${darkMode ? alpha('#fff', 0.14) : alpha('#0f172a', 0.12)}`,
                              boxShadow: darkMode ? '0 8px 24px rgba(0,0,0,0.42)' : '0 8px 24px rgba(15,23,42,0.16)',
                            },
                          },
                          arrow: {
                            sx: {
                              color: darkMode ? '#111827' : '#fff',
                            },
                          },
                        }}
                      >
                        <Box
                          onClick={() => sendTechMapCommand({ type: 'focusPin', order: job.order })}
                          tabIndex={0}
                          sx={{
                            borderRadius: '12px', overflow: 'hidden',
                            border: `${isSimulationMoved ? 2 : 1}px solid ${isSimulationMoved ? '#d32f2f' : (darkMode ? alpha('#fff', 0.1) : alpha('#0f172a', 0.1))}`,
                            bgcolor: isSimulationMoved ? (darkMode ? alpha('#d32f2f', 0.16) : '#fff5f5') : (darkMode ? alpha('#0b1220', 0.65) : '#fff'),
                            boxShadow: isSimulationMoved
                              ? `0 5px 18px ${alpha('#d32f2f', darkMode ? 0.28 : 0.18)}`
                              : (darkMode ? '0 4px 14px rgba(0,0,0,0.16)' : '0 4px 14px rgba(15,23,42,0.05)'),
                            height: 48,
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: alpha(pinBg, 0.35),
                              boxShadow: darkMode ? '0 8px 20px rgba(0,0,0,0.24)' : '0 8px 20px rgba(15,23,42,0.1)',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          {/* Kart başlık */}
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            px: 1.2, py: 0.75,
                            height: '100%',
                            bgcolor: isSimulationMoved
                              ? (darkMode ? alpha('#7f1d1d', 0.46) : '#fff5f5')
                              : (darkMode ? alpha('#111827', 0.82) : '#fff'),
                          }}>
                            {/* Sıra numarası pin */}
                            <Box sx={{
                              width: 24, height: 24, borderRadius: '50%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: pinBg, color: '#fff',
                              fontWeight: isSimulationMoved ? 950 : 800, fontSize: '0.7rem',
                              border: '2px solid #fff',
                              boxShadow: `0 2px 6px ${alpha(pinBg, 0.4)}`,
                              flexShrink: 0,
                            }}>
                              {job.serviceLocation ? 'S' : job.order}
                            </Box>
                            <Tooltip title={job.serviceLocation ? 'Servis Lokasyonu' : (job.ofsc ? `Randevu No: ${job.ofsc}` : '')} arrow>
                              <Typography sx={{
                                fontSize: '0.82rem',
                                fontWeight: isSimulationMoved ? 950 : 900,
                                color: isSimulationMoved ? '#d32f2f' : wColor.text,
                                letterSpacing: 0,
                                whiteSpace: 'nowrap',
                              }}>
                                {timeSlotText}
                              </Typography>
                            </Tooltip>
                            {job.neighbourhood && (
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: isSimulationMoved ? 900 : 700, color: isSimulationMoved ? (darkMode ? '#fecaca' : '#991b1b') : (darkMode ? '#cbd5e1' : '#475569'), flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.neighbourhood}</Typography>
                            )}
                            {isSimulationMoved && (
                              <Chip label="TAŞINAN İŞ" size="small" sx={{
                                height: 20, fontSize: '0.58rem', fontWeight: 950,
                                color: '#fff', bgcolor: '#d32f2f',
                                border: '1px solid #b91c1c',
                              }} />
                            )}
                            <Chip label={sc.label} size="small" sx={{
                              height: 20, fontSize: '0.6rem', fontWeight: 800,
                              bgcolor: darkMode ? alpha('#fff', 0.05) : alpha('#f1f5f9', 0.9),
                              color: darkMode ? '#cbd5e1' : '#475569',
                              border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
                            }} />
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  })}
                  </Box>
                </Box>
              </Box>
          ) : (
            <Box sx={{
              height: '100%',
              minHeight: 420,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 1.2,
              p: 3,
              textAlign: 'center',
              bgcolor: darkMode ? alpha('#0f172a', 0.2) : '#f8fafc',
            }}>
              <MapIcon sx={{ fontSize: 44, color: darkMode ? '#64748b' : '#94a3b8' }} />
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 850, color: darkMode ? '#e2e8f0' : '#334155' }}>
                Seçili tarihte haritada gösterilecek konum bulunamadı
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: darkMode ? '#94a3b8' : '#64748b' }}>
                {dayjs(selectedDate).format('DD MMMM YYYY, dddd')}
              </Typography>
            </Box>
          ))}
        </DraggableResizablePopup>
      )}

      {/* ── Teknisyen Fotoğraf Önizleme ── */}
      {techPhotoOpen && (
        <Backdrop open onClick={() => setTechPhotoOpen(false)}
          sx={{ zIndex: 1400, bgcolor: 'rgba(0,0,0,0.85)', cursor: 'pointer' }}>
          {techPhoto && (
            <Box component="img" src={techPhoto} alt="Teknisyen" onClick={(e) => e.stopPropagation()}
              sx={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', cursor: 'default' }} />
          )}
        </Backdrop>
      )}

      {/* ── Klavye Kısayolları Dialog ── */}
      {shortcutHelpOpen && (
        <Dialog
          open={shortcutHelpOpen}
          onClose={() => setShortcutHelpOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 3, minWidth: 380,
              background: darkMode ? 'linear-gradient(160deg, #1e1e2e 0%, #252538 100%)' : 'linear-gradient(160deg, #ffffff 0%, #f8f9ff 100%)',
              border: `1px solid ${alpha('#7c4dff', 0.15)}`,
              boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
            },
          }}
        >
          <Box sx={{ height: 3, background: 'linear-gradient(90deg, #7c4dff, #448AFF, #7c4dff)' }} />
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <KeyboardIcon sx={{ color: '#7c4dff' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Klavye Kısayolları</Typography>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pb: 2 }}>
            {[
              { key: '← →', desc: 'Önceki / Sonraki gün' },
              { key: 'T', desc: 'Bugüne git' },
              { key: '+ / -', desc: 'Yakınlaştır / Uzaklaştır' },
              { key: '0', desc: 'Varsayılan zoom' },
              { key: 'N', desc: 'Şu ana kaydır' },
              { key: 'R', desc: 'Yenile' },
              { key: 'F', desc: 'Tam ekran' },
              { key: '?', desc: 'Bu pencereyi aç/kapat' },
              { key: 'Esc', desc: 'Kapat' },
            ].map((s, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.6, borderBottom: i < 8 ? `1px solid ${darkMode ? alpha('#fff', 0.05) : alpha('#000', 0.05)}` : 'none' }}>
                <Box sx={{ minWidth: 50, display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{
                    px: 1, py: 0.3, borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace',
                    bgcolor: darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.06),
                    border: `1px solid ${darkMode ? alpha('#fff', 0.12) : alpha('#000', 0.1)}`,
                    color: darkMode ? '#e0e0e0' : '#333',
                    boxShadow: darkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.08)',
                    whiteSpace: 'nowrap',
                  }}>
                    {s.key}
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: darkMode ? '#bbb' : '#555' }}>{s.desc}</Typography>
              </Box>
            ))}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setShortcutHelpOpen(false)} variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: 'none' }}>Kapat</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── İş Taşıma Simülasyonu Popup ── */}
      {moveSimulationOpen && (
        <DraggableResizablePopup
          open={moveSimulationOpen}
          onClose={() => setMoveSimulationOpen(false)}
          title="İş Taşıma Simülasyonu"
          subtitle="İşlerin teknisyenler arasındaki taşıma etkisini karşılaştırın"
          icon={AltRouteIcon}
          maxWidth="xl"
          defaultWidth={1750}
          defaultHeight={1020}
          primaryColor={darkMode ? '#26a69a' : '#00796b'}
        >
          <MoveSimulation
            rawRows={rawSchedulingDataRef.current}
            calculatedRows={data}
            calculationOptions={schedulingCalculationOptionsRef.current}
            cacheScope={serviceShopId}
            cacheVersion={moveSimulationCacheVersion}
            onMove={handleSimulationMoveClick}
            onShowMap={handleSimulationMapClick}
          />
        </DraggableResizablePopup>
      )}

      {/* ── Çalışma Takvimi Popup ── */}
      {calendarOpen && (
        <DraggableResizablePopup
          open={calendarOpen}
          onClose={() => { setCalendarOpen(false); setCalendarSelectedTech(null); setCalendarEditRows([]); setCalendarData([]); setCalendarTechSearch(''); }}
          title="Çalışma Takvimi Yönetimi"
          subtitle="Teknisyen bazlı çalışma saatlerini düzenleyin"
          icon={EditCalendarIcon}
          maxWidth="xl"
          primaryColor={darkMode ? '#7c4dff' : '#1565c0'}
          headerActions={
            <Tooltip title="Seçilen tarihte tüm teknisyenleri kapat" arrow>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SecurityIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => { setCloseAllDate(null); setCloseAllOpen(true); }}
                sx={{
                  height: 28, fontSize: '0.6rem', fontWeight: 700, textTransform: 'none',
                  borderColor: darkMode ? alpha('#ef5350', 0.4) : alpha('#d32f2f', 0.4), color: darkMode ? '#ef5350' : '#d32f2f', borderRadius: '8px',
                  '&:hover': { bgcolor: darkMode ? alpha('#f44336', 0.12) : alpha('#f44336', 0.08), borderColor: darkMode ? '#ef5350' : '#d32f2f' },
                }}
              >
                Tüm Teknisyenleri Kapat
              </Button>
            </Tooltip>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Box sx={{ p: 0, display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: 'hidden', flex: 1, minHeight: 0 }}>
              {/* Sol panel - Teknisyen listesi */}
              <Box sx={{
                width: isMobile ? '100%' : { xs: 180, sm: 220, md: 260 },
                minWidth: isMobile ? 'auto' : { xs: 180, sm: 220, md: 260 },
                maxHeight: isMobile ? (calendarSelectedTech ? 190 : '100%') : 'none',
                borderRight: isMobile ? 'none' : `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08)}`,
                borderBottom: isMobile ? `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08)}` : 'none',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                bgcolor: darkMode ? alpha('#000', 0.15) : alpha('#f5f5f5', 0.8),
                flexShrink: 0,
              }}>
                <Box sx={{ p: isMobile ? 1 : 1.5, borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.04) : alpha('#000', 0.06)}` }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', textTransform: 'none', letterSpacing: '0.04em', mb: 0.5 }}>
                    Teknisyenler ({calendarTechList.length})
                  </Typography>
                  <FormControl size="small" fullWidth sx={{ mb: 0.5 }}>
                    <Select
                      value={calendarTechSort}
                      onChange={(event) => setCalendarTechSort(event.target.value)}
                      inputProps={{ 'aria-label': 'Teknisyen isim sıralaması' }}
                      MenuProps={{
                        sx: { zIndex: 1401 },
                        PaperProps: { sx: { bgcolor: darkMode ? '#252538' : '#fff' } },
                      }}
                      sx={{
                        height: 28,
                        borderRadius: '7px',
                        bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: darkMode ? '#b0bec5' : '#455a64',
                        '& .MuiSelect-select': { py: 0.4, pl: 1 },
                        '& .MuiSelect-icon': { fontSize: 18, color: darkMode ? '#888' : '#777' },
                        '& fieldset': { borderColor: darkMode ? alpha('#fff', 0.1) : alpha('#000', 0.12) },
                      }}
                    >
                      <MenuItem value="name-asc" sx={{ fontSize: '0.72rem' }}>İsme Göre (A-Z)</MenuItem>
                      <MenuItem value="name-desc" sx={{ fontSize: '0.72rem' }}>İsme Göre (Z-A)</MenuItem>
                      <MenuItem value="code-asc" sx={{ fontSize: '0.72rem' }}>Koda Göre (Artan)</MenuItem>
                      <MenuItem value="code-desc" sx={{ fontSize: '0.72rem' }}>Koda Göre (Azalan)</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    placeholder="Ara..."
                    value={calendarTechSearch}
                    onChange={(e) => setCalendarTechSearch(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ fontSize: 16, color: darkMode ? '#777' : '#999', mr: 0.5 }} />,
                      ...(calendarTechSearch && {
                        endAdornment: (
                          <IconButton size="small" onClick={() => setCalendarTechSearch('')} sx={{ p: 0.25 }}>
                            <CloseIcon sx={{ fontSize: 14, color: darkMode ? '#777' : '#999' }} />
                          </IconButton>
                        ),
                      }),
                    }}
                    sx={{
                      mt: 0.5,
                      '& .MuiOutlinedInput-root': {
                        height: 30, fontSize: '0.7rem', borderRadius: '8px',
                        bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff',
                        '& fieldset': { borderColor: darkMode ? alpha('#fff', 0.1) : alpha('#000', 0.1) },
                        '&:hover fieldset': { borderColor: darkMode ? alpha('#fff', 0.2) : alpha('#000', 0.2) },
                        '&.Mui-focused fieldset': { borderColor: darkMode ? '#7c4dff' : '#1565c0' },
                      },
                      '& .MuiOutlinedInput-input': { py: 0.5 },
                    }}
                    fullWidth
                  />
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
                  {calendarTechListLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} sx={{ color: darkMode ? '#b388ff' : '#1565c0' }} />
                    </Box>
                  ) : visibleCalendarTechList.map((tech) => {
                    const isSelected = calendarSelectedTech?.TechCode === tech.TechCode;
                    return (
                      <Box
                        key={tech.TechCode}
                        onClick={() => handleCalendarTechSelect(tech)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                          cursor: 'pointer', mx: 0.5, borderRadius: '10px', mb: 0.3,
                          bgcolor: isSelected
                            ? (darkMode ? alpha('#7c4dff', 0.2) : alpha('#1565c0', 0.1))
                            : 'transparent',
                          border: isSelected
                            ? `1px solid ${darkMode ? alpha('#7c4dff', 0.4) : alpha('#1565c0', 0.4)}`
                            : '1px solid transparent',
                          '&:hover': {
                            bgcolor: isSelected
                              ? (darkMode ? alpha('#7c4dff', 0.25) : alpha('#1565c0', 0.15))
                              : (darkMode ? alpha('#fff', 0.04) : alpha('#000', 0.04)),
                          },
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <Avatar sx={{
                          width: 30, height: 30, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: isSelected ? (darkMode ? '#7c4dff' : '#1565c0') : (darkMode ? alpha('#546e7a', 0.5) : alpha('#90a4ae', 0.4)),
                          color: isSelected ? '#fff' : (darkMode ? '#b0bec5' : '#546e7a'),
                        }}>
                          {(tech.TechName || '').substring(0, 2).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{
                            fontSize: '0.72rem', fontWeight: isSelected ? 700 : 600,
                            color: isSelected ? (darkMode ? '#e8e0ff' : '#0d47a1') : (darkMode ? '#ccc' : '#333'),
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {toTitleCase(tech.TechName)}
                          </Typography>
                        </Box>
                        {isSelected && <CheckCircleIcon sx={{ fontSize: 16, color: darkMode ? '#b388ff' : '#1565c0' }} />}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Sağ panel - Takvim düzenleme */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {!calendarSelectedTech ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <CalendarMonthIcon sx={{ fontSize: 64, color: darkMode ? alpha('#7c4dff', 0.3) : alpha('#1565c0', 0.2) }} />
                    <Typography sx={{ fontSize: '0.9rem', color: darkMode ? '#666' : '#999', fontWeight: 500 }}>
                      Düzenlemek için sol panelden bir teknisyen seçin
                    </Typography>
                  </Box>
                ) : calendarLoading ? (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={40} sx={{ color: darkMode ? '#b388ff' : '#1565c0' }} />
                  </Box>
                ) : (
                  <>
                    {/* Üst araç çubuğu */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.2,
                      borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06)}`,
                      bgcolor: darkMode ? alpha('#000', 0.1) : alpha('#fff', 0.7),
                      flexWrap: 'wrap',
                    }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: darkMode ? '#7c4dff' : '#1565c0', fontSize: '0.7rem', fontWeight: 700 }}>
                        {(calendarSelectedTech.TechName || '').substring(0, 2).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: darkMode ? '#e8e0ff' : '#0d47a1' }}>
                          {toTitleCase(calendarSelectedTech.TechName)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: darkMode ? '#777' : '#999' }}>
                          {calendarEditRows.length} gün tanımlı
                        </Typography>
                      </Box>

                      {/* Toplu ekleme kontrolleri + Kaydet/Sıfırla */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Tooltip title="Toplu çalışma takvimi ekle" arrow>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ContentCopyIcon sx={{ fontSize: '14px !important' }} />}
                            onClick={handleBulkAddOpen}
                            sx={{
                              height: 30, fontSize: '0.62rem', fontWeight: 700, textTransform: 'none',
                              borderColor: darkMode ? alpha('#b388ff', 0.4) : alpha('#1565c0', 0.4), color: darkMode ? '#b388ff' : '#1565c0', borderRadius: '8px',
                              '&:hover': { bgcolor: darkMode ? alpha('#7c4dff', 0.12) : alpha('#1565c0', 0.08), borderColor: darkMode ? '#b388ff' : '#1565c0' },
                            }}
                          >
                            Toplu Ekle
                          </Button>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          onClick={() => setCalendarEditRows(calendarData.map(r => ({ ...r })))}
                          size="small"
                          startIcon={<RestartAltIcon sx={{ fontSize: '14px !important' }} />}
                          sx={{
                            height: 30, textTransform: 'none', fontWeight: 600, borderRadius: '8px', fontSize: '0.62rem',
                            borderColor: alpha('#666', 0.3), color: darkMode ? '#aaa' : '#666',
                          }}
                        >
                          Sıfırla
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleCalendarSave}
                          disabled={calendarSaving}
                          startIcon={calendarSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: '14px !important' }} />}
                          size="small"
                          sx={{
                            height: 30, textTransform: 'none', fontWeight: 700, borderRadius: '8px', fontSize: '0.62rem',
                            bgcolor: darkMode ? '#7c4dff' : '#1565c0', px: 2, color: '#fff',
                            '&:hover': { bgcolor: darkMode ? '#651fff' : '#0d47a1' },
                            boxShadow: `0 4px 16px ${darkMode ? alpha('#7c4dff', 0.3) : alpha('#1565c0', 0.3)}`,
                          }}
                        >
                          {calendarSaving ? 'Kaydediliyor...' : 'Kaydet'}
                        </Button>
                      </Box>
                    </Box>

                    {/* Takvim Görünümü */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      {/* Ay navigasyonu */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, px: isMobile ? 1 : 2.5, py: isMobile ? 0.5 : 1,
                        borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06)}`,
                        bgcolor: darkMode ? alpha('#000', 0.08) : alpha('#e3f2fd', 0.3),
                        flexWrap: 'wrap',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => setCalendarMonth(prev => { const d = new Date(prev); d.setMonth(d.getMonth() - 1); return d; })}>
                            <ChevronLeftIcon sx={{ fontSize: isMobile ? 18 : 20, color: darkMode ? '#aaa' : '#555' }} />
                          </IconButton>
                          <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.9rem', fontWeight: 800, color: darkMode ? '#e8e0ff' : '#0d47a1', minWidth: isMobile ? 100 : 150, textAlign: 'center', letterSpacing: '-0.01em' }}>
                            {dayjs(calendarMonth).format('MMMM YYYY')}
                          </Typography>
                          <IconButton size="small" onClick={() => setCalendarMonth(prev => { const d = new Date(prev); d.setMonth(d.getMonth() + 1); return d; })}>
                            <ChevronRightIcon sx={{ fontSize: isMobile ? 18 : 20, color: darkMode ? '#aaa' : '#555' }} />
                          </IconButton>
                        </Box>
                        <Box sx={{ flex: 1 }} />
                        <Box sx={{ display: 'flex', gap: isMobile ? 0.75 : 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <Box sx={{ width: isMobile ? 8 : 10, height: isMobile ? 8 : 10, borderRadius: '3px', bgcolor: darkMode ? '#7c4dff' : '#1565c0' }} />
                            <Typography sx={{ fontSize: isMobile ? '0.5rem' : '0.58rem', color: darkMode ? '#999' : '#666' }}>Tanımlı</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <Box sx={{ width: isMobile ? 8 : 10, height: isMobile ? 8 : 10, borderRadius: '3px', bgcolor: darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.05) }} />
                            <Typography sx={{ fontSize: isMobile ? '0.5rem' : '0.58rem', color: darkMode ? '#999' : '#666' }}>Boş</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <Box sx={{ width: isMobile ? 8 : 10, height: isMobile ? 8 : 10, borderRadius: '3px', bgcolor: alpha('#ff9800', 0.2), border: `1px solid ${alpha('#ff9800', 0.4)}` }} />
                            <Typography sx={{ fontSize: isMobile ? '0.5rem' : '0.58rem', color: darkMode ? '#999' : '#666' }}>Pazar</Typography>
                          </Box>
                          <Typography sx={{ fontSize: isMobile ? '0.52rem' : '0.62rem', fontWeight: 600, color: darkMode ? '#b388ff' : '#1565c0', ml: isMobile ? 0 : 1 }}>
                            {calendarEditRows.length} gün
                          </Typography>
                        </Box>
                      </Box>

                      {/* Gün başlıkları */}
                      <Box sx={{
                        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0,
                        borderBottom: `2px solid ${darkMode ? alpha('#7c4dff', 0.2) : alpha('#1565c0', 0.12)}`,
                        bgcolor: darkMode ? alpha('#000', 0.12) : alpha('#f0f4f8', 0.8),
                      }}>
                        {(isMobile ? ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'] : ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar']).map((d, i) => (
                          <Typography key={d} sx={{
                            fontSize: isMobile ? '0.52rem' : '0.62rem', fontWeight: 700, textAlign: 'center', py: isMobile ? 0.4 : 0.6,
                            color: i === 6 ? (darkMode ? '#ff9800' : '#e65100') : (darkMode ? '#888' : '#666'),
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                            borderRight: i < 6 ? `1px solid ${darkMode ? alpha('#fff', 0.03) : alpha('#000', 0.04)}` : 'none',
                          }}>{d}</Typography>
                        ))}
                      </Box>

                      {/* Takvim grid */}
                      <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: '1fr', gap: 0, height: '100%' }}>
                          {(() => {
                            const firstDay = dayjs(calendarMonth).startOf('month');
                            const blanks = (firstDay.day() + 6) % 7;
                            const cells = [];
                            for (let i = 0; i < blanks; i++) {
                              cells.push(
                                <Box key={`b-${i}`} sx={{
                                  borderRight: `1px solid ${darkMode ? alpha('#fff', 0.02) : alpha('#000', 0.04)}`,
                                  borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.02) : alpha('#000', 0.04)}`,
                                  bgcolor: darkMode ? alpha('#000', 0.06) : alpha('#f5f5f5', 0.4),
                                }} />
                              );
                            }
                            calendarMonthDays.forEach(dateStr => {
                              const entry = calendarDayMap[dateStr];
                              const dayNum = dayjs(dateStr).date();
                              const dayOfWeek = dayjs(dateStr).day();
                              const isTodayDate = dateStr === dayjs().format('YYYY-MM-DD');
                              const isWeekend = dayOfWeek === 0;
                              const isPast = dateStr < dayjs().format('YYYY-MM-DD');
                              const origEntry = calendarData.find(r => r.Date === dateStr);
                              const isModified = (entry && !origEntry) || (!entry && origEntry) ||
                                (entry && origEntry && (entry.StartTime !== origEntry.StartTime || entry.EndTime !== origEntry.EndTime));
                              cells.push(
                                <Box key={dateStr} sx={{
                                  display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
                                  cursor: isPast ? 'not-allowed' : 'pointer',
                                  opacity: isPast ? 0.5 : 1,
                                  borderRight: `1px solid ${darkMode ? alpha('#fff', 0.04) : alpha('#000', 0.06)}`,
                                  borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.04) : alpha('#000', 0.06)}`,
                                  ...(isModified && !isPast && {
                                    outline: `2px solid ${darkMode ? '#ff9800' : '#e65100'}`,
                                    outlineOffset: '-2px',
                                    zIndex: 1,
                                  }),
                                  bgcolor: isPast
                                    ? (darkMode ? alpha('#000', 0.12) : alpha('#f5f5f5', 0.6))
                                    : isTodayDate
                                      ? (darkMode ? alpha('#ff9800', 0.08) : alpha('#fff8e1', 0.8))
                                      : isWeekend
                                        ? (darkMode ? alpha('#ff9800', 0.03) : alpha('#fff3e0', 0.4))
                                        : (darkMode ? 'transparent' : alpha('#fff', 0.6)),
                                  ...(!isPast && {
                                    '&:hover': {
                                      bgcolor: darkMode ? alpha('#7c4dff', 0.1) : alpha('#e3f2fd', 0.6),
                                    },
                                  }),
                                  transition: 'background 0.15s ease',
                                }}
                                  onClick={() => {
                                    if (isPast) return;
                                    if (!entry) {
                                      const newId = Date.now();
                                      setCalendarEditRows(prev => [...prev, { id: newId, Date: dateStr, StartTime: '09:00', EndTime: '18:00', isNew: true }].sort((a, b) => a.Date.localeCompare(b.Date)));
                                    }
                                  }}
                                >
                                  {/* Üst: Gün numarası + gün adı */}
                                  <Box sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.3, pt: 0.2, pb: 0.1,
                                    borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : alpha('#000', 0.04)}`,
                                    bgcolor: entry
                                      ? (darkMode ? alpha('#7c4dff', 0.08) : alpha('#1565c0', 0.04))
                                      : 'transparent',
                                    flexShrink: 0,
                                  }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.2 }}>
                                      <Typography sx={{
                                        fontSize: '0.78rem', fontWeight: 800, lineHeight: 1,
                                        color: isTodayDate
                                          ? '#ff9800'
                                          : entry
                                            ? (darkMode ? '#b388ff' : '#1565c0')
                                            : isWeekend
                                              ? (darkMode ? alpha('#ff9800', 0.5) : '#e65100')
                                              : (darkMode ? '#555' : '#bbb'),
                                      }}>
                                        {dayNum}
                                      </Typography>
                                      <Typography sx={{
                                        fontSize: '0.5rem', fontWeight: 900,
                                        color: isWeekend ? (darkMode ? alpha('#f44336', 0.4) : '#f44336')
                                          : (darkMode ? '#000' : '#000'),
                                        letterSpacing: '0.01em', display: isMobile ? 'none' : 'block',
                                      }}>
                                        {dayjs(dateStr).format('dddd')}
                                      </Typography>
                                    </Box>
                                    {isTodayDate && (
                                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#ff9800' }} />
                                    )}
                                    {!isPast && (
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {entry && (
                                          <IconButton
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); handleCalendarRemoveRow(entry.id); }}
                                            sx={{
                                              width: 16, height: 16,
                                              color: '#f44336',
                                              bgcolor: alpha('#f44336', 0.08),
                                              border: `1px solid ${alpha('#f44336', 0.25)}`,
                                              borderRadius: '5px',
                                              '&:hover': { color: '#fff', bgcolor: '#f44336', boxShadow: '0 2px 8px rgba(244,67,54,0.4)' },
                                              transition: 'all 0.2s ease',
                                            }}
                                          >
                                            <DeleteOutlineIcon sx={{ fontSize: 11 }} />
                                          </IconButton>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                  {isModified && !isPast && (
                                    <Box sx={{ position: 'absolute', top: 2, right: 2, width: 7, height: 7, borderRadius: '50%', bgcolor: '#ff9800', boxShadow: '0 0 4px rgba(255,152,0,0.5)' }} />
                                  )}

                                  {/* Alt: Çalışma saatleri (dikey) */}
                                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', px: 0.3, pt: 0.2, pb: 0, minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
                                    {entry ? (
                                      <Box sx={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, width: '100%',
                                        bgcolor: darkMode ? alpha('#7c4dff', 0.12) : alpha('#1565c0', 0.06),
                                        borderRadius: '4px', py: 0.3,
                                        border: `1px solid ${darkMode ? alpha('#7c4dff', 0.25) : alpha('#1565c0', 0.12)}`,
                                      }}>
                                        <input
                                          type="text"
                                          value={entry.StartTime}
                                          maxLength={5}
                                          placeholder="09:00"
                                          disabled={isPast}
                                          onChange={(e) => { e.stopPropagation(); handleCalendarRowChange(entry.id, 'StartTime', e.target.value); }}
                                          onClick={(e) => e.stopPropagation()}
                                          style={{
                                            border: 'none', background: 'transparent',
                                            color: isPast ? (darkMode ? '#666' : '#999') : (darkMode ? '#e0e0e0' : '#212121'),
                                            fontSize: isMobile ? '0.8rem' : '1.1rem', fontWeight: 400, width: isMobile ? 40 : 56, padding: '1px 0', outline: 'none',
                                            fontFamily: 'monospace', textAlign: 'center', letterSpacing: '0.03em',
                                            cursor: isPast ? 'not-allowed' : 'text', borderRadius: '4px',
                                          }}
                                          onFocus={(e) => { if (!isPast) { e.target.style.borderBottom = `2px solid ${darkMode ? '#b388ff' : '#1565c0'}`; e.target.style.background = darkMode ? 'rgba(124,77,255,0.1)' : 'rgba(21,101,192,0.06)'; } }}
                                          onBlur={(e) => { e.target.style.borderBottom = 'none'; e.target.style.background = 'transparent'; }}
                                        />
                                        <Typography sx={{ fontSize: '0.45rem', fontWeight: 700, color: darkMode ? '#546e7a' : '#90a4ae', lineHeight: 1 }}>—</Typography>
                                        <input
                                          type="text"
                                          value={entry.EndTime}
                                          maxLength={5}
                                          placeholder="18:00"
                                          disabled={isPast}
                                          onChange={(e) => { e.stopPropagation(); handleCalendarRowChange(entry.id, 'EndTime', e.target.value); }}
                                          onClick={(e) => e.stopPropagation()}
                                          style={{
                                            border: 'none', background: 'transparent',
                                            color: isPast ? (darkMode ? '#666' : '#999') : (darkMode ? '#e0e0e0' : '#212121'),
                                            fontSize: isMobile ? '0.8rem' : '1.1rem', fontWeight: 400, width: isMobile ? 40 : 56, padding: '1px 0', outline: 'none',
                                            fontFamily: 'monospace', textAlign: 'center', letterSpacing: '0.03em',
                                            cursor: isPast ? 'not-allowed' : 'text', borderRadius: '4px',
                                          }}
                                          onFocus={(e) => { if (!isPast) { e.target.style.borderBottom = `2px solid ${darkMode ? '#b388ff' : '#1565c0'}`; e.target.style.background = darkMode ? 'rgba(124,77,255,0.1)' : 'rgba(21,101,192,0.06)'; } }}
                                          onBlur={(e) => { e.target.style.borderBottom = 'none'; e.target.style.background = 'transparent'; }}
                                        />
                                      </Box>
                                    ) : (
                                      !isPast && (
                                        <Box sx={{
                                          display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, width: '100%',
                                          borderRadius: '6px',
                                          border: `1px dashed ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08)}`,
                                          '&:hover': { border: `1px dashed ${darkMode ? alpha('#7c4dff', 0.4) : alpha('#1565c0', 0.4)}`, bgcolor: darkMode ? alpha('#7c4dff', 0.04) : alpha('#1565c0', 0.04) },
                                          transition: 'all 0.15s ease',
                                        }}>
                                          <AddCircleOutlineIcon sx={{ fontSize: 14, color: darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.1) }} />
                                        </Box>
                                      )
                                    )}
                                  </Box>
                                </Box>
                              );
                            });
                            // Kalan hücreleri doldur (7'nin katına tamamla)
                            const remainder = (blanks + calendarMonthDays.length) % 7;
                            if (remainder > 0) {
                              for (let i = 0; i < 7 - remainder; i++) {
                                cells.push(
                                  <Box key={`e-${i}`} sx={{
                                    borderRight: `1px solid ${darkMode ? alpha('#fff', 0.02) : alpha('#000', 0.04)}`,
                                    borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.02) : alpha('#000', 0.04)}`,
                                    bgcolor: darkMode ? alpha('#000', 0.06) : alpha('#f5f5f5', 0.4),
                                  }} />
                                );
                              }
                            }
                            return cells;
                          })()}
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </Box>


          </Box>
        </DraggableResizablePopup>
      )}

      {/* ── Tüm Teknisyenleri Kapat Dialog ── */}
      {closeAllOpen && (
        <Dialog
          open={closeAllOpen}
          onClose={() => { setCloseAllOpen(false); setCloseAllDate(null); }}
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: darkMode
                ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
                : 'linear-gradient(135deg, #fff 0%, #fafafa 100%)',
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex', alignItems: 'center', gap: 1, py: 1.5, px: 2.5,
            borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08)}`,
          }}>
            <SecurityIcon sx={{ fontSize: 22, color: darkMode ? '#ef5350' : '#d32f2f' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: darkMode ? '#ffcdd2' : '#c62828' }}>
                Tüm Teknisyenleri Kapat
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: darkMode ? '#999' : '#777' }}>
                Seçilen tarihte tüm teknisyenlerin takvimi kapatılacak
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => { setCloseAllOpen(false); setCloseAllDate(null); }}>
              <CloseIcon sx={{ fontSize: 18, color: darkMode ? '#aaa' : '#666' }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
              <DateCalendar
                value={closeAllDate}
                onChange={(val) => setCloseAllDate(val)}
                sx={{
                  '& .MuiPickersDay-root.Mui-selected': {
                    bgcolor: darkMode ? '#ef5350' : '#d32f2f',
                    '&:hover': { bgcolor: darkMode ? '#f44336' : '#c62828' },
                  },
                  '& .MuiPickersCalendarHeader-label': {
                    color: darkMode ? '#e0e0e0' : '#333',
                    fontWeight: 700,
                  },
                  '& .MuiDayCalendar-weekDayLabel': {
                    color: darkMode ? '#888' : '#999',
                  },
                  '& .MuiPickersDay-root': {
                    color: darkMode ? '#ccc' : '#333',
                  },
                }}
              />
            </LocalizationProvider>
            {closeAllDate && (
              <Alert severity="warning" sx={{ mt: 1, fontSize: '0.72rem', borderRadius: 2 }}>
                <strong>{dayjs(closeAllDate).format('DD MMMM YYYY, dddd')}</strong> tarihinde tüm teknisyenler kapatılacak.
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => { setCloseAllOpen(false); setCloseAllDate(null); }}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '8px', fontSize: '0.72rem',
                borderColor: alpha('#666', 0.3), color: darkMode ? '#aaa' : '#666',
              }}
            >
              Vazgeç
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleCloseAllSubmit}
              disabled={!closeAllDate || closeAllSaving}
              startIcon={closeAllSaving ? <CircularProgress size={14} color="inherit" /> : <SecurityIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: '8px', fontSize: '0.72rem',
                bgcolor: darkMode ? '#ef5350' : '#d32f2f', color: '#fff', px: 3,
                '&:hover': { bgcolor: darkMode ? '#f44336' : '#c62828' },
                '&.Mui-disabled': { bgcolor: alpha('#f44336', 0.3), color: alpha('#fff', 0.5) },
              }}
            >
              {closeAllSaving ? 'İşleniyor...' : 'Tümünü Kapat'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Toplu Ekle Dialog ── */}
      {bulkAddDialogOpen && (
        <Dialog
          open={bulkAddDialogOpen}
          onClose={() => setBulkAddDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: 520,
              bgcolor: darkMode ? '#1a1a2e' : '#fff',
            },
          }}
        >
          <DialogTitle sx={{
            pb: 1, pt: 2.5, px: 3,
            borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
          }}>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: darkMode ? '#e8e0ff' : '#1a237e' }}>
              {calendarSelectedTech ? `${calendarSelectedTech.TechCode} ${toTitleCase(calendarSelectedTech.TechName)}, ${dayjs().format('MM/DD/YY')}` : 'Toplu Ekle'}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Schedule */}
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: darkMode ? '#ccc' : '#333', mb: 0.5 }}>Çalışma Planı</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={bulkScheduleType}
                  onChange={(e) => setBulkScheduleType(e.target.value)}
                  sx={{
                    borderRadius: '8px',
                    bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff',
                    '& .MuiSelect-select': { fontSize: '0.85rem', py: 1.2 },
                  }}
                >
                  {SCHEDULE_TYPES.map((s) => (
                    <MenuItem key={s.label} value={s.label} sx={{ fontSize: '0.85rem' }}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Custom Working Time - Start/End time */}
            {bulkScheduleType === 'Özel Çalışma Zamanı' && (
              <>
                <Box>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: darkMode ? '#ccc' : '#333', mb: 0.5 }}>Başlangıç Saati</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value={bulkCustomStartHour}
                        onChange={(e) => setBulkCustomStartHour(e.target.value)}
                        sx={{ borderRadius: '8px', bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1.2 } }}
                      >
                        {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (
                          <MenuItem key={h} value={h} sx={{ fontSize: '0.85rem' }}>{h}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value={bulkCustomStartMin}
                        onChange={(e) => setBulkCustomStartMin(e.target.value)}
                        sx={{ borderRadius: '8px', bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1.2 } }}
                      >
                        {['00', '15', '30', '45'].map(m => (
                          <MenuItem key={m} value={m} sx={{ fontSize: '0.85rem' }}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: darkMode ? '#ccc' : '#333', mb: 0.5 }}>Bitiş Saati</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value={bulkCustomEndHour}
                        onChange={(e) => setBulkCustomEndHour(e.target.value)}
                        sx={{ borderRadius: '8px', bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1.2 } }}
                      >
                        {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (
                          <MenuItem key={h} value={h} sx={{ fontSize: '0.85rem' }}>{h}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value={bulkCustomEndMin}
                        onChange={(e) => setBulkCustomEndMin(e.target.value)}
                        sx={{ borderRadius: '8px', bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1.2 } }}
                      >
                        {['00', '15', '30', '45'].map(m => (
                          <MenuItem key={m} value={m} sx={{ fontSize: '0.85rem' }}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </>
            )}

            {/* End Date */}
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: darkMode ? '#ccc' : '#333', mb: 0.5 }}>Bitiş Tarihi</Typography>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  size="small"
                  fullWidth
                  value={bulkDialogEndDate ? dayjs(bulkDialogEndDate).format('DD.MM.YYYY') : ''}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarMonthIcon sx={{ fontSize: 18, color: darkMode ? '#b388ff' : '#1565c0', opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' },
                  }}
                  sx={{ pointerEvents: 'none' }}
                />
                <input
                  type="date"
                  value={bulkDialogEndDate}
                  min={dayjs().format('YYYY-MM-DD')}
                  onChange={(e) => e.target.value && setBulkDialogEndDate(e.target.value)}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 2 }}
                />
              </Box>
            </Box>

            {/* Repeat */}
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: darkMode ? '#ccc' : '#333', mb: 0.5 }}>Tekrar</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={bulkRepeat}
                  onChange={(e) => setBulkRepeat(e.target.value)}
                  sx={{
                    borderRadius: '8px',
                    bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff',
                    '& .MuiSelect-select': { fontSize: '0.85rem', py: 1.2 },
                  }}
                >
                  {REPEAT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.85rem' }}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Weekly: day selector */}
            {bulkRepeat === 'Weekly' && (
              <Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {WEEK_DAYS.map((day) => (
                    <Button
                      key={day.value}
                      size="small"
                      variant={bulkWeekDays.includes(day.value) ? 'contained' : 'outlined'}
                      onClick={() => {
                        setBulkWeekDays(prev =>
                          prev.includes(day.value)
                            ? prev.filter(d => d !== day.value)
                            : [...prev, day.value].sort()
                        );
                      }}
                      sx={{
                        minWidth: 40, px: 1, py: 0.5,
                        fontSize: '0.75rem', fontWeight: 600,
                        borderRadius: '6px', textTransform: 'none',
                        bgcolor: bulkWeekDays.includes(day.value)
                          ? (darkMode ? '#0277bd' : '#1976d2')
                          : 'transparent',
                        color: bulkWeekDays.includes(day.value) ? '#fff' : (darkMode ? '#aaa' : '#555'),
                        borderColor: day.value === 0
                          ? (bulkWeekDays.includes(day.value) ? '#d32f2f' : '#ef5350')
                          : (darkMode ? alpha('#aaa', 0.3) : alpha('#333', 0.25)),
                        ...(day.value === 0 && !bulkWeekDays.includes(day.value) && {
                          borderColor: '#ef5350',
                          color: darkMode ? '#ef5350' : '#d32f2f',
                        }),
                        ...(day.value === 0 && bulkWeekDays.includes(day.value) && {
                          bgcolor: darkMode ? '#c62828' : '#d32f2f',
                          borderColor: '#d32f2f',
                        }),
                        '&:hover': {
                          bgcolor: day.value === 0
                            ? (bulkWeekDays.includes(day.value) ? (darkMode ? '#b71c1c' : '#c62828') : (darkMode ? alpha('#ef5350', 0.12) : alpha('#d32f2f', 0.08)))
                            : (bulkWeekDays.includes(day.value)
                              ? (darkMode ? '#01579b' : '#1565c0')
                              : (darkMode ? alpha('#fff', 0.08) : alpha('#1976d2', 0.08))),
                        },
                      }}
                    >
                      {day.label}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}


          </DialogContent>
          <Divider sx={{ my: 1 }} />
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1, justifyContent: 'space-between' }}>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setBulkAddDialogOpen(false)}
                size="medium"
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: '8px', fontSize: '0.82rem',
                  borderColor: darkMode ? alpha('#aaa', 0.3) : alpha('#333', 0.3),
                  color: darkMode ? '#ccc' : '#333',
                  px: 2.5,
                }}
              >
                Vazgeç
              </Button>
              <Button
                variant="contained"
                onClick={handleBulkAddSubmit}
                disabled={calendarSaving}
                startIcon={calendarSaving ? <CircularProgress size={14} color="inherit" /> : null}
                size="medium"
                sx={{
                  textTransform: 'none', fontWeight: 700, borderRadius: '8px', fontSize: '0.82rem',
                  bgcolor: darkMode ? '#0277bd' : '#0277bd', px: 3.5, color: '#fff',
                  '&:hover': { bgcolor: darkMode ? '#01579b' : '#01579b' },
                }}
              >
                {calendarSaving ? 'Kaydediliyor...' : 'Uygula'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Ayarlar Popup ── */}
      {settingsOpen && (
        <Dialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          maxWidth={false}
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 3,
              minHeight: isMobile ? '100vh' : '85vh',
              maxHeight: isMobile ? '100vh' : '95vh',
              width: isMobile ? '100vw' : '95vw',
              maxWidth: '1500px !important',
              background: darkMode
                ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
                : 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: isMobile ? 1.5 : 3,
            background: darkMode
              ? `linear-gradient(135deg, ${alpha('#ff9800', 0.12)} 0%, ${alpha('#1a1a2e', 0.9)} 100%)`
              : `linear-gradient(135deg, ${alpha('#e65100', 0.08)} 0%, ${alpha('#fff3e0', 0.9)} 100%)`,
            borderBottom: `1px solid ${darkMode ? alpha('#ff9800', 0.2) : alpha('#e65100', 0.15)}`,
          }}>
            <SettingsIcon sx={{ fontSize: 28, color: darkMode ? '#ffb74d' : '#e65100' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: isMobile ? '0.95rem' : '1.1rem', fontWeight: 800, color: darkMode ? '#ffe0b2' : '#bf360c' }}>
                Ayarlar
              </Typography>
              <Typography noWrap sx={{ fontSize: '0.65rem', color: darkMode ? alpha('#ffe0b2', 0.5) : alpha('#bf360c', 0.5), fontWeight: 500 }}>
                Planlama yapılandırma ayarlarını düzenleyin
              </Typography>
            </Box>
            <IconButton onClick={() => setSettingsOpen(false)} size="small">
              <CloseIcon sx={{ fontSize: 20, color: darkMode ? '#aaa' : '#666' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: 'hidden' }}>
            {/* Sol panel - Menü */}
            <Box sx={{
              width: isMobile ? '100%' : 240, minWidth: isMobile ? 'auto' : 240,
              borderRight: isMobile ? 'none' : `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08)}`,
              borderBottom: isMobile ? `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08)}` : 'none',
              display: 'flex', flexDirection: isMobile ? 'row' : 'column', overflow: isMobile ? 'auto' : 'hidden',
              bgcolor: darkMode ? alpha('#000', 0.15) : alpha('#f5f5f5', 0.8),
              flexShrink: 0,
            }}>
              {!isMobile && (
                <Box sx={{ p: 1.5, borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.04) : alpha('#000', 0.06)}` }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  
                  </Typography>
                </Box>
              )}
              {[
                { key: 'tekMahalle', label: 'Teknisyen Mahalle', icon: <HomeWorkIcon sx={{ fontSize: 18 }} /> },
                { key: 'tekUzmanlik', label: 'Teknisyen Uzmanlık', icon: <EngineeringIcon sx={{ fontSize: 18 }} /> },
                { key: 'bayiServis', label: 'Bayi Servis İlişkisi', icon: <StorefrontIcon sx={{ fontSize: 18 }} /> },
                { divider: true },
                { key: 'grupMahalle', label: 'Grup Mahalle', icon: <GroupWorkIcon sx={{ fontSize: 18 }} /> },
                { key: 'bagliYerler', label: 'Aynı Gün Bağlı Yerler', icon: <AltRouteIcon sx={{ fontSize: 18 }} /> },
                { key: 'mahalleKisit', label: 'Mahalle Kısıtlaması Hariç', icon: <SecurityIcon sx={{ fontSize: 18 }} /> },
              ].map((tab, tabIdx) => {
                if (tab.divider) {
                  return !isMobile ? (
                    <Divider key={`div-${tabIdx}`} sx={{ mx: 2, my: 0.5, borderColor: darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08) }} />
                  ) : null;
                }
                const isActive = settingsActiveTab === tab.key;
                return (
                  <Box
                    key={tab.key}
                    onClick={() => {
                      setSettingsActiveTab(tab.key);
                      if (tab.key === 'bayiServis') fetchDealerServiceList();
                      if (tab.key === 'mahalleKisit') fetchMkList();
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: isMobile ? 1.5 : 2, py: isMobile ? 1 : 1.2,
                      cursor: 'pointer',
                      borderRadius: isMobile ? '8px' : 0,
                      m: isMobile ? 0.5 : 0,
                      whiteSpace: 'nowrap',
                      bgcolor: isActive
                        ? (darkMode ? alpha('#ff9800', 0.15) : alpha('#e65100', 0.08))
                        : 'transparent',
                      borderLeft: !isMobile && isActive ? `3px solid ${darkMode ? '#ffb74d' : '#e65100'}` : !isMobile ? '3px solid transparent' : 'none',
                      borderBottom: isMobile && isActive ? `2px solid ${darkMode ? '#ffb74d' : '#e65100'}` : isMobile ? '2px solid transparent' : 'none',
                      color: isActive ? (darkMode ? '#ffb74d' : '#e65100') : (darkMode ? '#888' : '#666'),
                      '&:hover': {
                        bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#000', 0.04),
                        color: darkMode ? '#ffb74d' : '#e65100',
                      },
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {React.cloneElement(tab.icon, { sx: { ...tab.icon.props.sx, color: 'inherit' } })}
                    <Typography sx={{ fontSize: isMobile ? '0.62rem' : '0.75rem', fontWeight: isActive ? 700 : 600, color: 'inherit' }}>
                      {tab.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Sağ panel - İçerik */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: isMobile ? 1.5 : 2.5 }}>

              {/* ── Bayi Servis İlişkisi ── */}
              {settingsActiveTab === 'bayiServis' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: darkMode ? '#ffe0b2' : '#bf360c', mb: 2 }}>
                    Bayi Servis İlişkisi
                  </Typography>

                  {/* Combo alanları */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>

                      <Combo
                        tableName="V_YAPI_BAYI_SERVIS_ILISKISI"
                        idField="BAYI_KODU"
                        textField="ACIKLAMA"
                        label="Bayi Seçin"
                        filter={dealerFilter}
                        value={settingsDealerCombo}
                        onChange={(val) => setSettingsDealerCombo(val)}
                        size="small"
                        sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '8px' }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 200 }}>

                      <Combo
                        tableName="V_YAPI_TIME_SLOT"
                        idField="TIME_SLOT"
                        textField="ACIKLAMA"
                        label="Zaman Dilimi Seçin"
                        value={settingsTimeSlotCombo}
                        onChange={(val) => setSettingsTimeSlotCombo(val)}
                        size="small"
                        sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '8px' }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddDealerServiceRow}
                      startIcon={<AddCircleOutlineIcon sx={{ fontSize: '16px !important' }} />}
                      sx={{
                        height: 40, textTransform: 'none', fontWeight: 700, borderRadius: '8px', fontSize: '0.75rem',
                        bgcolor: darkMode ? '#ff9800' : '#e65100', color: '#fff', px: 2.5,
                        '&:hover': { bgcolor: darkMode ? '#f57c00' : '#bf360c' },
                        boxShadow: `0 3px 12px ${alpha('#ff9800', 0.3)}`,
                      }}
                    >
                      Ekle
                    </Button>
                  </Box>

                  <Divider sx={{ mb: 2, borderColor: darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08) }} />

                  {/* Liste */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64' }}>
                      Mevcut Kayıtlar
                      {settingsDealerList.length > 0 && (
                        <Typography component="span" sx={{ ml: 0.75, fontSize: '0.62rem', fontWeight: 600, color: darkMode ? '#777' : '#999' }}>
                          ({filteredDealerList.length} / {settingsDealerList.length})
                        </Typography>
                      )}
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Ara..."
                      value={settingsDealerSearch}
                      onChange={(e) => { setSettingsDealerSearch(e.target.value); setSettingsDealerPage(0); }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 16, color: darkMode ? '#777' : '#999' }} />
                          </InputAdornment>
                        ),
                        ...(settingsDealerSearch && {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => { setSettingsDealerSearch(''); setSettingsDealerPage(0); }} sx={{ p: 0.3 }}>
                                <CloseIcon sx={{ fontSize: 14, color: darkMode ? '#777' : '#999' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }),
                      }}
                      sx={{
                        width: isMobile ? '100%' : 260,
                        '& .MuiOutlinedInput-root': {
                          height: 32, fontSize: '0.72rem', borderRadius: '8px',
                          bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff',
                        },
                      }}
                    />
                  </Box>

                  {settingsDealerLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={32} sx={{ color: darkMode ? '#ffb74d' : '#e65100' }} />
                    </Box>
                  ) : settingsDealerList.length === 0 ? (
                    <Box sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4,
                      borderRadius: '12px', border: `1px dashed ${darkMode ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                      bgcolor: darkMode ? alpha('#000', 0.1) : alpha('#f5f5f5', 0.5),
                    }}>
                      <InboxIcon sx={{ fontSize: 40, color: darkMode ? '#555' : '#ccc', mb: 1 }} />
                      <Typography sx={{ fontSize: '0.78rem', color: darkMode ? '#666' : '#999' }}>Henüz kayıt bulunmuyor</Typography>
                    </Box>
                  ) : (
                    <Box sx={{
                      borderRadius: '10px', overflow: 'hidden',
                      border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
                      flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
                    }}>
                      {/* Tablo başlığı */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.5, py: 0.8,
                        bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#e3f2fd', 0.5),
                        borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06)}`,
                      }}>
                        <Typography sx={{ flex: 0.8, fontSize: '0.62rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', letterSpacing: '0.02em' }}>Bayi Kodu</Typography>
                        <Typography sx={{ flex: 0.8, fontSize: '0.62rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', letterSpacing: '0.02em' }}>Kullanıcı Kodu</Typography>
                        <Typography sx={{ flex: 1.5, fontSize: '0.62rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', letterSpacing: '0.02em' }}>Bayi Adı</Typography>
                        <Typography sx={{ flex: 0.8, fontSize: '0.62rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', letterSpacing: '0.02em' }}>Zaman Dilimi</Typography>
                        <Typography sx={{ width: 40, fontSize: '0.62rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', textAlign: 'center' }}>Sil</Typography>
                      </Box>
                      {/* Satırlar */}
                      <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {(() => {
                          const start = settingsDealerPage * settingsDealerRowsPerPage;
                          const paged = filteredDealerList.slice(start, start + settingsDealerRowsPerPage);
                          return paged.length === 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                              <Typography sx={{ fontSize: '0.72rem', color: darkMode ? '#666' : '#999' }}>Sonuç bulunamadı</Typography>
                            </Box>
                          ) : paged.map((row, idx) => (
                            <Box
                              key={row.Id || row.id || idx}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 1.5, py: 0.7,
                                bgcolor: idx % 2 === 0
                                  ? (darkMode ? 'transparent' : alpha('#fff', 0.8))
                                  : (darkMode ? alpha('#fff', 0.02) : alpha('#f5f5f5', 0.5)),
                                borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : alpha('#000', 0.04)}`,
                                '&:hover': { bgcolor: darkMode ? alpha('#ff9800', 0.06) : alpha('#fff3e0', 0.6) },
                                transition: 'background 0.1s',
                              }}
                            >
                              <Typography sx={{ flex: 0.8, fontSize: '0.72rem', fontWeight: 600, color: darkMode ? '#ccc' : '#333', fontFamily: 'monospace' }}>
                                {row.DealerCode || '-'}
                              </Typography>
                              <Typography sx={{ flex: 0.8, fontSize: '0.72rem', color: darkMode ? '#aaa' : '#555', fontFamily: 'monospace' }}>
                                {row.UserDealerCode || '-'}
                              </Typography>
                              <Typography sx={{ flex: 1.5, fontSize: '0.72rem', color: darkMode ? '#aaa' : '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {row.DealerName || '-'}
                              </Typography>
                              <Typography sx={{ flex: 0.8, fontSize: '0.72rem', fontWeight: 600, color: darkMode ? '#90caf9' : '#1565c0', fontFamily: 'monospace' }}>
                                {row.TimeSlot || '-'}
                              </Typography>
                              <Box sx={{ width: 40, display: 'flex', justifyContent: 'center' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteDealerServiceRow(row)}
                                  sx={{ width: 26, height: 26, color: alpha('#f44336', 0.6), '&:hover': { color: '#f44336', bgcolor: alpha('#f44336', 0.1) } }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          ));
                        })()}
                      </Box>
                      {/* Sayfalama */}
                      {(() => {
                        const totalPages = Math.ceil(filteredDealerList.length / settingsDealerRowsPerPage);
                        if (totalPages <= 1) return null;
                        return (
                          <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            px: 1.5, py: 0.6,
                            borderTop: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06)}`,
                            bgcolor: darkMode ? alpha('#fff', 0.02) : alpha('#f5f5f5', 0.5),
                            flexWrap: 'wrap', gap: 0.5,
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '0.6rem', color: darkMode ? '#777' : '#999' }}>
                                Sayfa {settingsDealerPage + 1} / {totalPages} ({filteredDealerList.length} kayıt)
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <Typography sx={{ fontSize: '0.58rem', color: darkMode ? '#777' : '#999', mr: 0.5 }}>Satır:</Typography>
                              {[10, 15, 25, 50].map(n => (
                                <Chip
                                  key={n}
                                  label={n}
                                  size="small"
                                  onClick={() => { setSettingsDealerRowsPerPage(n); setSettingsDealerPage(0); }}
                                  sx={{
                                    height: 20, fontSize: '0.55rem', fontWeight: settingsDealerRowsPerPage === n ? 700 : 500,
                                    bgcolor: settingsDealerRowsPerPage === n ? alpha(darkMode ? '#ff9800' : '#e65100', 0.15) : 'transparent',
                                    color: settingsDealerRowsPerPage === n ? (darkMode ? '#ffb74d' : '#e65100') : (darkMode ? '#777' : '#999'),
                                    border: `1px solid ${settingsDealerRowsPerPage === n ? alpha(darkMode ? '#ff9800' : '#e65100', 0.3) : 'transparent'}`,
                                    cursor: 'pointer', minWidth: 28,
                                    '&:hover': { bgcolor: alpha(darkMode ? '#ff9800' : '#e65100', 0.1) },
                                  }}
                                />
                              ))}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <IconButton
                                size="small"
                                disabled={settingsDealerPage === 0}
                                onClick={() => setSettingsDealerPage(0)}
                                sx={{ width: 24, height: 24, color: darkMode ? '#aaa' : '#666' }}
                              >
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700 }}>«</Typography>
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={settingsDealerPage === 0}
                                onClick={() => setSettingsDealerPage(p => p - 1)}
                                sx={{ width: 24, height: 24, color: darkMode ? '#aaa' : '#666' }}
                              >
                                <ChevronLeftIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) { pageNum = i; }
                                else if (settingsDealerPage < 3) { pageNum = i; }
                                else if (settingsDealerPage > totalPages - 4) { pageNum = totalPages - 5 + i; }
                                else { pageNum = settingsDealerPage - 2 + i; }
                                return (
                                  <Chip
                                    key={pageNum}
                                    label={pageNum + 1}
                                    size="small"
                                    onClick={() => setSettingsDealerPage(pageNum)}
                                    sx={{
                                      height: 22, minWidth: 28, fontSize: '0.58rem',
                                      fontWeight: settingsDealerPage === pageNum ? 800 : 500,
                                      bgcolor: settingsDealerPage === pageNum ? alpha(darkMode ? '#ff9800' : '#e65100', 0.2) : 'transparent',
                                      color: settingsDealerPage === pageNum ? (darkMode ? '#ffb74d' : '#e65100') : (darkMode ? '#888' : '#666'),
                                      border: settingsDealerPage === pageNum ? `1px solid ${alpha(darkMode ? '#ff9800' : '#e65100', 0.4)}` : '1px solid transparent',
                                      cursor: 'pointer',
                                      '&:hover': { bgcolor: alpha(darkMode ? '#ff9800' : '#e65100', 0.1) },
                                    }}
                                  />
                                );
                              })}
                              <IconButton
                                size="small"
                                disabled={settingsDealerPage >= totalPages - 1}
                                onClick={() => setSettingsDealerPage(p => p + 1)}
                                sx={{ width: 24, height: 24, color: darkMode ? '#aaa' : '#666' }}
                              >
                                <ChevronRightIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={settingsDealerPage >= totalPages - 1}
                                onClick={() => setSettingsDealerPage(totalPages - 1)}
                                sx={{ width: 24, height: 24, color: darkMode ? '#aaa' : '#666' }}
                              >
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700 }}>»</Typography>
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              )}

              {/* ── Teknisyen Mahalle ── */}
              {settingsActiveTab === 'tekMahalle' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: darkMode ? '#ffe0b2' : '#bf360c', mb: 1 }}>
                    Teknisyen Mahalle
                  </Typography>

                  {/* Filtre Comboları — compact */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <Box sx={{ flex: 1.5, minWidth: 180 }}>
                      <Combo
                        tableName="T_YAPI_TEKNISYEN"
                        idField="TEKNISYEN_KODU"
                        textField="KUL_TEK_KODU + ' - ' + ADI + ' ' + SOYADI"
                        label="Teknisyen Seçin"
                        filter={techFilter}
                        orderBy={(a, b) => String(a.name || '').replace(/^[^-]+-\s*/, '').localeCompare(String(b.name || '').replace(/^[^-]+-\s*/, ''), 'tr', { sensitivity: 'base' })}
                        value={tmTechCombo}
                        onChange={(val) => { setTmTechCombo(val); setTmData([]); }}
                        size="small"
                        sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Combo
                        functionName="GetTechNeighbourhoodIl"
                        functionParameters={tmIlParams}
                        idField="IL_KOD"
                        textField="IL"
                        label="İl"
                        value={tmIlCombo}
                        onChange={(val) => { setTmIlCombo(val); setTmIlceCombo(null); setTmMahalleCombo(null); setTmData([]); }}
                        size="small"
                        sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Combo
                        functionName="GetTechNeighbourhoodIlce"
                        functionParameters={tmIlceParams}
                        idField="ID"
                        textField="ACIKLAMA"
                        label="İlçe"
                        value={tmIlceCombo}
                        onChange={(val) => { setTmIlceCombo(val); setTmMahalleCombo(null); setTmData([]); }}
                        optionFilter={tmTumuFilter}
                        size="small"
                        sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Combo
                        functionName="GetTechNeighbourhoodMahalle"
                        functionParameters={tmMahalleParams}
                        idField="ID"
                        textField="ACIKLAMA"
                        label="Mahalle"
                        value={tmMahalleCombo}
                        onChange={(val) => { setTmMahalleCombo(val); setTmData([]); }}
                        size="small"
                        sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <Box sx={{ flex: 1, minWidth: 180 }}>
                      <Combo
                        functionName="GetTechNeighbourhoodGrupMahalle"
                        functionParameters={tmGrupParams}
                        idField="ID"
                        textField="ACIKLAMA"
                        label="Grup Mahalle"
                        value={tmGrupMahalleCombo}
                        onChange={(val) => { setTmGrupMahalleCombo(val); setTmData([]); }}
                        size="small"
                        sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleTmSorgula}
                      disabled={tmLoading}
                      startIcon={<SearchIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{
                        height: 36, textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.7rem',
                        bgcolor: darkMode ? '#1976d2' : '#1565c0', color: '#fff', px: 1.5,
                        '&:hover': { bgcolor: darkMode ? '#1565c0' : '#0d47a1' },
                        boxShadow: `0 2px 8px ${alpha('#1976d2', 0.25)}`,
                      }}
                    >
                      Sorgula
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleTmGrupSorgula}
                      disabled={tmLoading}
                      startIcon={<GroupWorkIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{
                        height: 36, textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.7rem',
                        bgcolor: darkMode ? '#7b1fa2' : '#6a1b9a', color: '#fff', px: 1.5,
                        '&:hover': { bgcolor: darkMode ? '#6a1b9a' : '#4a148c' },
                        boxShadow: `0 2px 8px ${alpha('#7b1fa2', 0.25)}`,
                      }}
                    >
                      Grup Sorgula
                    </Button>
                  </Box>

                  <Divider sx={{ mb: 1, borderColor: darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08) }} />

                  {/* Sonuç Başlığı + Ara + Kaydet */}
                  {tmData.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', mr: 'auto' }}>
                        {tmTechCombo ? tmTechCombo.name : 'Sonuçlar'}
                        <Typography component="span" sx={{ ml: 0.75, fontSize: '0.62rem', fontWeight: 600, color: darkMode ? '#777' : '#999' }}>
                          ({tmSearch ? `${tmData.filter(r => [r.NeighbourhoodName, r.GroupName, r.CityName, r.DistrictName].some(v => String(v || '').toLocaleLowerCase('tr').includes(tmSearch.toLocaleLowerCase('tr')))).length} / ${tmData.length} kayıt` : `${tmData.length} kayıt`})
                        </Typography>
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Ara..."
                        value={tmSearch}
                        onChange={(e) => setTmSearch(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: darkMode ? '#777' : '#999' }} /></InputAdornment>,
                          ...(tmSearch && {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setTmSearch('')} sx={{ p: 0.3 }}>
                                  <CloseIcon sx={{ fontSize: 14, color: darkMode ? '#777' : '#999' }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }),
                        }}
                        sx={{
                          width: 200,
                          '& .MuiOutlinedInput-root': {
                            height: 32, fontSize: '0.72rem', borderRadius: '8px',
                            bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff',
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleTmSave}
                        disabled={tmSaving}
                        startIcon={tmSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: '16px !important' }} />}
                        sx={{
                          height: 32, textTransform: 'none', fontWeight: 700, borderRadius: '8px', fontSize: '0.72rem',
                          bgcolor: darkMode ? '#388e3c' : '#2e7d32', color: '#fff', px: 2,
                          '&:hover': { bgcolor: darkMode ? '#2e7d32' : '#1b5e20' },
                          boxShadow: `0 3px 12px ${alpha('#388e3c', 0.3)}`,
                        }}
                      >
                        Kaydet
                      </Button>
                    </Box>
                  )}

                  {/* Yükleniyor */}
                  {tmLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={32} sx={{ color: darkMode ? '#ffb74d' : '#e65100' }} />
                    </Box>
                  )}

                  {/* Boş */}
                  {!tmLoading && tmData.length === 0 && (
                    <Box sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4,
                      borderRadius: '12px', border: `1px dashed ${darkMode ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                      bgcolor: darkMode ? alpha('#000', 0.1) : alpha('#f5f5f5', 0.5),
                    }}>
                      <HomeWorkIcon sx={{ fontSize: 40, color: darkMode ? '#555' : '#ccc', mb: 1 }} />
                      <Typography sx={{ fontSize: '0.78rem', color: darkMode ? '#666' : '#999' }}>Teknisyen ve filtre seçerek sorgulama yapın</Typography>
                    </Box>
                  )}

                  {/* Sonuç Tablosu */}
                  {!tmLoading && tmData.length > 0 && (
                    <Box sx={{
                      borderRadius: '10px', overflow: 'hidden',
                      border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
                      flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
                    }}>
                      {/* Tablo başlığı */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0,
                        px: 0.5, py: 0.6,
                        bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#e3f2fd', 0.5),
                        borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06)}`,
                        minWidth: 800,
                      }}>
                        {tmQueryMode === 'grup' ? (
                          <>
                            <Typography sx={{ width: 120, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5 }}>Grup Kodu</Typography>
                            <Typography sx={{ flex: 1, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5, minWidth: 120 }}>Grup Adı</Typography>
                          </>
                        ) : (
                          <>
                            <Typography sx={{ width: 60, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5 }}>İl</Typography>
                            <Typography sx={{ width: 90, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5 }}>İlçe</Typography>
                            <Typography sx={{ flex: 1, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5, minWidth: 120 }}>Mahalle</Typography>
                          </>
                        )}
                        <Typography sx={{ width: 200, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5 }}>İşlem</Typography>
                        <Typography sx={{ width: 120, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5 }}>1.Saat Aralığı</Typography>
                        <Typography sx={{ width: 120, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5 }}>2.Saat Aralığı</Typography>
                      </Box>
                      {/* Satırlar */}
                      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
                        {tmData.filter(r => !tmSearch || [r.NeighbourhoodName, r.GroupName, r.CityName, r.DistrictName].some(v => String(v || '').toLocaleLowerCase('tr').includes(tmSearch.toLocaleLowerCase('tr')))).map((row, idx) => {
                          const opId = Number(row.OperationID ?? 0);
                          const rowBg = idx % 2 === 0
                            ? (darkMode ? 'transparent' : alpha('#fff', 0.8))
                            : (darkMode ? alpha('#fff', 0.02) : alpha('#f5f5f5', 0.5));

                          return (
                            <Box
                              key={row.ID || idx}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 0,
                                px: 0.5, py: 0.3,
                                bgcolor: rowBg,
                                borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : alpha('#000', 0.04)}`,
                                minWidth: 800,
                                '&:hover': { filter: 'brightness(0.95)' },
                                transition: 'background 0.1s',
                              }}
                            >
                              {tmQueryMode === 'grup' ? (
                                <>
                                  <Typography sx={{ width: 120, fontSize: '0.68rem', fontWeight: 600, color: darkMode ? '#ccc' : '#333', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.GroupCode || '-'}
                                  </Typography>
                                  <Typography sx={{ flex: 1, fontSize: '0.68rem', color: darkMode ? '#aaa' : '#555', px: 0.5, minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.GroupName || '-'}
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <Typography sx={{ width: 60, fontSize: '0.68rem', fontWeight: 600, color: darkMode ? '#ccc' : '#333', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.CityName || '-'}
                                  </Typography>
                                  <Typography sx={{ width: 90, fontSize: '0.68rem', color: darkMode ? '#aaa' : '#555', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.DistrictName || '-'}
                                  </Typography>
                                  <Typography sx={{ flex: 1, fontSize: '0.68rem', color: darkMode ? '#aaa' : '#555', px: 0.5, minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.NeighbourhoodName || '-'}
                                  </Typography>
                                </>
                              )}
                              <Box sx={{ width: 200, px: 0.5 }}>
                                <select
                                  value={row.OperationID ?? ''}
                                  onChange={(e) => handleTmRowChange(idx, 'OperationID', Number(e.target.value))}
                                  style={{
                                    width: '100%', height: 26, fontSize: '0.65rem', borderRadius: 4,
                                    border: '1px solid #ccc', padding: '0 2px', cursor: 'pointer',
                                    backgroundColor: (TM_OPERATIONS.find(o => o.value === opId) || {}).bg || '#fff',
                                    color: opId === 0 ? '#fff' : '#333',
                                  }}
                                >
                                  {tmOperationOptions}
                                </select>
                              </Box>
                              <Box sx={{ width: 120, px: 0.5 }}>
                                <select
                                  value={row.StartTime ?? '00:00-00:00'}
                                  onChange={(e) => handleTmRowChange(idx, 'StartTime', e.target.value)}
                                  style={{
                                    width: '100%', height: 26, fontSize: '0.65rem', borderRadius: 4,
                                    border: '1px solid #ccc', padding: '0 2px', cursor: 'pointer',
                                    backgroundColor: darkMode ? '#2a2a3a' : '#fff', color: darkMode ? '#ccc' : '#333',
                                  }}
                                >
                                  {tmTimeSlotOptions}
                                </select>
                              </Box>
                              <Box sx={{ width: 120, px: 0.5 }}>
                                <select
                                  value={row.EndTime ?? '00:00-00:00'}
                                  onChange={(e) => handleTmRowChange(idx, 'EndTime', e.target.value)}
                                  style={{
                                    width: '100%', height: 26, fontSize: '0.65rem', borderRadius: 4,
                                    border: '1px solid #ccc', padding: '0 2px', cursor: 'pointer',
                                    backgroundColor: darkMode ? '#2a2a3a' : '#fff', color: darkMode ? '#ccc' : '#333',
                                  }}
                                >
                                  {tmTimeSlotOptions}
                                </select>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* ── Teknisyen Uzmanlık ── */}
              {settingsActiveTab === 'tekUzmanlik' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: darkMode ? '#ffe0b2' : '#bf360c', mb: 1 }}>
                    Teknisyen Uzmanlık
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Combo
                        tableName="T_YAPI_TEKNISYEN"
                        idField="TEKNISYEN_KODU"
                        textField="KUL_TEK_KODU + ' - ' + ADI + ' ' + SOYADI"
                        label="Teknisyen Seçin"
                        filter={techFilter}
                        orderBy={(a, b) => String(a.name || '').replace(/^[^-]+-\s*/, '').localeCompare(String(b.name || '').replace(/^[^-]+-\s*/, ''), 'tr', { sensitivity: 'base' })}
                        value={tuTechCombo}
                        onChange={(val) => { setTuTechCombo(val); setTuData([]); setTuSearch(''); }}
                        size="small"
                        sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleTuSorgula}
                      disabled={tuLoading}
                      startIcon={<SearchIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{
                        height: 36, textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.7rem',
                        bgcolor: darkMode ? '#1976d2' : '#1565c0', color: '#fff', px: 1.5,
                        '&:hover': { bgcolor: darkMode ? '#1565c0' : '#0d47a1' },
                        boxShadow: `0 2px 8px ${alpha('#1976d2', 0.25)}`,
                      }}
                    >
                      Sorgula
                    </Button>
                  </Box>

                  <Divider sx={{ mb: 1, borderColor: darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08) }} />

                  {tuData.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', mr: 'auto' }}>
                        {tuTechCombo ? tuTechCombo.name : 'Sonuçlar'}
                        <Typography component="span" sx={{ ml: 0.75, fontSize: '0.62rem', fontWeight: 600, color: darkMode ? '#777' : '#999' }}>
                          ({tuSearch ? `${tuData.filter(r => String(r.Expert || '').toLocaleLowerCase('tr').includes(tuSearch.toLocaleLowerCase('tr'))).length} / ${tuData.length} kayıt` : `${tuData.length} kayıt`})
                        </Typography>
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Ara..."
                        value={tuSearch}
                        onChange={(e) => setTuSearch(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: darkMode ? '#777' : '#999' }} /></InputAdornment>,
                          ...(tuSearch && {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setTuSearch('')} sx={{ p: 0.3 }}>
                                  <CloseIcon sx={{ fontSize: 14, color: darkMode ? '#777' : '#999' }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }),
                        }}
                        sx={{
                          width: 200,
                          '& .MuiOutlinedInput-root': {
                            height: 32, fontSize: '0.72rem', borderRadius: '8px',
                            bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff',
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleTuSave}
                        disabled={tuSaving}
                        startIcon={tuSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: '16px !important' }} />}
                        sx={{
                          height: 32, textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.72rem',
                          bgcolor: darkMode ? '#388e3c' : '#2e7d32', color: '#fff', px: 2,
                          '&:hover': { bgcolor: darkMode ? '#2e7d32' : '#1b5e20' },
                          boxShadow: `0 3px 12px ${alpha('#388e3c', 0.3)}`,
                        }}
                      >
                        Kaydet
                      </Button>
                    </Box>
                  )}

                  {tuLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={32} sx={{ color: darkMode ? '#ffb74d' : '#e65100' }} />
                    </Box>
                  )}

                  {!tuLoading && tuData.length === 0 && (
                    <Box sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4,
                      borderRadius: '12px', border: `1px dashed ${darkMode ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                      bgcolor: darkMode ? alpha('#000', 0.1) : alpha('#f5f5f5', 0.5),
                    }}>
                      <EngineeringIcon sx={{ fontSize: 40, color: darkMode ? '#555' : '#ccc', mb: 1 }} />
                      <Typography sx={{ fontSize: '0.78rem', color: darkMode ? '#666' : '#999' }}>Teknisyen seçerek sorgulama yapın</Typography>
                    </Box>
                  )}

                  {!tuLoading && tuData.length > 0 && (
                    <Box sx={{
                      borderRadius: '10px', overflow: 'hidden',
                      border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
                      flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
                    }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0,
                        px: 0.5, py: 0.6,
                        bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#e3f2fd', 0.5),
                        borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06)}`,
                        minWidth: 500,
                        flexShrink: 0,
                      }}>
                        <Typography sx={{ flex: 1, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5, minWidth: 150 }}>Uzmanlık</Typography>
                        <Typography sx={{ width: 200, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5 }}>İşlem</Typography>
                      </Box>
                      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
                        {tuData.filter(r => !tuSearch || String(r.Expert || '').toLocaleLowerCase('tr').includes(tuSearch.toLocaleLowerCase('tr'))).map((row, idx) => {
                          const opId = Number(row.OperationId ?? 100);
                          const rowBg = idx % 2 === 0
                            ? (darkMode ? 'transparent' : alpha('#fff', 0.8))
                            : (darkMode ? alpha('#fff', 0.02) : alpha('#f5f5f5', 0.5));
                          return (
                            <Box
                              key={row.ID || idx}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 0,
                                px: 0.5, py: 0.3,
                                bgcolor: rowBg,
                                borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : alpha('#000', 0.04)}`,
                                minWidth: 500,
                                '&:hover': { filter: 'brightness(0.95)' },
                                transition: 'background 0.1s',
                              }}
                            >
                              <Typography sx={{ flex: 1, fontSize: '0.68rem', color: darkMode ? '#aaa' : '#555', px: 0.5, minWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {row.Expert || '-'}
                              </Typography>
                              <Box sx={{ width: 200, px: 0.5 }}>
                                <select
                                  value={opId}
                                  onChange={(e) => handleTuRowChange(idx, 'OperationId', Number(e.target.value))}
                                  style={{
                                    width: '100%', height: 26, fontSize: '0.65rem', borderRadius: 4,
                                    border: '1px solid #ccc', padding: '0 2px', cursor: 'pointer',
                                    backgroundColor: (TU_OPERATIONS.find(o => o.value === opId) || {}).bg || '#fff',
                                    color: opId === 21 ? '#fff' : '#333',
                                  }}
                                >
                                  {tuOperationOptions}
                                </select>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* ── Grup Mahalle ── */}
              {settingsActiveTab === 'grupMahalle' && (
                <>
                  <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 1.5 }}>
                    {/* ── Sol Panel: Grup Oluştur & Mahalle Ekle ── */}
                    <Box sx={{
                      width: 280, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 1.5,
                      p: 1.5, borderRadius: '10px',
                      border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
                      bgcolor: darkMode ? alpha('#fff', 0.02) : alpha('#f5f5f5', 0.4),
                      overflowY: 'auto',
                    }}>
                      {/* Grup Oluştur bölümü */}
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: darkMode ? '#ffe0b2' : '#bf360c' }}>
                        Grup Adı Oluştur
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                          <input
                            type="text"
                            value={gmGrupName}
                            onChange={(e) => setGmGrupName(e.target.value)}
                            placeholder="Grup adı girin"
                            style={{
                              width: '100%', height: 30, fontSize: '0.72rem', borderRadius: 4,
                              border: '1px solid #ccc', padding: '0 8px', boxSizing: 'border-box',
                              backgroundColor: darkMode ? '#333' : '#fff', color: darkMode ? '#eee' : '#333',
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#546e7a', mb: 0.3 }}>Bağlı Yer</Typography>
                          <select
                            value={gmBagliYer}
                            onChange={(e) => setGmBagliYer(e.target.value)}
                            style={{
                              width: '100%', height: 28, fontSize: '0.68rem', borderRadius: 4,
                              border: '1px solid #ccc', padding: '0 4px', cursor: 'pointer',
                              backgroundColor: darkMode ? '#333' : '#fff', color: darkMode ? '#eee' : '#333',
                            }}
                          >
                            <option value="0">Hayır</option>
                            <option value="1">Evet</option>
                          </select>
                        </Box>
                        {gmBagliYer === '1' && (
                          <Box>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#546e7a', mb: 0.3 }}>Günler</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                              {Object.entries(gmDays).map(([day, checked]) => (
                                <label key={day} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.68rem', color: darkMode ? '#ccc' : '#444' }}>
                                  <input type="checkbox" checked={checked} onChange={() => handleGmDayToggle(day)} style={{ margin: 0, cursor: 'pointer' }} />
                                  {GM_DAY_LABELS[day]}
                                </label>
                              ))}
                            </Box>
                          </Box>
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleGmCreateGroup}
                          disabled={gmCreating}
                          startIcon={gmCreating ? <CircularProgress size={14} color="inherit" /> : <AddCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
                          sx={{
                            height: 32, textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.68rem',
                            bgcolor: darkMode ? '#2e7d32' : '#388e3c', color: '#fff',
                            '&:hover': { bgcolor: darkMode ? '#1b5e20' : '#2e7d32' },
                          }}
                        >
                          Grup Adı Oluştur
                        </Button>
                      </Box>

                      <Divider sx={{ borderColor: darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08) }} />

                      {/* Gruba Mahalle Ekle bölümü */}
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: darkMode ? '#90caf9' : '#1565c0' }}>
                        Gruba Mahalle Ekle
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ flex: 1 }}>
                            <Combo
                              key={gmGrupComboKey}
                              functionName="GetTechNeighbourhoodGrupMahalle"
                              functionParameters={gmGrupParams}
                              idField="ID"
                              textField="ACIKLAMA"
                              label="Grup Seçin"
                              value={gmGrupCombo}
                              onChange={(val) => { setGmGrupCombo(val); handleGmFetchDetail(val?.id); setGmFreeTech(false); }}
                              size="small"
                              sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                            />
                          </Box>
                          {gmGrupCombo && (
                            <Tooltip title="Grubu Sil" arrow>
                              <IconButton size="small" onClick={handleGmDeleteGroup} sx={{ mt: 1.8, color: '#d32f2f' }}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Box>
                          <Combo
                            functionName="GetTechNeighbourhoodIl"
                            functionParameters={gmIlParams}
                            idField="IL_KOD"
                            textField="IL"
                            label="İl"
                            value={gmIlCombo}
                            onChange={(val) => { setGmIlCombo(val); setGmIlceCombo(null); setGmMahalleCombo(null); }}
                            size="small"
                            sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                          />
                        </Box>
                        <Box>
                          <Combo
                            functionName="GetTechNeighbourhoodIlce"
                            functionParameters={gmIlceParams}
                            idField="ID"
                            textField="ACIKLAMA"
                            label="İlçe"
                            value={gmIlceCombo}
                            onChange={(val) => { setGmIlceCombo(val); setGmMahalleCombo(null); }}
                            size="small"
                            sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                          />
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ flex: 1 }}>
                              <Combo
                                functionName="GetTechNeighbourhoodMahalle"
                                functionParameters={gmMahalleParams}
                                idField="ID"
                                textField="ACIKLAMA"
                                label="Mahalle"
                                value={gmMahalleCombo}
                                onChange={(val) => setGmMahalleCombo(val)}
                                size="small"
                                sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                              />
                            </Box>
                            <Tooltip title="Çoklu Mahalle Seçimi" arrow>
                              <IconButton
                                size="small"
                                onClick={handleGmOpenMahallePopup}
                                sx={{ color: darkMode ? '#90caf9' : '#1565c0', p: 0.4 }}
                              >
                                <SearchIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleGmAddNeighbourhood}
                          disabled={gmAdding}
                          startIcon={gmAdding ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: '14px !important' }} />}
                          sx={{
                            height: 32, textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.68rem',
                            bgcolor: darkMode ? '#1976d2' : '#1565c0', color: '#fff',
                            '&:hover': { bgcolor: darkMode ? '#1565c0' : '#0d47a1' },
                          }}
                        >
                          Kaydet
                        </Button>

                        {/* Seçili Mahalleler Listesi */}
                        {gmStagedMahalles.length > 0 && (
                          <Box sx={{
                            mt: 0.5, p: 1, borderRadius: '6px',
                            border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
                            bgcolor: darkMode ? alpha('#fff', 0.02) : '#fff',
                            maxHeight: 150, overflowY: 'auto',
                          }}>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#546e7a', mb: 0.5 }}>Seçili Mahalleler</Typography>
                            {gmStagedMahalles.map((m) => (
                              <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.15 }}>
                                <Typography sx={{ fontSize: '0.65rem', color: darkMode ? '#ccc' : '#333' }}>{m.name}</Typography>
                                <IconButton size="small" onClick={() => setGmStagedMahalles(prev => prev.filter(s => s.id !== m.id))} sx={{ p: 0.2, color: '#d32f2f' }}>
                                  <CloseIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* ── Sağ Panel: Grup Detay ── */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      {!gmGrupCombo && (
                        <Box sx={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1,
                          borderRadius: '12px', border: `1px dashed ${darkMode ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                          bgcolor: darkMode ? alpha('#000', 0.1) : alpha('#f5f5f5', 0.5),
                        }}>
                          <GroupWorkIcon sx={{ fontSize: 40, color: darkMode ? '#555' : '#ccc', mb: 1 }} />
                          <Typography sx={{ fontSize: '0.78rem', color: darkMode ? '#666' : '#999' }}>Sol panelden grup seçin</Typography>
                        </Box>
                      )}

                      {gmGrupCombo && (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: darkMode ? '#4fc3f7' : '#0277bd' }}>
                              {gmGrupCombo.name}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleGmOpenDayPopup}
                              disabled={gmSaving}
                              sx={{
                                height: 28, textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.65rem',
                                bgcolor: '#e65100', color: '#fff', px: 1.5,
                                '&:hover': { bgcolor: '#bf360c' },
                              }}
                            >
                              Gün Değiştir
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleGmFetchDetail(gmGrupCombo.id)}
                              disabled={gmSaving}
                              startIcon={<SaveIcon sx={{ fontSize: '14px !important' }} />}
                              sx={{
                                height: 28, textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.65rem',
                                bgcolor: '#388e3c', color: '#fff', px: 1.5,
                                '&:hover': { bgcolor: '#2e7d32' },
                              }}
                            >
                              Kaydet
                            </Button>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.65rem', fontWeight: 600, color: darkMode ? '#b0bec5' : '#546e7a' }}>
                              <input type="checkbox" checked={gmFreeTech} onChange={(e) => handleGmFreeTech(e.target.checked)} style={{ margin: 0, cursor: 'pointer' }} />
                              Bu Bağlı Yerde Teknisyenleri Kilitle
                            </label>
                          </Box>

                          {gmDetailLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                              <CircularProgress size={28} sx={{ color: darkMode ? '#ffb74d' : '#e65100' }} />
                            </Box>
                          )}

                          {!gmDetailLoading && gmDetailData.length === 0 && (
                            <Box sx={{
                              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, py: 4,
                              borderRadius: '12px', border: `1px dashed ${darkMode ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                              bgcolor: darkMode ? alpha('#000', 0.1) : alpha('#f5f5f5', 0.5),
                            }}>
                              <Typography sx={{ fontSize: '0.78rem', color: darkMode ? '#666' : '#999' }}>Gruba henüz mahalle eklenmemiş</Typography>
                            </Box>
                          )}

                          {!gmDetailLoading && gmDetailData.length > 0 && (
                            <Box sx={{
                              borderRadius: '10px', overflow: 'hidden',
                              border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
                              flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
                            }}>
                              {/* Header */}
                              <Box sx={{
                                display: 'flex', alignItems: 'center',
                                px: 0.5, py: 0.6,
                                bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#e3f2fd', 0.5),
                                borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06)}`,
                                minWidth: 550,
                              }}>
                                <Typography sx={{ flex: 1, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5 }}>İl</Typography>
                                <Typography sx={{ flex: 1, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5 }}>İlçe</Typography>
                                <Typography sx={{ flex: 1.5, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5 }}>Mahalle</Typography>
                                <Typography sx={{ flex: 2, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', px: 0.5 }}>Günler</Typography>
                                <Box sx={{ width: 32 }} />
                              </Box>
                              {/* Rows */}
                              <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
                                {gmDetailData.map((row, idx) => {
                                  const rowBg = idx % 2 === 0
                                    ? (darkMode ? 'transparent' : alpha('#fff', 0.8))
                                    : (darkMode ? alpha('#fff', 0.02) : alpha('#f5f5f5', 0.5));
                                  return (
                                    <Box
                                      key={row.Id || idx}
                                      sx={{
                                        display: 'flex', alignItems: 'center',
                                        px: 0.5, py: 0.3, bgcolor: rowBg,
                                        borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : alpha('#000', 0.04)}`,
                                        minWidth: 550,
                                        '&:hover': { filter: 'brightness(0.95)' },
                                        transition: 'background 0.1s',
                                      }}
                                    >
                                      <Typography sx={{ flex: 1, fontSize: '0.66rem', color: darkMode ? '#aaa' : '#555', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {row.CityName || '-'}
                                      </Typography>
                                      <Typography sx={{ flex: 1, fontSize: '0.66rem', color: darkMode ? '#aaa' : '#555', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {row.DistrictName || '-'}
                                      </Typography>
                                      <Typography sx={{ flex: 1.5, fontSize: '0.66rem', color: darkMode ? '#aaa' : '#555', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {row.NeighbourhoodName || '-'}
                                      </Typography>
                                      <Typography sx={{ flex: 2, fontSize: '0.62rem', color: darkMode ? '#81c784' : '#2e7d32', fontWeight: 600, px: 0.5, whiteSpace: 'normal', lineHeight: 1.4 }}>
                                        {row.DayName || '-'}
                                      </Typography>
                                      <Tooltip title="Sil" arrow>
                                        <IconButton size="small" onClick={() => handleGmDeleteRow(row)} sx={{ color: '#d32f2f', p: 0.3 }}>
                                          <CloseIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* ── Mahalle Çoklu Seçim Popup ── */}
                  <Dialog
                    open={gmMahallePopupOpen}
                    onClose={() => setGmMahallePopupOpen(false)}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '12px', bgcolor: darkMode ? '#1e1e1e' : '#fff' } }}
                  >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, px: 2, bgcolor: darkMode ? '#263238' : '#e3f2fd' }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: darkMode ? '#fff' : '#1565c0' }}>Mahalle Seçimi</Typography>
                      <IconButton size="small" onClick={() => setGmMahallePopupOpen(false)} sx={{ color: darkMode ? '#aaa' : '#666' }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: 0 }}>
                      {gmMahalleListLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : (
                        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                          {/* Header */}
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.8,
                            position: 'sticky', top: 0, zIndex: 1,
                            bgcolor: darkMode ? '#263238' : '#f5f5f5',
                            borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
                          }}>
                            <input
                              type="checkbox"
                              checked={gmMahalleList.length > 0 && gmMahalleList.every(m => gmMahalleSelected[m.id])}
                              onChange={(e) => handleGmToggleAllMahalle(e.target.checked)}
                              style={{ margin: 0, cursor: 'pointer' }}
                            />
                            <Typography sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64' }}>Mahalle</Typography>
                          </Box>
                          {/* Rows */}
                          {gmMahalleList.map((m, idx) => (
                            <Box
                              key={m.id}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.4,
                                bgcolor: idx % 2 === 0 ? 'transparent' : (darkMode ? alpha('#fff', 0.02) : alpha('#f5f5f5', 0.5)),
                                '&:hover': { bgcolor: darkMode ? alpha('#fff', 0.05) : alpha('#e3f2fd', 0.5) },
                                cursor: 'pointer',
                              }}
                              onClick={() => setGmMahalleSelected(prev => ({ ...prev, [m.id]: !prev[m.id] }))}
                            >
                              <Typography sx={{ width: 28, fontSize: '0.62rem', color: darkMode ? '#777' : '#999', textAlign: 'right' }}>{idx + 1}</Typography>
                              <input
                                type="checkbox"
                                checked={!!gmMahalleSelected[m.id]}
                                onChange={() => setGmMahalleSelected(prev => ({ ...prev, [m.id]: !prev[m.id] }))}
                                onClick={(e) => e.stopPropagation()}
                                style={{ margin: 0, cursor: 'pointer' }}
                              />
                              <Typography sx={{ flex: 1, fontSize: '0.7rem', color: darkMode ? '#ccc' : '#333' }}>{m.name}</Typography>
                            </Box>
                          ))}
                          {gmMahalleList.length === 0 && (
                            <Typography sx={{ textAlign: 'center', py: 3, fontSize: '0.75rem', color: darkMode ? '#666' : '#999' }}>Mahalle bulunamadı</Typography>
                          )}
                        </Box>
                      )}
                    </DialogContent>
                    <DialogActions sx={{ px: 2, py: 1, borderTop: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08)}` }}>
                      <Typography sx={{ flex: 1, fontSize: '0.65rem', color: darkMode ? '#777' : '#999' }}>
                        {Object.values(gmMahalleSelected).filter(Boolean).length} / {gmMahalleList.length} seçili
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleGmBatchAddMahalle}
                        startIcon={<AddCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
                        sx={{
                          height: 30, textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.68rem',
                          bgcolor: darkMode ? '#1976d2' : '#1565c0', color: '#fff',
                          '&:hover': { bgcolor: darkMode ? '#1565c0' : '#0d47a1' },
                        }}
                      >
                        Ekle
                      </Button>
                    </DialogActions>
                  </Dialog>

                  {/* ── Gün Seçimi Popup ── */}
                  <Dialog
                    open={gmDayPopupOpen}
                    onClose={() => setGmDayPopupOpen(false)}
                    PaperProps={{ sx: { borderRadius: '12px', bgcolor: darkMode ? '#1e1e1e' : '#fff', minWidth: 220 } }}
                  >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, px: 2, bgcolor: darkMode ? '#263238' : '#e3f2fd' }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: darkMode ? '#fff' : '#1565c0' }}>Gün Seçimi</Typography>
                      <IconButton size="small" onClick={() => setGmDayPopupOpen(false)} sx={{ color: darkMode ? '#aaa' : '#666' }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ px: 2, py: 1.5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {Object.entries(gmDayPopupDays).map(([day, checked]) => (
                          <label key={day} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.75rem', color: darkMode ? '#ccc' : '#333' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setGmDayPopupDays(prev => ({ ...prev, [day]: !prev[day] }))}
                              style={{ margin: 0, cursor: 'pointer' }}
                            />
                            {GM_DAY_LABELS[day]}
                          </label>
                        ))}
                      </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 2, py: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleGmChangeDay}
                        disabled={gmSaving}
                        sx={{
                          width: '100%', height: 32, textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.72rem',
                          bgcolor: '#388e3c', color: '#fff',
                          '&:hover': { bgcolor: '#2e7d32' },
                        }}
                      >
                        Değiştir
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              )}

              {/* ── Aynı Gün Bağlı Yerler ── */}
              {settingsActiveTab === 'bagliYerler' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <Box sx={{
                    border: `1.5px solid ${darkMode ? alpha('#ff9800', 0.25) : alpha('#e65100', 0.18)}`,
                    borderRadius: '10px',
                    p: 2.5,
                    width: '100%',
                    mt: 2,
                    bgcolor: darkMode ? alpha('#fff', 0.02) : alpha('#fff', 0.7),
                    boxShadow: darkMode ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.06)',
                  }}>
                    {/* Başlık */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, pb: 1.5, borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06)}` }}>
                      <AltRouteIcon sx={{ fontSize: 20, color: darkMode ? '#ff9800' : '#e65100' }} />
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: darkMode ? '#ffe0b2' : '#bf360c' }}>
                        Bir teknisyenin aynı gün gidebileceği bağlı yerler
                      </Typography>
                    </Box>

                    {/* Bağlı Yer1 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', minWidth: 75, flexShrink: 0 }}>
                        Bağlı Yer1
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Combo
                          functionName="GetTechNeighbourhoodGrupMahalle"
                          functionParameters={byBagliYerParams}
                          optionFilter={bySplFilter}
                          idField="ID"
                          textField="ACIKLAMA"
                          label="Seçiniz"
                          value={byBagliYer1}
                          onChange={(val) => { setByBagliYer1(val); fetchByList(val?.id || 0, byBagliYer2?.id || 0); }}
                          size="small"
                          sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                        />
                      </Box>
                    </Box>

                    {/* Bağlı Yer2 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', minWidth: 75, flexShrink: 0 }}>
                        Bağlı Yer2
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Combo
                          functionName="GetTechNeighbourhoodGrupMahalle"
                          functionParameters={byBagliYerParams}
                          optionFilter={bySplFilter}
                          idField="ID"
                          textField="ACIKLAMA"
                          label="Seçiniz"
                          value={byBagliYer2}
                          onChange={(val) => { setByBagliYer2(val); if (byBagliYer1?.id) fetchByList(byBagliYer1.id, val?.id || 0); }}
                          size="small"
                          sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '6px' }}
                        />
                      </Box>
                    </Box>

                    {/* Kaydet Butonu */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleBySave}
                        disabled={bySaving}
                        startIcon={bySaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: '16px !important' }} />}
                        sx={{
                          height: 34,
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          color: '#fff',
                          px: 3,
                          background: darkMode
                            ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
                            : 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)',
                          boxShadow: '0 2px 8px rgba(230,81,0,0.3)',
                          '&:hover': {
                            background: darkMode
                              ? 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)'
                              : 'linear-gradient(135deg, #ff6d00 0%, #e65100 100%)',
                            boxShadow: '0 4px 12px rgba(230,81,0,0.4)',
                          },
                          '&.Mui-disabled': { opacity: 0.6 },
                        }}
                      >
                        Kaydet
                      </Button>
                    </Box>

                    {/* Mevcut Bağlı Yer Kayıtları */}
                    {(byLoading || byList.length > 0) && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 1.5, borderColor: darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08) }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64' }}>
                            Mevcut Kayıtlar
                          </Typography>
                          {byList.length > 0 && (
                            <Chip label={byList.length} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: darkMode ? alpha('#ff9800', 0.15) : alpha('#e65100', 0.1), color: darkMode ? '#ffb74d' : '#e65100' }} />
                          )}
                        </Box>
                        {byLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} sx={{ color: darkMode ? '#ffb74d' : '#e65100' }} />
                          </Box>
                        ) : (
                          <Box sx={{
                            borderRadius: '8px', overflow: 'hidden',
                            border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
                          }}>
                            {/* Tablo başlığı */}
                            <Box sx={{
                              display: 'flex', alignItems: 'center',
                              px: 1, py: 0.6,
                              bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#fff3e0', 0.7),
                              borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06)}`,
                            }}>
                              <Typography sx={{ flex: 3, fontSize: '0.6rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64' }}>Bağlı Yer</Typography>
                              <Box sx={{ width: 32 }} />
                            </Box>
                            {/* Satırlar */}
                            <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                              {byList.map((row, idx) => {
                                const idKey = row.IDKEY1 ?? row.IdKey1 ?? row.idKey1;
                                const isDeleting = byDeleting === idKey;
                                return (
                                  <Box
                                    key={idKey ?? idx}
                                    sx={{
                                      display: 'flex', alignItems: 'center',
                                      px: 1, py: 0.5,
                                      bgcolor: idx % 2 === 0
                                        ? (darkMode ? 'transparent' : alpha('#fff', 0.8))
                                        : (darkMode ? alpha('#fff', 0.02) : alpha('#f5f5f5', 0.5)),
                                      borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : alpha('#000', 0.04)}`,
                                      '&:hover': { bgcolor: darkMode ? alpha('#ff9800', 0.06) : alpha('#fff3e0', 0.6) },
                                    }}
                                  >
                                 
                                    <Typography sx={{ flex: 3, fontSize: '0.7rem', fontWeight: 600, color: darkMode ? '#ccc' : '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1 }}>
                                      {row.ID1 ?? '-'}
                                    </Typography>
                                    <Box sx={{ width: 32, display: 'flex', justifyContent: 'center' }}>
                                      {isDeleting ? (
                                        <CircularProgress size={14} sx={{ color: '#f44336' }} />
                                      ) : (
                                        <Tooltip title="Sil" arrow>
                                          <IconButton size="small" onClick={() => handleByDelete(idKey)}
                                            sx={{ color: alpha('#f44336', 0.6), p: 0.3, '&:hover': { color: '#f44336', bgcolor: alpha('#f44336', 0.1) } }}>
                                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* ── Mahalle Kısıtlaması Hariç ── */}
              {settingsActiveTab === 'mahalleKisit' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: darkMode ? '#ffe0b2' : '#bf360c', mb: 2 }}>
                    Bağlı yerden hizmet atandığında mahalle kısıtlaması uygulanmayacak teknisyenler
                  </Typography>

                  {/* Teknisyen Combo + Kaydet */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Combo
                        tableName="T_YAPI_TEKNISYEN"
                        idField="TEKNISYEN_KODU"
                        textField="KUL_TEK_KODU + ' - ' + ADI + ' ' + SOYADI"
                        label="Teknisyen Seçin"
                        filter={techFilter}
                        orderBy={(a, b) => String(a.name || '').replace(/^[^-]+-\s*/, '').localeCompare(String(b.name || '').replace(/^[^-]+-\s*/, ''), 'tr', { sensitivity: 'base' })}
                        value={mkTechCombo}
                        onChange={(val) => setMkTechCombo(val)}
                        size="small"
                        sx={{ bgcolor: darkMode ? alpha('#fff', 0.05) : '#fff', borderRadius: '8px' }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleMkSave}
                      disabled={mkSaving}
                      startIcon={mkSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: '16px !important' }} />}
                      sx={{
                        height: 40, textTransform: 'none', fontWeight: 700, borderRadius: '8px', fontSize: '0.75rem',
                        bgcolor: darkMode ? '#ff9800' : '#e65100', color: '#fff', px: 2.5,
                        '&:hover': { bgcolor: darkMode ? '#f57c00' : '#bf360c' },
                        boxShadow: `0 3px 12px ${alpha('#ff9800', 0.3)}`,
                        '&.Mui-disabled': { opacity: 0.6 },
                      }}
                    >
                      Kaydet
                    </Button>
                  </Box>

                  <Divider sx={{ mb: 2, borderColor: darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.08) }} />

                  {/* Mevcut Kayıtlar */}
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', mb: 1 }}>
                    Mevcut Kayıtlar
                    {mkList.length > 0 && (
                      <Chip label={mkList.length} size="small" sx={{ ml: 1, height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: darkMode ? alpha('#ff9800', 0.15) : alpha('#e65100', 0.1), color: darkMode ? '#ffb74d' : '#e65100' }} />
                    )}
                  </Typography>

                  {mkLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={32} sx={{ color: darkMode ? '#ffb74d' : '#e65100' }} />
                    </Box>
                  ) : mkList.length === 0 ? (
                    <Box sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4,
                      borderRadius: '12px', border: `1px dashed ${darkMode ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                      bgcolor: darkMode ? alpha('#000', 0.1) : alpha('#f5f5f5', 0.5),
                    }}>
                      <InboxIcon sx={{ fontSize: 40, color: darkMode ? '#555' : '#ccc', mb: 1 }} />
                      <Typography sx={{ fontSize: '0.78rem', color: darkMode ? '#666' : '#999' }}>Henüz kayıt bulunmuyor</Typography>
                    </Box>
                  ) : (
                    <Box sx={{
                      borderRadius: '10px', overflow: 'hidden',
                      border: `1px solid ${darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
                      flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
                    }}>
                      {/* Tablo başlığı */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.5, py: 0.8,
                        bgcolor: darkMode ? alpha('#fff', 0.04) : alpha('#e3f2fd', 0.5),
                        borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06)}`,
                      }}>
                        <Typography sx={{ flex: 1, fontSize: '0.62rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', letterSpacing: '0.02em' }}>Teknisyen Kodu</Typography>
                        <Typography sx={{ flex: 2, fontSize: '0.62rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', letterSpacing: '0.02em' }}>Teknisyen</Typography>
                        <Typography sx={{ width: 40, fontSize: '0.62rem', fontWeight: 700, color: darkMode ? '#b0bec5' : '#455a64', textAlign: 'center' }}>Sil</Typography>
                      </Box>
                      {/* Satırlar */}
                      <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {mkList.map((row, idx) => {
                          const techId = row.TechnicianId || row.technicianId || row.Id || row.id;
                          const techCode = row.TechnicianCode || row.technicianCode || '';
                          const fullName = [row.Name || row.name || '', row.Surname || row.surname || ''].filter(Boolean).join(' ');
                          return (
                            <Box
                              key={techId || idx}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 1.5, py: 0.7,
                                bgcolor: idx % 2 === 0
                                  ? (darkMode ? 'transparent' : alpha('#fff', 0.8))
                                  : (darkMode ? alpha('#fff', 0.02) : alpha('#f5f5f5', 0.5)),
                                borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.03) : alpha('#000', 0.04)}`,
                                '&:hover': { bgcolor: darkMode ? alpha('#ff9800', 0.06) : alpha('#fff3e0', 0.6) },
                                transition: 'background 0.1s',
                              }}
                            >
                              <Typography sx={{ flex: 1, fontSize: '0.72rem', fontWeight: 600, color: darkMode ? '#ccc' : '#333', fontFamily: 'monospace' }}>
                                {techCode || '-'}
                              </Typography>
                              <Typography sx={{ flex: 2, fontSize: '0.72rem', color: darkMode ? '#aaa' : '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {fullName || '-'}
                              </Typography>
                              <Box sx={{ width: 40, display: 'flex', justifyContent: 'center' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMkDelete(row)}
                                  sx={{ width: 26, height: 26, color: alpha('#f44336', 0.6), '&:hover': { color: '#f44336', bgcolor: alpha('#f44336', 0.1) } }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Teknisyen Grup Yönetimi Dialog ── */}
      {techGroupDialogOpen && (
        <Dialog
          open={techGroupDialogOpen}
          onClose={() => !techGroupSaving && setTechGroupDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              minHeight: 480,
              background: darkMode
                ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
                : 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            background: darkMode
              ? `linear-gradient(135deg, ${alpha('#b388ff', 0.12)} 0%, ${alpha('#1a1a2e', 0.9)} 100%)`
              : `linear-gradient(135deg, ${alpha('#5c6bc0', 0.08)} 0%, ${alpha('#e8eaf6', 0.9)} 100%)`,
            borderBottom: `1px solid ${darkMode ? alpha('#b388ff', 0.2) : alpha('#5c6bc0', 0.15)}`,
            py: 1.5,
          }}>
            <AccountTreeIcon sx={{ fontSize: 24, color: darkMode ? '#b388ff' : '#5c6bc0' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: darkMode ? '#e0e0e0' : '#283593' }}>
                Teknisyen Grup Yönetimi
              </Typography>
              <Typography sx={{ fontSize: '0.62rem', color: darkMode ? '#888' : '#9e9e9e' }}>
                Teknisyenleri gruplayarak organize edin
              </Typography>
            </Box>
            <IconButton onClick={() => setTechGroupDialogOpen(false)} disabled={techGroupSaving} size="small">
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Yeni grup oluştur */}
            <Box sx={{
              display: 'flex', gap: 1, alignItems: 'center',
              p: 1.5, borderRadius: 2,
              bgcolor: darkMode ? alpha('#b388ff', 0.06) : alpha('#5c6bc0', 0.04),
              border: `1px solid ${darkMode ? alpha('#b388ff', 0.15) : alpha('#5c6bc0', 0.12)}`,
            }}>
              <TextField
                size="small"
                placeholder="Yeni grup adı..."
                defaultValue=""
                inputRef={(el) => { if (el) techGroupNewNameRef.current = el; }}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem', bgcolor: darkMode ? alpha('#fff', 0.04) : '#fff' } }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={techGroupSaving}
                onClick={async () => {
                  const val = techGroupNewNameRef.current?.value?.trim();
                  if (!val) return;
                  const success = await saveTechGroup(val, []);
                  if (success && techGroupNewNameRef.current) techGroupNewNameRef.current.value = '';
                }}
                startIcon={techGroupSaving ? <CircularProgress size={14} /> : <AddCircleOutlineIcon />}
                sx={{
                  borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
                  bgcolor: darkMode ? '#7c4dff' : '#5c6bc0',
                  color: '#fff',
                  '&:hover': { bgcolor: darkMode ? '#651fff' : '#3f51b5' },
                }}
              >
                Oluştur
              </Button>
            </Box>

            {/* Mevcut gruplar */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {techGroupLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
              ) : techGroups.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: darkMode ? '#666' : '#bbb' }}>
                  <FolderIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>Henüz grup oluşturulmamış</Typography>
                  <Typography sx={{ fontSize: '0.72rem', mt: 0.5 }}>Yukarıdan yeni bir grup oluşturabilirsiniz</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {techGroups.map((group) => {
                    const isEditing = techGroupEditing?.Id === group.Id;
                    const groupTechsInView = nonBucketTechs.filter(t => group.TechCodes.includes(String(t.TechCode)));
                    const availableTechs = nonBucketTechs.filter(t => !group.TechCodes.includes(String(t.TechCode)));
                    return (
                      <Box key={group.Id} sx={{
                        borderRadius: 2, overflow: 'hidden',
                        border: `1px solid ${isEditing ? (darkMode ? alpha('#b388ff', 0.4) : alpha('#5c6bc0', 0.3)) : (darkMode ? alpha('#fff', 0.08) : alpha('#000', 0.08))}`,
                        bgcolor: darkMode ? alpha('#fff', 0.02) : '#fff',
                        transition: 'border-color 0.2s',
                      }}>
                        {/* Grup başlığı */}
                        <Box sx={{
                          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                          bgcolor: darkMode ? alpha('#b388ff', 0.06) : alpha('#5c6bc0', 0.04),
                          borderBottom: `1px solid ${darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06)}`,
                        }}>
                          <FolderIcon sx={{ fontSize: 18, color: darkMode ? '#b388ff' : '#5c6bc0' }} />
                          {isEditing ? (
                            <TextField
                              size="small"
                              value={techGroupEditing.GroupName}
                              onChange={(e) => setTechGroupEditing(p => ({ ...p, GroupName: e.target.value }))}
                              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem', height: 32 } }}
                              autoFocus
                            />
                          ) : (
                            <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 700, color: darkMode ? '#e0e0e0' : '#333' }}>
                              {group.GroupName}
                            </Typography>
                          )}
                          <Chip label={`${group.TechCodes.length}`} size="small" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha(darkMode ? '#b388ff' : '#5c6bc0', 0.15), color: darkMode ? '#b388ff' : '#5c6bc0' }} />
                          {isEditing ? (
                            <>
                              <IconButton size="small" disabled={techGroupSaving}
                                onClick={async () => {
                                  await saveTechGroup(techGroupEditing.GroupName, techGroupEditing.TechCodes, techGroupEditing.Id);
                                  setTechGroupEditing(null);
                                }}
                                sx={{ color: '#4caf50' }}
                              >
                                {techGroupSaving ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                              <IconButton size="small" onClick={() => setTechGroupEditing(null)} sx={{ color: darkMode ? '#888' : '#999' }}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Düzenle" arrow>
                                <IconButton size="small"
                                  onClick={() => setTechGroupEditing({ ...group })}
                                  sx={{ color: darkMode ? '#888' : '#999', '&:hover': { color: darkMode ? '#b388ff' : '#5c6bc0' } }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Grubu Sil" arrow>
                                <IconButton size="small"
                                  onClick={() => { if (window.confirm(`"${group.GroupName}" grubunu silmek istediğinize emin misiniz?`)) deleteTechGroup(group.Id); }}
                                  sx={{ color: darkMode ? '#888' : '#999', '&:hover': { color: '#f44336' } }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>

                        {/* Gruptaki teknisyenler */}
                        <Box sx={{ p: 1 }}>
                          {isEditing && (
                            <Box sx={{ mb: 1 }}>
                              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: darkMode ? '#888' : '#999', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                Teknisyen Ekle
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 120, overflow: 'auto' }}>
                                {availableTechs.map(t => (
                                  <Chip
                                    key={t.TechCode}
                                    label={toTitleCase(t.TechName)}
                                    size="small"
                                    clickable
                                    onClick={() => setTechGroupEditing(p => ({ ...p, TechCodes: [...p.TechCodes, String(t.TechCode)] }))}
                                    icon={<AddCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
                                    sx={{
                                      height: 24, fontSize: '0.68rem', fontWeight: 600,
                                      bgcolor: darkMode ? alpha('#4caf50', 0.08) : alpha('#4caf50', 0.06),
                                      border: `1px solid ${alpha('#4caf50', 0.2)}`,
                                      color: darkMode ? '#81c784' : '#2e7d32',
                                      '&:hover': { bgcolor: alpha('#4caf50', 0.15) },
                                    }}
                                  />
                                ))}
                                {availableTechs.length === 0 && (
                                  <Typography sx={{ fontSize: '0.68rem', color: darkMode ? '#666' : '#bbb', fontStyle: 'italic' }}>Tüm teknisyenler grupta</Typography>
                                )}
                              </Box>
                              <Divider sx={{ my: 1, borderColor: darkMode ? alpha('#fff', 0.06) : alpha('#000', 0.06) }} />
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(isEditing ? nonBucketTechs.filter(t => techGroupEditing.TechCodes.includes(String(t.TechCode))) : groupTechsInView).map(t => (
                              <Chip
                                key={t.TechCode}
                                label={toTitleCase(t.TechName)}
                                size="small"
                                onDelete={isEditing ? () => setTechGroupEditing(p => ({ ...p, TechCodes: p.TechCodes.filter(c => c !== String(t.TechCode)) })) : undefined}
                                deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                                sx={{
                                  height: 24, fontSize: '0.68rem', fontWeight: 600,
                                  bgcolor: darkMode ? alpha('#90caf9', 0.08) : alpha('#1565c0', 0.06),
                                  border: `1px solid ${darkMode ? alpha('#90caf9', 0.2) : alpha('#1565c0', 0.15)}`,
                                  color: darkMode ? '#90caf9' : '#1565c0',
                                }}
                              />
                            ))}
                            {group.TechCodes.length === 0 && !isEditing && (
                              <Typography sx={{ fontSize: '0.68rem', color: darkMode ? '#666' : '#bbb', fontStyle: 'italic', py: 0.5 }}>Grup boş — düzenleyerek teknisyen ekleyin</Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Bildirim Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration ?? 5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1500 }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          elevation={6}
          sx={{
            minWidth: { xs: 'calc(100vw - 24px)', sm: 360 },
            maxWidth: { xs: 'calc(100vw - 24px)', sm: 760 },
            borderRadius: 2,
            fontWeight: 600,
            fontSize: '0.85rem',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            alignItems: 'center',
            '& .MuiAlert-message': {
              lineHeight: 1.5,
              overflowWrap: 'anywhere',
              whiteSpace: 'normal',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

ToaScheduling.propTypes = {
  headerControls: PropTypes.node,
  selectedServiceShop: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired
};
